/**
 * PEGASUS FIELD — M1b redesign (Seth 2026-08-26 critique, applied):
 *
 * - ONE CALL AT A TIME. Today is the current call as a hero card (window color
 *   matches the board, address big, NO clock times — window only). Done stops
 *   collapse to a count; later stops stay hidden until you wrap ("3 more after
 *   this"). Admins in View-As get a peek toggle (office eyes only).
 * - Flags left the nav. Part Needed / Return Visit live INSIDE the call, where
 *   the decision actually happens. "Stuff I've flagged" moved under More.
 * - PARTS FOR THE DAY on Today — the same brain as the office's Truck-Parts
 *   report (filters/APR codes/pads mined from each call's notes), scanning
 *   THIS tech's whole day, live as the day changes; a banner announces when
 *   dispatch moves the day under them.
 * - PART PICKUPS are first-class calls: ESC's "Pick Up Part" tickets (pseudo-
 *   customer 0079690) and Pegasus pickups render as an amber PICKUP card with
 *   the where/PO front and center — run it like a stop, "Picked up ✓" wraps it.
 * - The CALL WORKSPACE: opening a call auto-checks your location — standing at
 *   the address logs Arrived by itself (the geofence clock's little brother;
 *   the closed-app version arrives with the native shell). Tabs: Job (the
 *   guided work + flags + wrap), Customer (member plan, gate/dog/lockbox
 *   notes, phones), Equipment, History (last visits + what they cost).
 * - Softer, bigger, more app-like design pass throughout.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  BadgeDollarSign,
  SquarePen,
  FolderOpen,
  Palmtree,
  Clock,
  ChevronLeft,
  Thermometer,
  CheckCircle2,
  Trash2,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  Home,
  AlertTriangle,
  ClipboardList,
  FileText,
  ImagePlus,
  Loader2,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  Minus,
  PenLine,
  Navigation,
  Package,
  PackageCheck,
  Phone,
  Plus,
  RotateCcw,
  Send,
  ShieldCheck,
  Snowflake,
  Sparkles,
  Sun,
  Truck,
  User,
  UserPlus,
  Search,
  Moon,
  Warehouse,
  Wrench,
  X,
  BellRing,
  Paperclip,
  Video,
  Monitor,
  LogOut,
  Trophy,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useItemCategories, memberEligibleSubtotal, memberDiscountByRule } from "@/lib/memberPricing";
import { useMediaQuery, TABLET_QUERY } from "@/lib/useMediaQuery";
import { useTechTheme, type ThemePref } from "./theme";
import { haptic } from "@/native/boot";
import { pagedAll } from "@/lib/pagedAll";
import { useOrgSettingNumber, useOrgSettingJson } from "@/lib/orgSettings";
import { computeSpan, AVAIL_KIND_LABEL, type AvailKind } from "@/features/board/availability";
import { displayCustomerName } from "@/lib/format";
import { usableCoords, driveMinutes } from "@/lib/route";
import { milesBetween } from "@/lib/batch";
import { windowColors } from "@/lib/window";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { useCurrentUser } from "@/lib/currentUser";
import { useCan, usePermissions } from "@/lib/permissions/PermissionProvider";
import type { Dispatch } from "@/lib/types";
import { TechAvatar } from "@/components/TechAvatar";
import { useBoardDispatches, useAllActiveTechs, isPseudoDispatch, timeBand } from "@/features/board/api";
import { useOncallNow, useMyOncallDays, useOncallHours, oncallPhase, oncallDateFor, blocksOf, blockLabel, techFirstName, fmtShort, fmtWeekday as fmtOncallDay, useOncallDaysPublished, myOncallDaysIn, useOncallSwapRequests, useRequestOncallSwap, useServiceTechs, useOncallPeriods, fmtMonth as fmtOncallMonth, monthStartISO, monthEndISO, addMonthsISO, addDaysISO, parseISO as parseOncallISO, isWeekendISO, oncallBlockICS, icsHref, daysUntil, useOncallPhoneRules, oncallClassOf, heldByTech, ONCALL_CLASS_OPTIONS, type OncallClass } from "@/features/team/oncall";
import { useOncallContacts } from "@/features/team/OncallPrintView";
import { useScheduledForBoard, useAddTicket, PART_STATE_LABEL, oncallCallerWords, type PendingTicket, type PartState } from "@/features/board/queue";
import { useWarehouseQueue } from "@/features/jobs/WarehouseView";
import { useMaterialRequestQueue } from "@/features/jobs/materialRequests";
// WH-RESTOCK-02: "Count Your Truck" — shows only when something's due.
import { TruckCountCard } from "@/features/jobs/CountsHub";
// TECH-EXTRAS: the Yesterday recap + More→Scoreboard; LIQUID-GLASS touch-tilt.
import { YesterdayCard, ScorePane, MyProposalsPane, OfflineChip, OfflineDayNote } from "./TechExtras";
import { useTouchTilt } from "./tilt";
import { useMembershipCard, useCustomerAgreementRenewal } from "@/features/agreements/api";
import {
  useItemSearch, useSuggestParts, useMemberDiscount, useAcceptedServiceQuote,
  useSaveServiceInvoice, useDispatchDraftInvoice, useCustomerEmail, invoiceTotal,
  saveInvoiceRpc, buildSaveLines, isInvoiceConflict, saverLabel,
  type BuilderLine, type PartSuggestion, type ItemHit, type DraftInvoice,
} from "@/features/invoice/officeInvoice";
import { useTechRates } from "@/features/board/api";
import { useEnqueueOutbox } from "@/features/comms/comms";
import { buildInvoiceEmailHtml } from "@/features/comms/SendInvoiceEmailModal";
// PROPOSAL-FLOW-02: presenting stamps, sending marks — the same two calls the office uses.
import { stampProposalPresented, markProposalSent } from "@/features/quotes/proposalSend";
import { tuneupType, type TruckPart } from "@/features/parts/truckParts";
import { usePickupDay, useStampPickupItem, visibleItems, openItems, nextPickupDay, type PickupItem } from "@/features/parts/pickupList";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { NotesEditor } from "@/components/NotesEditor";
import { JobVisitWork, InstallerDayCards, isJobWorkVisit } from "./jobVisit";
import { UnitLines } from "./UnitLines";
import { CashTurnInCards } from "./installWrap";
import { SignaturePad } from "./SignaturePad";
import {
  uploadFilesTo, useDispatchFiles, useCustomerFiles, useSignedUrls, prettySize, fileOrigin, fileWho, fileWhen, type PegasusFile, type FileOrigin,
} from "@/features/files/filesCore";
import { usePlanPriceCatalog, useSellPlan, repairPctForTerm } from "@/features/agreements/planPricing";
import { usePlanDiscounts, useFeeItems } from "@/features/customer-360/serviceTypes";
import { useCustomerPlanOffers, offerState, offerMoney, type PlanOfferRow } from "@/features/agreements/planOffers";
import { planCoversFor, planSystemsCount, planSystemsMultiplier, planCoversSummary, makeFromMfg, type PlanOfferCover } from "@/lib/planCovers";
import { UnitPickRow } from "@/components/UnitPickRow";
import { PlanBuilderModal, usePlanCatalog } from "@/features/agreements/PlanBuilder";
import { QUOTE_BRANDS } from "@/features/quotes/quotes";
import { useUtilities, useQuotePackages, pkgTotal, type QuotePackage } from "@/features/quotes/quotesAdmin";
import { InstallPacket } from "./SalePacket";
import { useEquipmentItems, useAccessorySubcategories, isSyntheticPart, type Item } from "@/features/items/items";
import { buildEquipmentView, categoryLabelFor, installDateInfo } from "@/lib/equipment";
import { useCustomerEquipment, useAiEquipmentOverrides } from "@/features/customer-360/CustomerEquipment";
import { useEquipmentOverrides } from "@/features/customer-360/equipmentOverrides";
import {
  useFieldStatusMap, useSetFieldState, useSendOtwText, useRosterGroup,
  useChatPeople, useMyDms, useSendDm, useMarkRead, useDmHides, useHideThread, plainBody, low,
  useLocationNote, useVisitHistory, useHouseVisitHistory, getPosition,
  useCustomerContacts, useSetWorkedEquipment,
  type FieldStatusRow, type Dm, type WorkedUnit, type ChatPerson, type VisitHistoryRow,
} from "./field";
// PROPERTY-LINK-01: the house's other accounts (builder / previous owner) — history, equipment, the header line.
import { useHouseAccounts, useHouseEquipment, useHouseTicketCheck, houseEraLabel, relationWord, paidByLabel, type HouseAccount } from "@/features/customer-360/houseHistory";
import { StreetViewThumb } from "@/features/customer-360/CustomerAvatar";
import { CopyCode } from "@/components/Copyable";
import { useVisitEquipment, visitEquipmentFor } from "@/features/customer-360/visitEquipment";
import { VisitEquipmentBlock } from "@/features/customer-360/VisitEquipmentBlock";
import { AddressAutocomplete } from "@/features/customer-360/AddressAutocomplete";
import { useCustomerMetrics } from "@/features/customer-360/metrics";
import { useCustomerFollowups, useTechFollowups, useAddFollowup, useCompleteFollowup, useSnoozeFollowup, useAssignFollowup, dueLabel, daysFromNowISO, monthsFromNowISO, nextSeasonISO, rollToWeekday, type FollowUp } from "@/features/followups/followups";
import { useLocationNotesSummary } from "@/features/customer-360/useLocationNotesSummary";
import { useWatchouts, stripStars, type WatchItem, type WatchSeverity } from "@/features/customer-360/watchouts";
import { InfoTip } from "@/components/InfoTip";

/* ------------------------------------------------------------------ utils */

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const dayLabel = () =>
  new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

/** One unified stop — ESC or Pegasus-native. */
interface Stop {
  key: string;
  kind: "esc" | "pegasus";
  ref: string;
  /** The number the office says out loud — ESC dispatch #, or the Pegasus
   *  dispatch # once numbering goes live (short ref meanwhile). */
  no: string;
  band: number;
  time: string | null; // kept ONLY for ordering — never shown to techs
  window: string | null;
  status: string | null;
  name: string;
  custno: string | null;
  locno: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  lat: number | null;
  lng: number | null;
  notes: string | null;
  /** What the office quoted at booking (quoted_amount, else the resolved
   *  service-type fee) — the tech must never be blind to the promised price. */
  quoted: number | null;
  payStatus: "prepaid" | "cod" | null;
  serviceType: string | null;
  serviceTypeId: string | null;
  /** PO-01: PO / job number booked on the dispatch (customer-supplied or office). */
  po: string | null;
  pseudo: boolean;
  pickup: boolean;
  done: boolean;
  /** ONCALL-HUB: true = the TECH booked this call himself (source tech_mobile)
   *  — self-built days show in full; office days stay one-at-a-time. */
  selfBooked: boolean;
  /** PARTS-EQUIP-01: a booked part ticket IS the return visit — the part, its
   *  PO / state, and the unit it's for ride along so the phone pre-marks the
   *  unit and the tech never re-asks which one. */
  part: StopPart | null;
  /** INSTALL-PATH-01: the job this visit belongs to, and whether the visit IS
   *  job work (install / new construction) — those never run the service
   *  invoice; they get the job screen (scope, documents, permit, wrap up). */
  jobId: string | null;
  visitRole: string | null;
  jobVisit: boolean;
  /** TEST-FLAG-01: a test visit — never texts the customer. */
  test?: boolean;
}
interface StopPart { summary: string; partNo: string | null; po: string | null; state: PartState | null; unit: WorkedUnit | null; unitText: string | null }

/** PartEquipment (ticket / PO shape) → the worked-on key the Equipment step uses. */
function unitFromPartEquipment(e: PendingTicket["part_equipment"]): WorkedUnit | null {
  if (!e) return null;
  const label = [e.mfg, e.model].filter(Boolean).join(" ") || e.eq_type || "Unit";
  const counter = e.counter ?? "";
  if (!counter) return null;
  return { k: e.src === "peg" || counter.startsWith("peg:") ? `peg-${counter}` : `esc-${counter}`, label };
}

const DONE_RE = /^(c|comp|complete|completed|done|x)$/i;
const PICKUP_RE = /pick\s*up\s*part|part\s*pick\s*up/i;

/** Phone numbers written INTO the call notes ("call Jeff 507-459-0346 on way").
 *  Dispatch's instruction beats the account's stored number — the note number
 *  is who actually asked to be called (Seth 2026-08-26: Andrew's call dialed
 *  the wrong person). */
/** PO-01: a PO/job number the office (or ESC) typed into the notes —
 *  "PO 48211", "Job #1042". Prefill only; the booked field wins. Requires a
 *  digit in the token so "job today" never matches. */
function minePo(notes: string | null): string | null {
  const m = (notes ?? "").match(/\b(?:P\.?O\.?|JOB)\s*#?\s*[:\-]?\s*((?=[A-Za-z0-9\-\/]*\d)[A-Za-z0-9\-\/]{2,16})\b/i);
  return m ? m[1] : null;
}

const NOTE_PHONE_RE = /\(?\b(\d{3})\)?[\s.\-]?(\d{3})[\s.\-]?(\d{4})\b/g;
interface CallPhone { n: string; label: string }
function phonesFor(s: Stop): { primary: CallPhone | null; all: CallPhone[] } {
  const seen = new Set<string>();
  const all: CallPhone[] = [];
  for (const m of (s.notes ?? "").matchAll(NOTE_PHONE_RE)) {
    const digits = `${m[1]}${m[2]}${m[3]}`;
    if (seen.has(digits)) continue;
    seen.add(digits);
    all.push({ n: `${m[1]}-${m[2]}-${m[3]}`, label: "from the call notes" });
  }
  const rec = (s.phone ?? "").replace(/\D/g, "").slice(-10);
  if (rec.length === 10 && !seen.has(rec)) {
    all.push({ n: `${rec.slice(0, 3)}-${rec.slice(3, 6)}-${rec.slice(6)}`, label: "on the customer record" });
  }
  return { primary: all[0] ?? null, all };
}

function escToStop(d: Dispatch): Stop {
  const name = displayCustomerName(d.locname, d.customer_fullname) || d.custno;
  const pickup = d.custno === "0079690" || PICKUP_RE.test(name);
  return {
    key: `esc:${d.dispatch}:${d.tech_empno}`,
    kind: "esc",
    ref: d.dispatch,
    no: d.dispatch,
    band: timeBand(d),
    time: d.tpromtime,
    window: d.window_code,
    status: d.status,
    name: pickup ? "Part Pickup" : name,
    custno: d.custno,
    locno: d.locno,
    address: d.service_address,
    city: d.city,
    zip: d.zip,
    phone: d.customer_phone,
    lat: usableCoords(d.loc_latitude, d.loc_longitude) ? d.loc_latitude : null,
    lng: usableCoords(d.loc_latitude, d.loc_longitude) ? d.loc_longitude : null,
    notes: d.notes,
    quoted: null,
    payStatus: null,
    serviceType: null,
    serviceTypeId: null,
    po: null,
    pseudo: isPseudoDispatch(d) && !pickup,
    selfBooked: false,
    pickup,
    done: DONE_RE.test((d.status ?? "").trim()),
    part: null,
    jobId: null,
    visitRole: null,
    jobVisit: false,
  };
}

function pegToStop(t: PendingTicket): Stop {
  const band = (() => {
    const w = (t.scheduled_window ?? "").trim();
    const m = /^(\d{1,2})/.exec(w);
    if (m) {
      let h = parseInt(m[1], 10);
      if (h >= 1 && h <= 7) h += 12;
      return h;
    }
    const tm = /^(\d{2}):/.exec(t.scheduled_time ?? "");
    return tm ? parseInt(tm[1], 10) : 50;
  })();
  const pickup = PICKUP_RE.test(t.summary ?? "");
  return {
    key: `peg:${t.id}`,
    kind: "pegasus",
    ref: t.id,
    no: t.dispatch_no != null ? String(t.dispatch_no) : t.id.slice(0, 6).toUpperCase(),
    band,
    time: t.scheduled_time,
    window: t.scheduled_window,
    status: t.status,
    name: pickup ? "Part Pickup" : displayCustomerName(null, t.customer_name) || t.customer_name,
    custno: t.custno,
    locno: t.locno,
    address: t.address,
    city: t.city,
    zip: null,
    phone: t.phone,
    lat: t.lat,
    lng: t.lng,
    notes: [t.summary, t.notes].filter(Boolean).join(" — "),
    quoted: (Number(t.quoted_amount) || 0) > 0 ? Number(t.quoted_amount) : (Number(t.diag_fee) || 0) > 0 ? Number(t.diag_fee) : null,
    payStatus: t.payment_status ?? null,
    serviceType: t.service_type_name ?? null,
    serviceTypeId: t.service_type_id ?? null,
    po: t.po_number ?? null,
    selfBooked: t.source === "tech_mobile" && t.lane_key !== "order_parts" && t.lane_key !== "return_visit",
    pseudo: false,
    pickup,
    done: t.status === "completed",
    part: t.lane_key === "order_parts" && !pickup
      ? (() => {
          const unit = unitFromPartEquipment(t.part_equipment);
          const e = t.part_equipment;
          return { summary: t.summary, partNo: t.part_number ?? null, po: t.part_po ?? null, state: t.part_state ?? null, unit,
            unitText: e ? `${[e.mfg, e.model].filter(Boolean).join(" ") || e.eq_type || "unit on file"}${e.serial ? ` · S/N ${e.serial}` : ""}` : null };
        })()
      : null,
    jobId: t.job_id ?? null,
    visitRole: t.visit_role ?? null,
    jobVisit: isJobWorkVisit(t),
    test: !!t.is_test,
  };
}

interface EquipRow {
  counter: string;
  mfg: string | null;
  model: string | null;
  serial: string | null;
  eq_type: string | null;
  equip_loc: string | null;
  install_date: string | null;
  /** 'esc' = the ESC mirror; 'peg' = added in the field / by an install job. */
  src?: "esc" | "peg";
}
function useEquipmentAt(custno: string | null, locno: string | null) {
  return useQuery({
    queryKey: ["m_equipment", custno, locno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EquipRow[]> => {
      let q = supabase
        .from("mirror_equipment")
        .select("counter, mfg, model, serial, eq_type, equip_loc, install_date")
        .eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      let p = supabase
        .from("pegasus_equipment")
        .select("id, brand, model, serial, category, equip_loc, install_date, status")
        .eq("custno", custno!);
      if (locno) p = p.eq("locno", locno);
      const [esc, peg] = await Promise.all([
        q.order("install_date", { ascending: false }).limit(12),
        p.order("created_at", { ascending: false }).limit(12),
      ]);
      if (esc.error) throw esc.error;
      const escRows = ((esc.data ?? []) as EquipRow[]).map((r) => ({ ...r, src: "esc" as const }));
      const pegRows = ((peg.data ?? []) as Array<{ id: string; brand: string | null; model: string | null; serial: string | null; category: string | null; equip_loc: string | null; install_date: string | null; status: string | null }>)
        .filter((r) => (r.status ?? "active") === "active")
        .map((r) => ({
          counter: `peg:${r.id}`,
          mfg: r.brand,
          model: r.model,
          serial: r.serial,
          eq_type: r.category,
          equip_loc: r.equip_loc,
          install_date: r.install_date,
          src: "peg" as const,
        }));
      // Field-added first — it's the freshest knowledge of what's actually there.
      return [...pegRows, ...escRows];
    },
  });
}

function useMyFlags(empno: string | null, email: string | null) {
  return useQuery({
    queryKey: ["m_my_flags", empno, email],
    enabled: !!empno || !!email,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const ors: string[] = [];
      if (email) ors.push(`created_by.eq.${email}`);
      if (empno) ors.push(`affinity_empno.eq.${empno}`);
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .in("lane_key", ["order_parts", "return_visit"])
        .is("deleted_at", null)
        .or(ors.join(","))
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

interface UploadedPhoto { path: string; name: string }
async function uploadPartPhoto(file: File): Promise<UploadedPhoto> {
  const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-60) || "photo.jpg";
  const ym = todayISO().slice(0, 7);
  const path = `part-tickets/${ym}/${crypto.randomUUID()}_${safe}`;
  const { error } = await supabase.storage.from("job-media").upload(path, file, {
    contentType: file.type || "image/jpeg",
    upsert: false,
  });
  if (error) throw error;
  return { path, name: safe };
}

/* =============================================================== SHELL == */

type MTab = "today" | "job" | "oncall" | "customers" | "chat" | "more";

export function TechApp() {
  const { signOut } = useAuth();
  const me = useCurrentUser().data;
  // TECH-IDENTITY-BIND (review 2026-11-02 P0 #7): "view as" belongs to the Owner/Admin and to the
  // manager view (mobile.manager — the weekend dispatcher). Everyone else IS their linked employee
  // number and nothing else: a login without one does not get to pick a tech.
  const { isAdmin: realAdmin } = usePermissions();
  const canManage = useCan("mobile.manager");
  const isAdmin = realAdmin || canManage;
  const [tab, setTab] = useState<MTab>("today");

  const [viewAs, setViewAs] = useState<string | null>(() => {
    try { return localStorage.getItem("pegasus.m.viewas"); } catch { return null; }
  });
  const empno = (isAdmin && viewAs) || me?.empno || null;
  const techs = useAllActiveTechs().data ?? [];
  const techLabel = (e: string | null) => {
    if (!e) return null;
    const t = techs.find((x) => x.empno === e);
    return t?.empname ?? (`${t?.firstname ?? ""} ${t?.lastname ?? ""}`.trim() || e);
  };
  const techFirst = (techLabel(empno) ?? me?.firstName ?? "").split(/\s+/)[0] || null;
  const pickViewAs = (e: string | null) => {
    setViewAs(e);
    try {
      if (e) localStorage.setItem("pegasus.m.viewas", e);
      else localStorage.removeItem("pegasus.m.viewas");
    } catch { /* fine */ }
  };

  const date = todayISO();
  const esc = useBoardDispatches(date);
  const peg = useScheduledForBoard(date);
  const fieldMap = useFieldStatusMap(date);
  const rosterGroup = useRosterGroup(empno).data;
  const isWarehouse = rosterGroup === "Warehouse";
  const myEmail = low(me?.email);
  // CHAT-PERM-01: chat is a capability — on for every role, revocable per user.
  const canChat = useCan("chat.use");
  const canManager = useCan("mobile.manager"); // MGR-01
  // CHAT-AS (Seth 2026-08-28): when an ADMIN is in View-As, chat acts as that
  // tech — sends carry the tech's name/address, so the real tech→office look
  // and feel is testable before techs have logins. Non-admins always chat as
  // themselves (RLS enforces the same rule server-side: chat_perm_02).
  const rosterForId = useChatPeople(myEmail).data;
  const viewAsEmail = isAdmin && viewAs ? ((rosterForId ?? []).find((p) => p.empno === viewAs)?.email || null) : null;
  const chatMe = viewAsEmail || myEmail;
  const chatActingAs = viewAsEmail && viewAsEmail !== myEmail ? (techLabel(viewAs) ?? viewAs ?? "tech") : null;
  const dmsRaw = useMyDms(canChat ? chatMe : "").data;
  // While acting as someone, show ONLY threads that involve them — the
  // admin's own conversations must not bleed into the tech's chat list.
  const dms = useMemo(
    () => (dmsRaw ?? []).filter((d) => low(d.from_email) === chatMe || low(d.to_email) === chatMe),
    [dmsRaw, chatMe],
  );
  const unreadChats = canChat ? dms.filter((d) => low(d.to_email) === chatMe && !d.read_at).length : 0;
  // ONCALL-HUB: open service-center requests light the tab for every tech —
  // backup can pick up any device and run the call (Seth 2026-08-28).
  // ONCALL-PHONE-01: the badge counts exactly what the tab lists as waiting
  // (it used to count every open lead, so it sat at 40 — the query's limit).
  const oncallOpen = (useOncallRequests().data ?? []).filter(oncallIsOpen).length;
  // Qualify hand-off: the On-Call pane sends a caller's name/phone here.
  const [custSearchSeed, setCustSearchSeed] = useState<string | null>(null);

  const stops = useMemo(() => {
    if (!empno) return [];
    const a = (esc.data ?? []).filter((d) => d.tech_empno === empno).map(escToStop);
    const b = (peg.data ?? [])
      .filter((t) => t.assigned_empno === empno && t.status !== "cancelled")
      .map(pegToStop);
    return [...a, ...b].sort((x, y) => x.band - y.band || (x.time ?? "").localeCompare(y.time ?? ""));
  }, [esc.data, peg.data, empno]);
  const loading = esc.isLoading || peg.isLoading;

  // OFFLINE-01 (dead zones): keep the composed day on the device; when the
  // network is gone and the queries have nothing, serve the snapshot so the
  // tech still sees WHERE TO GO. Status taps queue via the outbox meanwhile.
  const liveReady = !!esc.data && !!peg.data && !esc.isError && !peg.isError;
  useEffect(() => {
    if (!empno || !liveReady) return;
    try {
      localStorage.setItem(`pegasus.m.day.${empno}`, JSON.stringify({ date, stops }));
    } catch {
      // storage unavailable — fine
    }
  }, [empno, liveReady, date, stops]);
  const offlineDay = !liveReady && (esc.isError || peg.isError) && stops.length === 0;
  const stopsShown = useMemo(() => {
    if (!offlineDay || !empno) return stops;
    try {
      const s = JSON.parse(localStorage.getItem(`pegasus.m.day.${empno}`) ?? "null") as { date?: string; stops?: Stop[] } | null;
      if (s?.date === date && Array.isArray(s.stops)) return s.stops;
    } catch {
      // fine
    }
    return stops;
  }, [offlineDay, stops, empno, date]);

  // One-at-a-time queue math. "Done" = ESC completed OR wrapped from the phone.
  // Deliberately NO totals or progress anywhere (Seth: "I don't want the techs
  // to even know the total calls for the day") — the queue is internal only.
  const workable = useMemo(() => stopsShown.filter((s) => !s.pseudo), [stopsShown]);
  const isDoneStop = (s: Stop) => s.done || fieldMap.data?.get(s.ref)?.state === "wrapped";
  const current = workable.find((s) => !isDoneStop(s)) ?? null;

  // "Your day changed" banner — the stop set shifted under them (call added,
  // moved, or a pickup dropped in).
  const dayHash = workable.map((s) => s.ref).join("|");
  const prevHash = useRef<string | null>(null);
  const [dayChanged, setDayChanged] = useState(false);
  useEffect(() => {
    if (loading) return;
    if (prevHash.current !== null && prevHash.current !== dayHash) setDayChanged(true);
    prevHash.current = dayHash;
  }, [dayHash, loading]);

  const [openCall, setOpenCall] = useState<Stop | null>(null);
  const openCurrent = openCall ? (workable.find((s) => s.key === openCall.key) ?? openCall) : null;
  const [partsOpen, setPartsOpen] = useState(false);
  const [partsDay, setPartsDay] = useState<"today" | "tomorrow">("today"); // PARTS-PICKUP-03
  // PARTS-PICKUP-03 (Seth: "it adjusts and adds in real time as calls are added
  // or removed correct?"): the day and the parts list refresh within ~2 s of a
  // change landing in Pegasus — ESC's sync, a Pegasus booking, an office edit
  // to the list — instead of the 60 s poll. (Tables must be in the realtime
  // publication; the pegasus_* ones were added 2026-09-15.)
  useRealtimeRefresh(
    ["mirror_disptech", "mirror_dispatch", "pegasus_dispatch", "pegasus_pickup_item", "pegasus_dispatch_part", "pegasus_field_status"],
    [["board_dispatches"], ["scheduled_board"], ["parts_pickup"], ["field_status"]],
  );
  const [flagsOpen, setFlagsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);   // LIB-02: Library + Ask Pegasus
  const [calOpen, setCalOpen] = useState(false);     // LIB-02b: Install Calendar
  const [oncallCalOpen, setOncallCalOpen] = useState(false); // ONCALL-SCHED-01d: the on-call calendar (More)
  const [timeOpen, setTimeOpen] = useState(false);   // TIME-01: My Timesheet & Time Off
  const [scoreOpen, setScoreOpen] = useState(false); // TECH-EXTRAS: More → Scoreboard
  const [proposalsOpen, setProposalsOpen] = useState(false); // QUOTE-PKG-01: More → My Proposals
  const [mgrOpen, setMgrOpen] = useState(false);     // MGR-01: whole-board manager view
  const [peek, setPeek] = useState(false); // admin View-As full-day peek
  const [wizard, setWizard] = useState<{ stop: Stop; kind: FlagKind } | null>(null);
  const flags = useMyFlags(empno, me?.email ?? null);

  const needsIdentity = !me?.empno && !(isAdmin && viewAs);
  // A tech whose phone remembered a pick from before the bind: forget it.
  useEffect(() => {
    if (!isAdmin && viewAs) {
      setViewAs(null);
      try { localStorage.removeItem("pegasus.m.viewas"); } catch { /* ignore */ }
    }
  }, [isAdmin, viewAs]);

  // IPAD-01: on a tablet the call opens on the RIGHT and the day stays on the
  // left, so the left column keeps showing Today while the "job" tab is up.
  const tablet = useMediaQuery(TABLET_QUERY);
  const leftTab = tablet && tab === "job" ? "today" : tab;

  // MOBILE-LOOK-01 "Glass": light or dark follows the device (or More →
  // Appearance); the large title collapses into a compact glass bar on scroll.
  const theme = useTechTheme();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled((document.scrollingElement?.scrollTop ?? window.scrollY) > 28);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const pickTab = (t: MTab) => { if (t !== tab) haptic("light"); setTab(t); };
  const screenTitle =
    leftTab === "today" ? "Today" : leftTab === "oncall" ? "On-Call" : leftTab === "customers" ? "Customers" : leftTab === "chat" ? "Chat" : leftTab === "more" ? "More" : "Job";

  return (
    // MOBILE-LOOK-01 "Glass" (Seth 2026-09-22): the tech app in the iPad's own
    // design language — no colored header, one large title, a floating glass
    // tab bar, depth from light, one accent, a real dark palette. System font
    // stack = REAL SF Pro on iPhone/iPad. The tokens + skin layer live in
    // index.css under [data-tech-app].
    <div
      data-tech-app=""
      data-theme={theme.dark ? "dark" : "light"}
      className="min-h-dvh bg-[#eae7e1] antialiased [-webkit-tap-highlight-color:transparent]"
      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}
    >
      {/* IPAD-01: the right pane's resting state on a tablet — the panes
          (call, parts, calendars) sit above it when they open. */}
      <div className="fixed inset-y-0 left-[26rem] right-0 z-[5] hidden flex-col items-center justify-center bg-[#eae7e1] px-8 text-center md:flex" aria-hidden>
        <span className="m-mark !h-14 !w-14 opacity-20" />
        <p className="mt-3 text-[15px] font-semibold text-slate-500">Open a call and it lands here.</p>
      </div>
      <div className="relative mx-auto flex min-h-dvh max-w-md flex-col bg-[#f6f5f1]">
        {/* Header (Glass) — the mark and the avatar sit quietly on the wash;
            the greeting IS the title of the screen. Once you scroll, the big
            title folds away and a compact glass bar carries the screen name. */}
        <header className={cn("m-head px-4 pb-3 pt-[max(env(safe-area-inset-top),14px)]", scrolled && "is-scrolled")}>
          <div className="relative flex h-9 items-center justify-between">
            <span className="m-mark" aria-label="Pegasus" role="img" />
            <span className="m-head-small">{screenTitle}</span>
            {/* OFFLINE-01: the truth chip — amber when no signal, count = queued. */}
            <span className="absolute right-12 top-1/2 -translate-y-1/2"><OfflineChip /></span>
            <span className="shrink-0 rounded-full ring-2 ring-[var(--m-surface)]">
              {empno ? (
                <TechAvatar empno={empno} name={techLabel(empno) ?? techFirst ?? "Tech"} size="md" />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--m-tint)] text-sm font-bold text-[var(--m-tint-ink)]">P</span>
              )}
            </span>
          </div>
          <div className="m-head-big mt-3">
            <div className="m-eyebrow">{dayLabel()}</div>
            <h1 className="m-lt mt-0.5 whitespace-nowrap">
              {leftTab === "today" ? <>{greeting()}{techFirst ? `, ${techFirst}` : ""}</> : screenTitle}
            </h1>
          </div>
          {isAdmin && viewAs && viewAs !== me?.empno && (
            <div className="mt-2 flex items-center justify-between rounded-xl bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
              Viewing as {techLabel(viewAs)}
              <button type="button" onClick={() => pickViewAs(null)} className="rounded p-0.5 hover:bg-amber-100" aria-label="Stop viewing as">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </header>

        {/* Body */}
        <main key={leftTab} className="m-enter min-h-0 flex-1 overflow-y-auto px-3 pb-32 pt-1">
          {leftTab === "today" && (
            needsIdentity ? (
              isAdmin
                ? <IdentityPicker techs={techs.map((t) => ({ empno: t.empno, label: techLabel(t.empno) ?? t.empno }))} onPick={pickViewAs} />
                : <IdentityLocked />
            ) : isWarehouse ? (
              <WarehouseToday />
            ) : loading ? (
              <div className="space-y-3">
                <div className="h-52 animate-pulse rounded-3xl bg-slate-200/70" />
                <div className="h-16 animate-pulse rounded-3xl bg-slate-200/60" />
              </div>
            ) : (
              <>
              {offlineDay && <OfflineDayNote />}
              <TodayTech
                empno={empno}
                stops={stopsShown}
                workable={workable}
                current={current}
                fieldMap={fieldMap.data}
                dayChanged={dayChanged}
                onDismissChange={() => setDayChanged(false)}
                canPeek={isAdmin && !!viewAs}
                peek={peek}
                onTogglePeek={() => setPeek((v) => !v)}
                onOpen={(s) => { setOpenCall(s); setTab("job"); }}
                onParts={(which) => { setPartsDay(which); setPartsOpen(true); }}
                onOncall={() => setOncallCalOpen(true)}
                installer={rosterGroup === "Install"}
                nameOf={techLabel}
              />
                {/* WH-RESTOCK-02: truck cycle counts — due items only, else nothing. */}
                <TruckCountCard empno={empno} />
                {/* TECH-EXTRAS: yesterday's true numbers — facts, no lectures. */}
                <YesterdayCard empno={empno} />
              </>
            )
          )}

          {leftTab === "oncall" && (
            <OncallPane
              empno={empno}
              techFirst={techFirst}
              onQualify={(term) => { setCustSearchSeed(term); setTab("customers"); }}
              onOpenCalendar={() => setOncallCalOpen(true)}
            />
          )}

          {leftTab === "customers" && (
            <CustomersPane empno={empno} techFirst={techFirst} seed={custSearchSeed} onSeedUsed={() => setCustSearchSeed(null)} />
          )}

          {leftTab === "chat" && (canChat
            ? <ChatPane me={chatMe} dms={dms} currentStop={openCall} actingAs={chatActingAs} empno={empno} techFirst={techFirst} />
            : <EmptyCard Icon={MessageCircle} title="Chat is turned off for your login" sub="Ask the office to turn it on." />)}

          {leftTab === "more" && (
            <MorePane
              isAdmin={isAdmin}
              canManager={canManager}
              myEmpno={empno}
              myName={techLabel(me?.empno ?? null) ?? me?.firstName ?? null}
              roleLine={rosterGroup ? `${rosterGroup} crew` : "Pegasus Field · M1"}
              viewAs={viewAs}
              techs={techs.map((t) => ({ empno: t.empno, label: techLabel(t.empno) ?? t.empno }))}
              openFlagCount={(flags.data ?? []).filter((t) => t.status === "pending_schedule").length}
              onFlags={() => setFlagsOpen(true)}
              onDocs={() => setDocsOpen(true)}
              onCal={() => setCalOpen(true)}
              onOncall={() => setOncallCalOpen(true)}
              onTime={() => setTimeOpen(true)}
              onScore={() => setScoreOpen(true)}
              onProposals={() => setProposalsOpen(true)}
              onMgr={() => setMgrOpen(true)}
              onPick={pickViewAs}
              onSignOut={() => void signOut()}
              themePref={theme.pref}
              onTheme={theme.setPref}
            />
          )}
        </main>

        {/* Bottom nav — Today · On-Call · Customers · Chat · More.
            (Chat slot drops out entirely when the capability is revoked.)
            Glass: a floating capsule over the content; the selected tab sits
            in a soft blue lens; badges stay red. */}
        <nav className={cn("m-tabbar m-glass", canChat ? "grid-cols-5" : "grid-cols-4")} aria-label="Pegasus">
          {openCall && tab !== "job" ? (
            <NavBtn label="Job" Icon={Wrench} on={false} onClick={() => pickTab("job")} />
          ) : openCall && tab === "job" ? (
            <NavBtn label="Today" Icon={Sun} on={tablet} onClick={() => pickTab("today")} />
          ) : (
            <NavBtn label="Today" Icon={Sun} on={tab === "today"} onClick={() => pickTab("today")} />
          )}
          <NavBtn label="On-Call" Icon={Moon} on={tab === "oncall"} onClick={() => pickTab("oncall")} badge={oncallOpen || undefined} />
          <NavBtn label="Customers" Icon={Search} on={tab === "customers"} onClick={() => pickTab("customers")} />
          {canChat && <NavBtn label="Chat" Icon={MessageCircle} on={tab === "chat"} onClick={() => pickTab("chat")} badge={unreadChats || undefined} />}
          <NavBtn label="More" Icon={User} on={tab === "more"} onClick={() => pickTab("more")} />
        </nav>

        {/* The call workspace */}
        {/* APP-CHROME (Seth 2026-08-28): the call is a TAB, not a lid — the
            bottom nav stays live, so mid-call the tech can hop to Chat and
            back without losing a keystroke (everything stashes locally). */}
        {tab === "job" && openCurrent && (
          <CallScreen
            s={openCurrent}
            field={fieldMap.data?.get(openCurrent.ref)}
            fieldReady={fieldMap.isFetched}
            empno={empno}
            techFirst={techFirst}
            nextStop={workable.find((x) => x.key !== openCurrent.key && !isDoneStop(x)) ?? null}
            onClose={() => {
              // Back = MINIMIZE (the Job slot keeps the call alive); a wrapped
              // call closes for real.
              const done2 = openCurrent.done || fieldMap.data?.get(openCurrent.ref)?.state === "wrapped";
              if (done2) setOpenCall(null);
              setTab("today");
            }}
            onFlag={(kind) => setWizard({ stop: openCurrent, kind })}
          />
        )}

        {/* Parts for the day */}
        {partsOpen && <PartsDaySheet empno={empno} today={todayISO()} initial={partsDay} stops={workable} isDone={isDoneStop} onClose={() => setPartsOpen(false)} />}

        {/* Stuff I've flagged (under More) */}
        {docsOpen && <DocsPane onClose={() => setDocsOpen(false)} />}
        {calOpen && <InstallCalPane onClose={() => setCalOpen(false)} />}
        {oncallCalOpen && <OncallCalPane empno={empno} onClose={() => setOncallCalOpen(false)} />}
        {timeOpen && <TimesheetPane empno={empno} actingAs={chatActingAs} onClose={() => setTimeOpen(false)} />}
        {scoreOpen && <ScorePane empno={empno} onClose={() => setScoreOpen(false)} />}
        {proposalsOpen && <MyProposalsPane empno={empno} onClose={() => setProposalsOpen(false)} />}
        {mgrOpen && <ManagerPane empno={empno} techFirst={techFirst} onClose={() => setMgrOpen(false)} />}
        {flagsOpen && (
          <div className="fixed inset-0 z-40 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
            <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
              <button type="button" onClick={() => setFlagsOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="text-base font-bold text-slate-900">My Flags</div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              <MyFlags flags={flags.data ?? []} loading={flags.isLoading} />
            </div>
          </div>
        )}

        {/* Flag wizard (opened from inside a call) */}
        {wizard && wizard.kind === "follow_up" && (
          <FollowUpWizard stop={wizard.stop} empno={empno} onClose={() => setWizard(null)} onSent={() => setWizard(null)} />
        )}
        {wizard && wizard.kind !== "follow_up" && (
          <FlagWizard
            stop={wizard.stop}
            kind={wizard.kind}
            empno={empno}
            workedUnits={fieldMap.data?.get(wizard.stop.ref)?.worked_equipment ?? []}
            onClose={() => setWizard(null)}
            onSent={() => setWizard(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ nav pieces */

function NavBtn({ label, Icon, on, onClick, badge }: {
  label: string; Icon: typeof Sun; on: boolean; onClick: () => void; badge?: number;
}) {
  // Glass tab item: the selected one sits in a soft blue lens (the ::before).
  return (
    <button type="button" onClick={onClick} className={cn("m-tab", on && "on")} aria-current={on ? "page" : undefined}>
      <Icon className="h-6 w-6" strokeWidth={on ? 2.3 : 1.9} />
      <span>{label}</span>
      {badge != null && badge > 0 && <span className="m-badge">{badge}</span>}
    </button>
  );
}

function EmptyCard({ title, sub, Icon = Snowflake }: { title: string; sub?: string; Icon?: typeof Snowflake }) {
  return (
    <div className="rounded-3xl border border-black/[0.08] bg-white p-8 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <Icon className="mx-auto h-8 w-8 text-slate-300" />
      <h3 className="mt-2 text-sm font-bold text-slate-800">{title}</h3>
      {sub && <p className="mt-1 text-xs leading-relaxed text-slate-500">{sub}</p>}
    </div>
  );
}

/** TECH-IDENTITY-BIND: a non-admin login with no employee number sees this, not a list of techs. */
function IdentityLocked() {
  return (
    <div className="rounded-3xl border border-black/[0.08] bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <h3 className="text-base font-bold text-slate-900">Login Not Linked</h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        This login isn't linked to a team member yet, so the day can't load. Ask the office to link it under Settings → People & Access.
      </p>
    </div>
  );
}

/** Admin / manager only: look at the day as one tech (View-As). */
function IdentityPicker({ techs, onPick }: {
  techs: { empno: string; label: string }[]; onPick: (e: string) => void;
}) {
  return (
    <div className="rounded-3xl border border-black/[0.08] bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <h3 className="text-base font-bold text-slate-900">View As</h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        Your login isn't a tech — pick whose day to look at. This phone remembers.
        <> <InfoTip label="Link it for good">Settings → People & Access links a login to a team member.</InfoTip></>
      </p>
      <div className="mt-3 max-h-80 space-y-1.5 overflow-y-auto">
        {techs.map((t) => (
          <button key={t.empno} type="button" onClick={() => onPick(t.empno)}
            className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left text-sm font-semibold text-slate-800 active:bg-slate-50">
            {t.label}
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------- Today (one at a time) */

/** ONCALL-HUB: the tech's own running order. Writes sequential arrival times
 *  onto his self-booked calls (90-min steps) — the sort everyone shares
 *  (board included) then reads the same story. */
function useSelfDayOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { orderedIds: string[]; startMins: number }) => {
      const now = new Date().toISOString();
      await Promise.all(
        v.orderedIds.map((id, i) => {
          const mins = v.startMins + i * 90;
          const t = `${String(Math.min(23, Math.floor(mins / 60))).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}:00`;
          return supabase.from("pegasus_dispatch").update({ scheduled_time: t, updated_at: now }).eq("id", id);
        }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduled_board"] }),
  });
}

/** BULLETIN-01 — the office's one message for the day, pushed from the
 *  desktop Today tab. Shows at the very top of every tech's day. */
function useDayBulletin() {
  return useQuery({
    queryKey: ["m_day_bulletin", todayISO()],
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<{ body: string; created_by: string | null } | null> => {
      const { data } = await supabase
        .from("pegasus_bulletin")
        .select("body, created_by")
        .eq("day", todayISO())
        // BULLETIN-02: 'office' bulletins are desktop-only — the field never
        // sees "Brent's truck is in the shop."
        .eq("audience", "team")
        .maybeSingle();
      return (data as { body: string; created_by: string | null }) ?? null;
    },
  });
}

function DayBulletinCard() {
  const b = useDayBulletin().data;
  if (!b) return null;
  const who = b.created_by ? b.created_by.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "The office";
  // v2 (Seth 2026-09-01: "matches too much like the header"): not navy chrome —
  // a bright white note card with the amber megaphone, same identity as the
  // office's bulletin bar on desktop. Reads as a MESSAGE, not part of the app.
  return (
    <div className="rounded-3xl border border-amber-300/60 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100">
          <Megaphone className="h-5 w-5 text-amber-700" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-amber-700">Today&apos;s Bulletin</div>
          <p className="mt-0.5 whitespace-pre-wrap text-[15px] font-semibold leading-snug text-slate-900">{b.body}</p>
          <div className="mt-1 text-[11px] text-slate-400">— {who}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * ONCALL-SCHED-01a — the on-call line on the phone. The tech who is carrying
 * the phone gets the amber card ("You're on call · Mon Sep 14 – Tue Sep 15 ·
 * backup Jeremy"); everyone else gets one quiet line naming the pair. Reads
 * the PUBLISHED calendar only, by the office-open rule (2 AM is still
 * yesterday's pair).
 */
function OncallTodayCard({ empno, onOpen }: { empno: string | null; onOpen?: () => void }) {
  const now = useOncallNow().data;
  const mine = useMyOncallDays(empno).data;
  const { open, close } = useOncallHours();
  const techs = useAllActiveTechs().data ?? [];
  if (!now) return null;
  const nameOf = (e: string | null) => { if (!e) return null; const t = techs.find((x) => x.empno === e); return t ? techFirstName(t) : e; };
  const phase = oncallPhase(new Date(), open, close);
  const role = empno === now.primary_empno ? "primary" : empno === now.backup_empno ? "backup" : null;
  if (role) {
    // The block this on-call day sits in, so the card reads "Mon – Tue", not just today.
    const todayOn = oncallDateFor(new Date(), open);
    const block = blocksOf((mine ?? []).filter((d) => (role === "primary" ? d.primary_empno === empno : d.backup_empno === empno))).find((b) => b.start <= todayOn && b.end >= todayOn);
    const other = role === "primary" ? nameOf(now.backup_empno) : nameOf(now.primary_empno);
    return (
      <button type="button" onClick={onOpen} className="w-full rounded-3xl border border-amber-300/70 bg-amber-50 p-4 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] [touch-action:manipulation] active:bg-amber-100">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-200/70">
            <Moon className="h-5 w-5 text-amber-800" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-amber-800">{role === "primary" ? (phase === "now" ? "You're on call now" : "You're on call tonight") : "You're the backup"}</div>
            <div className="mt-0.5 text-[15px] font-bold leading-snug text-slate-900">{block ? blockLabel(block) : fmtShort(now.oncall_day)}</div>
            <div className="mt-0.5 text-[12.5px] text-slate-600">
              {role === "primary" ? (other ? `Backup: ${other}` : "No backup on this day") : `Primary: ${other}`}
              {now.note ? ` · ${now.note}` : ""}
            </div>
          </div>
          <ChevronRight className="mt-3 h-4 w-4 shrink-0 text-amber-700/60" />
        </div>
      </button>
    );
  }
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-2 rounded-2xl border border-black/[0.06] bg-white/70 px-3.5 py-2 text-left text-[12.5px] text-slate-600 [touch-action:manipulation] active:bg-white">
      <Moon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold text-slate-500">{phase === "now" ? "On call now:" : "On call tonight:"}</span>{" "}
        <span className="font-bold text-slate-900">{now.primary_name}</span>
        {now.backup_name ? <span> · backup <span className="font-semibold text-slate-800">{now.backup_name}</span></span> : <span className="text-slate-400"> · no backup</span>}
      </span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
    </button>
  );
}

/** ONCALL-SCHED-01d — one line on the On-Call tab: the tech's next block → the calendar. */
function OncallTabLine({ empno, onOpen }: { empno: string | null; onOpen: () => void }) {
  const mine = useMyOncallDays(empno).data;
  const techs = useAllActiveTechs().data ?? [];
  const { open } = useOncallHours();
  const todayOn = oncallDateFor(new Date(), open);
  const next = blocksOf(mine ?? []).find((b) => b.end >= todayOn) ?? null;
  const nameOf = (e: string | null) => { if (!e) return null; const t = techs.find((x) => x.empno === e); return t ? techFirstName(t) : e; };
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-2.5 rounded-3xl border border-black/[0.08] bg-white px-4 py-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] [touch-action:manipulation] active:bg-slate-50">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-100"><CalendarDays className="h-4 w-4 text-slate-600" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">On-Call Calendar</span>
        <span className="block truncate text-[13.5px] font-bold text-slate-900">
          {!empno ? "Who is on call, every day" : next
            ? (next.start <= todayOn ? "You're on call now" : `Your next: ${blockLabel(next)}`) + (next.primary_empno === empno ? (next.backup_empno ? ` · backup ${nameOf(next.backup_empno)}` : "") : ` · backup for ${nameOf(next.primary_empno)}`)
            : "No on-call days for you on the published calendar"}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
    </button>
  );
}

/**
 * ONCALL-SCHED-01d — More → On-Call Calendar. Everything a tech wants to
 * know about on-call, on one screen: who has the phone tonight, their own
 * next stretch (with a countdown, the other half of the pair, add-to-phone-
 * calendar, request a swap), the whole month the way the sheet reads
 * (Sunday first, primary over backup, their days ringed), their swap
 * requests, and how on-call works (the hours). Read-only for techs — the
 * office builds it under Team → On-Call.
 */
function OncallCalPane({ empno, onClose }: { empno: string | null; onClose: () => void }) {
  const techs = useAllActiveTechs().data ?? [];
  const nameOf = (e: string | null | undefined) => { if (!e) return ""; const t = techs.find((x) => x.empno === e); return t ? techFirstName(t) : e; };
  const { open, close } = useOncallHours();
  const now = useOncallNow().data;
  const phase = oncallPhase(new Date(), open, close);
  const todayOn = oncallDateFor(new Date(), open);
  const todayIso = todayISO();
  const periods = useOncallPeriods().data ?? [];
  const published = periods.filter((p) => p.status === "published");
  const range = published.length
    ? { from: published.reduce((m, p) => (p.starts_on < m ? p.starts_on : m), published[0].starts_on), to: published.reduce((m, p) => (p.ends_on > m ? p.ends_on : m), published[0].ends_on) }
    : null;
  const [month, setMonth] = useState<string>(() => monthStartISO(todayIso));
  const days = useOncallDaysPublished(range?.from ?? todayIso, range?.to ?? todayIso, !!range).data ?? [];
  const dayMap = useMemo(() => new Map(days.map((d) => [d.day, d])), [days]);
  const holidays = useOrgSettingJson<string[]>("payroll_holidays", []).data ?? [];
  const holidaySet = useMemo(() => new Set(holidays), [holidays]);
  const mine = useMemo(() => days.filter((d) => empno && (d.primary_empno === empno || d.backup_empno === empno)), [days, empno]);
  const myBlocks = useMemo(() => blocksOf(mine).filter((b) => b.end >= todayOn), [mine, todayOn]);
  const next = myBlocks[0] ?? null;
  const partnerEmpno = next ? (next.primary_empno === empno ? next.backup_empno : next.primary_empno) : null;
  const contacts = useOncallContacts(partnerEmpno ? [partnerEmpno] : []).data ?? [];
  const partnerCell = contacts[0]?.cell ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const counts = useMemo(() => {
    const inPeriod = mine.filter((d) => range && d.day >= range.from && d.day <= range.to);
    return { primary: inPeriod.filter((d) => d.primary_empno === empno).length, backup: inPeriod.filter((d) => d.backup_empno === empno).length, weekend: inPeriod.filter((d) => d.primary_empno === empno && isWeekendISO(d.day)).length };
  }, [mine, range, empno]);

  // Month grid, Sunday first, like the sheet.
  const grid = useMemo(() => {
    const first = parseOncallISO(month); const lead = first.getDay(); const end = monthEndISO(month);
    const cells: (string | null)[] = Array.from({ length: lead }, () => null);
    for (let d = month; d <= end; d = addDaysISO(d, 1)) cells.push(d);
    while (cells.length % 7) cells.push(null);
    const rows: (string | null)[][] = []; for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [month]);
  const fmtHour = (h: number) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
  const pickedDay = picked ? dayMap.get(picked) ?? null : null;

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="min-w-0 flex-1 text-[17px] font-extrabold tracking-tight text-slate-900">On-Call Calendar</h2>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setMonth(addMonthsISO(month, -1))} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Previous month">‹</button>
            <button type="button" onClick={() => setMonth(monthStartISO(todayIso))} className="rounded-full bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600 [touch-action:manipulation]">Today</button>
            <button type="button" onClick={() => setMonth(addMonthsISO(month, 1))} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Next month">›</button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {/* Who has the phone right now */}
        {now && (
          <div className="flex items-center gap-2 rounded-2xl border border-black/[0.06] bg-white px-3.5 py-2.5 text-[13px] text-slate-700">
            <Moon className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="min-w-0 truncate">
              <span className="font-semibold text-slate-500">{phase === "now" ? "On call now:" : "On call tonight:"}</span>{" "}
              <span className="font-bold text-slate-900">{now.primary_name}{now.primary_empno === empno ? " (you)" : ""}</span>
              {now.backup_name ? <span> · backup <span className="font-semibold">{now.backup_name}{now.backup_empno === empno ? " (you)" : ""}</span></span> : <span className="text-slate-400"> · no backup</span>}
            </span>
          </div>
        )}

        {/* Your next stretch */}
        {empno && (
          next ? (
            <div className={cn("rounded-3xl border p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", next.start <= todayOn ? "border-amber-300/70 bg-amber-50" : "border-black/[0.08] bg-white")}>
              <div className={cn("text-[10px] font-extrabold uppercase tracking-[0.14em]", next.start <= todayOn ? "text-amber-800" : "text-slate-500")}>
                {next.start <= todayOn
                  ? (next.primary_empno === empno ? (phase === "now" ? "You're on call now" : "You're on call tonight") : (phase === "now" ? "You're the backup now" : "You're the backup tonight"))
                  : `Your next on-call · ${daysUntil(next.start) === 1 ? "tomorrow" : `in ${daysUntil(next.start)} days`}`}
              </div>
              <div className="mt-0.5 text-[17px] font-extrabold leading-tight text-slate-900">{blockLabel(next)}</div>
              <div className="mt-0.5 text-[13px] text-slate-600">
                {next.primary_empno === empno
                  ? (next.backup_empno ? <>Primary · backup <span className="font-semibold text-slate-800">{nameOf(next.backup_empno)}</span></> : "Primary · no backup that day")
                  : <>Backup for <span className="font-semibold text-slate-800">{nameOf(next.primary_empno)}</span></>}
                {next.days > 1 ? ` · ${next.days} days` : ""}
              </div>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {partnerCell && (
                  <>
                    <a href={`tel:${partnerCell.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-[12px] font-bold text-white [touch-action:manipulation] active:opacity-90"><Phone className="h-3.5 w-3.5" /> Call {nameOf(partnerEmpno)}</a>
                    <a href={`sms:${partnerCell.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-[12px] font-bold text-slate-700 [touch-action:manipulation] active:bg-slate-200"><MessageCircle className="h-3.5 w-3.5" /> Text</a>
                  </>
                )}
                <a
                  href={icsHref(oncallBlockICS(next, `On call${next.primary_empno === empno ? "" : " (backup)"} — Sedgwick`, `${next.primary_empno === empno ? `Primary. Backup: ${nameOf(next.backup_empno) || "none"}` : `Backup for ${nameOf(next.primary_empno)}`}. On-call runs from ${fmtHour(close)} to ${fmtHour(open)} the next morning; weekends all day.`))}
                  download={`on-call-${next.start}.ics`}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-[12px] font-bold text-slate-700 [touch-action:manipulation] active:bg-slate-200"
                >
                  <CalendarDays className="h-3.5 w-3.5" /> Add to my calendar
                </a>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/60 px-4 py-3 text-[13px] text-slate-500">
              {range ? "You have no on-call days on the published calendar." : "The office has not published an on-call calendar yet."}
            </div>
          )
        )}

        {/* The month, the way the sheet reads */}
        <div className="overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <div className="text-[15px] font-extrabold text-slate-900">{fmtOncallMonth(month)}</div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> primary</span>
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-orange-500" /> backup</span>
            </div>
          </div>
          <table className="w-full table-fixed border-collapse select-none">
            <thead>
              <tr>{["S", "M", "T", "W", "T", "F", "S"].map((w, i) => <th key={i} className="py-1 text-center text-[10px] font-bold uppercase text-slate-400">{w}</th>)}</tr>
            </thead>
            <tbody>
              {grid.map((row, ri) => (
                <tr key={ri}>
                  {row.map((iso, ci) => {
                    if (!iso) return <td key={ci} className="h-[3.6rem] border-t border-slate-100 bg-slate-50/60" />;
                    const d = dayMap.get(iso);
                    const meP = !!empno && d?.primary_empno === empno;
                    const meB = !!empno && d?.backup_empno === empno;
                    const isToday = iso === todayIso;
                    const hol = holidaySet.has(iso);
                    const selected = picked === iso;
                    return (
                      <td key={iso} onClick={() => setPicked(selected ? null : iso)}
                        className={cn("h-[3.6rem] border-t border-slate-100 p-0.5 align-top [touch-action:manipulation]",
                          meP && "bg-amber-50", meB && !meP && "bg-orange-50/50",
                          selected && "ring-2 ring-inset ring-slate-900/70", isToday && !selected && "ring-2 ring-inset ring-red-500/70")}>
                        <div className="flex items-center justify-between px-0.5">
                          <span className={cn("text-[10px] font-bold tabular-nums", isToday ? "text-red-600" : "text-slate-500")}>{Number(iso.slice(8, 10))}</span>
                          {hol && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Office closed" />}
                        </div>
                        {d ? (
                          <div className="mt-0.5 px-0.5 leading-[1.15]">
                            <div className={cn("truncate text-[10.5px] font-bold text-blue-700", meP && "text-amber-900")}>{nameOf(d.primary_empno)}</div>
                            <div className={cn("truncate text-[10px] font-semibold text-orange-700", meB && "text-amber-800", !d.backup_empno && "text-slate-300")}>{d.backup_empno ? nameOf(d.backup_empno) : "—"}</div>
                          </div>
                        ) : (
                          <div className="mt-1 text-center text-[10px] text-slate-200">·</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-slate-100 px-4 py-2.5 text-[12.5px] text-slate-600">
            {picked ? (
              pickedDay ? (
                <>
                  <span className="font-bold text-slate-900">{fmtOncallDay(picked)}</span>
                  {" · "}<span className="font-semibold text-blue-700">{nameOf(pickedDay.primary_empno)}</span>{pickedDay.primary_empno === empno ? " (you)" : ""} primary
                  {pickedDay.backup_empno ? <> · <span className="font-semibold text-orange-700">{nameOf(pickedDay.backup_empno)}</span>{pickedDay.backup_empno === empno ? " (you)" : ""} backup</> : " · no backup"}
                  {pickedDay.note ? <span className="text-slate-500"> · {pickedDay.note}</span> : null}
                  {holidaySet.has(picked) ? <span className="text-amber-700"> · office closed</span> : null}
                </>
              ) : (
                <><span className="font-bold text-slate-900">{fmtOncallDay(picked)}</span> · nobody assigned yet</>
              )
            ) : (
              <span className="text-slate-400">Shaded days are yours · tap a day to see who&apos;s on</span>
            )}
          </div>
        </div>

        {/* My days + swap requests */}
        {empno && myBlocks.length > 0 && (
          <div className="rounded-3xl border border-black/[0.08] bg-white px-4 py-3 text-[12.5px] text-slate-600 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
            <span className="font-bold text-slate-900">{counts.primary} primary · {counts.backup} backup</span> day{counts.primary + counts.backup === 1 ? "" : "s"} for you{range ? ` this period` : ""}{counts.weekend ? ` · ${counts.weekend} on a weekend` : ""}.
          </div>
        )}
        <MyOncallCard empno={empno} />

        {/* How it works */}
        <div className="rounded-3xl border border-black/[0.06] bg-white/70 px-4 py-3 text-[12px] leading-relaxed text-slate-500">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">How on-call works</div>
          <p className="mt-1">A day&apos;s on-call runs from office close ({fmtHour(close)}) to office open ({fmtHour(open)}) the next morning; on weekends and closed days it is the whole day. The answering service calls the primary first and the backup if the primary doesn&apos;t answer. Need a day covered? Request a swap above — the office approves it and you hear back in chat. The office builds and changes this calendar; if something looks wrong, tell them.</p>
        </div>
      </div>
    </div>
  );
}

/** ONCALL-SCHED-01a/b — "My On-Call": the tech's own upcoming days, as blocks,
 *  each with "Request a swap" (the office approves; the answer comes back in chat). */
function MyOncallCard({ empno }: { empno: string | null }) {
  const mine = useMyOncallDays(empno).data;
  const techs = useAllActiveTechs().data ?? [];
  const { techs: serviceTechs } = useServiceTechs();
  const swaps = useOncallSwapRequests("all").data ?? [];
  const request = useRequestOncallSwap();
  const [asking, setAsking] = useState<string | null>(null); // block start
  const [withEmpno, setWithEmpno] = useState("");
  const [note, setNote] = useState("");
  if (!empno || !mine || mine.length === 0) return null;
  const nameOf = (e: string | null) => { if (!e) return null; const t = techs.find((x) => x.empno === e); return t ? techFirstName(t) : e; };
  const today = todayISO();
  const blocks = blocksOf(mine).filter((b) => b.end >= today).slice(0, 8);
  const pendingFor = (b: { start: string; end: string }) => swaps.find((r) => r.empno === empno && r.from_day <= b.end && r.to_day >= b.start && r.status === "requested");
  const decidedFor = (b: { start: string; end: string }) => swaps.find((r) => r.empno === empno && r.from_day <= b.end && r.to_day >= b.start && r.status !== "requested");
  return (
    <div className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">My On-Call · next few months</div>
      <ul className="mt-2 divide-y divide-black/[0.06]">
        {blocks.map((b) => {
          const primary = b.primary_empno === empno;
          const other = primary ? nameOf(b.backup_empno) : nameOf(b.primary_empno);
          const pending = pendingFor(b);
          const decided = decidedFor(b);
          const open = asking === b.start;
          return (
            <li key={b.start} className="py-2">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-[13.5px] font-semibold text-slate-900">{blockLabel(b)}</span>
                <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold", primary ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700")}>{primary ? "Primary" : "Backup"}</span>
                <span className="w-24 shrink-0 truncate text-right text-[11.5px] text-slate-500">{primary ? (other ? `backup ${other}` : "no backup") : `for ${other}`}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                {pending ? (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold text-amber-700">Swap requested · waiting on the office</span>
                ) : decided?.status === "denied" ? (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10.5px] font-bold text-red-600">Swap not approved{decided.decision_note ? ` — ${decided.decision_note}` : ""}</span>
                ) : (
                  <button type="button" onClick={() => { setAsking(open ? null : b.start); setWithEmpno(""); setNote(""); }}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[11.5px] font-bold text-slate-600 [touch-action:manipulation] active:bg-slate-200">
                    {open ? "Never mind" : "Request a swap"}
                  </button>
                )}
              </div>
              {open && !pending && (
                <div className="mt-2 space-y-1.5 rounded-2xl bg-slate-50 p-2.5">
                  <select value={withEmpno} onChange={(e) => setWithEmpno(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[13px] font-semibold">
                    <option value="">Anyone the office picks</option>
                    {serviceTechs.filter((t) => t.empno !== empno).map((t) => <option key={t.empno} value={t.empno}>Ask {techFirstName(t)}</option>)}
                  </select>
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional)"
                    className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[13px]" />
                  <button type="button" disabled={request.isPending}
                    onClick={() => request.mutate({ empno, from_day: b.start < today ? today : b.start, to_day: b.end, role: primary ? "primary" : "backup", with_empno: withEmpno || null, note: note.trim() || null }, { onSuccess: () => setAsking(null) })}
                    className="w-full rounded-xl bg-slate-900 py-2.5 text-[13px] font-bold text-white [touch-action:manipulation] active:opacity-90 disabled:opacity-50">
                    {request.isPending ? "Sending…" : "Send to the office"}
                  </button>
                  {request.isError && <div className="text-[11.5px] font-semibold text-red-600">{(request.error as Error).message}</div>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TodayTech({ empno, stops, workable, current, fieldMap, dayChanged, onDismissChange, canPeek, peek, onTogglePeek, onOpen, onParts, onOncall, installer, nameOf }: {
  empno: string | null;
  stops: Stop[];
  workable: Stop[];
  current: Stop | null;
  fieldMap: Map<string, FieldStatusRow> | undefined;
  dayChanged: boolean;
  onDismissChange: () => void;
  canPeek: boolean;
  peek: boolean;
  onTogglePeek: () => void;
  onOpen: (s: Stop) => void;
  onParts: (which: "today" | "tomorrow") => void;
  onOncall: () => void;
  /** FIELD-CREWS-01: an Install-crew login — adds tomorrow's installs (scope)
   *  and every install on the board today (decision 7). */
  installer: boolean;
  nameOf: (e: string | null) => string | null;
}) {
  // PARTS-PICKUP-01: the card previews the SAME list the sheet shows (notes +
  // arrived parts + office adds), not a notes-only recount.
  // PARTS-PICKUP-03: items for wrapped calls are history, not to-dos; once the
  // day is done (or nothing is left to grab) the card turns into tomorrow's list.
  const today = todayISO();
  const tomorrow = nextPickupDay(today);
  const pickup = usePickupDay(today, { empno, enabled: !!empno });
  const pickupNext = usePickupDay(tomorrow, { empno, enabled: !!empno });
  const doneRefs = useMemo(() => new Set(workable.filter((st) => st.done || fieldMap?.get(st.ref)?.state === "wrapped").map((st) => st.ref)), [workable, fieldMap]);
  const dayDone = workable.length === 0 || workable.every((st) => doneRefs.has(st.ref));
  const dayParts = useMemo(() => {
    const mine = (pickup.data ?? []).find((t) => t.empno === empno);
    const all = mine ? visibleItems(mine) : [];
    const open = mine ? openItems(mine, doneRefs) : [];
    const toGrab = open.filter((i) => !i.pickedUpAt);
    const preview: TruckPart[] = (toGrab.length ? toGrab : open).slice(0, 3).map((i) => ({ key: i.key, label: i.label, kind: i.kind, qty: i.qty }));
    return { n: open.length, wrapped: all.length - open.length, toGrab: toGrab.length, preview, picked: open.filter((i) => i.pickedUpAt).length };
  }, [pickup.data, empno, doneRefs]);
  const nextParts = useMemo(() => {
    const mine = (pickupNext.data ?? []).find((t) => t.empno === empno);
    const items = mine ? visibleItems(mine) : [];
    return { n: items.length, preview: items.slice(0, 3).map((i) => i.label), picked: items.filter((i) => i.pickedUpAt).length };
  }, [pickupNext.data, empno]);
  // Show tomorrow once the day is wrapped up, or when nothing is left to grab today.
  const showTomorrow = dayDone || (dayParts.toGrab === 0 && nextParts.n > 0);
  const tomorrowLabel = new Date(`${tomorrow}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const offBlocks = stops.filter((s) => s.pseudo);
  const orderDay = useSelfDayOrder();

  if (workable.length === 0) {
    return (
      <div className="space-y-3">
        <DayBulletinCard />
        <OncallTodayCard empno={empno} onOpen={onOncall} />
        <CashTurnInCards empno={empno} />
        {offBlocks.map((s) => (
          <div key={s.key} className="rounded-3xl border border-dashed border-slate-300 bg-white/60 px-4 py-3 text-sm font-semibold text-slate-500">
            {s.name}{s.notes ? <span className="block text-xs font-normal text-slate-400">{s.notes}</span> : null}
          </div>
        ))}
        <EmptyCard title="Nothing on the board today" />
        {installer && <InstallerDayCards empno={empno} nameOf={nameOf} />}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* The office's message of the day rides on top of everything. */}
      <DayBulletinCard />
      <OncallTodayCard empno={empno} onOpen={onOncall} />
      <CashTurnInCards empno={empno} />
      {dayChanged && (
        <button type="button" onClick={onDismissChange}
          className="flex w-full items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-left text-xs font-semibold text-amber-900 active:bg-amber-100">
          <span className="flex h-2 w-2 shrink-0 rounded-full bg-amber-500" />
          Your schedule changed — a call was added or moved. Check Parts for the Day. <span className="ml-auto text-amber-500/70">✕</span>
        </button>
      )}

      {current ? (
        <CurrentCallHero s={current} field={fieldMap?.get(current.ref)} onOpen={() => onOpen(current)} />
      ) : (
        <div className="m-card p-6 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-6 w-6" /></span>
          <div className="mt-3 text-lg font-bold text-slate-900">All wrapped up</div>
          <div className="mt-1 text-sm text-slate-500">Nothing else scheduled.</div>
        </div>
      )}

      {/* One at a time — no totals, no progress, no "N more" (Seth's rule).
          The next call simply appears when this one wraps. */}

      {installer && <InstallerDayCards empno={empno} nameOf={nameOf} />}

      {/* Parts for the day — flips to tomorrow's list once today is wrapped up (PARTS-PICKUP-03). */}
      <button type="button" onClick={() => onParts(showTomorrow ? "tomorrow" : "today")}
        className="flex w-full items-center gap-3 rounded-3xl border border-black/[0.08] bg-white px-4 py-3.5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100">
          <Truck className="h-5 w-5 text-slate-600" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold text-slate-900">{showTomorrow ? `Parts for Tomorrow` : "Parts for the Day"}{showTomorrow && <span className="ml-1.5 text-[11px] font-semibold text-slate-400">{tomorrowLabel}</span>}</span>
          <span className="block truncate text-xs text-slate-500">
            {showTomorrow ? (
              nextParts.n === 0 ? "Nothing on tomorrow's list yet"
                : nextParts.picked === nextParts.n ? <>All {nextParts.n} picked up already ✓</>
                : <>{nextParts.picked ? `${nextParts.n - nextParts.picked} still to grab` : `${nextParts.n} item${nextParts.n === 1 ? "" : "s"} to grab`} · {nextParts.preview.join(" · ")}{nextParts.n > 3 ? " …" : ""}</>
            ) : dayParts.n === 0 ? (dayParts.wrapped ? `Nothing left to grab · ${dayParts.wrapped} on wrapped calls` : "Nothing to pick up today") : dayParts.toGrab === 0 ? (
              <>All {dayParts.n} picked up ✓</>
            ) : (
              <>{dayParts.toGrab} to grab{dayParts.picked ? ` · ${dayParts.picked} picked up` : ""} · {dayParts.preview.map((p) => p.label).join(" · ")}{dayParts.toGrab > 3 ? " …" : ""}</>
            )}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
      </button>

      {/* FOLLOW-UP-01: the follow-ups he chose to handle himself — due, overdue,
          or coming this week. They stay here every day until he clears them. */}
      <TechFollowUpsCard empno={empno} />

      {/* ONCALL-HUB: the day the tech built HIMSELF shows in full — only
          office-built days stay one-at-a-time (Seth 2026-08-28). ▲▼ set his
          running order (writes arrival times; next-up follows the same sort). */}
      {(() => {
        const mine = workable
          .filter((s2) => s2.selfBooked && s2 !== current && !(s2.done || fieldMap?.get(s2.ref)?.state === "wrapped"))
          .sort((x, y) => (x.time ?? "99:99").localeCompare(y.time ?? "99:99"));
        if (mine.length === 0) return null;
        const move = (idx: number, dir: -1 | 1) => {
          const arr = [...mine];
          const j = idx + dir;
          if (j < 0 || j >= arr.length) return;
          [arr[idx], arr[j]] = [arr[j], arr[idx]];
          // Sequence AFTER the current call so the one being run stays first.
          const curMins = current?.time ? Number(current.time.slice(0, 2)) * 60 + Number(current.time.slice(3, 5)) : null;
          const startMins = curMins != null ? curMins + 60 : 8 * 60;
          orderDay.mutate({ orderedIds: arr.map((x) => x.ref), startMins });
        };
        return (
          <div className="m-card p-3.5">
            <div className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-slate-900">
              Booked by you — your on-call day
              <InfoTip label="Running order">The arrows set the order you&apos;ll run these — their times move to match.</InfoTip>
            </div>
            <ul className="mt-1.5 divide-y divide-slate-100">
              {mine.map((s2, idx) => (
                <li key={s2.key} className="flex items-center gap-1.5 py-2">
                  <button type="button" onClick={() => onOpen(s2)} className="flex min-w-0 flex-1 items-center gap-2 text-left active:opacity-80">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white font-mono text-[11px] font-extrabold text-slate-700 ring-1 ring-slate-200">{idx + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-bold text-slate-800">{s2.name}</span>
                      <span className="block truncate text-[11px] text-slate-500">{[s2.address, s2.city].filter(Boolean).join(", ")}{s2.time ? ` · ~${s2.time.slice(0, 5)}` : ""}</span>
                    </span>
                  </button>
                  <span className="flex shrink-0 flex-col">
                    <button type="button" disabled={idx === 0 || orderDay.isPending} onClick={() => move(idx, -1)}
                      className="rounded p-0.5 text-slate-400 active:bg-white disabled:opacity-25" aria-label="Run earlier">
                      <ChevronDown className="h-4 w-4 rotate-180" />
                    </button>
                    <button type="button" disabled={idx === mine.length - 1 || orderDay.isPending} onClick={() => move(idx, 1)}
                      className="rounded p-0.5 text-slate-400 active:bg-white disabled:opacity-25" aria-label="Run later">
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {canPeek && (
        <div className="rounded-3xl border border-amber-200/80 bg-amber-50/60 p-3">
          <button type="button" onClick={onTogglePeek} className="flex w-full items-center justify-between text-xs font-bold text-amber-800">
            Full day (View-As only)
            <ChevronDown className={cn("h-4 w-4 transition-transform", peek && "rotate-180")} />
          </button>
          {peek && (
            <ul className="mt-2 space-y-1">
              {workable.map((s) => {
                const f = fieldMap?.get(s.ref);
                const done = s.done || f?.state === "wrapped";
                return (
                  <li key={s.key}>
                    <button type="button" onClick={() => onOpen(s)} className="flex w-full items-center gap-2 rounded-xl bg-white/70 px-2.5 py-1.5 text-left text-xs active:bg-white">
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", done ? "bg-emerald-500" : f ? "bg-amber-500" : "bg-slate-300")} />
                      <span className="min-w-0 flex-1 truncate font-semibold text-slate-700">{s.name}</span>
                      <span className="shrink-0 font-mono text-[10px] text-slate-400">#{s.no} · {s.window ?? "any"}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** The hero — the tech's current call. HERO v2 (Seth 2026-09-01: "not very
 *  clear what needs to go where"): the card answers three questions in
 *  order — WHERE am I going (name + address, wrapping, never cut off),
 *  WHAT am I there for (a labeled job section), and GO (Directions opens
 *  Maps, Open the Call runs the job). Board window color stays on the band. */
function CurrentCallHero({ s, field, onOpen }: { s: Stop; field?: FieldStatusRow; onOpen: () => void }) {
  // LIQUID-GLASS-01: the hero leans toward your thumb, a soft light follows.
  const tilt = useTouchTilt(4.5);
  const wc = windowColors(s.window);
  const state = field?.state ?? null;
  const tu = detectChecklist(s.serviceType, s.notes);
  const mapsUrl = s.address
    ? `http://maps.apple.com/?daddr=${encodeURIComponent([s.address, s.city].filter(Boolean).join(", "))}`
    : null;
  // Glass: the board's window color rides as a chip (exact color kept), the
  // call number beside it, the state as a tint on the right.
  const stateChip = s.pickup
    ? { label: "Part pickup", cls: "m-chip-warn" }
    : state === "on_site" ? { label: "You're on site", cls: "m-chip-good" }
    : state === "en_route" ? { label: "On your way", cls: "m-chip-warn" }
    : { label: "Up next", cls: "m-chip-tint" };
  return (
    <button type="button" onClick={onOpen} ref={tilt.ref} {...tilt.handlers} className="m-card m-tilt block w-full overflow-hidden p-4 text-left [touch-action:manipulation]">
      <div className="flex items-center gap-2">
        <span data-board-color="" className={cn("rounded-full px-2.5 py-1 font-mono text-[11.5px] font-extrabold tabular-nums", wc.bg, wc.text)}>
          {s.window ?? "ANYTIME"}
        </span>
        <span className="font-mono text-[12px] font-semibold text-slate-400">#{s.no}</span>
        {s.test && <span className="m-chip m-chip-bad">Test</span>}
        <span className={cn("m-chip ml-auto", stateChip.cls)}>{stateChip.label}</span>
      </div>
      {/* WHERE — name + address, big, wrapping, never cut off. */}
      <div className="mt-3 flex items-start gap-3">
        <span className={cn("mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", s.pickup ? "bg-amber-100" : "bg-slate-100")}>
          {s.pickup ? <Package className="h-5 w-5 text-amber-700" /> : tu === "A/C tune-up" ? <Snowflake className="h-5 w-5 text-sky-500" /> : <Wrench className="h-5 w-5 text-slate-600" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[22px] font-bold leading-tight tracking-tight text-slate-900">{s.name}</div>
          <div className="mt-1 text-[16px] font-medium leading-snug text-slate-600">
            {s.address ?? (s.pickup ? "See pickup details" : "No address on file")}
            {s.city && <span className="text-slate-500"> · {s.city}</span>}
          </div>
        </div>
      </div>
      {(s.serviceType || s.quoted != null || s.payStatus) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {s.serviceType && <span className="m-chip">{s.serviceType}</span>}
          {s.quoted != null && <span className="m-chip m-chip-good">Quoted {money$(s.quoted)}</span>}
          {s.payStatus && <span className={cn("m-chip capitalize", s.payStatus === "prepaid" ? "m-chip-good" : "m-chip-warn")}>{s.payStatus}</span>}
        </div>
      )}
      {/* WHAT — the job itself, labeled so it never reads as filler text. */}
      {s.notes && (
        <div className="mt-3 rounded-2xl bg-slate-50 px-3.5 py-2.5">
          <div className="text-[12.5px] font-semibold text-slate-600">What you&apos;re there for</div>
          <p className="mt-1 line-clamp-4 text-[14px] leading-relaxed text-slate-700">{s.notes}</p>
        </div>
      )}
      <HeroFilesLine s={s} />
      {/* GO — directions on the left, the call on the right (the one blue button). */}
      <div className="mt-3.5 grid grid-cols-2 gap-2">
        {mapsUrl ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); window.location.href = mapsUrl; }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); window.location.href = mapsUrl; } }}
            className="m-btn m-btn-tint"
          >
            <Navigation className="h-[18px] w-[18px]" /> Directions
          </span>
        ) : <span aria-hidden />}
        <span className="m-btn m-btn-primary">
          Open the Call <ChevronRight className="h-[18px] w-[18px]" />
        </span>
      </div>
    </button>
  );
}

/* ----------------------------------------------------- Parts for the Day */

const PART_CHIP_M: Record<string, string> = {
  filter: "border-slate-200 bg-slate-50 text-slate-700",
  aprilaire: "border-slate-200 bg-slate-50 text-slate-700",
  cabinet: "border-slate-200 bg-slate-100 text-slate-600",
  humidifier: "border-slate-200 bg-slate-50 text-slate-700",
  part: "border-slate-300 bg-white text-slate-800",
  gear: "border-amber-300 bg-amber-50 text-amber-800",
};

function PartsDaySheet({ empno, today, initial, stops, isDone, onClose }: { empno: string | null; today: string; initial: "today" | "tomorrow"; stops: Stop[]; isDone: (s: Stop) => boolean; onClose: () => void }) {
  // PARTS-PICKUP-01: the SAME list the office edits on Today → Parts Desk
  // (notes + structured parts + arrived part tickets + vendor pickups + what
  // the office added by hand). The tech ticks items off; the office sees it.
  // PARTS-PICKUP-03 (Seth 2026-09-15): Today / Tomorrow tabs — the sheet opens
  // on tomorrow once today is wrapped up; items for wrapped calls fold into a
  // "done calls" section instead of counting as to-dos; ticks are instant and
  // each item guards its own in-flight write, so fast taps never undo a tick.
  const [view, setView] = useState<"today" | "tomorrow">(initial);
  const tomorrow = nextPickupDay(today);
  const day = view === "today" ? today : tomorrow;
  const { data, isLoading } = usePickupDay(day, { empno, enabled: !!empno });
  const stamp = useStampPickupItem();
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [showDone, setShowDone] = useState(false);
  const mine = (data ?? []).find((t) => t.empno === empno) ?? null;
  const items = mine ? visibleItems(mine) : [];
  const doneRefs = new Set(view === "today" ? stops.filter((st) => isDone(st)).map((st) => st.ref) : []);
  // Group by the call (office "for the truck" items first); wrapped calls go last, folded.
  const groups = new Map<string, { title: string; sub: string | null; wrapped: boolean; items: PickupItem[] }>();
  for (const i of items) {
    const k = i.job ? i.job.ref : "__truck";
    const g = groups.get(k) ?? {
      title: i.job ? i.job.name : "For the truck",
      sub: i.job ? [i.job.address, i.job.city].filter(Boolean).join(", ") || null : "Added by the office",
      wrapped: i.job ? doneRefs.has(i.job.ref) : false,
      items: [],
    };
    g.items.push(i);
    groups.set(k, g);
  }
  const openGroups = [...groups.entries()].filter(([, g]) => !g.wrapped);
  const doneGroups = [...groups.entries()].filter(([, g]) => g.wrapped);
  const open = mine ? openItems(mine, doneRefs) : [];
  const toGrab = open.filter((i) => !i.pickedUpAt).length;
  const picked = open.filter((i) => i.pickedUpAt).length;
  const doneN = items.length - open.length;
  const tick = (i: PickupItem, on: boolean) => {
    if (!empno || pending.has(i.key)) return;
    setPending((p) => new Set(p).add(i.key));
    stamp.mutate({ day, empno, item: i, field: "picked", on }, { onSettled: () => setPending((p) => { const n = new Set(p); n.delete(i.key); return n; }) });
  };
  const dayLabelOf = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const renderGroup = ([k, g]: [string, { title: string; sub: string | null; wrapped: boolean; items: PickupItem[] }]) => (
    <div key={k} className={cn("rounded-3xl border bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", g.wrapped && "opacity-60")}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-bold text-slate-900">{g.title}</div>
          {g.sub && <div className="truncate text-xs text-slate-500">{g.sub}</div>}
        </div>
        {g.wrapped && <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700">Wrapped ✓</span>}
      </div>
      <ul className="mt-2 space-y-1.5">
        {g.items.map((i) => {
          const on = !!i.pickedUpAt;
          const busy = pending.has(i.key);
          return (
            <li key={i.key}>
              <button type="button" disabled={!empno} aria-pressed={on}
                onClick={() => tick(i, !on)}
                className={cn("flex w-full items-center gap-3 rounded-2xl border px-3 py-2.5 text-left [touch-action:manipulation] active:bg-slate-50", on ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white", busy && "opacity-70")}>
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors", on ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white")}>
                  {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn("block text-[14px] font-bold", on ? "text-emerald-800 line-through decoration-emerald-300" : "text-slate-900")}>
                    {i.qty > 1 ? `${i.qty}× ` : ""}{i.label}
                  </span>
                  <span className="block text-[11px] text-slate-500">
                    {/* PARTS-PICKUP-02: WHERE comes from the PO / the office, never a guess. */}
                    {i.whereLabel
                      ? i.whereLabel
                      : i.source === "ticket" ? "Ordered part — at the shop" : i.source === "pickup" ? "Part pickup — ask the office where" : i.source === "office" ? "Added by the office" : "From the call notes"}
                    {i.note && i.whereLabel && i.source === "ticket" ? "" : i.note ? ` · ${i.note}` : ""}
                    {i.preppedAt ? " · prepped ✓" : ""}
                  </span>
                </span>
                <span className={cn("shrink-0 rounded-lg border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", PART_CHIP_M[i.kind] ?? PART_CHIP_M.part)}>{i.kind}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
  return (
    <div className="fixed inset-0 z-40 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-base font-bold text-slate-900">
              {view === "today" ? "Parts for the Day" : "Parts for Tomorrow"}
              <InfoTip label="About this list">The same list the office sees on Today → Parts Desk. It updates by itself as calls are added, moved or wrapped. Pickups the office books for you show as their own stop.</InfoTip>
            </div>
            <div className="text-[11px] text-slate-500">
              {isLoading ? "loading…" : items.length === 0 ? (view === "today" ? "nothing on the list" : "nothing on the list yet")
                : toGrab === 0 ? `All ${open.length || items.length} picked up ✓${doneN ? ` · ${doneN} on wrapped calls` : ""}`
                : `${toGrab} to grab${picked ? ` · ${picked} picked up` : ""}${doneN ? ` · ${doneN} on wrapped calls` : ""}`}
            </div>
          </div>
        </div>
        <div className="mt-2.5 flex gap-0.5 rounded-full bg-slate-100 p-0.5">
          {(["today", "tomorrow"] as const).map((k) => (
            <button key={k} type="button" onClick={() => setView(k)}
              className={cn("flex-1 rounded-full py-1.5 text-[12.5px] font-bold transition-all [touch-action:manipulation]",
                view === k ? "bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.12)]" : "text-slate-500")}>
              {k === "today" ? "Today" : `Tomorrow · ${dayLabelOf(tomorrow)}`}
            </button>
          ))}
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
        {isLoading ? (
          <div className="h-24 animate-pulse rounded-3xl bg-white" />
        ) : items.length === 0 ? (
          <EmptyCard Icon={Truck}
            title={view === "today" ? "Nothing to pick up today" : "Nothing on tomorrow's list yet"}
            sub={view === "today" ? undefined : "Check again before you leave the shop."} />
        ) : (
          <>
            {openGroups.length === 0 && (
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-[13px] font-semibold text-emerald-800">
                Every call with parts is wrapped — nothing left to grab today.{view === "today" ? " Tomorrow's list is on the other tab." : ""}
              </div>
            )}
            {openGroups.map(renderGroup)}
            {doneGroups.length > 0 && (
              <div>
                <button type="button" onClick={() => setShowDone((v) => !v)}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400 [touch-action:manipulation]">
                  <span className="h-px flex-1 bg-slate-200" />
                  {showDone ? "Hide" : "Show"} {doneGroups.length} wrapped call{doneGroups.length === 1 ? "" : "s"} · {doneN} item{doneN === 1 ? "" : "s"}
                  <span className="h-px flex-1 bg-slate-200" />
                </button>
                {showDone && <div className="space-y-2.5">{doneGroups.map(renderGroup)}</div>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------- the call screen */

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "") : null;

/* ============================================================== CALL SCREEN
 * FIELD-CALL-01 → Direction B (Seth 2026-09-11: "I regret C. I don't like
 * that the tabs are gone. Let's go with B."). The four tabs — Job / Customer /
 * Equipment / History — are exactly what they were. What B adds: the navy
 * header carries ONE quiet prose line about who this is and a row of
 * watch-out chips (tap a chip to read it; "+N" opens the Customer tab), and
 * the full Pegasus snapshot + the office's Watch-outs list sit at the top of
 * the Customer tab. The Job tab body does not change — except the wrapped
 * banner now names the next call and the drive to it.
 * ========================================================================= */

type CallTab = "job" | "customer" | "equipment" | "history";
/** The three things a tech can flag from a call: a part, a return visit, a follow-up. */
type FlagKind = "order_parts" | "return_visit" | "follow_up";

function CallScreen({ s, field, fieldReady, empno, techFirst, nextStop, onClose, onFlag }: {
  s: Stop;
  field?: FieldStatusRow;
  fieldReady: boolean;
  empno: string | null;
  techFirst: string | null;
  /** The next open stop on the day — named after the wrap so the tech knows where he's headed. */
  nextStop: Stop | null;
  onClose: () => void;
  onFlag: (kind: FlagKind) => void;
}) {
  const wc = windowColors(s.window);
  const setState = useSetFieldState();
  const sendOtw = useSendOtwText();
  const [ctab, setCtab] = useState<CallTab>("job");
  const [note, setNote] = useState<string | null>(null);
  const state = field?.state ?? null;
  const busy = setState.isPending || sendOtw.isPending;
  const base = { ref: s.ref, source: s.kind, custno: s.custno, locno: s.locno, empno, prev: field ?? null } as const;

  // AUTO-ARRIVE: opening the call while standing at the address logs Arrived by
  // itself (foreground location — the native shell's closed-app geofence is M3).
  const attempted = useRef<string | null>(null);
  const [distMi, setDistMi] = useState<number | null>(null);
  useEffect(() => {
    if (!fieldReady || attempted.current === s.ref) return;
    if (!s.lat || !s.lng) { attempted.current = s.ref; return; }
    if (state === "on_site" || state === "wrapped") { attempted.current = s.ref; return; }
    attempted.current = s.ref;
    void (async () => {
      const pos = await getPosition();
      if (!pos) return;
      const mi = milesBetween(pos, { lat: s.lat as number, lng: s.lng as number });
      setDistMi(mi);
      if (mi <= 0.15) {
        await setState.mutateAsync({ ...base, state: "on_site", on_site_source: "device" });
        haptic("success");
        setNote("Arrived — logged automatically (you're at the address).");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldReady, s.ref]);

  // Who to actually call/text: a number written INTO the notes ("call Jeff on
  // way") beats the account's stored number — dispatch wrote it there on purpose.
  const phones = phonesFor(s);

  const startOtw = async () => {
    haptic("light");
    const result = await sendOtw.mutateAsync({
      ref: s.ref, custno: s.custno, locno: s.locno,
      customerName: s.name, phone: phones.primary?.n ?? null, address: s.address, techFirst,
    });
    await setState.mutateAsync({ ...base, state: "en_route", otw_sent: result });
    setNote(
      result === "queued" ? "On-the-way text queued to the customer."
      : result === "held" ? "Marked on the way — the text is held until customer texting goes live."
      : result === "already" ? "Marked on the way — the text already went out today."
      : result === "no_phone" ? "Marked on the way — no phone on file, so no text."
      : "Marked on the way — the on-the-way template is empty (Settings → Comms Automations).",
    );
  };

  const mapsHref = s.address
    ? `https://maps.apple.com/?daddr=${encodeURIComponent([s.address, s.city, s.zip].filter(Boolean).join(", "))}`
    : null;
  const onSite = state === "on_site" || state === "wrapped";

  return (
    <div className="fixed inset-0 z-[25] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      {/* Job header (Glass) — no navy block: a glass back circle, the
          customer's name as the title, the call number, the board's window
          chip (exact color), (Direction B) one quiet line about who this is +
          the watch-out chips, and a segmented control for the sub-tabs. */}
      <header className="relative pt-[max(env(safe-area-inset-top),10px)]">
        <div className="relative flex items-start gap-2.5 px-3 pt-2">
          <button type="button" onClick={onClose} className="m-circ" aria-label="Back to Today">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="truncate text-[22px] font-bold leading-[26px] tracking-tight text-slate-900">{s.name}</div>
            <div className="mt-0.5 truncate text-[13px] font-medium text-slate-500">
              {[s.address, s.city].filter(Boolean).join(", ") || "No address on the call"}{s.zip ? ` ${s.zip}` : ""}
            </div>
          </div>
          <div className="shrink-0 pt-1 text-right">
            <div className="font-mono text-[15px] font-semibold tabular-nums leading-tight text-slate-500">
              <CopyCode value={s.no} label="Copy call number" variant="text" size="lg" className="text-[15px] font-semibold text-slate-500">#{s.no}</CopyCode>
            </div>
            <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">{s.kind === "pegasus" ? "Pegasus" : "Call"}</div>
          </div>
        </div>
        <div className="relative flex flex-wrap items-center gap-1.5 px-3 pt-2.5">
          <span data-board-color="" className={cn("rounded-full px-2.5 py-1 font-mono text-[11.5px] font-extrabold tabular-nums", wc.bg, wc.text)}>
            {s.window ?? "ANYTIME"}
          </span>
          {s.test && <span className="m-chip m-chip-bad">Test — no texts go out</span>}
          {s.serviceType && <span className="m-chip">{s.serviceType}</span>}
          {s.quoted != null && s.quoted > 0 && <span className="m-chip m-chip-good">Quoted {money$(s.quoted)}</span>}
          {s.po && (
            <CopyCode value={s.po} label="Copy PO number" mono={false} className="m-chip ring-0">PO {s.po}</CopyCode>
          )}
        </div>
        {/* Direction B: who this is, in one line, on every tab. */}
        {!s.pickup && <CallWhoLine s={s} />}
        {/* Direction B: the office's watch-outs as chips — tap one to read it. */}
        {!s.pickup && <CallWatchChips s={s} onMore={() => setCtab("customer")} />}
        {/* sub-nav — the segmented control */}
        {!s.pickup && (
          <div className="relative px-3 pb-2.5 pt-3">
            <div className="m-seg">
              {([["job", "Job"], ["customer", "Customer"], ["equipment", "Equipment"], ["history", "History"]] as const).map(([k, label]) => (
                <button key={k} type="button" onClick={() => setCtab(k)} className={cn(ctab === k && "on")}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {s.pickup && <div className="h-2.5" />}
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 pb-32">
        {/* PICKUP variant — the details ARE the job. */}
        {s.pickup ? (
          <>
            <section className="rounded-3xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-amber-700">
                <Package className="h-4 w-4" /> Part pickup
              </div>
              <p className="mt-2 whitespace-pre-wrap text-[15px] font-semibold leading-relaxed text-slate-800">
                {s.notes?.trim() || "See dispatch for pickup details."}
              </p>
              {s.address && <p className="mt-2 text-sm font-bold text-slate-700">{[s.address, s.city].filter(Boolean).join(", ")}</p>}
            </section>
            <div className="grid grid-cols-1 gap-2.5">
              {mapsHref && (
                <a href={mapsHref} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl bg-slate-900 py-4 text-[15px] font-bold text-white active:opacity-90">
                  <Navigation className="h-4 w-4" /> Navigate there
                </a>
              )}
              {state !== "wrapped" ? (
                <button type="button" disabled={busy} onClick={() => void setState.mutateAsync({ ...base, state: "wrapped" })}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-4 text-[15px] font-bold text-white active:opacity-90 disabled:opacity-60">
                  <PackageCheck className="h-4 w-4" /> Picked up ✓
                </button>
              ) : (
                <div className="rounded-2xl bg-emerald-50 py-3 text-center text-sm font-bold text-emerald-700">Picked up {fmtTime(field?.wrapped_at ?? null)} ✓ — next call is on Today</div>
              )}
            </div>
          </>
        ) : ctab === "job" ? (
          <JobTab
            s={s} field={field} state={state} busy={busy} note={note} distMi={distMi}
            empno={empno} techFirst={techFirst} nextStop={nextStop}
            onOtw={() => void startOtw()}
            onArrive={() => { setNote(null); haptic("success"); void setState.mutateAsync({ ...base, state: "on_site", on_site_source: "manual" }); }}
            onWrap={() => { setNote(null); haptic("success"); void setState.mutateAsync({ ...base, state: "wrapped" }); }}
            onUndo={() => {
              setNote(null);
              const backTo = state === "wrapped" ? "on_site" : state === "on_site" ? "en_route" : null;
              void setState.mutateAsync({ ...base, state: backTo });
            }}
            onFlag={onFlag}
            onClose={onClose}
            mapsHref={mapsHref}
            phones={phones}
          />
        ) : ctab === "customer" ? (
          <CustomerTab s={s} />
        ) : ctab === "equipment" ? (
          <EquipmentTabM s={s} empno={empno} techFirst={techFirst} />
        ) : (
          <HistoryTab s={s} />
        )}
        {!s.pickup && onSite && ctab !== "job" && (
          <button type="button" onClick={() => setCtab("job")}
            className="mx-auto block rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white active:opacity-90">
            ← back to the job
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ call: JOB tab */

function JobTab({ s, field, state, busy, note, distMi, empno, techFirst, nextStop, onOtw, onArrive, onWrap, onUndo, onFlag, onClose, mapsHref, phones }: {
  s: Stop;
  field?: FieldStatusRow;
  state: "en_route" | "on_site" | "wrapped" | null;
  busy: boolean;
  note: string | null;
  distMi: number | null;
  empno: string | null;
  techFirst: string | null;
  nextStop: Stop | null;
  onOtw: () => void;
  onArrive: () => void;
  onWrap: () => void;
  onUndo: () => void;
  onFlag: (kind: FlagKind) => void;
  onClose: () => void;
  mapsHref: string | null;
  phones: { primary: CallPhone | null; all: CallPhone[] };
}) {
  const onSite = state === "on_site";
  const wrapped = state === "wrapped";
  const telHref = phones.primary ? `tel:${phones.primary.n.replace(/[^0-9]/g, "")}` : null;
  const fromNotes = phones.primary?.label === "from the call notes";

  const miLabel = distMi != null ? `${distMi < 10 ? distMi.toFixed(1) : Math.round(distMi)} mi` : null;
  // ARRIVE-01: every arrival stamp says where it came from.
  const arrivedFrom =
    field?.on_site_source === "device" ? `from your ${deviceWord()}`
    : field?.on_site_source === "truck" ? "from the truck"
    : field?.on_site_source === "manual" ? "entered by you"
    : null;

  return (
    <>
      {/* The status strip: where you are on this call, and the one Undo. */}
      {state !== null && (
        <div className="flex items-center gap-2">
          {state === "en_route" && (
            <span className="m-pill m-chip-warn">
              <Truck className="h-[15px] w-[15px]" /> On your way{field?.en_route_at ? ` · ${field.otw_sent === "queued" || field.otw_sent === "already" ? "texted " : ""}${fmtTime(field.en_route_at)}` : ""}
            </span>
          )}
          {state === "on_site" && (
            <span className="m-pill m-chip-good">
              <Check className="h-[15px] w-[15px]" /> Arrived {fmtTime(field?.on_site_at ?? null)}{arrivedFrom ? ` · ${arrivedFrom}` : ""}
            </span>
          )}
          {state === "wrapped" && (
            <span className="m-pill m-chip-good">
              <Check className="h-[15px] w-[15px]" /> Wrapped {fmtTime(field?.wrapped_at ?? null)}
            </span>
          )}
          <span className="ml-auto flex items-center gap-3">
            {state === "en_route" && miLabel && <span className="text-[13px] tabular-nums text-slate-400">{miLabel}</span>}
            <button type="button" disabled={busy} onClick={onUndo} className="text-[14px] font-semibold text-brand-700 [touch-action:manipulation] disabled:opacity-50">
              Undo
            </button>
          </span>
        </div>
      )}
      {note && <p className="px-2 text-center text-[12.5px] font-medium text-slate-500">{note}</p>}

      {/* The reason for the visit — always visible. */}
      <TheCallCard s={s} />
      {/* FILES-CALL-01: what the office pinned and the customer sent — for THIS
          call only, before he even arrives. */}
      <ForThisCallCard s={s} />

      {/* BEFORE arrival: get-there panel. One blue button at a time — On My
          Way first, then I've Arrived (which is the backup: the iPad logs the
          arrival itself when it reaches the address). */}
      {!onSite && !wrapped && (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <a href={telHref ?? undefined} aria-disabled={!telHref}
              className={cn("m-btn", telHref ? "m-btn-tint" : "m-btn-soft opacity-60")}>
              <Phone className="h-[18px] w-[18px] shrink-0" />
              <span className="flex min-w-0 flex-col items-start leading-tight">
                <span>Call</span>
                {phones.primary && (
                  <span className={cn("text-[10.5px] font-semibold", fromNotes ? "text-amber-600" : "opacity-70")}>
                    {phones.primary.n}{fromNotes ? " · in the notes" : ""}
                  </span>
                )}
              </span>
            </a>
            <a href={mapsHref ?? undefined} target="_blank" rel="noreferrer" aria-disabled={!mapsHref}
              className={cn("m-btn", mapsHref ? "m-btn-tint" : "m-btn-soft opacity-60")}>
              <Navigation className="h-[18px] w-[18px]" /> Navigate
            </a>
          </div>
          {state === null && (
            <button type="button" disabled={busy} onClick={onOtw} className="m-btn m-btn-primary m-btn-big w-full">
              {busy ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Truck className="h-[18px] w-[18px]" />}
              On My Way{phones.primary ? " — text the customer" : ""}
            </button>
          )}
          <button type="button" disabled={busy} onClick={onArrive} className={cn("m-btn m-btn-big w-full", state === "en_route" ? "m-btn-primary" : "m-btn-tint")}>
            {busy ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <MapPin className="h-[18px] w-[18px]" />}
            I've Arrived
          </button>
          <p className="flex items-center justify-center gap-1.5 px-3 text-center text-[12.5px] leading-snug text-slate-400">
            {distMi != null && distMi > 0.15 ? `You're ~${miLabel} away` : null}
            <InfoTip label="How arrival works">Arrival logs itself when you open the call at the address. The button is the backup.</InfoTip>
          </p>
        </>
      )}

      {/* ON SITE: the run-a-call workspace — write-up, parts, invoice. Time is
          not managed here: it derives from the arrival stamp automatically. */}
      {/* INSTALL-PATH-01: job work (install / new construction) gets the job
          screen and never the service invoice. */}
      {/* SALE-FINALIZE-01: a Finalize-sale (site survey) stop opens the same
          Install Packet an on-site sale uses — never the service invoice. */}
      {onSite && s.visitRole === "survey" && s.jobId && (
        <FinalizeSaleWork jobId={s.jobId} s={s} techFirst={techFirst} empno={empno} onWrap={onWrap} />
      )}
      {onSite && s.jobVisit && s.jobId && s.visitRole !== "survey" && (
        <JobVisitWork jobId={s.jobId} dispatchId={s.kind === "pegasus" ? s.ref : null} techFirst={techFirst} empno={empno} onWrap={onWrap} />
      )}
      {onSite && !s.jobVisit && s.visitRole !== "survey" && (
        <WorkInvoice s={s} field={field} empno={empno} techFirst={techFirst} onFlag={onFlag} onWrapped={onWrap} />
      )}
      {wrapped && (
        <>
          <div className="m-card p-5 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-6 w-6" /></span>
            <div className="mt-3 text-base font-bold text-slate-900">Wrapped {fmtTime(field?.wrapped_at ?? null)}</div>
          </div>
          {/* Where the tech is headed next, with the drive (the one thing kept
              from the C build — Seth can say the word and it goes). */}
          <NextCallLine from={s} next={nextStop} onGo={onClose} />
        </>
      )}
    </>
  );
}

/** "your iPad" / "your iPhone" — what the arrival stamp calls the device. */
function deviceWord(): string {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)) return "iPad";
  return "device";
}

/** HOUSE-TICKET-CHECK (Seth 2026-10-16): the ticket said "2024 — no leaks
 *  found"; the house record said two leaks were found and repaired. One cached
 *  model read compares the office's booking note with the house's prior visits
 *  (every owner's) and speaks only when they disagree. Silent otherwise. */
function TicketCheck({ s, variant }: { s: Stop; variant: "job" | "history" }) {
  const own = useVisitHistory(s.custno, s.locno);
  const house = useHouseVisitHistory(s.custno, s.locno);
  const visits = useMemo(() => {
    const mine = (own.data ?? []).map((v) => ({ date: v.date, tech: v.tech, account: null as string | null, call: v.note, did: v.workNote }));
    const theirs = house.others.flatMap((b) => b.rows.map((v) => ({ date: v.date, tech: v.tech, account: `${b.account.name} (${b.account.role})`, call: v.note, did: v.workNote })));
    return [...mine, ...theirs].filter((v) => (v.call || v.did)).sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "")).slice(0, 8);
  }, [own.data, house.others]);
  const check = useHouseTicketCheck({
    dispatch: s.kind === "esc" ? s.ref : s.no || s.ref,
    custno: s.custno, locno: s.locno, note: s.notes,
    visits, ready: !own.isLoading && !house.isLoading && !s.pickup && !s.pseudo,
  });
  const v = check.data;
  if (!v?.conflict) return null;
  if (variant === "job") {
    return (
      <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-amber-900">{v.headline}</div>
            {v.detail && <div className="mt-0.5 text-[12px] leading-snug text-amber-800">{v.detail} <span className="font-semibold">See History.</span></div>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <section className="rounded-3xl border border-amber-300 bg-amber-50 p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0">
          <div className="text-[13.5px] font-bold text-amber-900">{v.headline}</div>
          {v.detail && <p className="mt-1 text-[12.5px] leading-relaxed text-amber-900">{v.detail} Read the earlier write-up below before you write yours.</p>}
        </div>
      </div>
    </section>
  );
}

/** The reason for the visit — the dispatcher's write-up, the part it installs. */
function TheCallCard({ s }: { s: Stop }) {
  return (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <h4 className="text-[15px] font-bold tracking-tight text-slate-900">The Call</h4>
      {(s.serviceType || s.quoted != null || s.payStatus) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {s.serviceType && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11.5px] font-bold text-slate-700">{s.serviceType}</span>}
          {s.quoted != null && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11.5px] font-extrabold text-emerald-700">Quoted {money$(s.quoted)}</span>}
          {s.payStatus && <span className={cn("rounded-full px-2.5 py-1 text-[11.5px] font-extrabold uppercase", s.payStatus === "prepaid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>{s.payStatus}</span>}
        </div>
      )}
      {s.part && (
        <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="flex items-center gap-2 text-[13px] font-bold text-amber-900">
            <Package className="h-4 w-4 shrink-0 text-amber-700" />
            <span className="min-w-0 flex-1 truncate">Part: {s.part.summary}{s.part.partNo ? ` · #${s.part.partNo}` : ""}</span>
          </div>
          <div className="mt-0.5 text-[11.5px] font-semibold text-amber-800">
            {[s.part.unitText ? `For the ${s.part.unitText}` : "Unit not on the ticket", s.part.po ? `PO ${s.part.po}` : null, s.part.state ? PART_STATE_LABEL[s.part.state] : null].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}
      <p className="mt-1.5 whitespace-pre-wrap text-[14.5px] leading-relaxed text-slate-700">
        {s.notes?.trim() || "No notes on this call."}
      </p>
      <TicketCheck s={s} variant="job" />
      <p className="mt-2 text-xs text-slate-400">{[s.address, s.city, s.zip].filter(Boolean).join(", ")}</p>
    </section>
  );
}

const fmtShortDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  // this year → "May 21"; another year → "May 21, 2027" (a "use by" a year out needs it)
  return d.toLocaleDateString("en-US", d.getFullYear() === new Date().getFullYear() ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
};

/** Years as a customer, from the AI-ML-01 metrics (same rule as the 360's prose). */
function useTenureLine(custno: string | null) {
  const { data: m } = useCustomerMetrics(custno ?? undefined);
  if (!m) return null;
  if (m.tenure_data_quality === "chimera_suspected") return "recent buyer at this property";
  if ((m.total_dispatches ?? 0) === 0 && (m.lifetime_revenue_cents ?? 0) === 0) return null;
  if (!m.first_seen_at) return null;
  const years = new Date().getFullYear() - new Date(m.first_seen_at).getFullYear();
  return years >= 1 ? `${years}-year customer` : "new customer";
}

/** The header's one quiet line (Direction B): tenure · member · last here. */
function CallWhoLine({ s }: { s: Stop }) {
  const tenureRaw = useTenureLine(s.custno);
  const member = useMembershipCard(s.custno ?? undefined).data;
  const hist = useVisitHistory(s.custno, s.locno).data;
  const last = hist?.[0];
  // PROPERTY-LINK-01: the HOUSE has been served before this account existed
  // (the builder, the previous owner) — say so instead of "first visit".
  const house = useHouseVisitHistory(s.custno, s.locno);
  const houseLast = house.others[0]?.rows[0] ?? null;
  const houseWho = house.others[0]?.account ?? null;
  const priorVisits = house.others.reduce((n, b) => n + b.rows.length, 0);
  const ordinal = (n: number) => `${n}${n % 10 === 1 && n % 100 !== 11 ? "st" : n % 10 === 2 && n % 100 !== 12 ? "nd" : n % 10 === 3 && n % 100 !== 13 ? "rd" : "th"}`;
  const tenure = tenureRaw === "new customer" && house.others.some((b) => b.account.relation === "before") ? "new owners here" : tenureRaw;
  // MEMBERSHIPS-03: a plan check on a plan that is active or "ended · checks
  // owed" is COVERED — the tech should never hear "win-back" on that call.
  const isPlanCheck = !!tuneupType(s.notes) || /\b(plus|tune|check|ck)\b/i.test(s.serviceType ?? "");
  const covered = isPlanCheck && !!member && (member.status === "active" || member.status === "expiring" || member.status === "ended");
  const parts = [
    tenure,
    member
      ? member.status === "active" ? `PLUS member${member.expireDate ? ` (expires ${fmtShortDate(member.expireDate)})` : ""}`
        : member.status === "expiring" ? `PLUS member · expires ${fmtShortDate(member.expireDate) ?? "soon"}`
        // MEMBERSHIP-ESC-FULFILLED: still a member (paid through), no plan check left — the renewal call.
        : member.status === "fulfilled" ? `PLUS member · all plan checks used · paid through ${fmtShortDate(member.expireDate) ?? ""} · renewal due`
        : member.status === "ended" ? `plan ended ${fmtShortDate(member.expireDate) ?? ""} · ${member.checksOwed} check${member.checksOwed === 1 ? "" : "s"} owed`
        : member.status === "expired" ? "plan expired"
        : null
      : null,
    covered ? "this check is covered" : null,
    last?.date ? `last here ${fmtShortDate(last.date)}${last.tech ? ` with ${last.tech.split(/\s+/)[0]}` : ""}`
      : hist && hist.length === 0 && houseLast?.date && houseWho
        ? `${ordinal(priorVisits + 1)} visit to this house · last here ${fmtShortDate(houseLast.date)}${houseLast.tech ? ` with ${houseLast.tech.split(/\s+/)[0]}` : ""} (${houseWho.name})`
        : hist && hist.length === 0 ? "first visit" : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return <div className="relative px-3 pt-2 text-[12.5px] font-medium leading-snug text-slate-500">{parts.join(" · ")}</div>;
}

const WATCH_DOT: Record<WatchSeverity, string> = { blocking: "bg-rose-500", routing: "bg-amber-500", access: "bg-sky-500", info: "bg-slate-300" };

const shortLabel = (t: string, n = 22) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t);

/** A watch-out as a chip headline ("Paired address", "Call ahead") — the
 *  sentence itself is one tap away. */
function watchChipLabel(it: WatchItem, daysLeft: number | null): string {
  const v = it.signal ? stripStars(it.signal.value) : "";
  switch (it.kind) {
    case "dns": return "Do not service";
    case "inactive": return "Inactive account";
    case "renewal": return daysLeft != null ? `Plan expires · ${daysLeft} day${daysLeft === 1 ? "" : "s"}` : "Plan expiring";
    case "ended": return "Plan ended · check owed · renewal due";
    case "followup": return it.severity === "routing" ? "Follow-up due" : "Follow-up set";
    case "lapsed": return daysLeft != null ? `Plan expired · ${Math.abs(daysLeft)} days ago` : "Plan expired";
    case "hazard": return "Caution";
    case "pairing": return "Paired address";
    case "tech_pref": {
      const exclude = !!(it.signal?.detail as { exclude?: boolean } | null)?.exclude || /^(not|no|don'?t)\b/i.test(v);
      const who = v.replace(/^(not|no|don'?t send)\s+/i, "");
      return shortLabel(exclude ? `Don't send ${who}` : `Requests ${who}`);
    }
    case "skill_req": return shortLabel(v || "Crew requirement");
    case "window_pref": return shortLabel(v || "Time window");
    case "seasonal": return "Away for the season";
    case "vip": return "VIP";
    case "access_instruction": return /\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/.test(v) ? "Call ahead" : "Getting in";
    case "access_code": return "Gate code";
    case "animal": return v && v.split(/\s+/).length <= 3 ? shortLabel(v) : "Animal";
    case "business_hours": return "Hours";
    case "caller_verification": return "Who may authorize";
    case "consumable": return "Consumable";
    case "heads_up": return /^\+?[\d\s().-]{10,16}$/.test(v) ? "Call ahead" : "Heads-up";
    default: return it.kind.replace(/_/g, " ");
  }
}

/** Direction B: the office's Watch-outs as header chips — headlines, four at
 *  most; tap one to read the sentence; "+N" (or the sentence) opens the
 *  Customer tab, where the whole list lives. */
function CallWatchChips({ s, onMore }: { s: Stop; onMore: () => void }) {
  const w = useWatchouts({ custno: s.custno ?? "", locno: s.locno });
  const renewal = useCustomerAgreementRenewal(s.custno ?? undefined).data;
  const [openKey, setOpenKey] = useState<string | null>(null);
  const items = w.items;
  if (items.length === 0) return null;
  const shown = items.slice(0, 4);
  const open = openKey ? items.find((it) => it.key === openKey) ?? null : null;
  return (
    <div className="relative px-3 pt-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {shown.map((it) => {
          const urgent = it.severity === "blocking" || it.kind === "renewal" || it.kind === "lapsed";
          const warm = it.kind === "ended";
          const Icon = it.icon;
          const on = openKey === it.key;
          return (
            <button key={it.key} type="button" onClick={() => setOpenKey(on ? null : it.key)}
              className={cn(
                "m-chip !text-[11.5px] [touch-action:manipulation]",
                urgent ? "m-chip-bad" : warm ? "m-chip-warn" : "",
                on && "ring-2 ring-[var(--m-accent2)]",
              )}>
              <Icon className="h-3 w-3 shrink-0" /> {watchChipLabel(it, renewal?.days_left ?? null)}
            </button>
          );
        })}
        {items.length > shown.length && (
          <button type="button" onClick={onMore} className="m-chip !text-[11.5px] [touch-action:manipulation]">
            +{items.length - shown.length}
          </button>
        )}
      </div>
      {open && (
        <div className="m-card mt-2 flex items-start gap-1.5 px-3 py-2 text-[13px] leading-snug text-slate-700">
          <span className={cn("mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full", WATCH_DOT[open.severity])} />
          <span className="min-w-0 flex-1">{open.text}</span>
          <button type="button" onClick={onMore} className="shrink-0 text-[12.5px] font-semibold text-brand-700 [touch-action:manipulation]">All ›</button>
        </div>
      )}
    </div>
  );
}

/** Direction B, top of the Customer tab: the full Pegasus snapshot. */
function CallSnapshotCard({ s }: { s: Stop }) {
  const snap = useLocationNotesSummary(s.custno ?? undefined, s.locno ?? "00001");
  const summary = snap.data?.summary?.trim() ?? "";
  return (
    <section className="rounded-3xl border border-sky-200 bg-sky-50 p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-brand-700">
        <Sparkles className="h-3.5 w-3.5" /> Pegasus snapshot
        <InfoTip label="Where this comes from">Written by Pegasus from the notes and history — double-check anything critical.</InfoTip>
      </div>
      {snap.isLoading ? (
        <div className="mt-2 space-y-1.5"><div className="h-3.5 w-full animate-pulse rounded bg-sky-100" /><div className="h-3.5 w-5/6 animate-pulse rounded bg-sky-100" /><div className="h-3.5 w-2/3 animate-pulse rounded bg-sky-100" /></div>
      ) : summary ? (
        <>
          <p className="mt-1.5 whitespace-pre-line text-[13.5px] leading-relaxed text-slate-800">{summary}</p>
        </>
      ) : (
        <p className="mt-1.5 text-[12.5px] text-slate-500">No snapshot yet for this address.</p>
      )}
    </section>
  );
}

/** Direction B, top of the Customer tab: the office's Watch-outs — the SAME
 *  list the 360's rail shows, with its severity dots. */
function CallWatchList({ s }: { s: Stop }) {
  const w = useWatchouts({ custno: s.custno ?? "", locno: s.locno });
  const items = w.items;
  return (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-center gap-2 text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
        Watch for {items.length > 0 && <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-extrabold text-slate-600">{items.length}</span>}
      </div>
      {w.isLoading ? (
        <div className="mt-2 h-8 animate-pulse rounded-xl bg-slate-100" />
      ) : items.length === 0 ? (
        <p className="mt-1 text-[12.5px] text-slate-400">Nothing flagged on this account.</p>
      ) : (
        <ul className="mt-1">
          {items.map((it) => (
            <li key={it.key} className="flex items-start gap-2 py-1.5 text-[13px] leading-snug text-slate-900">
              <span className={cn("mt-[6px] h-2 w-2 shrink-0 rounded-full", WATCH_DOT[it.severity])} />
              <span className={cn("min-w-0 flex-1", it.proposed && "text-slate-600")}>{it.text}{it.proposed && <span className="ml-1 text-[10.5px] text-slate-400">· from the notes</span>}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** After the wrap: where the tech is headed next, with the drive. */
function NextCallLine({ from, next, onGo }: { from: Stop; next: Stop | null; onGo: () => void }) {
  if (!next) return <p className="text-center text-[12.5px] font-semibold text-slate-500">That was your last call of the day.</p>;
  const mi = from.lat && from.lng && next.lat && next.lng ? milesBetween({ lat: from.lat, lng: from.lng }, { lat: next.lat, lng: next.lng }) : null;
  const min = mi != null ? Math.round(driveMinutes(mi)) : null;
  return (
    <button type="button" onClick={onGo} className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left [touch-action:manipulation] active:bg-slate-50">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white"><ChevronRight className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Next call</span>
        <span className="block truncate text-[14px] font-extrabold text-slate-900">{next.pickup ? "Part pickup" : next.name}</span>
        <span className="block truncate text-[12px] text-slate-500">{[next.city, next.window ?? "anytime", min != null ? `~${min} min` : null].filter(Boolean).join(" · ")}</span>
      </span>
    </button>
  );
}

/* ------------------------------------------------------- FOLLOW-UP-01 */

/** Common follow-ups, one tap: the title, a sensible "when", a sensible repeat. */
const FOLLOWUP_PRESETS: { label: string; when: FuWhen; repeat: number | null }[] = [
  { label: "Filter change", when: "3m", repeat: 3 },
  { label: "Humidifier pad", when: "fall", repeat: 12 },
  { label: "UV bulb", when: "12m", repeat: 12 },
  { label: "Water heater aging", when: "6m", repeat: null },
  { label: "Quote check-in", when: "1m", repeat: null },
  { label: "Replacement talk", when: "spring", repeat: null },
  { label: "Warranty ending", when: "6m", repeat: null },
];
type FuWhen = "1m" | "3m" | "6m" | "12m" | "spring" | "fall" | "date";
const FU_WHEN: { key: FuWhen; label: string }[] = [
  { key: "1m", label: "In 1 month" }, { key: "3m", label: "In 3 months" }, { key: "6m", label: "In 6 months" }, { key: "12m", label: "In a year" },
  { key: "spring", label: "Next spring" }, { key: "fall", label: "Next fall" }, { key: "date", label: "Pick a date" },
];
const FU_REPEAT: { key: number | null; label: string }[] = [
  { key: null, label: "Once" }, { key: 3, label: "Every 3 months" }, { key: 6, label: "Every 6 months" }, { key: 12, label: "Every year" }, { key: 24, label: "Every 2 years" },
];
function fuDateFor(when: FuWhen, picked: string): string | null {
  switch (when) {
    case "1m": return rollToWeekday(monthsFromNowISO(1));
    case "3m": return rollToWeekday(monthsFromNowISO(3));
    case "6m": return rollToWeekday(monthsFromNowISO(6));
    case "12m": return rollToWeekday(monthsFromNowISO(12));
    case "spring": return rollToWeekday(nextSeasonISO("spring"));
    case "fall": return rollToWeekday(nextSeasonISO("fall"));
    default: return picked || null;
  }
}
const fmtFuDate = (iso: string) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

/** Set a dated follow-up from the call. It comes due on Today — the office's,
 *  or his own phone if he keeps it — and stays until someone clears it. */
function FollowUpWizard({ stop, empno, onClose, onSent }: { stop: Stop; empno: string | null; onClose: () => void; onSent: () => void }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [when, setWhen] = useState<FuWhen>("6m");
  const [picked, setPicked] = useState("");
  const [repeat, setRepeat] = useState<number | null>(null);
  const [mine, setMine] = useState(false);
  const add = useAddFollowup();
  const [sent, setSent] = useState<string | null>(null);
  const due = fuDateFor(when, picked);
  const canSend = !!stop.custno && title.trim().length > 0 && !!due && !add.isPending;

  const tryClose = () => {
    if (title.trim() || details.trim()) {
      if (!window.confirm("Discard this follow-up?")) return;
    }
    onClose();
  };
  const send = () => {
    if (!stop.custno || !due) return;
    add.mutate(
      {
        custno: stop.custno, locno: stop.locno, title: title.trim(), details: details.trim() || null,
        due_date: due, repeat_months: repeat, assigned_empno: mine ? empno : null,
        origin_dispatch: stop.ref, created_empno: empno,
      },
      { onSuccess: () => setSent(due) },
    );
  };
  useEffect(() => {
    if (!sent) return;
    const id = window.setTimeout(onSent, 1600);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col items-center justify-center bg-[#f6f5f1] p-8 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100"><BellRing className="h-8 w-8 text-amber-600" /></span>
        <h3 className="mt-4 text-xl font-extrabold text-slate-900">Follow-up set for {fmtFuDate(sent)}</h3>
        <p className="mt-2 max-w-[32ch] text-sm leading-relaxed text-slate-500">
          {mine ? "It'll be on your Today that day until you clear it." : "It'll be on the office's Today that day until someone clears it."}
          {repeat ? ` Clearing it sets the next one.` : ""}
        </p>
        <button type="button" onClick={onSent} className="mt-6 w-full rounded-2xl bg-gradient-to-b from-red-600 to-red-700 py-4 text-[15px] font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_rgba(185,28,28,0.5)] [touch-action:manipulation] active:scale-[0.98]">Back to the call</button>
      </div>
    );
  }

  const chip = (on: boolean, cls = "bg-slate-900 text-white") => cn("rounded-xl px-2.5 py-2 text-[12.5px] font-bold leading-tight [touch-action:manipulation]", on ? cls : "bg-slate-100 text-slate-600 active:bg-slate-200");
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={tryClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Cancel"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-base font-bold text-slate-900">Follow Up <InfoTip label="What a follow-up is">A dated reminder for you or the office. The customer never sees it.</InfoTip></div>
            <div className="truncate text-[11px] text-slate-500">{stop.name}</div>
          </div>
          <button type="button" onClick={tryClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 active:bg-slate-100" aria-label="Cancel"><X className="h-5 w-5" /></button>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {!stop.custno && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">This call has no customer account yet — set the follow-up from the office once it does.</div>
        )}
        <div>
          <label className="block text-sm font-bold text-slate-800">What should we follow up on?</label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {FOLLOWUP_PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => { setTitle(p.label); setWhen(p.when); setRepeat(p.repeat); }} className={chip(title === p.label, "bg-amber-600 text-white")}>{p.label}</button>
            ))}
          </div>
          <textarea value={title} onChange={(e) => setTitle(e.target.value)} rows={2} placeholder="Water heater near end of life — talk replacement in the spring" autoFocus
            className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] leading-relaxed focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/25" />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-800">When?</label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {FU_WHEN.map((w) => <button key={w.key} type="button" onClick={() => setWhen(w.key)} className={chip(when === w.key)}>{w.label}</button>)}
          </div>
          {when === "date" && (
            <input type="date" value={picked} onChange={(e) => setPicked(e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-amber-500 focus:outline-none" />
          )}
          <p className="mt-1.5 text-[12px] text-slate-500">{due ? <>Comes due <b className="text-slate-700">{fmtFuDate(due)}</b>{when !== "date" ? " — weekends roll to Monday" : ""} <InfoTip label="Until it's cleared">It stays on Today until it&apos;s cleared, closed days included.</InfoTip></> : "Pick a date."}</p>
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-800">Repeat?</label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {FU_REPEAT.map((r) => <button key={String(r.key)} type="button" onClick={() => setRepeat(r.key)} className={chip(repeat === r.key)}>{r.label}</button>)}
          </div>
          {repeat && <p className="mt-1.5 text-[12px] text-slate-500">When you clear it, the next one comes due {repeat === 12 ? "a year" : repeat === 24 ? "two years" : `${repeat} months`} later.</p>}
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-800">Who handles it?</label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMine(false)} className={chip(!mine)}>The office</button>
            <button type="button" onClick={() => setMine(true)} disabled={!empno} className={chip(mine)}>Me — on my phone</button>
          </div>
          <p className="mt-1.5 text-[12px] text-slate-500">{mine ? "On your Today when it's due — the office sees it too." : "On the office's Today when it's due."}</p>
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-800">Anything else? <span className="font-normal text-slate-400">(optional)</span></label>
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={2} placeholder="Customer said call after the holidays · Bradford White, 2009"
            className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/25" />
        </div>
      </div>
      <div className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button type="button" disabled={!canSend} onClick={send}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-600 py-4 text-[15px] font-bold text-white [touch-action:manipulation] active:opacity-90 disabled:opacity-50">
          {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Set the follow-up
        </button>
      </div>
    </div>
  );
}

/** The tech's own follow-ups on Today: due, overdue, or coming this week.
 *  Nothing here expires — it stays until Done, a snooze, or handing it to the office. */
function TechFollowUpsCard({ empno }: { empno: string | null }) {
  const list = useTechFollowups(empno).data ?? [];
  const complete = useCompleteFollowup();
  const snooze = useSnoozeFollowup();
  const assign = useAssignFollowup();
  const custnos = useMemo(() => [...new Set(list.map((f) => f.custno))], [list]);
  const names = useQuery({
    queryKey: ["fu_names", custnos.join(",")],
    enabled: custnos.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos);
      return new Map(((data ?? []) as { custno: string; fullname: string | null }[]).map((c) => [c.custno, displayCustomerName(null, c.fullname)]));
    },
  });
  if (list.length === 0) return null;
  const today = todayISO();
  const dueNow = list.filter((f) => f.due_date <= today && !(f.snoozed_until && f.snoozed_until > today));
  const soon = list.filter((f) => !dueNow.includes(f));
  const row = (f: FollowUp) => {
    const dl = dueLabel(f);
    return (
      <li key={f.id} className="py-2.5">
        <div className="flex items-start gap-2.5">
          <button type="button" onClick={() => complete.mutate(f)} title={f.repeat_months ? "Done — the next one is created automatically" : "Done"}
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-amber-300 bg-white text-transparent [touch-action:manipulation] active:bg-emerald-50 active:text-emerald-600">
            <Check className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-bold leading-snug text-slate-900">{f.title}</div>
            <div className="truncate text-[12px] text-slate-600">{names.data?.get(f.custno) ?? f.custno}{f.details ? ` — ${f.details}` : ""}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-bold", dl.tone === "overdue" ? "bg-rose-50 text-rose-700" : dl.tone === "today" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600")}>{dl.text}</span>
              {f.repeat_months && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">↻ every {f.repeat_months === 12 ? "year" : f.repeat_months === 24 ? "2 years" : `${f.repeat_months} mo`}</span>}
              <button type="button" onClick={() => snooze.mutate({ id: f.id, until: daysFromNowISO(7) })} className="text-[11px] font-semibold text-slate-500 [touch-action:manipulation] active:text-slate-800">Snooze a week</button>
              <button type="button" onClick={() => assign.mutate({ id: f.id, assigned_empno: null })} className="text-[11px] font-semibold text-slate-500 [touch-action:manipulation] active:text-slate-800">Hand to the office</button>
            </div>
          </div>
        </div>
      </li>
    );
  };
  return (
    <div className="rounded-3xl border border-amber-200/70 bg-amber-50/40 p-3.5">
      <div className="flex items-center gap-2">
        <BellRing className="h-4 w-4 text-amber-600" />
        <span className="text-[15px] font-bold tracking-tight text-slate-900">Your Follow-Ups</span>
        {dueNow.length > 0 && <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[11px] font-extrabold text-white">{dueNow.length} due</span>}
      </div>
      {dueNow.length > 0 && <ul className="mt-1 divide-y divide-amber-100">{dueNow.map(row)}</ul>}
      {soon.length > 0 && (
        <>
          <div className="mt-2 text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Coming up this week</div>
          <ul className="divide-y divide-amber-100">{soon.map(row)}</ul>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------ call: CUSTOMER tab */

/** Press-and-hold anywhere to copy; a normal tap still does its job. The
 *  copy chip confirms without a toast layer (Seth 2026-08-27). */
function HoldCopy({ text, onTap, className, children }: {
  text: string;
  onTap?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const timer = useRef<number | null>(null);
  const fired = useRef(false);
  const [copied, setCopied] = useState(false);
  const start = () => {
    fired.current = false;
    timer.current = window.setTimeout(() => {
      fired.current = true;
      try { if (navigator.vibrate) navigator.vibrate(12); } catch { /* fine */ }
      void navigator.clipboard?.writeText(text).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      }).catch(() => { /* clipboard blocked — nothing to say */ });
    }, 480);
  };
  const cancel = () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };
  return (
    <button
      type="button"
      className={cn("relative text-left", className)}
      onTouchStart={start}
      onTouchEnd={cancel}
      onTouchMove={cancel}
      onMouseDown={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => { if (fired.current) { fired.current = false; return; } onTap?.(); }}
    >
      {children}
      {copied && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold text-white shadow">
          Copied ✓
        </span>
      )}
    </button>
  );
}

function CustomerTab({ s }: { s: Stop }) {
  const member = useMembershipCard(s.custno ?? undefined);
  const locNote = useLocationNote(s.custno, s.locno);
  const contacts = useCustomerContacts(s.custno, s.locno);
  const m = member.data;
  const c = contacts.data;
  const phones = phonesFor(s);

  // This-call numbers first (a number dispatch wrote into the notes is the one
  // to use), then every account + location number the office knows, deduped.
  const allPhones = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ n: string; label: string }> = [];
    const push = (n: string, label: string) => {
      const k = n.replace(/[^0-9]/g, "");
      if (k.length < 7 || seen.has(k)) return;
      seen.add(k);
      out.push({ n, label });
    };
    for (const p of phones.all) push(p.n, p.label);
    for (const p of c?.phones ?? []) push(p.n, p.label);
    return out;
  }, [phones.all, c?.phones]);

  const add1 = s.address ?? c?.add1 ?? null;
  const cityLine = [s.city ?? c?.city, [c?.state ?? "MN", s.zip ?? c?.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const fullAddr = [add1, c?.add2, cityLine].filter(Boolean).join(", ");
  const mapsHref = add1 ? `https://maps.apple.com/?daddr=${encodeURIComponent(fullAddr)}` : null;

  return (
    <>
      {/* Direction B (FIELD-CALL-01): the full Pegasus snapshot and the
          office's Watch-outs come first — the brief a tech reads before the
          door lives here; the header chips are its headlines. */}
      <CallSnapshotCard s={s} />
      <CallWatchList s={s} />

      {/* Overview — the house itself, name, address. Hold anything to copy. */}
      <section className="overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        {add1 && (
          <StreetViewThumb
            address={{ add1, city: s.city ?? c?.city ?? null, state: c?.state ?? "MN", zip: s.zip ?? c?.zip ?? null }}
            className="h-36 w-full"
          />
        )}
        <div className="p-4">
          <HoldCopy text={c?.fullname || s.name} className="block w-full">
            <div className="text-[16px] font-extrabold leading-tight text-slate-900">{c?.fullname || s.name}</div>
          </HoldCopy>
          {add1 && (
            <HoldCopy text={fullAddr} onTap={() => { if (mapsHref) window.open(mapsHref, "_blank"); }} className="mt-1 block w-full active:opacity-80">
              <div className="text-[13.5px] font-semibold leading-snug text-slate-600">{[add1, c?.add2].filter(Boolean).join(", ")}</div>
              {cityLine && <div className="text-[12px] text-slate-400">{cityLine}</div>}
            </HoldCopy>
          )}
        </div>
      </section>

      {/* Their plan — WHAT they bought, what it included, what they paid. */}
      <section className={cn("rounded-3xl border p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", m ? "border-emerald-200 bg-emerald-50/70" : "border-black/[0.08] bg-white")}>
        {/* Plain-English kicker so a brand-new tech knows exactly what this
            section IS (Seth 2026-08-31: "it could be confusing"). */}
        <div className={cn("mb-1 text-[9.5px] font-extrabold uppercase tracking-widest", m ? "text-emerald-700/70" : "text-slate-400")}>
          Maintenance plan (PLUS membership)
        </div>
        <div className="flex items-center gap-2">
          <ShieldCheck className={cn("h-5 w-5", m ? "text-emerald-600" : "text-slate-300")} />
          <h4 className="min-w-0 flex-1 truncate text-[13px] font-extrabold text-slate-800">
            {member.isLoading ? "Checking plan…" : m ? m.planName : "Not a member"}
          </h4>
          {m && (
            <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold",
              m.status === "active" ? "bg-emerald-100 text-emerald-700" : m.status === "expiring" || m.status === "ended" || m.status === "fulfilled" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700")}>
              {m.status === "active" ? "Active member" : m.status === "ended" ? "Ended · checks owed" : m.status === "fulfilled" ? "All checks used" : m.status}
            </span>
          )}
        </div>
        {m && m.status === "ended" ? (
          <p className="mt-1 text-[11px] leading-snug text-amber-800/90">
            The plan ended {fmtShortDate(m.expireDate) ?? ""} but still owes {m.checksOwed === 1 ? "a check" : `${m.checksOwed} checks`}{m.owedLabels ? ` (${m.owedLabels})` : ""}{m.owedUntil ? ` — good until ${fmtShortDate(m.owedUntil)}` : ""}. Member pricing still applies. This is the renewal moment.
          </p>
        ) : m && m.status === "fulfilled" ? (
          // MEMBERSHIP-ESC-FULFILLED: paid through, every check of the term done — no plan
          // visit left to give away; member pricing holds; the call is the renewal.
          <p className="mt-1 text-[11px] leading-snug text-amber-800/90">
            Every tune-up on this plan has been done — it is paid through {fmtShortDate(m.expireDate) ?? ""}. Member pricing still applies on repairs, but there is no plan check left to book. This is the renewal moment.
          </p>
        ) : m ? (
          <p className="mt-1 text-[11px] leading-snug text-emerald-800/80">
            This home pays for a maintenance plan — the chips below are the equipment their plan covers. Member pricing applies on repairs.
          </p>
        ) : null}
        {m ? (
          (() => {
            const fd = (iso: string | null) =>
              iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
            const pos = (() => {
              if (!m.begDate || !m.expireDate) return null;
              const b = new Date(m.begDate + "T00:00:00").getTime();
              const e = new Date(m.expireDate + "T00:00:00").getTime();
              if (!(e > b)) return null;
              const now = Date.now();
              const pct = Math.max(0, Math.min(100, Math.round(((now - b) / (e - b)) * 100)));
              const yrs = Math.max(1, Math.round((e - b) / (365.25 * 86400000)));
              const yr = Math.min(yrs, Math.max(1, Math.ceil((now - b) / (365.25 * 86400000))));
              return { pct, yrs, yr };
            })();
            return (
              <>
                {/* Where in the agreement's life we are — at a glance. */}
                {pos && (
                  <div className="mt-2.5 rounded-2xl bg-white/80 px-3 py-2.5">
                    <div className="flex items-baseline justify-between">
                      <span className="text-[11.5px] font-semibold text-slate-500">Agreement</span>
                      <span className="text-[11px] font-extrabold text-emerald-700">Year {pos.yr} of {pos.yrs}</span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pos.pct}%` }} />
                    </div>
                    <div className="mt-1 flex justify-between text-[10px] font-semibold text-slate-400">
                      <span>started {fd(m.begDate)}</span>
                      <span>ends {fd(m.expireDate)}</span>
                    </div>
                  </div>
                )}
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div className="rounded-2xl bg-white/80 px-3 py-2">
                    <div className="text-[11.5px] font-semibold text-slate-500">Paid for the plan</div>
                    <div className="text-[15px] font-extrabold tabular-nums text-slate-900">{m.priceAmount != null ? money$(m.priceAmount) : "—"}</div>
                    {m.termLabel && <div className="text-[10px] font-semibold text-slate-400">{m.termLabel}</div>}
                  </div>
                  <div className="rounded-2xl bg-white/80 px-3 py-2">
                    <div className="text-[11.5px] font-semibold text-slate-500">Plan visits</div>
                    {m.planVisits ? (
                      <>
                        <div className="text-[15px] font-extrabold tabular-nums text-slate-900">{m.planVisits.used} of {m.planVisits.total}</div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, Math.round((m.planVisits.used / Math.max(1, m.planVisits.total)) * 100))}%` }} />
                        </div>
                      </>
                    ) : (
                      <div className="text-[15px] font-extrabold text-slate-300">—</div>
                    )}
                  </div>
                </div>
                {(m.includes.length > 0 || m.secondSystem) && (
                  <div className="mt-2">
                    <div className="text-[11.5px] font-semibold text-emerald-700">What their plan covers</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {m.includes.map((x) => (
                        <span key={x} className="rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold text-emerald-700 ring-1 ring-emerald-200">{x}</span>
                      ))}
                      {m.secondSystem && <span className="rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold text-emerald-700 ring-1 ring-emerald-200">2nd system</span>}
                    </div>
                  </div>
                )}
                <div className="mt-2 space-y-0.5 text-[11.5px] text-slate-500">
                  {m.nextCheck && <div>Next up: <b className="text-slate-700">{m.nextCheck}</b></div>}
                  {(m.renewDate || m.lastService) && (
                    <div>
                      {[m.renewDate ? `Last renewed ${fd(m.renewDate)}` : null, m.lastService ? `last serviced ${fd(m.lastService)}` : null]
                        .filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <div>
                    {[m.memberSinceYear ? `Member since ${m.memberSinceYear}` : null, m.autopay ? "autopay" : null]
                      .filter(Boolean).join(" · ")}
                  </div>
                </div>
              </>
            );
          })()
        ) : null}
      </section>

      {/* Location notes — gate codes, dogs, lockboxes. */}
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">House Notes</h4>
        {locNote.isLoading ? (
          <div className="mt-2 h-8 animate-pulse rounded-lg bg-slate-100" />
        ) : locNote.data ? (
          <p className="mt-1.5 whitespace-pre-wrap rounded-2xl bg-amber-50 px-3 py-2 text-[13.5px] leading-relaxed text-amber-900">{locNote.data}</p>
        ) : (
          <p className="mt-1 text-xs text-slate-400">No gate codes, pets or access notes on file.</p>
        )}
      </section>

      {/* Contact — every number and email the company knows. Tap calls or
          emails; press & hold copies. */}
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900">Reach Them <InfoTip label="Copy a number">Press and hold a name, number, email or the address to copy it. Tap the address to navigate.</InfoTip></h4>
        {allPhones.length > 0 || (c?.emails.length ?? 0) > 0 ? (
          <div className="mt-2 space-y-2">
            {allPhones.map((p, i) => {
              // Call + Text per number (Seth 2026-08-31) — the text button
              // hides when the label says it's a landline-type number.
              const digits = p.n.replace(/[^0-9]/g, "");
              const textable = !/home|house|land|work|office|fax|main/i.test(p.label);
              return (
                <div key={p.n}
                  className={cn("flex w-full items-center gap-2.5 rounded-2xl px-3.5 py-2.5",
                    i === 0 ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-800")}>
                  <HoldCopy text={p.n} className="flex min-w-0 flex-1 flex-col text-left leading-tight">
                    <span className="text-sm font-bold tabular-nums">{p.n}</span>
                    <span className={cn("text-[10.5px] font-semibold", i === 0 ? "text-slate-300" : "text-slate-400")}>
                      {p.label}{i === 0 && allPhones.length > 1 ? " · best number" : ""}
                    </span>
                  </HoldCopy>
                  <a href={`tel:${digits}`} aria-label={`Call ${p.n}`}
                    className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full [touch-action:manipulation] active:scale-95",
                      i === 0 ? "bg-white/15 text-white" : "bg-slate-900 text-white")}>
                    <Phone className="h-4 w-4" />
                  </a>
                  {textable && (
                    <a href={`sms:${digits}`} aria-label={`Text ${p.n}`}
                      className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full [touch-action:manipulation] active:scale-95",
                        i === 0 ? "bg-white/15 text-white" : "border border-slate-200 bg-white text-slate-700")}>
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  )}
                </div>
              );
            })}
            {(c?.emails ?? []).map((em) => (
              <HoldCopy key={em.e} text={em.e} onTap={() => { window.location.href = `mailto:${em.e}`; }}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-800 active:bg-slate-50">
                <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate text-sm font-bold">{em.e}</span>
                  <span className="text-[10.5px] font-semibold text-slate-400">{em.label}</span>
                </span>
              </HoldCopy>
            ))}
          </div>
        ) : contacts.isLoading ? (
          <div className="mt-2 h-12 animate-pulse rounded-2xl bg-slate-100" />
        ) : (
          <p className="mt-1 text-xs text-slate-400">No phone or email on file — flag it to the office in Chat.</p>
        )}
      </section>

      {/* Files & documents — signed proposals, permits, past-visit photos. */}
      <CustomerDocs s={s} />
    </>
  );
}

/* ---------------------------------------------------- call: EQUIPMENT tab */

/** Equipment tab — SAME brain as the office 360 and portal (buildEquipmentView):
 *  classified into Systems / Auxiliary & IAQ / Consumables, with older and
 *  replaced units resolved out of sight behind a toggle. Field-added Pegasus
 *  units ride on top. */
function EquipmentTabM({ s, empno, techFirst }: { s: Stop; empno: string | null; techFirst: string | null }) {
  const custno = s.custno ?? undefined;
  const rows = useCustomerEquipment(custno, s.locno);
  const aiOverrides = useAiEquipmentOverrides(rows.data ?? []).data;
  const { data: lifecycleOverrides } = useEquipmentOverrides(custno, s.locno);
  const pegUnits = (useEquipmentAt(s.custno, s.locno).data ?? []).filter((e) => e.src === "peg");
  const [addOpen, setAddOpen] = useState(false);
  const [showOld, setShowOld] = useState(false);
  // PROPERTY-LINK-01: units the house's OTHER accounts carry (the furnace we
  // installed for the builder, still in the basement under the builder's name).
  const houseAccounts = useHouseAccounts(s.custno, s.locno).data ?? [];
  const houseOthers = useMemo(() => houseAccounts.filter((a) => !a.isSelf), [houseAccounts]);
  const houseUnits = useHouseEquipment(houseOthers).data ?? [];
  const houseViews = useMemo(
    () => houseUnits.map((b) => ({ account: b.account, view: buildEquipmentView(b.rows) })).filter((x) => x.view.systems.length + x.view.auxiliary.length > 0),
    [houseUnits],
  );

  // Office-identical field-correction merge before classifying.
  const merged = useMemo(() => {
    const data = rows.data ?? [];
    if (!lifecycleOverrides || lifecycleOverrides.size === 0) return data;
    return data.map((r) => {
      const ov = lifecycleOverrides.get(r.counter);
      if (!ov) return r;
      return { ...r, mfg: ov.mfg ?? r.mfg, model: ov.model ?? r.model, serial: ov.serial ?? r.serial, install_date: ov.install_date ?? r.install_date, eq_type: ov.eq_type ?? r.eq_type };
    });
  }, [rows.data, lifecycleOverrides]);
  const view = useMemo(() => buildEquipmentView(merged, aiOverrides, lifecycleOverrides), [merged, aiOverrides, lifecycleOverrides]);
  const oldCount = view.replaced.length;

  // TECH-READ-01: one unit = what it is, the model (17px), the serial on its
  // own line (16px copy chip) — tap or hold either to copy (COPY-01).
  const UnitRow = ({ label, category, mfg, model, serial, loc, year, chip, muted }: {
    label: string | null; category?: string | null; mfg: string | null; model: string | null; serial: string | null;
    loc?: string | null; year?: number | string | null; chip?: string | null; muted?: boolean;
  }) => (
    <li className="py-3">
      <div className="flex items-start gap-2">
        <UnitLines
          kind={label}
          category={category}
          brand={mfg}
          model={model}
          serial={serial}
          muted={muted}
          meta={[loc, year ? `installed ${year}` : null].filter(Boolean).join(" · ") || null}
        />
        {chip && <span className="m-chip ml-auto shrink-0">{chip}</span>}
      </div>
    </li>
  );

  const Section = ({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) => (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-baseline justify-between">
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">{title}</h4>
        {hint && <span className="text-[10px] text-slate-300">{hint}</span>}
      </div>
      <ul className="mt-1 divide-y divide-slate-100">{children}</ul>
    </section>
  );

  const empty = !rows.isLoading && merged.length === 0 && pegUnits.length === 0 && houseViews.length === 0;

  return (
    <>
      <div className="flex items-center justify-between px-1">
        <span className="text-[15px] font-bold tracking-tight text-slate-900">Equipment at this address</span>
        {s.custno && (
          <button type="button" onClick={() => setAddOpen(true)} className="m-btn m-btn-tint px-4">
            <Camera className="h-4 w-4" /> Add a Unit
          </button>
        )}
      </div>

      {rows.isLoading ? (
        <div className="h-24 animate-pulse rounded-3xl bg-slate-100" />
      ) : empty ? (
        <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <p className="text-xs leading-relaxed text-slate-400">
            {houseOthers.length > 0 ? "Nothing on file from any owner of this house yet." : "Nothing on file yet."}
          </p>
        </section>
      ) : (
        <>
          {pegUnits.length > 0 && (
            <Section title="Added in the field" hint="Pegasus-verified">
              {pegUnits.map((e) => (
                <UnitRow key={e.counter} label={e.eq_type} mfg={e.mfg} model={e.model} serial={e.serial} loc={e.equip_loc} year={e.install_date?.slice(0, 4)} chip="field-added" />
              ))}
            </Section>
          )}
          {view.systems.length > 0 && (
            <Section title="Systems">
              {view.systems.map((e) => (
                <UnitRow key={e.counter} label={e.categoryLabel} category={e.category} mfg={e.mfg} model={e.model} serial={e.serial} loc={e.equip_loc} year={e.installYear} />
              ))}
            </Section>
          )}
          {view.auxiliary.length > 0 && (
            <Section title="Auxiliary & indoor air quality">
              {view.auxiliary.map((e) => (
                <UnitRow key={e.counter} label={e.categoryLabel} category={e.category} mfg={e.mfg} model={e.model} serial={e.serial} loc={e.equip_loc} year={e.installYear} />
              ))}
            </Section>
          )}
          {view.consumables.length > 0 && (
            <Section title="Consumables" hint="filters, pads, bulbs">
              {view.consumables.map((e) => (
                <UnitRow key={e.counter} label={e.categoryLabel} category={e.category} mfg={e.mfg} model={e.model} serial={e.serial} loc={e.equip_loc} year={e.installYear} />
              ))}
            </Section>
          )}
          {/* PROPERTY-LINK-01: the house's units under another account — read
              here as what's in the house, labeled with whose record it is. */}
          {houseViews.map(({ account, view: hv }) => (
            <Section key={`${account.custno}/${account.locno ?? ""}`} title={`On file under ${account.name}`} hint={[account.role, account.years].filter(Boolean).join(" · ")}>
              {[...hv.systems, ...hv.auxiliary].map((e) => (
                <UnitRow key={`${account.custno}-${e.counter}`} label={e.categoryLabel} category={e.category} mfg={e.mfg} model={e.model} serial={e.serial} loc={e.equip_loc} year={e.installYear}
                  chip={account.isCompany ? "builder's record" : "previous owner's record"} />
              ))}
            </Section>
          ))}
          {oldCount > 0 && (
            <div>
              <button type="button" onClick={() => setShowOld((v) => !v)}
                className="m-btn m-btn-soft mx-auto flex px-4">
                <ChevronDown className={cn("h-4 w-4 transition-transform", showOld && "rotate-180")} />
                {showOld ? "Hide" : "Show"} older &amp; replaced ({oldCount})
              </button>
              {showOld && (
                <div className="mt-2">
                  <Section title="Older & replaced" hint="superseded units">
                    {view.replaced.map((e) => (
                      <UnitRow key={e.counter} label={e.categoryLabel} category={e.category} mfg={e.mfg} model={e.model} serial={e.serial}
                        year={e.installYear}
                        chip={e.deletedInEsc ? "deleted in ESC" : e.replacedByModel ? `replaced → ${e.replacedByModel}` : "replaced"} muted />
                    ))}
                  </Section>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {addOpen && <AddUnitSheet s={s} empno={empno} techFirst={techFirst} onClose={() => setAddOpen(false)} />}
    </>
  );
}

/* ------------------------------------------------------ call: HISTORY tab */

/** PROPERTY-LINK-01 (Seth 2026-10-16, 300 Lake St W): the HOUSE's visits, not
 *  the account's. ESC keeps one customer per party, so the builder's 2024 gas
 *  leak repair sat under The Landschute Group while the new owners' call said
 *  "First visit on record at this address." Every account that has served
 *  this address lists here under an era divider; another owner's write-ups
 *  read in full, their money stays muted ("builder paid") and is never quoted. */
function HistoryTab({ s, empno = null, techFirst = null }: { s: Stop; empno?: string | null; techFirst?: string | null }) {
  const hist = useVisitHistory(s.custno, s.locno);
  const house = useHouseVisitHistory(s.custno, s.locno);
  // HIST-EQUIP-01: which unit each visit installed / worked on.
  const visitEq = useVisitEquipment(s.custno).data;
  const [open, setOpen] = useState<string | null>(null);
  const [other, setOther] = useState<HouseAccount | null>(null);
  const money = (n: number) => n === 0 ? "covered" : `$${Math.round(n).toLocaleString()}`;
  const own = hist.data ?? [];
  const others = house.others;
  const otherVisits = others.reduce((n, b) => n + b.rows.length, 0);
  const owners = 1 + others.length;

  const renderVisit = (v: VisitHistoryRow, paidBy: string | null) => {
    const on = open === `${v.source}-${v.invoice}`;
    return (
      <li key={`${v.source}-${v.invoice}`}>
        <button type="button" onClick={() => setOpen(on ? null : `${v.source}-${v.invoice}`)} className="w-full py-2.5 text-left active:bg-slate-50">
          {/* The important stuff RIGHT IN THE HEADER — date · tech ·
              invoice # · dollars (Seth 2026-08-31). */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-extrabold text-slate-900">
              {v.date ? new Date(`${v.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </span>
            {v.tech && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{v.tech}</span>
            )}
            {paidBy ? (
              <span className="ml-auto text-[12px] font-bold tabular-nums text-slate-400" title="Another owner's invoice — not this customer's price">{money(v.amount)} · {paidBy}</span>
            ) : (
              <span className={cn("ml-auto text-[13px] font-extrabold tabular-nums", v.amount === 0 ? "text-emerald-600" : "text-slate-800")}>{money(v.amount)}</span>
            )}
            <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-slate-300 transition-transform", on && "rotate-180")} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] font-semibold tabular-nums text-slate-400">
            <span className="inline-flex items-center gap-1">Inv <CopyCode value={v.invoice} label="Copy invoice number" /></span>
            {v.dispatch && <span className="inline-flex items-center gap-1">· call <CopyCode value={v.dispatch} label="Copy call number">#{v.dispatch}</CopyCode></span>}
            {v.source === "pegasus" && <span>· Pegasus</span>}
          </div>
          {!on && (v.workNote || v.note) && (
            <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-500">{v.workNote || v.note}</p>
          )}
        </button>
        {on && (
          <div className="space-y-2 pb-3">
            {/* (the Inv / call # line lives in the header as copy chips now) */}
            {v.note && (
              <div className="rounded-2xl bg-slate-50 px-3 py-2">
                <div className="text-[11.5px] font-semibold text-slate-500">The call</div>
                <p className="mt-0.5 whitespace-pre-line text-xs leading-relaxed text-slate-600">{v.note}</p>
              </div>
            )}
            {v.workNote && (
              <div className="rounded-2xl bg-emerald-50/60 px-3 py-2">
                <div className="text-[11.5px] font-semibold text-emerald-600">What we did</div>
                <p className="mt-0.5 whitespace-pre-line text-xs leading-relaxed text-slate-700">{v.workNote}</p>
              </div>
            )}
            {/* HIST-EQUIP-01: the unit(s) — installed by this invoice, or
                tapped by the tech in the field. Honest empty line otherwise. */}
            <VisitEquipmentBlock
              variant="field"
              source={v.source}
              equipment={visitEquipmentFor(visitEq, [v.dispatch, v.invoice])}
            />
            {v.charges.length > 0 && (
              <div className="rounded-2xl bg-slate-50 px-3 py-2">
                <div className="text-[11.5px] font-semibold text-slate-500">What was charged</div>
                <ul className="mt-0.5 divide-y divide-slate-200/60">
                  {v.charges.map((cg, ci) => (
                    <li key={ci} className="flex items-baseline justify-between gap-2 py-1 text-xs">
                      <span className="min-w-0 flex-1 truncate text-slate-600">{cg.d}</span>
                      <span className={cn("shrink-0 font-bold tabular-nums", paidBy ? "text-slate-400" : "text-slate-700")}>${Math.round(cg.amt).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!v.note && !v.workNote && v.charges.length === 0 && (
              <p className="text-xs text-slate-400">No write-up on this visit.</p>
            )}
          </div>
        )}
      </li>
    );
  };

  const eraChip = (label: string) => (
    <div className="flex items-center gap-2 pt-3">
      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10.5px] font-bold leading-snug text-slate-600">{label}</span>
      <span className="h-px flex-1 bg-slate-100" />
    </div>
  );

  return (
    <>
    <TicketCheck s={s} variant="history" />
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Visits at This Address</h4>
        {others.length > 0 && (
          <span className="text-[11px] font-semibold text-slate-400">{own.length + otherVisits} visit{own.length + otherVisits === 1 ? "" : "s"} · {owners} owners</span>
        )}
      </div>
      {hist.isLoading || house.isLoading ? (
        <div className="mt-2 h-16 animate-pulse rounded-lg bg-slate-100" />
      ) : own.length === 0 && others.length === 0 ? (
        <p className="mt-1 text-xs text-slate-400">First visit on record at this address.</p>
      ) : (
        <>
          {others.length > 0 && eraChip(`${s.name} · ${house.self?.years || "now"}${house.self?.years ? " →" : ""}`)}
          {own.length === 0 ? (
            <p className="mt-1 text-xs text-slate-400">Nothing under this account yet — the house's earlier visits are below.</p>
          ) : (
            <ul className={cn("divide-y divide-slate-100", others.length > 0 ? "mt-0.5" : "mt-1.5")}>
              {own.map((v) => renderVisit(v, null))}
            </ul>
          )}
          {others.map((b) => (
            <div key={`${b.account.custno}/${b.account.locno ?? ""}`}>
              {eraChip([relationWord(b.account.relation), houseEraLabel(b.account)].filter(Boolean).join(" · "))}
              <ul className="mt-0.5 divide-y divide-slate-100 border-t border-slate-100">
                {b.rows.map((v) => renderVisit(v, paidByLabel(b.account)))}
              </ul>
              <button type="button" onClick={() => setOther(b.account)}
                className="mt-1 flex w-full items-center gap-2 border-t border-slate-100 py-2.5 text-left text-[12px] text-slate-500 active:bg-slate-50">
                <Home className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate">Their account: <b className="font-bold text-slate-700">{b.account.name}</b> · {b.account.role}</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              </button>
            </div>
          ))}
          {others.length > 0 && (
            <p className="mt-2 text-[11px] leading-snug text-slate-400">Another owner's visits show what was done here. Their prices stay muted — never quote them.</p>
          )}
        </>
      )}
      {other && (
        <CustomerSheet
          hit={{ custno: other.custno, locno: other.locno, name: other.name, address: s.address, city: s.city, phone: null, src: "esc" }}
          empno={empno}
          techFirst={techFirst}
          onClose={() => setOther(null)}
        />
      )}
    </section>
    </>
  );
}

/* --------------------------------------------------- photos on the visit */

const fileToB64 = (file: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(file);
  });

/** Photos taken ON this call — same pegasus_file store the office's 360 and
 *  dispatch pages read (one bucket, one table, every surface). */
function VisitPhotos({ s, techFirst }: { s: Stop; techFirst: string | null }) {
  const qc = useQueryClient();
  const files = useDispatchFiles(s.ref);
  // FILES-CALL-01: only what the tech took — the office's and the customer's
  // files for this call sit in For This Call on the Job tab, never mixed in.
  const photos = (files.data ?? []).filter((f) => f.kind === "photo" && fileOrigin(f) === "tech");
  const urls = useSignedUrls(photos).data;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<PegasusFile | null>(null);
  const [markup, setMarkup] = useState<PegasusFile | null>(null);
  if (!s.custno) return null;

  const onPicked = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setErr(null);
    setBusy(true);
    try {
      const { skipped } = await uploadFilesTo(
        { custno: s.custno!, locno: s.locno, dispatch: s.ref, origin: "tech", caption: `Visit photo — call #${s.no}${techFirst ? ` (${techFirst})` : ""}` },
        Array.from(list),
      );
      if (skipped.length) setErr(`Skipped: ${skipped.join(", ")}`);
      qc.invalidateQueries({ queryKey: ["dispatch_files"] });
      qc.invalidateQueries({ queryKey: ["customer_files"] });
    } catch {
      setErr("Upload didn't go through — check signal and try again.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-center justify-between">
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Photos from This Visit</h4>
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void onPicked(e.target.files)} />
      {photos.length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {photos.map((f) => (
            <button key={f.id} type="button" onClick={() => setView(f)} className="relative aspect-square overflow-hidden rounded-xl bg-slate-100 active:opacity-80">
              {urls?.get(f.storage_path)
                ? <img src={urls.get(f.storage_path)} alt={f.caption ?? f.filename} className="h-full w-full object-cover" />
                : <span className="flex h-full w-full items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></span>}
              {/marked up/i.test(f.caption ?? "") && <span className="absolute bottom-0.5 right-0.5 rounded bg-red-600 px-1 text-[8px] font-bold text-white">✎</span>}
            </button>
          ))}
        </div>
      )}
      <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 py-3.5 text-sm font-bold text-slate-600 active:bg-slate-50 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-red-600" /> : <Camera className="h-4 w-4 text-red-600" />}
        {busy ? "Uploading…" : photos.length ? "Add more photos" : "Take a photo"}
      </button>
      {err && <p className="mt-1 text-center text-[11px] font-semibold text-red-600">{err}</p>}

      {/* viewer */}
      {view && !markup && (
        <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-black/95">
          <div className="flex items-center justify-between p-3">
            <span className="min-w-0 truncate text-xs font-semibold text-white/70">{view.caption ?? view.filename}</span>
            <button type="button" onClick={() => setView(null)} className="rounded-xl p-2 text-white/80 active:bg-white/10" aria-label="Close photo"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center p-2">
            {urls?.get(view.storage_path) && <img src={urls.get(view.storage_path)} alt="" className="max-h-full max-w-full rounded-xl object-contain" />}
          </div>
          <div className="p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
            <button type="button" onClick={() => setMarkup(view)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 py-3.5 text-sm font-bold text-white active:opacity-90">
              <PenLine className="h-4 w-4" /> Mark it up
            </button>
          </div>
        </div>
      )}
      {markup && urls?.get(markup.storage_path) && (
        <MarkupModal
          url={urls.get(markup.storage_path)!}
          custno={s.custno}
          locno={s.locno}
          dispatch={s.ref}
          baseCaption={markup.caption ?? markup.filename}
          callNo={s.no}
          onClose={() => setMarkup(null)}
          onSaved={() => { setMarkup(null); setView(null); }}
        />
      )}
    </section>
  );
}

/** Draw on a photo with your finger (red marker), save as a NEW copy — the
 *  original photo is never touched. */
function MarkupModal({ url, custno, locno, dispatch, baseCaption, callNo, onClose, onSaved }: {
  url: string;
  custno: string;
  locno: string | null;
  dispatch: string;
  baseCaption: string;
  callNo: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [ready, setReady] = useState(false);
  const [strokes, setStrokes] = useState<Array<Array<[number, number]>>>([]);
  const live = useRef<Array<[number, number]> | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      const c = canvasRef.current;
      if (!c) return;
      const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      setReady(true);
    };
    img.onerror = () => setErr("Couldn't load the photo — try again on better signal.");
    img.src = url;
  }, [url]);

  const paintStroke = (ctx: CanvasRenderingContext2D, pts: Array<[number, number]>, w: number) => {
    if (pts.length < 2) return;
    ctx.strokeStyle = "#ef4444";
    ctx.lineWidth = w;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.stroke();
  };
  const redraw = () => {
    const c = canvasRef.current, img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const w = Math.max(4, Math.round(c.width / 200));
    for (const s of strokes) paintStroke(ctx, s, w);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { redraw(); }, [ready, strokes]);

  const toPt = (e: React.PointerEvent): [number, number] => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return [((e.clientX - r.left) * c.width) / r.width, ((e.clientY - r.top) * c.height) / r.height];
  };
  const down = (e: React.PointerEvent) => {
    if (!ready) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    live.current = [toPt(e)];
  };
  const move = (e: React.PointerEvent) => {
    const c = canvasRef.current;
    if (!live.current || !c) return;
    const p = toPt(e);
    const prev = live.current[live.current.length - 1];
    live.current.push(p);
    const ctx = c.getContext("2d");
    if (ctx) paintStroke(ctx, [prev, p], Math.max(4, Math.round(c.width / 200)));
  };
  const up = () => {
    if (!live.current) return;
    const s = live.current;
    live.current = null;
    if (s.length > 1) setStrokes((all) => [...all, s]);
  };

  const save = () => {
    const c = canvasRef.current;
    if (!c || strokes.length === 0) return;
    setSaving(true);
    c.toBlob(async (blob) => {
      try {
        if (!blob) throw new Error("no blob");
        const f = new File([blob], `markup-${Date.now()}.jpg`, { type: "image/jpeg" });
        await uploadFilesTo({ custno, locno, dispatch, origin: "tech", caption: `Marked up — ${baseCaption} (call #${callNo})` }, [f]);
        qc.invalidateQueries({ queryKey: ["dispatch_files"] });
        qc.invalidateQueries({ queryKey: ["customer_files"] });
        onSaved();
      } catch {
        setErr("Couldn't save the marked-up copy — try again.");
        setSaving(false);
      }
    }, "image/jpeg", 0.85);
  };

  return (
    <div className="fixed inset-0 z-[60] mx-auto flex max-w-md flex-col bg-slate-950">
      <div className="flex items-center justify-between p-3">
        <span className="text-xs font-bold text-white/80"><PenLine className="mr-1 inline h-3.5 w-3.5 text-red-400" />Draw on the photo</span>
        <button type="button" onClick={onClose} className="rounded-xl p-2 text-white/80 active:bg-white/10" aria-label="Cancel markup"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-2">
        {err ? (
          <p className="px-6 text-center text-sm font-semibold text-red-400">{err}</p>
        ) : (
          <canvas
            ref={canvasRef}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            className="max-h-full max-w-full rounded-xl"
            style={{ touchAction: "none" }}
          />
        )}
      </div>
      <div className="flex gap-2 p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button type="button" disabled={strokes.length === 0} onClick={() => setStrokes((s) => s.slice(0, -1))}
          className="flex-1 rounded-2xl border border-white/20 py-3 text-sm font-bold text-white/90 active:bg-white/10 disabled:opacity-40">
          <RotateCcw className="mr-1 inline h-4 w-4" /> Undo
        </button>
        <button type="button" disabled={strokes.length === 0 || saving} onClick={save}
          className="flex-[2] rounded-2xl bg-red-600 py-3 text-sm font-bold text-white active:opacity-90 disabled:opacity-50">
          {saving ? "Saving…" : "Save marked-up copy"}
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------- customer files & documents */

/* FILES-CALL-01 — the phone keeps two questions apart: "what is FOR this
   call" (the office pinned it, the customer sent it) and "what does this
   customer have on file" (every visit, dated and named). One table
   underneath (pegasus_file); the dispatch column and the origin column
   are what separate them. */

/** Everyone but the tech: what the office pinned to this call and what the
 *  customer sent with the request. His own photos live in Photos from This
 *  Visit, so nothing shows twice. */
function useCallFiles(ref: string | undefined) {
  const files = useDispatchFiles(ref);
  const list = useMemo(() => (files.data ?? []).filter((f) => fileOrigin(f) !== "tech"), [files.data]);
  return { list, isLoading: files.isLoading };
}

const ORIGIN_GROUPS: { key: FileOrigin; label: string; sub: string | null }[] = [
  { key: "customer", label: "From the customer", sub: "sent with their service request" },
  { key: "office", label: "From the office", sub: null },
  { key: "import", label: "From ESC", sub: "attached to this dispatch in ESC" },
  { key: "tech", label: "Taken on site", sub: null },
];

const isVideo = (f: PegasusFile) => (f.mime ?? "").startsWith("video/");
/** The customer's rows carry a generic caption — the filename is the title. */
const fileTitle = (f: PegasusFile) => (fileOrigin(f) === "customer" ? f.filename : f.caption?.trim() || f.filename);

/** A full-screen look at one photo: who sent it, when, and a way out. */
function FileLightbox({ f, url, onClose }: { f: PegasusFile; url: string | undefined; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-black/95">
      <div className="flex items-center justify-between gap-2 p-3">
        <span className="min-w-0">
          <span className="block truncate text-xs font-semibold text-white/90">{fileTitle(f)}</span>
          <span className="block text-[11px] text-white/60">{fileWho(f)} · {fileWhen(f.created_at)}</span>
        </span>
        <button type="button" onClick={onClose} className="rounded-xl p-2 text-white/80 active:bg-white/10" aria-label="Close photo"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-2">
        {url ? <img src={url} alt="" className="max-h-full max-w-full rounded-xl object-contain" /> : <Loader2 className="h-6 w-6 animate-spin text-white/50" />}
      </div>
      <div className="p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <a href={url} target="_blank" rel="noreferrer" className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white/10 py-3.5 text-sm font-bold text-white active:bg-white/20">Open full size</a>
      </div>
    </div>
  );
}

/** The Job tab card: everything pinned to THIS call by someone other than
 *  the tech — grouped by who sent it, photos as tiles, documents as rows,
 *  each with who + when. Hidden when there is nothing, so a call with no
 *  attachments stays quiet. */
function ForThisCallCard({ s }: { s: Stop }) {
  const { list } = useCallFiles(s.pickup ? undefined : s.ref);
  const urls = useSignedUrls(list).data;
  const [view, setView] = useState<PegasusFile | null>(null);
  if (!s.custno || list.length === 0) return null;
  const fromCustomer = list.filter((f) => fileOrigin(f) === "customer").length;
  return (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900">
          <Paperclip className="h-4 w-4 text-red-600" /> For This Call
          <InfoTip label="Which files show here">Files pinned to this call. The customer&apos;s older files are on the Customer tab.</InfoTip>
        </h4>
        <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-extrabold tabular-nums text-white">{list.length}</span>
      </div>
      {fromCustomer ? <p className="mt-0.5 text-[11px] text-slate-400">{fromCustomer} from the customer</p> : null}
      {ORIGIN_GROUPS.map((g) => {
        const rows = list.filter((f) => fileOrigin(f) === g.key);
        if (rows.length === 0) return null;
        const photos = rows.filter((f) => f.kind === "photo");
        const docs = rows.filter((f) => f.kind !== "photo");
        return (
          <div key={g.key} className="mt-3">
            <div className="flex items-baseline gap-1.5">
              <span className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">{g.label}</span>
              {g.sub && <span className="text-[10.5px] text-slate-400">· {g.sub}</span>}
            </div>
            {photos.length > 0 && (
              <div className="mt-1.5 grid grid-cols-4 gap-1.5">
                {photos.map((f) => (
                  <button key={f.id} type="button" onClick={() => setView(f)} className="relative aspect-square overflow-hidden rounded-xl bg-slate-100 [touch-action:manipulation] active:opacity-80">
                    {urls?.get(f.storage_path)
                      ? <img src={urls.get(f.storage_path)} alt={fileTitle(f)} className="h-full w-full object-cover" />
                      : <span className="flex h-full w-full items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></span>}
                  </button>
                ))}
              </div>
            )}
            {docs.length > 0 && (
              <ul className="mt-1 divide-y divide-slate-100">
                {docs.map((f) => (
                  <li key={f.id}>
                    <a href={urls?.get(f.storage_path) ?? undefined} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 py-2 active:opacity-70">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                        {isVideo(f) ? <Video className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-bold text-slate-800">{fileTitle(f)}</span>
                        <span className="block truncate text-[10.5px] text-slate-400">
                          {fileWho(f)} · {fileWhen(f.created_at)}{f.size_bytes ? ` · ${prettySize(f.size_bytes)}` : ""}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {view && <FileLightbox f={view} url={urls?.get(view.storage_path)} onClose={() => setView(null)} />}
    </section>
  );
}

/** One line on the Today hero so he knows before opening the call. */
function HeroFilesLine({ s }: { s: Stop }) {
  const { list } = useCallFiles(s.pickup || !s.custno ? undefined : s.ref);
  if (list.length === 0) return null;
  const fromCustomer = list.filter((f) => fileOrigin(f) === "customer").length;
  const photos = list.filter((f) => f.kind === "photo").length;
  const docs = list.length - photos;
  const what = [
    photos ? `${photos} photo${photos === 1 ? "" : "s"}` : null,
    docs ? `${docs} file${docs === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" + ");
  return (
    <div className="mt-2.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600">
      <Paperclip className="h-3.5 w-3.5 shrink-0 text-red-600" />
      <span>{what} for this call{fromCustomer ? <span className="font-medium text-slate-400"> · {fromCustomer} from the customer</span> : null}</span>
    </div>
  );
}

const NOT_A_PERSON_RE = /^(serv\s*agr|serv|office|sales|install|warehouse|shop|unassigned)\b/i;
function visitLabel(date: string | null, tech: string | null, what: string | null): string {
  const d = date
    ? new Date(date.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  const t = (tech ?? "").trim();
  const w = (what ?? "").replace(/\s+/g, " ").trim();
  return [
    d ? `${d} visit` : "an earlier visit",
    t && !NOT_A_PERSON_RE.test(t) ? t : null,
    w ? (w.length > 28 ? `${w.slice(0, 27)}…` : w) : null,
  ].filter(Boolean).join(" · ");
}

/** "Oct 24, 2024 visit · Jeremy · Main level fireplace" for the dispatch a file
 *  is pinned to — ESC numbers through the dispatch view, Pegasus ids through
 *  the ticket table. Old files then read as history, not as today's. */
function useFileVisitLabels(refs: (string | null)[]) {
  const key = [...new Set(refs.filter((r): r is string => !!r))].slice(0, 60).join(",");
  return useQuery({
    queryKey: ["m_file_visit_labels", key],
    enabled: key.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const uniq = key.split(",");
      const escNos = uniq.filter((r) => /^\d+$/.test(r));
      const pegIds = uniq.filter((r) => /^[0-9a-f-]{36}$/i.test(r));
      const m = new Map<string, string>();
      await Promise.all([
        (async () => {
          if (!escNos.length) return;
          // The ticket itself is the truth (a completed ESC ticket can have no
          // tech leg at all); the leg view only adds the tech + appointment.
          const [{ data: base }, { data: legs }] = await Promise.all([
            supabase.from("mirror_dispatch").select("dispatch, complete, recdate, notes").in("dispatch", escNos),
            supabase.from("v_dispatch_full").select("dispatch, appointment_date, tech_first").in("dispatch", escNos),
          ]);
          const leg = new Map<string, { appointment_date: string | null; tech_first: string | null }>();
          for (const l of (legs ?? []) as { dispatch: string; appointment_date: string | null; tech_first: string | null }[]) {
            if (!leg.has(l.dispatch)) leg.set(l.dispatch, l);
          }
          for (const d of (base ?? []) as { dispatch: string; complete: string | null; recdate: string | null; notes: string | null }[]) {
            const l = leg.get(d.dispatch);
            m.set(d.dispatch, visitLabel(l?.appointment_date ?? d.complete ?? d.recdate, l?.tech_first ?? null, d.notes));
          }
        })(),
        (async () => {
          if (!pegIds.length) return;
          const { data } = await supabase
            .from("pegasus_dispatch")
            .select("id, scheduled_date, service_type_name, summary")
            .in("id", pegIds);
          for (const d of (data ?? []) as { id: string; scheduled_date: string | null; service_type_name: string | null; summary: string | null }[]) {
            m.set(d.id, visitLabel(d.scheduled_date, null, d.service_type_name || d.summary));
          }
        })(),
      ]);
      return m;
    },
  });
}

/** Everything on file for this customer — split into THIS CALL (pinned to
 *  the dispatch he's on, with who added it) and EARLIER (every other file,
 *  labeled with the visit it belongs to). Upload for anything handed over
 *  at the door. */
function CustomerDocs({ s }: { s: Stop }) {
  const qc = useQueryClient();
  const files = useCustomerFiles(s.custno ?? "");
  const all = useMemo(
    () => (files.data ?? []).filter((f) => !s.locno || !f.locno || f.locno === s.locno),
    [files.data, s.locno],
  );
  // An install visit's photos, tags and checks are filed on the JOB, not the
  // dispatch — they're this visit's too (were showing under "Earlier").
  const isThis = (f: PegasusFile) => f.dispatch === s.ref || (!!s.jobId && f.job_id === s.jobId);
  const thisCall = useMemo(() => all.filter(isThis), [all, s.ref, s.jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  const earlier = useMemo(() => all.filter((f) => !isThis(f)), [all, s.ref, s.jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showAll, setShowAll] = useState(false);
  const shownEarlier = showAll ? earlier : earlier.slice(0, 6);
  const urls = useSignedUrls([...thisCall, ...shownEarlier]).data;
  const labels = useFileVisitLabels(shownEarlier.map((f) => f.dispatch)).data;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  if (!s.custno) return null;

  const onPicked = async (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    setBusy(true);
    try {
      await uploadFilesTo({ custno: s.custno!, locno: s.locno, dispatch: s.ref, origin: "tech", caption: `Uploaded from the truck — call #${s.no}` }, Array.from(picked));
      qc.invalidateQueries({ queryKey: ["customer_files"] });
      qc.invalidateQueries({ queryKey: ["dispatch_files"] });
    } catch { /* quiet — the row simply won't appear */ } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const fmtDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const earlierMeta = (f: PegasusFile) => {
    const visit = f.dispatch ? labels?.get(f.dispatch) : null;
    const when = visit ?? (f.dispatch ? `${fmtDay(f.created_at)} visit` : `on file · ${fmtDay(f.created_at)}`);
    return `${when}${f.size_bytes ? ` · ${prettySize(f.size_bytes)}` : ""}`;
  };
  const row = (f: PegasusFile, meta: string) => (
    <li key={f.id}>
      <a href={urls?.get(f.storage_path) ?? undefined} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 py-2 active:opacity-70">
        <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl", f.kind === "photo" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-600")}>
          {f.kind === "photo" && urls?.get(f.storage_path)
            ? <img src={urls.get(f.storage_path)} alt="" className="h-full w-full object-cover" />
            : f.kind === "photo" ? <Camera className="h-4 w-4" /> : isVideo(f) ? <Video className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-bold text-slate-800">{fileTitle(f)}</span>
          <span className="block truncate text-[10.5px] text-slate-400">{meta}</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
      </a>
    </li>
  );

  return (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-center justify-between">
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Files &amp; Documents</h4>
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 active:bg-slate-200 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />} Upload
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => void onPicked(e.target.files)} />
      {files.isLoading ? (
        <div className="mt-2 h-10 animate-pulse rounded-lg bg-slate-100" />
      ) : all.length === 0 ? (
        <p className="mt-1 text-xs text-slate-400">Nothing on file yet.</p>
      ) : (
        <>
          {thisCall.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-red-600">This call</span>
                <span className="rounded-full bg-red-50 px-1.5 text-[10px] font-extrabold tabular-nums text-red-700">{thisCall.length}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {thisCall.map((f) => row(f, `${fileWho(f)} · ${fileWhen(f.created_at)}${f.size_bytes ? ` · ${prettySize(f.size_bytes)}` : ""}`))}
              </ul>
            </div>
          )}
          {earlier.length > 0 && (
            <div className={thisCall.length > 0 ? "mt-3 border-t border-slate-100 pt-2" : "mt-2"}>
              <div className="flex items-center gap-1.5">
                <span className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Earlier</span>
                <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-extrabold tabular-nums text-slate-500">{earlier.length}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {shownEarlier.map((f) => row(f, earlierMeta(f)))}
              </ul>
              {earlier.length > 6 && (
                <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-1 text-xs font-bold text-slate-400 underline-offset-2 active:underline">
                  {showAll ? "Show fewer" : `Show all ${earlier.length}`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* --------------------------------------------- add a unit (data-plate AI) */

/** Snap the data plate → Gemini reads brand/model/serial → tech confirms →
 *  the unit is on file for every future call (Pegasus-native; ESC stays
 *  read-only). Photo is retained so the office can always double-check. */
function AddUnitSheet({ s, empno, techFirst, onClose }: {
  s: Stop;
  empno: string | null;
  techFirst: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [reading, setReading] = useState(false);
  const [readDone, setReadDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [category, setCategory] = useState<string>("");
  const [loc, setLoc] = useState("");
  const [saving, setSaving] = useState(false);

  const CATS: Array<[string, string]> = [
    ["furnace", "Furnace"], ["ac", "A/C"], ["heat_pump", "Heat pump"],
    ["water_heater", "Water heater"], ["air_handler", "Air handler"],
    ["humidifier", "Humidifier"], ["other", "Other"],
  ];

  const onPlate = async (file: File | undefined) => {
    if (!file || !s.custno) return;
    setErr(null);
    setReading(true);
    try {
      const b64 = await fileToB64(file);
      const [aiRes] = await Promise.all([
        supabase.functions.invoke("read-serial", { body: { image_base64: b64, mime: file.type || "image/jpeg" } }),
        uploadFilesTo({ custno: s.custno, locno: s.locno, dispatch: s.ref, origin: "tech", caption: `Data plate — call #${s.no}` }, [file])
          .then(() => { qc.invalidateQueries({ queryKey: ["dispatch_files"] }); qc.invalidateQueries({ queryKey: ["customer_files"] }); })
          .catch(() => { /* the read still helps even if the photo upload missed */ }),
      ]);
      if (aiRes.error) throw aiRes.error;
      const d = aiRes.data as { serial?: string; model?: string; manufacturer?: string } | null;
      if (d?.serial) setSerial(d.serial);
      if (d?.model) setModel(d.model);
      if (d?.manufacturer) setBrand(d.manufacturer);
      setReadDone(true);
      if (!d?.serial && !d?.model) setErr("Couldn't read the plate clearly — type what you can see.");
    } catch {
      setErr("The reader didn't respond — type it by hand.");
      setReadDone(true);
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!s.custno) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("pegasus_equipment").insert({
        custno: s.custno,
        locno: s.locno,
        brand: brand.trim() || null,
        model: model.trim() || null,
        serial: serial.trim().toUpperCase() || null,
        category: category || null,
        equip_loc: loc.trim() || null,
        origin_dispatch: s.ref,
        source: "tech_mobile",
        created_by: techFirst ? `${techFirst}${empno ? ` (${empno})` : ""}` : empno,
      });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["m_equipment"] });
      qc.invalidateQueries({ queryKey: ["installed_equipment"] });
      onClose();
    } catch {
      setErr("Couldn't save the unit — check signal and try again.");
      setSaving(false);
    }
  };

  const canSave = Boolean(model.trim() || serial.trim() || brand.trim());

  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Cancel">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-slate-900">Add a unit</div>
          <div className="truncate text-[11px] text-slate-500">{s.name}</div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void onPlate(e.target.files?.[0])} />
        <button type="button" disabled={reading} onClick={() => fileRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-1.5 rounded-3xl border-2 border-dashed border-slate-300 bg-white py-7 text-slate-500 active:bg-slate-50">
          {reading ? <Loader2 className="h-7 w-7 animate-spin text-red-600" /> : <Camera className="h-7 w-7 text-red-600" />}
          <span className="text-sm font-bold text-slate-700">{reading ? "Reading the plate…" : readDone ? "Retake the data plate" : "Snap the data plate"}</span>
          <span className="text-[11px]">Reads the model &amp; serial — you confirm</span>
        </button>
        {err && <p className="text-center text-xs font-semibold text-amber-600">{err}</p>}
        {readDone && !reading && (brand || model || serial) && (
          <p className="text-center text-[11px] font-semibold text-emerald-700">Read from the plate ✓ — double-check letters vs numbers before saving.</p>
        )}

        <div className="space-y-2.5 rounded-3xl border border-slate-200 bg-white p-4">
          <div>
            <label className="text-[12.5px] font-semibold text-slate-600">What is it?</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {CATS.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setCategory(category === k ? "" : k)}
                  className={cn("rounded-full px-3 py-1.5 text-xs font-bold", category === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 active:bg-slate-200")}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {([["Brand", brand, setBrand, "Lennox"], ["Model", model, setModel, "SLP98UH070XV36B"], ["Serial", serial, setSerial, "5817F04129"], ["Where in the home", loc, setLoc, "Basement / attic / closet"]] as Array<[string, string, (v: string) => void, string]>).map(([label, val, set, ph]) => (
            <div key={label}>
              <label className="text-[12.5px] font-semibold text-slate-600">{label}</label>
              <input type="text" value={val} onChange={(e) => set(e.target.value)} placeholder={ph}
                className={cn("mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25", label === "Serial" && "font-mono uppercase")} />
            </div>
          ))}
        </div>
      </div>
      <footer className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button type="button" disabled={!canSave || saving} onClick={() => void save()}
          className={cn("w-full rounded-2xl py-4 text-[15px] font-bold text-white", canSave ? "bg-red-700 active:opacity-90" : "bg-slate-300")}>
          {saving ? "Saving…" : "Save this unit"}
        </button>
      </footer>
    </div>
  );
}

/* ------------------------------------- repair options & replacement leads */

/** The newest service quote tied to THIS call — drives the status chip in the
 *  workspace ("presented", "accepted ✓"). Same pegasus_quote the office's
 *  Proposals list and the portal read. */
interface CallQuoteRow { id: string; status: string; accepted_option_id: string | null; title: string | null; quote_type: string; job_id: string | null; token: string | null; presented_at: string | null; sent_at: string | null }
function useCallQuote(no: string) {
  return useQuery({
    queryKey: ["m_quote", no],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<CallQuoteRow | null> => {
      const { data } = await supabase
        .from("pegasus_quote")
        .select("id, status, accepted_option_id, title, quote_type, job_id, token, presented_at, sent_at")
        .eq("dispatch_no", no)
        .eq("is_practice", false)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data ?? null) as CallQuoteRow | null;
    },
  });
}

interface QLine {
  kind: string; description: string; qty: number; unitPrice: number;
  item_id: string | null; model: string | null; manufacturer: string | null; image_url: string | null;
  /** Removability: required (in package, locked) | recommended | optional. */
  role: string;
  /** INDEPENDENT of role (Seth): Included = $0 to the customer, value struck. */
  inc?: boolean;
  warranty?: string | null;
}
/** Legacy shapes → the two-axis model: old role "included" was the $0 freebie. */
function normalizeQLine(l: QLine): QLine {
  const rawRole = l.role ?? "required";
  return {
    ...l,
    role: rawRole === "included" ? "required" : rawRole,
    inc: l.inc ?? (rawRole === "included"),
    image_url: l.image_url ?? null,
    model: l.model ?? null,
    manufacturer: l.manufacturer ?? null,
  };
}
interface RepairOpt { label: string; blurb: string; lines: QLine[] }
const OPT_DEFAULT_LABELS = ["Best — the complete fix", "Better", "Good — the essentials"];
const optTotal = (o: { lines: QLine[] }) => o.lines.reduce((t, l) => t + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);

/* ── PLAN-OFFER-01 · the tech's kitchen-table plan (2026-10-29) ─────────────
   The repair wizard offers the Maintenance Plus Plan on every repair quote a
   tech builds — ON by default (the shops that grow their memberships require
   the offer on every call; a yes costs the homeowner nothing today), one tap
   to switch off. It rides the quote's plan_offer_* columns, so the present
   screen, the homeowner's page and the office all see the same offer; the
   member math is the real one (PLAN-DISC-SCOPE-01: parts & labor only, the
   member diagnostic on today's visit). ─────────────────────────────────── */
const TECH_PLAN_TERMS = [1, 3, 5];
/** PLAN-OFFER-SYSTEMS: covers = the home's systems (kind + room, never model/serial) with the tech's on/off; the price is per system. */
interface TechPlanState { on: boolean; term: number; covers?: PlanOfferCover[] | null }

/** The plan catalog + tiers + the diagnostic pair, as the tech's screens need them. */
function useTechPlanFacts() {
  const bases = (usePlanPriceCatalog().data ?? []).filter((p) => p.kind === "base" && p.active);
  const tiers = usePlanDiscounts().data;
  const fees = useFeeItems().data;
  const diagOf = (scope: string) => {
    const rows = (fees ?? []).filter((f) => f.active && f.segment === "residential" && f.section === "residential_diag" && f.plan_scope === scope && (f.zone ?? "any") === "any" && !f.after_hours);
    return rows.length ? Math.min(...rows.map((f) => Number(f.amount) || 0)) : 0;
  };
  const base = bases.find((b) => b.id === "base-furnace-ac-humi") ?? bases[0] ?? null;
  const secondDisc = Number(usePlanCatalog().data?.config?.second_system_discount ?? 0.3);
  const priceFor = (term: number, systems = 1): number => {
    if (!base) return 0;
    const m: Record<number, number | null> = { 1: base.p1, 2: base.p2, 3: base.p3, 5: base.p5, 10: base.p10 };
    return Math.round(Number(m[term] ?? 0) * planSystemsMultiplier(systems, secondDisc) * 100) / 100;
  };
  const pctFor = (term: number): number => (tiers && tiers.length ? repairPctForTerm(tiers, term) : 0);
  return { base, priceFor, pctFor, secondDisc, diagStd: diagOf("standard"), diagMember: diagOf("plus"), ready: !!base && !!fees };
}

/** The home's coverable systems for the plan card (the same record the office and the 360 read). */
function useTechPlanCovers(custno: string | null, locno: string | null, saved: PlanOfferCover[] | null | undefined): PlanOfferCover[] {
  const rows = useCustomerEquipment(custno ?? undefined, locno).data;
  const overrides = useEquipmentOverrides(custno ?? undefined, locno).data;
  return useMemo(() => planCoversFor(rows, overrides, saved ?? null), [rows, overrides, saved]);
}

/** The repair dollars the member % touches in an option (parts & labor, never a fee / diagnostic line). */
const optRepairEligible = (o: { lines: QLine[] }) =>
  o.lines.filter((l) => memberDiscountByRule({ kind: l.kind || "custom", description: l.description, item_id: l.item_id ?? null }, null))
    .reduce((t, l) => t + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);

/** Builder: the plan toggle + term, under the options. */
function TechPlanOfferCard({ plan, onChange, custno, locno }: { plan: TechPlanState; onChange: (p: TechPlanState) => void; custno: string | null; locno: string | null }) {
  const f = useTechPlanFacts();
  const covers = useTechPlanCovers(custno, locno, plan.covers);
  const systems = planSystemsCount(covers);
  if (!f.base) return null;
  return (
    <section className={cn("rounded-3xl border p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", plan.on ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white")}>
      <button type="button" onClick={() => onChange({ ...plan, on: !plan.on })} className="flex w-full items-center gap-3 text-left">
        <span className={cn("relative h-7 w-12 shrink-0 rounded-full transition-colors", plan.on ? "bg-emerald-500" : "bg-slate-300")}>
          <span className={cn("absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all", plan.on ? "left-[22px]" : "left-0.5")} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn("block text-[13px] font-extrabold leading-tight", plan.on ? "text-emerald-900" : "text-slate-600")}>
            <ShieldCheck className="mr-1 inline h-4 w-4 align-[-2px]" />Maintenance Plus Plan {plan.on ? "offered" : "not offered"}
          </span>
          <span className={cn("block text-[11px] leading-snug", plan.on ? "text-emerald-700" : "text-slate-400")}>
            {plan.on ? `On every option · they tick it on their screen · ${plan.term} yr ${money$(f.priceFor(plan.term, systems))} shown first` : "Tap to offer it — the member math shows on their screen"}
          </span>
        </span>
      </button>
      {plan.on && (
        <>
          {/* PLAN-OFFER-SYSTEMS: which units the plan covers — tap one off to leave it out; the price follows. */}
          {covers.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10.5px] font-bold uppercase tracking-wide text-emerald-800/70">
                <span>Covers</span>
                <span>{systems} system{systems === 1 ? "" : "s"}{systems > 1 ? ` · each extra ${Math.round(f.secondDisc * 100)}% off` : ""}</span>
              </div>
              {/* r2 (Seth: "I hate the pills"): rows — kind · room, make + model, the serial as data, the tick at the end. */}
              <div className="mt-1.5 space-y-1.5">
                {covers.map((c) => (
                  <UnitPickRow key={c.counter} kind={c.kind} label={c.kind === "ac" ? "A/C" : c.label} loc={c.loc}
                    brand={c.brand ?? null} model={c.model ?? null} serial={c.serial ?? null} year={c.year ?? null}
                    on={c.on} tone="plan"
                    onToggle={() => onChange({ ...plan, covers: covers.map((x) => (x.counter === c.counter ? { ...x, on: !x.on } : x)) })} />
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-1.5">
            {TECH_PLAN_TERMS.map((t) => (
              <button key={t} type="button" onClick={() => onChange({ ...plan, term: t })}
                className={cn("flex-1 rounded-xl border py-2 text-center", plan.term === t ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-200 bg-white text-emerald-900")}>
                <span className="block text-[12px] font-extrabold">{t} yr</span>
                <span className={cn("block text-[10.5px]", plan.term === t ? "text-emerald-100" : "text-emerald-700/80")}>{money$(f.priceFor(t, systems))}{f.pctFor(t) > 0 ? ` · ${Math.round(f.pctFor(t) * 100)}% off repairs` : ""}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/** Present screen: the plan card with the real savings on today's bill — the homeowner picks a term and ticks. */
function TechPlanPresentCard({ plan, choice, onChoice, repairEligible, custno, locno }: {
  plan: TechPlanState;
  choice: { on: boolean; term: number };
  onChoice: (c: { on: boolean; term: number }) => void;
  /** The chosen (or recommended) option's repair dollars the % touches. */
  repairEligible: number;
  custno: string | null; locno: string | null;
}) {
  const f = useTechPlanFacts();
  const covers = useTechPlanCovers(custno, locno, plan.covers);
  const systems = planSystemsCount(covers);
  const coversText = planCoversSummary(covers);
  if (!plan.on || !f.base) return null;
  const pct = f.pctFor(choice.term);
  const diagSave = Math.max(0, f.diagStd - f.diagMember);
  const repairSave = Math.round(repairEligible * pct * 100) / 100;
  const save = diagSave + repairSave;
  const price = f.priceFor(choice.term, systems);
  return (
    <div className={cn("overflow-hidden rounded-3xl border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", choice.on ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200")}>
      {save > 0 && (
        <div className="bg-emerald-600 px-4 py-1.5 text-center text-[11px] font-extrabold uppercase tracking-wider text-white">Join today and this repair costs less</div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <h4 className="text-[15px] font-extrabold text-slate-900">Sedgwick Heating Maintenance Plus Plan</h4>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">
              Two tune-ups a year · front of the line in a cold snap · the member visit price{pct > 0 ? ` · ${Math.round(pct * 100)}% off repairs` : ""}.
            </p>
            {coversText && (
              <p className="mt-1.5 text-[12px] text-slate-600">Covers: <b className="text-slate-900">{coversText}</b>{systems > 1 ? <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">{systems} systems</span> : null}</p>
            )}
          </div>
        </div>
        <div className="mt-3 flex gap-1.5">
          {TECH_PLAN_TERMS.map((t) => (
            <button key={t} type="button" onClick={() => onChoice({ on: true, term: t })}
              className={cn("flex-1 rounded-xl border py-2 text-center", choice.on && choice.term === t ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-800")}>
              <span className="block text-[12px] font-extrabold">{t} yr</span>
              <span className={cn("block text-[11px] tabular-nums", choice.on && choice.term === t ? "text-emerald-100" : "text-slate-500")}>{money$(f.priceFor(t, systems))}</span>
            </button>
          ))}
        </div>
        <button type="button" onClick={() => onChoice({ ...choice, on: !choice.on })} className="mt-3 flex w-full items-start gap-2.5 text-left">
          <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2", choice.on ? "border-emerald-600 bg-emerald-600" : "border-slate-300")}>
            {choice.on && <Check className="h-3.5 w-3.5 text-white" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-extrabold text-slate-900">Add the {choice.term}-year plan · {money$(price)}</span>
            <span className="block text-[12px] leading-snug text-slate-500">
              {save > 0
                ? <>Today&apos;s bill is <b className="text-emerald-700">{money$(save)} less</b> — the member diagnostic ({money$(f.diagStd)} → {money$(f.diagMember)}){repairSave > 0 ? ` and ${Math.round(pct * 100)}% off the repair` : ""}. Goes on today&apos;s invoice.</>
                : <>Goes on today&apos;s invoice — the plan starts when it is paid.</>}
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}

/** The live (not-yet-decided) quote for this call, loaded back into a builder —
 *  closing a wizard must NEVER read as "everything's gone" (Seth 2026-08-27,
 *  Andre Nicole Behm). Same one-live-quote-per-call rule as invoices. */
interface LiveQuote {
  quoteId: string;
  token: string;
  status: string;
  options: Array<{ tier: string; label: string; blurb: string; brand: string | null; rebates: TierRebate[]; discount: number; discMode: "amount" | "percent"; discLabel: string; lines: QLine[] }>;
}
async function fetchLiveQuote(dispatchNo: string, type: "service" | "install"): Promise<LiveQuote | null> {
  const { data: q } = await supabase
    .from("pegasus_quote")
    .select("id, token, status")
    .eq("dispatch_no", dispatchNo)
    .eq("quote_type", type)
    .eq("is_practice", false)
    .in("status", ["draft", "sent", "viewed"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  const qr = q as { id: string; token: string; status: string } | null;
  if (!qr) return null;
  const { data: opts } = await supabase
    .from("pegasus_quote_option")
    .select("id, tier, label, description, brand, rebate_total, discount_total, rebate_utility_id, sort")
    .eq("quote_id", qr.id)
    .order("sort");
  const optRows = (opts ?? []) as Array<{ id: string; tier: string; label: string | null; description: string | null; brand: string | null; rebate_total: number | null; discount_total: number | null; rebate_utility_id: string | null }>;
  const ids = optRows.map((o) => o.id);
  const [{ data: lines }, { data: discs }, { data: rebs }] = await Promise.all([
    ids.length
      ? supabase.from("pegasus_quote_line").select("option_id, kind, description, model, manufacturer, image_url, qty, unit_price, item_id, role, warranty, warranty_parts_years, warranty_labor_years, discount, sort").in("option_id", ids).order("sort")
      : Promise.resolve({ data: [] as never[] }),
    ids.length
      ? supabase.from("pegasus_quote_discount").select("option_id, label, mode, value").in("option_id", ids)
      : Promise.resolve({ data: [] as never[] }),
    ids.length
      ? supabase.from("pegasus_quote_rebate").select("option_id, utility_id, custom_name, amount, sort").in("option_id", ids).order("sort")
      : Promise.resolve({ data: [] as never[] }),
  ]);
  const lineRows = (lines ?? []) as Array<{ option_id: string; kind: string; description: string | null; model: string | null; manufacturer: string | null; image_url: string | null; qty: number; unit_price: number; item_id: string | null; role: string | null; warranty: string | null; warranty_parts_years: number | null; warranty_labor_years: number | null; discount: number | null }>;
  const discRows = (discs ?? []) as Array<{ option_id: string; label: string | null; mode: string; value: number }>;
  const rebRows = (rebs ?? []) as Array<{ option_id: string; utility_id: string | null; custom_name: string | null; amount: number }>;
  return {
    quoteId: qr.id,
    token: qr.token,
    status: qr.status,
    options: optRows.map((o) => {
      const named = discRows.find((d) => d.option_id === o.id);
      const optRebates: TierRebate[] = rebRows
        .filter((r) => r.option_id === o.id)
        .map((r) => ({ utilityId: r.utility_id, name: r.custom_name ?? "", amount: Number(r.amount) || 0 }));
      // Legacy single-rebate quotes (pre-table) fold into the list.
      if (optRebates.length === 0 && (Number(o.rebate_total) || 0) > 0) {
        optRebates.push({ utilityId: o.rebate_utility_id, name: "", amount: Number(o.rebate_total) || 0 });
      }
      return {
        tier: o.tier,
        label: o.label ?? "",
        blurb: o.description ?? "",
        brand: o.brand,
        rebates: optRebates,
        discount: named ? Number(named.value) || 0 : Number(o.discount_total) || 0,
        discMode: (named?.mode === "percent" ? "percent" : "amount") as "amount" | "percent",
        discLabel: named?.label ?? "",
        lines: lineRows
          .filter((l) => l.option_id === o.id)
          .map((l) => {
            const rawRole = l.role ?? "included";
            // Two-axis mapping: removability from role (legacy "included" ⇒ in
            // package), $0-included from the netting line discount.
            return {
              kind: l.kind, description: l.description ?? "", qty: Number(l.qty) || 1, unitPrice: Number(l.unit_price) || 0,
              item_id: l.item_id, model: l.model, manufacturer: l.manufacturer, image_url: l.image_url,
              role: rawRole === "included" ? "required" : rawRole,
              inc: l.kind === "accessory" && (Number(l.discount) || 0) > 0,
              // QUOTE-POLISH-07: the unit's structured years (set from the
              // pricebook model or the office chip) read back as the picker text.
              warranty: l.warranty_parts_years != null || l.warranty_labor_years != null
                ? `${l.warranty_parts_years ?? (o.tier === "good" ? 5 : 10)}-Year Parts · ${l.warranty_labor_years ?? 5}-Year Labor`
                : l.warranty,
            };
          }),
      };
    }),
  };
}

/* ── QUOTE-PKG-01 shared bits: the Repair|Replace mode segment (one wizard
   feel, two engines underneath — tapping the other side swaps wizards, each
   keeps its own draft stash) and the package chips (one tap fills an option
   from Settings → Quotes → Packages). ─────────────────────────────────── */

/* ── SALE-FINALIZE-01: the Finalize-sale stop (Seth 2026-10-14: "customer accepts
   after the fact … tech didn't measure … we need a dispatch on the board for the
   tech to go finalize the sale and then launch into the builder"). The office
   sends the selling tech back from the job's Sold stage; on the phone this stop
   is one button into the Install Packet — photos, site facts, installer guide —
   the exact flow an on-site sale uses, so nothing new to learn. ─────────── */
function FinalizeSaleWork({ jobId, s, techFirst, empno, onWrap }: {
  jobId: string; s: Stop; techFirst: string | null; empno: string | null; onWrap: () => void;
}) {
  const [open, setOpen] = useState(false);
  const job = useQuery({
    queryKey: ["m_finalize_job", jobId],
    staleTime: 10 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("pegasus_job").select("id, title, sell_price, sold_estimate_ref, sale_packet").eq("id", jobId).maybeSingle();
      return (data ?? null) as { id: string; title: string | null; sell_price: number | null; sold_estimate_ref: string | null; sale_packet: { sent_at?: string | null } | null } | null;
    },
  });
  const quoteId = job.data?.sold_estimate_ref ?? null;
  const sent = !!job.data?.sale_packet?.sent_at;
  return (
    <section className="m-card space-y-3 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700"><Camera className="h-5 w-5" /></span>
        <div className="min-w-0">
          <div className="text-[17px] font-bold text-slate-900">Finalize this sale</div>
          <div className="text-[13px] leading-snug text-slate-500">
            {job.data?.title ? <span className="font-semibold text-slate-700">{job.data.title}</span> : "Sold system"}
            {job.data?.sell_price != null ? ` · ${money$(Number(job.data.sell_price))}` : ""}
          </div>
        </div>
      </div>
      <p className="text-[13.5px] leading-snug text-slate-600">
        They said yes after your visit, so the office has no photos or measurements yet. Walk the house with the Install Packet —
        the photo checklist, the site facts and the installer guide — and send it. That&apos;s what the load calc and the crew run on.
      </p>
      {sent ? (
        <div className="flex items-center gap-2 rounded-2xl bg-emerald-50 px-3.5 py-3 text-[13.5px] font-semibold text-emerald-800">
          <Check className="h-4 w-4" /> Packet Sent to the Office ✓
        </div>
      ) : null}
      <button type="button" disabled={!quoteId || job.isLoading} onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-[15px] font-bold text-white active:opacity-90 disabled:opacity-50">
        {sent ? "Open the Install Packet again →" : "Open the Install Packet →"}
      </button>
      {!quoteId && !job.isLoading && (
        <p className="text-center text-[12px] text-amber-700">This job isn&apos;t linked to a proposal — call the office before you start.</p>
      )}
      {sent && (
        <button type="button" onClick={onWrap}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-[14px] font-bold text-slate-700 active:bg-slate-50">
          <Check className="h-4 w-4" /> Done Here — Wrap Up
        </button>
      )}
      {open && quoteId && (
        <InstallPacket s={s} techFirst={techFirst} empno={empno} quoteId={quoteId} onClose={() => { setOpen(false); void job.refetch(); }} />
      )}
    </section>
  );
}

function WizardModeSeg({ mode, onSwitch }: { mode: "repair" | "replace"; onSwitch?: () => void }) {
  if (!onSwitch) return null;
  const btn = (active: boolean) =>
    cn("flex-1 rounded-full py-2 text-[12.5px] font-bold transition-colors", active ? "bg-white text-slate-900 shadow-[0_2px_6px_rgba(23,21,15,0.12)]" : "text-slate-500");
  return (
    <div className="flex rounded-full bg-slate-200/60 p-1">
      <button type="button" className={btn(mode === "repair")} onClick={mode === "repair" ? undefined : onSwitch}>Repair</button>
      <button type="button" className={btn(mode === "replace")} onClick={mode === "replace" ? undefined : onSwitch}>Replace</button>
    </div>
  );
}

function PkgChips({ kind, onPick }: { kind: "repair" | "replace"; onPick: (p: QuotePackage) => void }) {
  const { data } = useQuotePackages(kind);
  const pkgs = data ?? [];
  if (pkgs.length === 0) return null;
  return (
    <div>
      <div className="px-1 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Start from a package</div>
      <div className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
        {pkgs.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p)}
            className="shrink-0 rounded-full bg-white px-3.5 py-2 text-[12.5px] font-bold text-slate-700 shadow-[inset_0_0_0_1px_#e2e0d8] active:bg-slate-900 active:text-white"
          >
            {p.label} · {money$(pkgTotal(p))}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A package's preset lines → builder lines (prices, roles, $0-included ride along). */
function pkgToQLines(p: QuotePackage): QLine[] {
  return p.lines.map((l) => ({
    kind: l.kind || "custom",
    description: l.description,
    qty: Number(l.qty) || 1,
    unitPrice: Number(l.unit_price) || 0,
    item_id: l.item_id,
    model: l.model,
    manufacturer: l.manufacturer,
    image_url: l.image_url,
    role: l.role || "required",
    inc: l.inc,
    warranty: l.warranty,
  }));
}

/** Build 1–3+ repair options, show the customer clean choices, and a tap on
 *  "we'll do this one" writes a REAL accepted pegasus_quote — the same record
 *  the office's Proposals list, the 360, and the portal read. Reopening the
 *  wizard resumes the live quote; presenting again UPDATES it (never a dupe). */
function RepairQuoteWizard({ s, empno, techFirst, onClose, onSwitch, workedEq }: {
  s: Stop;
  empno: string | null;
  techFirst: string | null;
  onClose: () => void;
  /** QUOTE-PKG-01: swap to the Replace wizard (drafts persist per wizard). */
  onSwitch?: () => void;
  /** PROPOSAL-EQUIPMENT-02: the units the tech said this visit is about ("What are you working on?") —
   *  written onto the quote so the homeowner page leads with them. */
  workedEq?: WorkedUnit[];
}) {
  // Worked keys ("esc-00219344" / "peg-<id>") → the equipment record's counters ("00219344" / "peg:<id>").
  const workUnits: string[] = (workedEq ?? []).map((w) => (w.k.startsWith("esc-") ? w.k.slice(4) : w.k.startsWith("peg-") ? (w.k.slice(4).startsWith("peg:") ? w.k.slice(4) : `peg:${w.k.slice(4)}`) : w.k));
  const qc = useQueryClient();
  const stashKey = `pegasus.m.rq.${s.ref}`;
  const [opts, setOpts] = useState<RepairOpt[]>(() =>
    OPT_DEFAULT_LABELS.map((label) => ({ label, blurb: "", lines: [] })));
  const [resume, setResume] = useState<{ quoteId: string } | null>(null);
  const [booted, setBooted] = useState(false);
  const [presented, setPresented] = useState<{ quoteId: string; optionIds: string[] } | null>(null);
  const [acceptedIdx, setAcceptedIdx] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // OFFICE-NOTE-01 (queued Sept 30): context for the OFFICE that rides the
  // quote record — financing talk, access quirks, who to call. The customer
  // proposal never shows it (quote.notes renders office-side only).
  const [officeNote, setOfficeNote] = useState("");
  // PLAN-OFFER-01: the plan rides every repair quote — ON by default, 3 yr first.
  const [plan, setPlan] = useState<TechPlanState>({ on: true, term: 3 });
  // The homeowner's choice on the present screen (the term they picked + the tick).
  const [planChoice, setPlanChoice] = useState<{ on: boolean; term: number }>({ on: true, term: 3 });
  const planFacts = useTechPlanFacts();
  // PLAN-OFFER-SYSTEMS: the home's systems with the tech's on/off, as saved on the quote.
  const planCovers = useTechPlanCovers(s.custno, s.locno, plan.covers);
  const planSystems = planSystemsCount(planCovers);

  // Boot: live-quote IDENTITY always resolves first (one live repair quote per
  // call — presenting UPDATES it, never stacks); the stash only wins for
  // unsaved content.
  useEffect(() => {
    let dead = false;
    void (async () => {
      const live = await fetchLiveQuote(s.no, "service").catch(() => null);
      if (dead) return;
      if (live) {
        setResume({ quoteId: live.quoteId });
        // OFFICE-NOTE-01: an update must not silently drop the note already
        // on the quote — seed the field from the saved record.
        void supabase.from("pegasus_quote").select("notes").eq("id", live.quoteId).maybeSingle().then(({ data }) => {
          if (dead) return;
          const m = /(?:^|\n)Note to the office: ([\s\S]*)$/.exec((data as { notes?: string | null } | null)?.notes ?? "");
          if (m) setOfficeNote((cur) => cur || m[1].trim());
        });
      }
      if (live) {
        // the saved quote's own offer (a re-opened quote keeps what was offered)
        void supabase.from("pegasus_quote").select("plan_offer_base_id, plan_offer_default_term, plan_offer_covers").eq("id", live.quoteId).maybeSingle().then(({ data }) => {
          if (dead || !data) return;
          const d = data as { plan_offer_base_id: string | null; plan_offer_default_term: number | null; plan_offer_covers: PlanOfferCover[] | null };
          setPlan({ on: !!d.plan_offer_base_id, term: d.plan_offer_default_term ?? 3, covers: d.plan_offer_covers ?? null });
          setPlanChoice({ on: !!d.plan_offer_base_id, term: d.plan_offer_default_term ?? 3 });
        });
      }
      try {
        const raw = localStorage.getItem(stashKey);
        if (raw) {
          const p = JSON.parse(raw) as { o: RepairOpt[]; q?: string | null; n?: string | null; p?: TechPlanState };
          if (p.n && !dead) setOfficeNote((cur) => cur || p.n!);
          if (p.p && !dead) { setPlan(p.p); setPlanChoice({ on: p.p.on, term: p.p.term }); }
          if (Array.isArray(p.o) && p.o.length) {
            if (!dead) {
              setOpts(p.o.map((o) => ({ label: o.label ?? "", blurb: o.blurb ?? "", lines: o.lines ?? [] })));
              setBooted(true);
            }
            return;
          }
        }
      } catch { /* fall through */ }
      if (live && live.options.length) {
        setOpts(live.options.map((o) => ({ label: o.label || "Option", blurb: o.blurb, lines: o.lines })));
      }
      setBooted(true);
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!booted) return;
    try { localStorage.setItem(stashKey, JSON.stringify({ o: opts, q: resume?.quoteId ?? null, n: officeNote || null, p: plan, t: Date.now() })); } catch { /* fine */ }
  }, [opts, booted, resume, stashKey, officeNote, plan]);

  const patchOpt = (i: number, p: Partial<RepairOpt>) => setOpts((os) => os.map((o, j) => (j === i ? { ...o, ...p } : o)));
  const filled = opts.filter((o) => o.lines.length > 0);

  // QUOTE-PKG-01: one tap fills the first EMPTY option from a package —
  // label, customer line, parts, prices. Nothing empty left? It appends.
  const applyPkg = (p: QuotePackage) => {
    const next: RepairOpt = { label: p.label, blurb: p.blurb ?? "", lines: pkgToQLines(p) };
    setOpts((os) => {
      const i = os.findIndex((o) => o.lines.length === 0);
      return i === -1 ? [...os, next] : os.map((o, j) => (j === i ? next : o));
    });
  };

  const present = useMutation({
    mutationFn: async (): Promise<{ quoteId: string; optionIds: string[] }> => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      let quoteId = resume?.quoteId ?? null;
      if (quoteId) {
        const { error: ue } = await supabase
          .from("pegasus_quote")
          .update({
            offering_mode: filled.length > 1 ? "tiered" : "single",
            // PROPOSAL-FLOW-02: saving is not sending — it stays a draft; the
            // in-app present screen stamps presented_at, Send Link marks Sent.
            status: "draft", updated_at: now,
            prepared_by_empno: empno,
            notes: [`Presented on site at call #${s.no}${techFirst ? ` by ${techFirst}` : ""}.`, officeNote.trim() ? `Note to the office: ${officeNote.trim()}` : null].filter(Boolean).join("\n"),
            // PLAN-OFFER-01: the plan rides the quote (the present screen, the homeowner page and the office read the same offer)
            plan_offer_base_id: plan.on ? (planFacts.base?.id ?? null) : null,
            plan_offer_mode: plan.on ? "optional" : null,
            plan_offer_default_term: plan.on ? plan.term : null,
            plan_offer_terms: plan.on ? TECH_PLAN_TERMS : null,
            plan_offer_covers: plan.on ? planCovers : null,
            plan_offer_systems: plan.on ? planSystems : null,
            work_units: workUnits.length ? workUnits : null,
          })
          .eq("id", quoteId);
        if (ue) throw ue;
        await supabase.from("pegasus_quote_option").delete().eq("quote_id", quoteId);
      } else {
        const { data: q, error } = await supabase
          .from("pegasus_quote")
          .insert({
            custno: s.custno, locno: s.locno,
            title: `Repair options — ${s.name}`,
            title_is_auto: true,
            quote_type: "service",
            offering_mode: filled.length > 1 ? "tiered" : "single",
            status: "draft",
            dispatch_no: s.no,
            prepared_by_empno: empno,
            notes: [`Presented on site at call #${s.no}${techFirst ? ` by ${techFirst}` : ""}.`, officeNote.trim() ? `Note to the office: ${officeNote.trim()}` : null].filter(Boolean).join("\n"),
            created_by: u?.user?.email ?? null,
            plan_offer_base_id: plan.on ? (planFacts.base?.id ?? null) : null,
            plan_offer_mode: plan.on ? "optional" : null,
            plan_offer_default_term: plan.on ? plan.term : null,
            plan_offer_terms: plan.on ? TECH_PLAN_TERMS : null,
            plan_offer_covers: plan.on ? planCovers : null,
            plan_offer_systems: plan.on ? planSystems : null,
            work_units: workUnits.length ? workUnits : null,
          })
          .select("id")
          .single();
        if (error) throw error;
        quoteId = (q as { id: string }).id;
      }
      const tiers = ["best", "better", "good"] as const;
      const optionIds: string[] = [];
      for (let i = 0; i < filled.length; i++) {
        const { data: o, error: oe } = await supabase
          .from("pegasus_quote_option")
          .insert({
            quote_id: quoteId, tier: tiers[i] ?? "good", label: filled[i].label.trim() || null,
            description: filled[i].blurb.trim() || null, brand: null, sort: i,
          })
          .select("id")
          .single();
        if (oe) throw oe;
        const oid = (o as { id: string }).id;
        optionIds.push(oid);
        // QUOTE-PKG-01: package-built lines keep their roles (recommended /
        // optional render as homeowner toggles on the proposal) and their
        // $0-included netting; hand-typed lines stay locked-in as before.
        const rows = filled[i].lines.map((l, j) => ({
          option_id: oid, kind: l.kind || "custom", item_id: l.item_id, description: l.description,
          model: l.model ?? null, manufacturer: l.manufacturer ?? null, image_url: l.image_url ?? null,
          qty: l.qty, unit_price: l.unitPrice,
          discount: l.inc ? l.qty * l.unitPrice : 0,
          sort: j, role: l.role === "included" ? "required" : (l.role || "required"),
          warranty: l.warranty ?? null,
        }));
        if (rows.length) {
          const { error: le } = await supabase.from("pegasus_quote_line").insert(rows);
          if (le) throw le;
        }
      }
      return { quoteId: quoteId!, optionIds };
    },
    onSuccess: (r) => {
      setResume({ quoteId: r.quoteId });
      try { localStorage.setItem(stashKey, JSON.stringify({ o: opts, q: r.quoteId, n: officeNote || null, p: plan, t: Date.now() })); } catch { /* fine */ }
      setPlanChoice({ on: plan.on, term: plan.term });
      setPresented(r);
      // PROPOSAL-FLOW-02: the screen that follows IS the presentation — stamp it
      // (presented_at + "Presented the customer view · Miles S."), status stays draft.
      void stampProposalPresented(r.quoteId).catch(() => { /* never blocks the screen */ });
      qc.invalidateQueries({ queryKey: ["m_quote"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
    onError: () => setErr("Couldn't save the options — check signal and tap again."),
  });

  const accept = useMutation({
    mutationFn: async (v: { optionId: string }) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("pegasus_quote")
        .update({ status: "accepted", accepted_option_id: v.optionId, accepted_at: now, updated_at: now })
        .eq("id", presented!.quoteId);
      if (error) throw error;
      // PLAN-OFFER-01: the plan's yes (accepted · unpaid — it rides today's invoice and
      // activates when that is paid) or its no, recorded with the repair choice.
      if (plan.on) {
        if (planChoice.on) {
          const { error: pe } = await supabase.rpc("pegasus_plan_offer_accept", { p_quote_id: presented!.quoteId, p_term: planChoice.term, p_via: "tech" });
          if (pe) console.error("plan offer accept failed:", pe);
        } else {
          const { error: pe } = await supabase.rpc("pegasus_plan_offer_decline", { p_quote_id: presented!.quoteId, p_reason: "not chosen on site" });
          if (pe) console.error("plan offer decline failed:", pe);
        }
      }
    },
    onSuccess: () => {
      try { localStorage.removeItem(stashKey); } catch { /* fine */ }
      qc.invalidateQueries({ queryKey: ["accepted_service_pkg"] });
      qc.invalidateQueries({ queryKey: ["m_quote"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["plan_offers"] });
      qc.invalidateQueries({ queryKey: ["customer_proposals"] });
    },
    onError: () => setErr("Couldn't record the choice — tap it again."),
  });

  // ---- accepted: the closing screen
  if (presented && acceptedIdx != null) {
    const o = filled[acceptedIdx];
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col items-center justify-center bg-[#f6f5f1] p-8 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-8 w-8 text-emerald-600" />
        </span>
        <h3 className="mt-4 text-xl font-extrabold text-slate-900">Locked in ✓</h3>
        <p className="mt-2 max-w-[32ch] text-sm leading-relaxed text-slate-500">
          <b className="text-slate-700">{o.label}</b> · {money$(optTotal(o))} — one tap away in Parts &amp; materials on your invoice.
          {plan.on && planChoice.on && <><br /><b className="text-emerald-700">+ the {planChoice.term}-year plan</b> — it&apos;s waiting on the invoice&apos;s Maintenance Plan card.</>}
        </p>
        <button type="button" onClick={onClose} className="mt-6 w-full rounded-2xl bg-emerald-600 py-4 text-[15px] font-bold text-white active:opacity-90">
          Back to the call
        </button>
      </div>
    );
  }

  // ---- customer-facing present screen (light, matches the app)
  if (presented) {
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
        <div className="border-b border-slate-200 bg-white p-4 text-center">
          <div className="text-[15px] font-bold tracking-tight text-slate-900">Your repair options</div>
          <div className="mt-0.5 text-lg font-extrabold text-slate-900">{s.name}</div>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {filled.map((o, i) => (
            <div key={i} className={cn("overflow-hidden rounded-3xl border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", i === 0 && filled.length > 1 ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200")}>
              {i === 0 && filled.length > 1 && (
                <div className="bg-emerald-500 py-1 text-center text-[10px] font-extrabold uppercase tracking-widest text-white">Our recommendation</div>
              )}
              <div className="p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="text-[15px] font-extrabold text-slate-900">{o.label}</h4>
                  <span className="text-xl font-extrabold tabular-nums text-slate-900">{money$(optTotal(o))}</span>
                </div>
                {o.blurb.trim() && <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500">{o.blurb.trim()}</p>}
                <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                  {o.lines.map((l, j) => (
                    <li key={j} className="flex justify-between gap-2 text-[12.5px] text-slate-600">
                      <span className="min-w-0 flex-1">{l.qty > 1 ? `${l.qty}× ` : ""}{l.description}</span>
                      <span className="tabular-nums text-slate-400">{money$(l.qty * l.unitPrice)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate({ optionId: presented.optionIds[i] }, { onSuccess: () => setAcceptedIdx(i) })}
                  className="mt-3 w-full rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:opacity-90 disabled:opacity-60"
                >
                  We'll do this one{plan.on && planChoice.on ? ` + the ${planChoice.term}-yr plan` : ""}
                </button>
              </div>
            </div>
          ))}
          {/* PLAN-OFFER-01: the plan, with the real savings on the recommended option's repair. */}
          <TechPlanPresentCard plan={plan} choice={planChoice} onChoice={setPlanChoice} repairEligible={filled[0] ? optRepairEligible(filled[0]) : 0} custno={s.custno} locno={s.locno} />
          {err && <p className="text-center text-xs font-semibold text-red-600">{err}</p>}
        </div>
        <div className="border-t border-slate-200 bg-white p-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-2.5">
          <button type="button" onClick={onClose} className="w-full rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-600 active:bg-slate-50">
            They'll think it over
          </button>
          <p className="mt-1.5 text-center text-[10px] leading-snug text-slate-400">
            It stays saved — they can approve it later from their portal.
          </p>
        </div>
      </div>
    );
  }

  // ---- builder
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back to the call">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-slate-900">Repair options</div>
          <div className="truncate text-[11px] text-slate-500">{s.name} · empty options aren&apos;t sent</div>
        </div>
        {resume && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">editing the saved quote</span>}
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {!booted ? (
          <div className="h-24 animate-pulse rounded-3xl bg-slate-100" />
        ) : (
          <>
            <WizardModeSeg mode="repair" onSwitch={onSwitch} />
            <PkgChips kind="repair" onPick={applyPkg} />
            {opts.map((o, i) => (
              <section key={i} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={o.label}
                    onChange={(e) => patchOpt(i, { label: e.target.value })}
                    className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-[14px] font-bold focus:border-red-500 focus:outline-none"
                    aria-label={`Option ${i + 1} name`}
                  />
                  {opts.length > 1 && (
                    <button type="button" onClick={() => setOpts((os) => os.filter((_, j) => j !== i))} className="rounded-lg p-1.5 text-slate-300 active:text-red-500" aria-label={`Remove option ${i + 1}`}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <textarea
                  value={o.blurb}
                  onChange={(e) => patchOpt(i, { blurb: e.target.value })}
                  rows={2}
                  placeholder="A line or two in your words — what this option gets them. Shows on their screen."
                  className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-[13px] leading-relaxed focus:border-red-500 focus:outline-none"
                />
                {o.lines.length > 0 && (
                  <ul className="mt-2 divide-y divide-slate-100">
                    {o.lines.map((l, j) => (
                      <li key={j} className="flex items-center gap-2 py-2">
                        <span className="min-w-0 flex-1 text-[13px] font-semibold text-slate-700">{l.qty > 1 ? `${l.qty}× ` : ""}{l.description}</span>
                        <span className="relative">
                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            value={l.unitPrice}
                            onChange={(e) => patchOpt(i, { lines: o.lines.map((x, k) => (k === j ? { ...x, unitPrice: Number(e.target.value) || 0 } : x)) })}
                            className="w-20 rounded-lg border border-slate-200 py-1 pl-5 pr-1 text-right text-xs font-bold tabular-nums focus:border-red-500 focus:outline-none"
                            aria-label="Price"
                          />
                        </span>
                        <button type="button" onClick={() => patchOpt(i, { lines: o.lines.filter((_, k) => k !== j) })} className="rounded p-1 text-slate-300 active:text-red-500" aria-label="Remove line">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <PartSearchAdd onAdd={(l) => patchOpt(i, { lines: [...o.lines, { kind: "custom", description: l.description, qty: l.qty, unitPrice: l.unitPrice, item_id: l.item_id ?? null, model: null, manufacturer: null, image_url: null, role: "included" }] })} />
                <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 text-sm font-extrabold text-slate-900">
                  <span>This option</span><span className="tabular-nums">{money$(optTotal(o))}</span>
                </div>
              </section>
            ))}
            <button
              type="button"
              onClick={() => setOpts((os) => [...os, { label: "Another Option", blurb: "", lines: [] }])}
              className="w-full rounded-2xl border-2 border-dashed border-slate-300 py-3 text-sm font-bold text-slate-500 active:bg-white"
            >
              + Give them another choice
            </button>
            <TechPlanOfferCard plan={plan} onChange={setPlan} custno={s.custno} locno={s.locno} />
            {err && <p className="text-center text-xs font-semibold text-red-600">{err}</p>}
          </>
        )}
      </div>
      <footer className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        {/* OFFICE-NOTE-01: what the office should know — rides the quote
            record; the customer proposal never shows it. */}
        <textarea
          value={officeNote}
          onChange={(e) => setOfficeNote(e.target.value)}
          rows={2}
          placeholder="Note to the office (optional) — financing talk, access quirks, who to call back. Customer never sees this."
          className="mb-2 w-full rounded-xl border border-slate-200 p-2.5 text-[13px] focus:border-brand-400 focus:outline-none"
        />
        <button
          type="button"
          disabled={filled.length === 0 || present.isPending}
          onClick={() => present.mutate()}
          className={cn("w-full rounded-2xl py-4 text-[15px] font-bold text-white", filled.length > 0 ? "bg-slate-900 active:opacity-90" : "bg-slate-300")}
        >
          {present.isPending ? "Getting it ready…" : filled.length === 0 ? "Add a priced line to at least one option" : resume ? "Update & show the customer →" : "Show the customer →"}
        </button>
      </footer>
    </div>
  );
}

/** Replacement opportunity → one clean lead to the office's Sales leads lane.
 *  The comfort advisor builds the pretty install quote from the desk; the
 *  tech's job is the facts: which unit, why, how urgent, who asked. */
function TurnoverSheet({ s, empno, techFirst, onClose }: {
  s: Stop;
  empno: string | null;
  techFirst: string | null;
  onClose: () => void;
}) {
  const equip = useEquipmentAt(s.custno, s.locno);
  const add = useAddTicket();
  const [unit, setUnit] = useState<EquipRow | null>(null);
  const [why, setWhy] = useState("");
  const [urgency, setUrgency] = useState<string>("this season");
  const [asked, setAsked] = useState(true);
  const [sent, setSent] = useState(false);

  const send = () => {
    const unitTxt = unit ? [unit.mfg, unit.model].filter(Boolean).join(" ") || unit.eq_type || "unit on file" : "system";
    add.mutate(
      {
        lane_key: "sales_lead",
        custno: s.custno,
        locno: s.locno,
        customer_name: s.name,
        address: s.address,
        city: s.city,
        lat: s.lat,
        lng: s.lng,
        phone: s.phone,
        summary: `Replacement quote — ${unitTxt}`,
        notes: [
          why.trim() || null,
          unit ? `Unit: ${unitTxt}${unit.serial ? ` · S/N ${unit.serial}` : ""}${unit.install_date ? ` · installed ${unit.install_date.slice(0, 4)}` : ""}` : null,
          `Urgency: ${urgency}`,
          asked ? "Customer asked for the quote." : "Tech recommendation — customer open to it.",
          `Flagged from Pegasus Field at call #${s.no}${techFirst ? ` by ${techFirst}` : ""}.`,
        ].filter(Boolean).join(" · "),
        origin_dispatch: s.ref,
        affinity_empno: empno,
        source: "tech_mobile",
      },
      { onSuccess: () => setSent(true) },
    );
  };

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col items-center justify-center bg-[#f6f5f1] p-8 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-8 w-8 text-emerald-600" />
        </span>
        <h3 className="mt-4 text-xl font-extrabold text-slate-900">Sent to the office</h3>
        <p className="mt-2 max-w-[30ch] text-sm leading-relaxed text-slate-500">
          A comfort advisor takes it from here.
        </p>
        <button type="button" onClick={onClose} className="mt-6 w-full rounded-2xl bg-gradient-to-b from-red-600 to-red-700 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_rgba(185,28,28,0.5)] transition-transform [touch-action:manipulation] active:scale-[0.98] text-[15px] font-bold text-white active:opacity-90">
          Back to the call
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Cancel">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-slate-900">Replacement quote lead</div>
          <div className="truncate text-[11px] text-slate-500">{s.name}</div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {(equip.data ?? []).length > 0 && (
          <div>
            <label className="block text-sm font-bold text-slate-800">Which unit? <span className="font-normal text-slate-400">(optional)</span></label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(equip.data ?? []).map((e) => {
                const sel = unit?.counter === e.counter;
                return (
                  <button key={e.counter} type="button" onClick={() => setUnit(sel ? null : e)}
                    className={cn("rounded-full px-3 py-1.5 text-xs font-bold", sel ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 active:bg-slate-50")}>
                    {[e.mfg, e.model].filter(Boolean).join(" ") || e.eq_type || "Unit"}{e.install_date ? ` · ${e.install_date.slice(0, 4)}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-bold text-slate-800">Why replace?</label>
          <textarea
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            rows={3}
            autoFocus
            placeholder="22-year-old furnace, heat exchanger starting to go, repair quote is $1,900…"
            className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] leading-relaxed focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-800">How urgent?</label>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {["needs it now", "this season", "down the road"].map((u) => (
              <button key={u} type="button" onClick={() => setUrgency(u)}
                className={cn("rounded-2xl py-2.5 text-xs font-bold capitalize", urgency === u ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 active:bg-slate-50")}>
                {u}
              </button>
            ))}
          </div>
        </div>
        <button type="button" onClick={() => setAsked((v) => !v)}
          className="flex w-full items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left active:bg-slate-50">
          <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2", asked ? "border-emerald-500 bg-emerald-500" : "border-slate-300")}>
            {asked && <Check className="h-3.5 w-3.5 text-white" />}
          </span>
          <span className="text-[13px] font-bold text-slate-700">The customer asked for this quote</span>
        </button>
      </div>
      <footer className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button type="button" disabled={!why.trim() || add.isPending} onClick={send}
          className={cn("w-full rounded-2xl py-4 text-[15px] font-bold text-white", why.trim() ? "bg-red-700 active:opacity-90" : "bg-slate-300")}>
          {add.isPending ? "Sending…" : "Send to the office"}
        </button>
      </footer>
    </div>
  );
}

/* --------------------------------------------------- customer sign-off */
// SignaturePad lives in ./SignaturePad (shared with the installer app).

/* ----------------------------------- replacement G/B/B quote (full builder) */

interface TierRebate { utilityId: string | null; name: string; amount: number }
interface TierDraft {
  tier: "best" | "better" | "good";
  label: string;
  blurb: string;
  brand: (typeof QUOTE_BRANDS)[number];
  lines: QLine[];
  rebates: TierRebate[];
  discount: number;
  discMode: "amount" | "percent";
  discLabel: string;
}
const TIER_SEED: Omit<TierDraft, "lines">[] = [
  { tier: "best", label: "Best Collection", blurb: "", brand: "Lennox", rebates: [], discount: 0, discMode: "amount", discLabel: "" },
  { tier: "better", label: "Better Collection", blurb: "", brand: "Lennox", rebates: [], discount: 0, discMode: "amount", discLabel: "" },
  { tier: "good", label: "Good Collection", blurb: "", brand: "Lennox", rebates: [], discount: 0, discMode: "amount", discLabel: "" },
];
const EQUIP_CATS: Array<[string, string]> = [["furnace", "Furnace"], ["ac", "A/C"], ["heat_pump", "Heat pump"], ["water_heater", "Water heater"], ["accessory", "Accessory"]];
/** Proposal-app parity — Included = thrown in, $0 to the customer;
 *  Required = code-required, PRICED, can't be removed; Recommended = shelf;
 *  Optional = customer toggle. */
/** A line's price to the customer: an accessory marked Included is $0 (its
 *  value shows struck-through on the proposal). */
const qlineNet = (l: QLine) => (l.kind === "accessory" && l.inc ? 0 : l.qty * l.unitPrice);
/** MONEY v2: gross − sale discount = DUE TO SEDGWICK. Rebates never touch it. */
const tierGross = (t: TierDraft) =>
  // QUOTE-POLISH-13: the package price is everything on the package — Optional
  // lines included (the homeowner may take one off; the price drops then).
  // Only Recommended Add-Ons (the shelf) stay out until they add one.
  t.lines.filter((l) => l.role !== "recommended").reduce((s, l) => s + qlineNet(l), 0);
const tierDiscount = (t: TierDraft) => {
  const v = Number(t.discount) || 0;
  return t.discMode === "percent" ? Math.max(0, (tierGross(t) * v) / 100) : Math.max(0, v);
};
const tierTotal = (t: TierDraft) => Math.max(0, tierGross(t) - tierDiscount(t));
const tierRebateSum = (t: TierDraft) => t.rebates.reduce((s, r) => s + (Number(r.amount) || 0), 0);

/** Equipment warranty, dropdown-style (Seth: "display the default warranty and
 *  if they want to change it they can mess with the drop downs, 1–20"). Shows
 *  the tier default until touched; two selects compose "X-Year Parts ·
 *  Y-Year Labor"; Standard resets to the default. */
const WARRANTY_RE = /(\d+)-Year Parts · (\d+)-Year Labor/;
function WarrantyPicker({ value, tier, onChange }: {
  value: string | null;
  tier: "best" | "better" | "good";
  onChange: (w: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const std = tier === "good" ? [5, 5] : [10, 5];
  const m = value ? WARRANTY_RE.exec(value) : null;
  const parts = m ? Number(m[1]) : std[0];
  const labor = m ? Number(m[2]) : std[1];
  const label = value?.trim() || `${std[0]}-Year Parts · ${std[1]}-Year Labor (standard)`;
  const set = (p: number, l: number) => onChange(`${p}-Year Parts · ${l}-Year Labor`);
  const YEARS = Array.from({ length: 20 }, (_, y) => y + 1);
  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={cn("flex w-full items-center justify-between rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold",
          value ? "border-emerald-200 bg-emerald-50/60 text-emerald-800" : "border-slate-200 text-slate-500 active:bg-slate-50")}>
        <span><ShieldCheck className="mr-1 inline h-3 w-3" />{label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-1 flex items-center gap-1.5 rounded-lg bg-slate-50 p-2">
          {([["Parts", parts, (y: number) => set(y, labor)], ["Labor", labor, (y: number) => set(parts, y)]] as Array<[string, number, (y: number) => void]>).map(([lab, val, fn]) => (
            <label key={lab} className="flex min-w-0 flex-1 items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
              {lab}
              <select value={val} onChange={(e) => fn(Number(e.target.value))}
                className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] font-bold text-slate-700">
                {YEARS.map((y) => <option key={y} value={y}>{y} yr</option>)}
              </select>
            </label>
          ))}
          <button type="button" onClick={() => { onChange(null); setOpen(false); }}
            className="shrink-0 rounded-md px-2 py-1 text-[10px] font-bold text-slate-400 active:bg-white">
            Standard
          </button>
        </div>
      )}
    </div>
  );
}

/** The proposal-app catalog picker, on the phone: browse the WHOLE pricebook
 *  slot (tier-ordered, brand-scoped, photos + installed price) the moment it
 *  opens — search narrows, accessory CATEGORIES tab across the top. Same
 *  useEquipmentItems the office G/B/B builder reads. */
function EquipPickerModal({ kind, brand, onPick, onClose }: {
  kind: string;
  brand: (typeof QUOTE_BRANDS)[number];
  onPick: (it: Item) => void;
  onClose: () => void;
}) {
  const equipType = kind === "furnace" ? "Furnace" : kind === "ac" ? "Air Conditioner" : kind === "heat_pump" ? "Heat Pump" : kind === "water_heater" ? "Water Heater" : "Accessory";
  const isAccessory = kind === "accessory";
  const [q, setQ] = useState("");
  const [subcat, setSubcat] = useState<string | null>(null);
  const [allBrands, setAllBrands] = useState(isAccessory);
  const subcats = useAccessorySubcategories().data ?? [];
  const items = useEquipmentItems(equipType, q, allBrands ? null : brand, isAccessory ? subcat : null);
  const kindLabel = EQUIP_CATS.find(([k]) => k === kind)?.[1] ?? "equipment";

  return (
    <div className="fixed inset-0 z-[60] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Close picker">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold text-slate-900">Pick the {kindLabel.toLowerCase()}</div>
            <div className="text-[11px] text-slate-500">{isAccessory ? "By category" : allBrands ? "All brands" : brand}</div>
          </div>
          {!isAccessory && (
            <button type="button" onClick={() => setAllBrands((v) => !v)}
              className={cn("shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold", allBrands ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500")}>
              {allBrands ? "all brands" : `${brand} only`}
            </button>
          )}
        </div>
        {isAccessory && subcats.length > 0 && (
          <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5">
            <button type="button" onClick={() => setSubcat(null)}
              className={cn("shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold", subcat == null ? "bg-slate-900 text-white" : "bg-white text-slate-500 border border-slate-200")}>
              All
            </button>
            {subcats.map((sc) => (
              <button key={sc} type="button" onClick={() => setSubcat(subcat === sc ? null : sc)}
                className={cn("shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold", subcat === sc ? "bg-slate-900 text-white" : "bg-white text-slate-500 border border-slate-200")}>
                {sc}
              </button>
            ))}
          </div>
        )}
        <div className="relative mt-2">
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={isAccessory ? "Search accessories…" : "Search — SLP99V, ML17XC1…"}
            className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
          />
          {items.isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-300" />}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {items.isLoading ? (
          <div className="mt-2 h-24 animate-pulse rounded-2xl bg-slate-100" />
        ) : (items.data?.length ?? 0) === 0 ? (
          <p className="mt-8 text-center text-xs text-slate-400">Nothing here{q ? " for that search" : ""} — try another category, all brands, or add a custom line.</p>
        ) : (
          <ul className="space-y-2">
            {(items.data ?? []).map((it) => (
              <li key={it.id}>
                <button type="button" onClick={() => onPick(it)}
                  className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-2.5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100">
                    {it.image_url
                      ? <img src={it.image_url} alt="" className="h-full w-full object-contain" loading="lazy" />
                      : <Wrench className="h-5 w-5 text-slate-300" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-bold text-slate-800">{it.description ?? it.model ?? it.part_number}</span>
                    <span className="block truncate text-[11px] text-slate-400">
                      {[it.manufacturer, it.model, it.efficiency].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    {it.tier && <span className="block text-[9px] font-bold uppercase tracking-wide text-emerald-600">{it.tier}</span>}
                    <span className="block text-sm font-extrabold tabular-nums text-slate-800">
                      {it.sell_price != null || it.base_price != null ? money$(Number(it.sell_price ?? it.base_price)) : "—"}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Branded "your proposal is ready" email — same look as the invoice email,
 *  red CTA straight into the token proposal. Comms-gated like everything. */
function buildProposalEmailHtml(v: { title: string; url: string; techFirst: string | null }): string {
  const LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";
  const esc = (x: string) => x.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<div style="max-width:560px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#fff;border:1px solid #e8eaed;border-radius:14px;overflow:hidden">
  <div style="text-align:center;padding:24px 24px 18px;border-bottom:3px solid #16314f"><img src="${LOGO}" alt="Sedgwick Heating" style="width:180px;max-width:60%"/></div>
  <div style="padding:28px 32px">
    <h1 style="font-size:19px;color:#16314f;margin:0 0 8px">Your proposal is ready</h1>
    <p style="font-size:14px;color:#334155;line-height:1.6;margin:0 0 6px">${esc(v.title)}</p>
    <p style="font-size:14px;color:#334155;line-height:1.6;margin:0 0 18px">${v.techFirst ? `${esc(v.techFirst)} put together` : "We put together"} your options — full specs, pricing, monthly payment estimates, and a simple approve button. Take your time and review it whenever you're ready.</p>
    <p style="margin:0;text-align:center"><a href="${v.url}" style="display:inline-block;background:#c0392b;color:#ffffff;text-decoration:none;font-weight:700;font-size:15px;padding:12px 28px;border-radius:9px">Review my options</a></p>
    <p style="color:#9aa4b1;font-size:13px;line-height:1.6;margin:24px 0 0">Questions? Call <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a> — we're happy to walk through it.</p>
  </div>
  <div style="text-align:center;font-size:12px;color:#9aa4b1;padding:0 0 20px">Sedgwick Heating &amp; Air Conditioning &middot; Eagan, MN</div>
</div>`;
}

/** The FULL Good/Better/Best system quote, built on the truck: pricebook
 *  equipment with photos, accessories (included / recommended / optional),
 *  utility rebates, named sale discounts, a blurb per collection — presented
 *  through the REAL homeowner proposal, or emailed instead. Reopening resumes
 *  the live quote; presenting again UPDATES it. */
function ReplaceQuoteWizard({ s, empno, techFirst, onAccepted, onLead, onClose, onSwitch }: {
  s: Stop;
  empno: string | null;
  techFirst: string | null;
  onAccepted: (quoteId: string) => void;
  onLead: () => void;
  onClose: () => void;
  /** QUOTE-PKG-01: swap to the Repair wizard (drafts persist per wizard). */
  onSwitch?: () => void;
}) {
  const qc = useQueryClient();
  const stashKey = `pegasus.m.iq.${s.ref}`;
  const utilities = useUtilities().data ?? [];
  const custEmail = useCustomerEmail(s.custno, s.locno).data;
  const enqueue = useEnqueueOutbox();
  const [tiers, setTiers] = useState<TierDraft[]>(() => TIER_SEED.map((t) => ({ ...t, lines: [] })));
  // QUOTE-POLISH-10: Recommended Add-Ons are QUOTE-level suggestions — shown to the
  // homeowner below the packages, offered on every collection, and only the
  // homeowner moves one into their package. Saved as a "recommended" line on
  // each option (that's how the proposal page, accept and the job read them).
  const [addons, setAddons] = useState<QLine[]>([]);
  const addonKeyOf = (l: QLine) => l.item_id ?? `d:${l.description.trim().toLowerCase()}`;
  const liftAddons = (opts: Array<{ lines: QLine[] }>): QLine[] => {
    const m = new Map<string, QLine>();
    for (const o of opts) for (const l of o.lines) if (l.role === "recommended" && !m.has(addonKeyOf(l))) m.set(addonKeyOf(l), { ...l, role: "recommended", inc: false });
    return [...m.values()];
  };
  const [booted, setBooted] = useState(false);
  const [resume, setResume] = useState<{ quoteId: string; token: string } | null>(null);
  const [picker, setPicker] = useState<{ i: number; kind: string } | null>(null);
  const [handoff, setHandoff] = useState<{ quoteId: string; token: string } | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSeeded, setEmailSeeded] = useState(false);
  const [emailStatus, setEmailStatus] = useState<null | "queued" | "held" | "failed">(null);
  const [err, setErr] = useState<string | null>(null);
  // OFFICE-NOTE-01 (queued Sept 30): context for the OFFICE that rides the
  // quote record — never shown on the customer proposal.
  const [officeNote, setOfficeNote] = useState("");
  // QUOTE-POLISH-11: the EXTERNAL note — his app's "external notes" — shown on the
  // homeowner page as a note from the advisor, in the tech's own words.
  const [homeownerNote, setHomeownerNote] = useState("");

  // Boot: the live server quote is ALWAYS resolved first — it is the IDENTITY
  // (one live proposal per call; presenting UPDATES it, never stacks a new one
  // — Seth 2026-08-27: "it keeps adding multiple ones"). The stash only wins
  // for unsaved CONTENT on top of that identity.
  useEffect(() => {
    let dead = false;
    void (async () => {
      const live = await fetchLiveQuote(s.no, "install").catch(() => null);
      if (dead) return;
      if (live) {
        setResume({ quoteId: live.quoteId, token: live.token });
        // OFFICE-NOTE-01: an update must not drop the note already on file.
        void supabase.from("pegasus_quote").select("notes, homeowner_note").eq("id", live.quoteId).maybeSingle().then(({ data }) => {
          if (dead) return;
          const row = data as { notes?: string | null; homeowner_note?: string | null } | null;
          const m = /(?:^|\n)Note to the office: ([\s\S]*)$/.exec(row?.notes ?? "");
          if (m) setOfficeNote((cur) => cur || m[1].trim());
          if (row?.homeowner_note) setHomeownerNote((cur) => cur || row.homeowner_note!);
        });
      }
      try {
        const raw = localStorage.getItem(stashKey);
        if (raw) {
          const p = JSON.parse(raw) as { b?: string; t?: TierDraft[]; a?: QLine[]; q?: { quoteId: string; token: string } | null; n?: string | null; hn?: string | null };
          if (p.n && !dead) setOfficeNote((cur) => cur || p.n!);
          if (p.hn && !dead) setHomeownerNote((cur) => cur || p.hn!);
          if (Array.isArray(p.t) && p.t.length) {
            if (!dead) {
              const legacyBrand = p.b === "Armstrong" ? "Armstrong" as const : "Lennox" as const;
              // Older stashes kept recommended lines inside the tiers — lift them out.
              setAddons(Array.isArray(p.a) ? p.a.map((l) => normalizeQLine(l)) : liftAddons(p.t.map((t) => ({ lines: (t.lines ?? []).map((l) => normalizeQLine(l)) }))));
              setTiers(p.t.map((t, ti) => {
                const legacy = t as TierDraft & { utilityId?: string | null; rebate?: number };
                return {
                  ...TIER_SEED[Math.min(ti, 2)],
                  ...t,
                  brand: t.brand === "Armstrong" || t.brand === "Lennox" ? t.brand : legacyBrand,
                  discMode: t.discMode === "percent" ? "percent" : "amount",
                  // Old single-rebate stashes fold into the rebates list.
                  rebates: Array.isArray(t.rebates)
                    ? t.rebates
                    : (Number(legacy.rebate) || 0) > 0
                      ? [{ utilityId: legacy.utilityId ?? null, name: "", amount: Number(legacy.rebate) || 0 }]
                      : [],
                  // Older stashes predate roles/photos/inc on lines — normalize.
                  lines: (t.lines ?? []).map((l) => normalizeQLine(l)).filter((l) => l.role !== "recommended"),
                };
              }));
              setBooted(true);
            }
            return;
          }
        }
      } catch { /* fall through */ }
      if (live && live.options.length) {
        const tierKeys = ["best", "better", "good"] as const;
        setAddons(liftAddons(live.options.map((o) => ({ lines: o.lines.map((l) => normalizeQLine(l)) }))));
        setTiers(live.options.map((o, i) => ({
          tier: (tierKeys.includes(o.tier as never) ? o.tier : tierKeys[Math.min(i, 2)]) as TierDraft["tier"],
          label: o.label || "Option",
          blurb: o.blurb,
          brand: o.brand === "Armstrong" ? "Armstrong" : "Lennox",
          lines: o.lines.map((l) => normalizeQLine(l)).filter((l) => l.role !== "recommended"),
          rebates: o.rebates,
          discount: o.discount,
          discMode: o.discMode,
          discLabel: o.discLabel,
        })));
      }
      setBooted(true);
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!booted) return;
    try { localStorage.setItem(stashKey, JSON.stringify({ t: tiers, a: addons, q: resume, n: officeNote || null, hn: homeownerNote || null, ts: Date.now() })); } catch { /* fine */ }
  }, [tiers, addons, booted, resume, stashKey, officeNote, homeownerNote]);

  useEffect(() => {
    if (emailSeeded || !custEmail) return;
    setEmailSeeded(true);
    if (custEmail.email) setEmailTo(custEmail.email);
  }, [custEmail, emailSeeded]);

  const patchTier = (i: number, p: Partial<TierDraft>) => setTiers((ts) => ts.map((t, j) => (j === i ? { ...t, ...p } : t)));
  const filled = tiers.filter((t) => t.lines.length > 0);

  // QUOTE-PKG-01: a replace package fills the first EMPTY collection —
  // label, customer line, equipment + accessories with roles and $0 marks.
  // Brand, rebates and discounts stay the tech's call per collection.
  const applyPkgTier = (p: QuotePackage) => {
    setTiers((ts) => {
      const i = ts.findIndex((t) => t.lines.length === 0);
      if (i === -1) return ts;
      return ts.map((t, j) => (j === i ? { ...t, label: p.label, blurb: p.blurb ?? t.blurb, lines: pkgToQLines(p) } : t));
    });
  };
  // Proposal-app "copy to all": one tap pushes this option's accessories,
  // rebate, and discount onto the other collections — equipment stays put.
  const copyToAll = (i: number) =>
    setTiers((ts) => ts.map((t, j) => (j === i ? t : {
      ...t,
      rebates: ts[i].rebates.map((r) => ({ ...r })),
      discount: ts[i].discount,
      discMode: ts[i].discMode,
      discLabel: ts[i].discLabel,
      lines: [...t.lines.filter((l) => l.kind !== "accessory"), ...ts[i].lines.filter((l) => l.kind === "accessory").map((l) => ({ ...l }))],
    })));

  const present = useMutation({
    mutationFn: async (openAfter: boolean): Promise<{ quoteId: string; token: string; openAfter: boolean }> => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      let quoteId = resume?.quoteId ?? null;
      let token = resume?.token ?? null;
      if (quoteId) {
        const { error: ue } = await supabase
          .from("pegasus_quote")
          .update({
            title: `System replacement — ${s.name}`,
            offering_mode: filled.length > 1 ? "tiered" : "single",
            // PROPOSAL-FLOW-02: saving is not sending — it stays a draft. Opening
            // the proposal stamps presented_at; the staff bar's Send Link marks Sent.
            status: "draft", updated_at: now,
            prepared_by_empno: empno,
            notes: [`Built on site at call #${s.no}${techFirst ? ` by ${techFirst}` : ""}.`, officeNote.trim() ? `Note to the office: ${officeNote.trim()}` : null].filter(Boolean).join("\n"),
            homeowner_note: homeownerNote.trim() || null,
          })
          .eq("id", quoteId);
        if (ue) throw ue;
        await supabase.from("pegasus_quote_option").delete().eq("quote_id", quoteId);
      } else {
        const { data: quote, error } = await supabase
          .from("pegasus_quote")
          .insert({
            custno: s.custno, locno: s.locno,
            title: `System replacement — ${s.name}`,
            quote_type: "install",
            offering_mode: filled.length > 1 ? "tiered" : "single",
            status: "draft",
            dispatch_no: s.no,
            prepared_by_empno: empno,
            notes: [`Built on site at call #${s.no}${techFirst ? ` by ${techFirst}` : ""}.`, officeNote.trim() ? `Note to the office: ${officeNote.trim()}` : null].filter(Boolean).join("\n"),
            homeowner_note: homeownerNote.trim() || null,
            created_by: u?.user?.email ?? null,
          })
          .select("id, token")
          .single();
        if (error) throw error;
        quoteId = (quote as { id: string; token: string }).id;
        token = (quote as { id: string; token: string }).token;
      }
      for (let i = 0; i < filled.length; i++) {
        const t = filled[i];
        const { data: o, error: oe } = await supabase
          .from("pegasus_quote_option")
          .insert({
            quote_id: quoteId, tier: t.tier, label: t.label.trim() || null,
            description: t.blurb.trim() || null, brand: t.brand,
            // rebate_total mirrors the rebate rows (legacy consumers); the
            // itemized truth lives in pegasus_quote_rebate below.
            rebate_total: tierRebateSum(t), discount_total: 0,
            rebate_utility_id: t.rebates.find((r) => r.utilityId)?.utilityId ?? null, sort: i,
          })
          .select("id")
          .single();
        if (oe) throw oe;
        const oid = (o as { id: string }).id;
        // QUOTE-POLISH-10: the quote-level Recommended Add-Ons ride on EVERY
        // collection as "recommended" lines (never $0, never locked).
        const rows = [...t.lines.filter((l) => l.role !== "recommended"), ...addons.map((a) => ({ ...a, role: "recommended", inc: false }))].map((l, j) => ({
          option_id: oid, kind: l.kind, item_id: l.item_id, description: l.description,
          model: l.model, manufacturer: l.manufacturer, image_url: l.image_url,
          qty: l.qty, unit_price: l.unitPrice,
          // INCLUDED accessory ⇒ line discount equals its price, so every
          // pricing engine (proposal, office, accept RPC) nets it to $0 while
          // the gross value still shows struck-through.
          discount: l.kind === "accessory" && l.inc ? l.qty * l.unitPrice : 0,
          sort: j, role: l.role || "required",
          warranty: l.warranty?.trim() || null,
        }));
        if (rows.length) {
          const { error: le } = await supabase.from("pegasus_quote_line").insert(rows);
          if (le) throw le;
        }
        if ((Number(t.discount) || 0) > 0) {
          const { error: de } = await supabase.from("pegasus_quote_discount").insert({
            option_id: oid, label: t.discLabel.trim() || "Sedgwick discount", mode: t.discMode, value: Number(t.discount) || 0, sort: 0,
          });
          if (de) throw de;
        }
        // Itemized rebates — utility-linked or custom-named, any number of them.
        const rebRows = t.rebates
          .filter((r) => (Number(r.amount) || 0) > 0)
          .map((r, k) => ({
            option_id: oid,
            utility_id: r.utilityId,
            custom_name: r.utilityId ? null : (r.name.trim() || "Rebate"),
            amount: Number(r.amount) || 0,
            sort: k,
          }));
        if (rebRows.length) {
          const { error: re } = await supabase.from("pegasus_quote_rebate").insert(rebRows);
          if (re) throw re;
        }
      }
      return { quoteId: quoteId!, token: token!, openAfter };
    },
    onSuccess: (r) => {
      setResume({ quoteId: r.quoteId, token: r.token });
      try { localStorage.setItem(stashKey, JSON.stringify({ t: tiers, a: addons, q: { quoteId: r.quoteId, token: r.token }, ts: Date.now() })); } catch { /* fine */ }
      setHandoff({ quoteId: r.quoteId, token: r.token });
      qc.invalidateQueries({ queryKey: ["m_quote"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      if (r.openAfter) window.open(`/proposal?t=${r.token}`, "_blank");
    },
    onError: () => setErr("Couldn't save the quote — check signal and tap again."),
  });

  // The proposal decides for itself — while the handoff screen is open we
  // watch the quote every few seconds. No "check now" button to remember
  // (Seth: "can't the system just auto show that?").
  const decisionQ = useQuery({
    queryKey: ["m_decide", handoff?.quoteId ?? "none"],
    enabled: !!handoff,
    refetchInterval: 4000,
    queryFn: async (): Promise<string> => {
      const { data } = await supabase.from("pegasus_quote").select("status").eq("id", handoff!.quoteId).single();
      return (data as { status: string } | null)?.status ?? "draft";
    },
  });
  const decision: "accepted" | "declined" | "waiting" =
    decisionQ.data === "accepted" ? "accepted" : decisionQ.data === "declined" ? "declined" : "waiting";
  useEffect(() => {
    if (decision !== "waiting") {
      qc.invalidateQueries({ queryKey: ["m_quote"] });
      if (decision === "accepted") qc.invalidateQueries({ queryKey: ["accepted_service_pkg"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision]);

  const emailProposal = async () => {
    if (!handoff || !/\S+@\S+\.\S+/.test(emailTo.trim())) return;
    try {
      const st = await enqueue.mutateAsync({
        automation_key: "quote_send",
        dedupe_key: `quotesend:${handoff.quoteId}:${Date.now()}`,
        custno: s.custno,
        locno: s.locno,
        customer_name: s.name,
        channel: "email",
        to_address: emailTo.trim(),
        subject: "Your Sedgwick Heating proposal",
        body: buildProposalEmailHtml({
          title: `System replacement — ${s.name}`,
          url: `${window.location.origin}/proposal?t=${handoff.token}`,
          techFirst,
        }),
        trigger_kind: "quote",
        trigger_ref: handoff.quoteId,
      });
      setEmailStatus(st);
      // PROPOSAL-FLOW-02: queued = the link left the building → Sent, with
      // "Emailed the link to …" on the activity. Held (comms in testing) is not a send.
      if (st === "queued") void markProposalSent(handoff.quoteId, "email", emailTo.trim()).catch(() => { /* the email still goes */ });
    } catch {
      setEmailStatus("failed");
    }
  };

  // ---- handoff: written to the server; present, email, or head back
  if (handoff) {
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
        <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back to the work order">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold text-slate-900">Proposal saved ✓</div>
            <div className="truncate text-[11px] text-slate-500">{s.name} · saved to their portal</div>
          </div>
        </header>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {decision === "accepted" ? (
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><Check className="h-7 w-7 text-emerald-600" /></span>
              <h3 className="text-lg font-extrabold text-slate-900">They accepted ✓</h3>
              <p className="max-w-[32ch] text-sm leading-relaxed text-slate-600">Complete the install packet while you're on site.</p>
              <button type="button" onClick={() => onAccepted(handoff.quoteId)} className="w-full rounded-2xl bg-emerald-600 py-4 text-[15px] font-bold text-white active:opacity-90">
                Start the install packet →
              </button>
            </div>
          ) : (
            <>
              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900">Present It Here <InfoTip label="What they'll see">The full homeowner proposal — spec cards, monthly payments, finger-sign approval. Only you see the staff bar at the bottom: Send Link, Accepted in Person, Not Now.</InfoTip></h4>
                <button type="button" onClick={() => window.open(`/proposal?t=${handoff.token}`, "_blank")}
                  className="mt-2 w-full rounded-2xl bg-slate-900 py-3.5 text-sm font-bold text-white active:opacity-90">
                  Open the proposal
                </button>
                <button type="button" onClick={() => setHandoff(null)}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-600 active:bg-slate-50">
                  ✎ Edit / keep working on the options
                </button>
                {/* live decision watcher — no button, it just knows */}
                <p className={cn("mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] font-semibold", decision === "declined" ? "text-red-500" : "text-slate-400")}>
                  {decision === "waiting" && <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />}
                  {decision === "waiting" ? "Waiting for their decision" : "Marked declined"}
                </p>
              </section>

              {/* Do the homework while you're in the house — if they say yes
                  from home next week, nobody rolls a truck for measurements. */}
              <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Likely to accept?</h4>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Site photos, measurements, and a preferred week. If they accept later, the office has everything it needs.
                </p>
                <button type="button" onClick={() => onAccepted(handoff.quoteId)}
                  className="mt-2 w-full rounded-2xl bg-slate-900 py-3 text-[13px] font-bold text-white active:opacity-90">
                  Prep the install packet →
                </button>
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Or email it to the customer</h4>
                <input
                  type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="customer@email.com"
                  className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
                />
                {emailStatus ? (
                  <p className={cn("mt-2 rounded-xl px-3 py-2 text-center text-xs font-bold", emailStatus === "queued" ? "bg-emerald-50 text-emerald-700" : emailStatus === "held" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600")}>
                    {emailStatus === "queued" ? "Queued — it sends shortly." : emailStatus === "held" ? "Held — comms is in testing; the office has it in the outbox." : "Didn't queue — try again."}
                  </p>
                ) : (
                  <button type="button" disabled={!/\S+@\S+\.\S+/.test(emailTo.trim()) || enqueue.isPending} onClick={() => void emailProposal()}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-700 active:bg-slate-50 disabled:opacity-40">
                    <Mail className="mr-1 inline h-3.5 w-3.5" /> Email the proposal
                  </button>
                )}
              </section>

            </>
          )}
        </div>
        <footer className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
          <button type="button" onClick={onClose} className="w-full rounded-2xl bg-red-700 py-3.5 text-[14px] font-bold text-white active:opacity-90">
            Back to the work order
          </button>
        </footer>
      </div>
    );
  }

  // ---- builder
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back to the work order">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-slate-900">Replacement quote</span>
              {resume && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">editing saved</span>}
            </div>
            <div className="truncate text-[11px] text-slate-500">{s.name} · Good / Better / Best</div>
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {!booted ? (
          <div className="h-32 animate-pulse rounded-3xl bg-slate-100" />
        ) : (
          <>
            <WizardModeSeg mode="replace" onSwitch={onSwitch} />
            <PkgChips kind="replace" onPick={applyPkgTier} />
            {tiers.map((t, i) => (
              <section key={i} className={cn("rounded-3xl border bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]", t.tier === "best" ? "border-emerald-300" : "border-slate-200")}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={t.label}
                    onChange={(e) => patchTier(i, { label: e.target.value })}
                    className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-[16px] font-bold focus:border-red-500 focus:outline-none"
                    aria-label={`Option ${i + 1} label`}
                  />
                  {tiers.length > 1 && (
                    <button type="button" onClick={() => setTiers((ts) => ts.filter((_, j) => j !== i))} className="rounded-lg p-1.5 text-slate-300 active:text-red-500" aria-label="Remove this option">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {/* per-collection brand — the proposal app's Lennox ↔ Armstrong toggle */}
                <div className="mt-1.5 flex overflow-hidden rounded-full border border-slate-200 self-start w-fit">
                  {QUOTE_BRANDS.map((b) => (
                    <button key={b} type="button" onClick={() => patchTier(i, { brand: b })}
                      className={cn("px-2.5 py-1 text-[10px] font-bold", t.brand === b ? "bg-slate-900 text-white" : "bg-white text-slate-400")}>
                      {b}
                    </button>
                  ))}
                </div>
                <textarea
                  value={t.blurb}
                  onChange={(e) => patchTier(i, { blurb: e.target.value })}
                  rows={2}
                  placeholder="A line or two on this collection — shows on the proposal."
                  className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-[13px] leading-relaxed focus:border-red-500 focus:outline-none"
                />
                {t.lines.length > 0 && (
                  <ul className="mt-2 divide-y divide-slate-100">
                    {(() => {
                      // Locked lines first, then the ones the homeowner may take
                      // off under their own header — so what they can change is
                      // obvious at a glance. Patching stays keyed to the ORIGINAL
                      // index.
                      const isAddon = (x: QLine) => x.role === "recommended" || x.role === "optional";
                      const ordered = t.lines.map((l, idx) => ({ l, idx })).sort((a, b) => (Number(isAddon(a.l)) - Number(isAddon(b.l))) || a.idx - b.idx);
                      const firstAddon = ordered.findIndex((pr) => isAddon(pr.l));
                      return ordered.map(({ l, idx: j }, pos) => (
                      <Fragment key={j}>
                      {pos === firstAddon && (
                        <li className="list-none pb-1 pt-2.5">
                          <span className="text-[12.5px] font-semibold text-slate-600">Their Call</span>
                        </li>
                      )}
                      <li className="py-2">
                        <div className="flex items-center gap-2">
                        {l.image_url && (
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                            <img src={l.image_url} alt="" className="h-full w-full object-contain" loading="lazy" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14.5px] font-bold text-slate-800">{l.description}</span>
                          {l.kind !== "accessory" && (
                            <span className="block text-[11px] uppercase tracking-wide text-slate-400">
                              {EQUIP_CATS.find(([k]) => k === l.kind)?.[1] ?? l.kind}
                            </span>
                          )}
                        </span>
                        {l.kind === "accessory" && (
                          <span className="inline-flex items-center overflow-hidden rounded-lg border border-slate-200">
                            <button type="button" onClick={() => patchTier(i, { lines: t.lines.map((x, k) => (k === j ? { ...x, qty: Math.max(1, x.qty - 1) } : x)) })} className="px-1.5 py-1 text-slate-400 active:bg-slate-100" aria-label="Less"><Minus className="h-3 w-3" /></button>
                            <span className="min-w-[1.5ch] text-center text-[11px] font-bold tabular-nums">{l.qty}</span>
                            <button type="button" onClick={() => patchTier(i, { lines: t.lines.map((x, k) => (k === j ? { ...x, qty: x.qty + 1 } : x)) })} className="px-1.5 py-1 text-slate-400 active:bg-slate-100" aria-label="More"><Plus className="h-3 w-3" /></button>
                          </span>
                        )}
                        <span className="relative">
                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                          <input
                            type="number" inputMode="decimal" value={l.unitPrice}
                            onChange={(e) => patchTier(i, { lines: t.lines.map((x, k) => (k === j ? { ...x, unitPrice: Number(e.target.value) || 0 } : x)) })}
                            className="w-24 rounded-lg border border-slate-200 py-1.5 pl-5 pr-1.5 text-right text-sm font-bold tabular-nums focus:border-red-500 focus:outline-none"
                            aria-label="Installed price"
                          />
                        </span>
                        <button type="button" onClick={() => patchTier(i, { lines: t.lines.filter((_, k) => k !== j) })} className="rounded p-1 text-slate-300 active:text-red-500" aria-label="Remove line">
                          <X className="h-3.5 w-3.5" />
                        </button>
                        </div>
                        {l.kind === "accessory" && (() => {
                          // QUOTE-POLISH-10 / -13 — his app's two settings, each a small
                          // segmented switch (both choices visible, the lit one is on):
                          //   INCLUDED ↔ CHARGEABLE  does the customer pay for it?
                          //   OPTIONAL ↔ REQUIRED    can the customer take it off?
                          // Neither moves the package price except Included.
                          const choice = l.role === "optional" || l.role === "recommended";
                          const setInc = (inc: boolean) => patchTier(i, { lines: t.lines.map((x, k) => (k === j ? { ...x, inc } : x)) });
                          const setRole = (role: "optional" | "required") => patchTier(i, { lines: t.lines.map((x, k) => (k === j ? { ...x, role } : x)) });
                          const seg = "inline-flex h-9 flex-1 items-stretch overflow-hidden rounded-lg border border-slate-200 bg-white text-[12px] font-bold";
                          const cell = (on: boolean, lit: string) => cn("inline-flex flex-1 items-center justify-center gap-1 px-2 leading-tight", on ? lit : "text-slate-400 active:bg-slate-50");
                          return (
                            <div className="mt-1.5 flex gap-1.5">
                              <span role="radiogroup" className={seg}>
                                <button type="button" role="radio" aria-checked={l.inc} onClick={() => setInc(true)} className={cell(!!l.inc, "bg-emerald-600 text-white")} title="Customer pays $0 — its value shows struck through">
                                  Included
                                </button>
                                <button type="button" role="radio" aria-checked={!l.inc} onClick={() => setInc(false)} className={cn(cell(!l.inc, "bg-slate-800 text-white"), "border-l border-slate-200")} title="Customer pays this price">
                                  Chargeable
                                </button>
                              </span>
                              <span role="radiogroup" className={seg}>
                                <button type="button" role="radio" aria-checked={choice} onClick={() => setRole("optional")} className={cell(choice, "bg-slate-800 text-white")} title="In the package; the customer can take it off (the price drops)">
                                  Optional
                                </button>
                                <button type="button" role="radio" aria-checked={!choice} onClick={() => setRole("required")} className={cn(cell(!choice, "bg-amber-400 text-amber-950"), "border-l border-slate-200")} title="Locked in the package">
                                  <Lock className="h-3 w-3" /> Required
                                </button>
                              </span>
                            </div>
                          );
                        })()}
                        {l.kind !== "accessory" && (
                          <WarrantyPicker
                            value={l.warranty ?? null}
                            tier={t.tier}
                            onChange={(w) => patchTier(i, { lines: t.lines.map((x, k) => (k === j ? { ...x, warranty: w } : x)) })}
                          />
                        )}
                      </li>
                      </Fragment>
                      ));
                    })()}
                  </ul>
                )}
                {/* add equipment / accessories via the picture picker */}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {EQUIP_CATS.map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setPicker({ i, kind: k })}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-600 active:bg-slate-50">
                      + {label}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => patchTier(i, { lines: [...t.lines, { kind: "accessory", description: "Describe it — set the price", qty: 1, unitPrice: 0, item_id: null, model: null, manufacturer: null, image_url: null, role: "required", inc: false }] })}
                    className="rounded-full px-2 py-1.5 text-[11px] font-bold text-slate-400 underline-offset-2 active:underline">
                    + custom
                  </button>
                </div>
                {/* MONEY v2: itemized rebates (utility or custom, any number),
                    a $/%-switchable sale discount, DUE TO SEDGWICK, and the
                    net-after-rebates savings line. */}
                <div className="mt-2.5 space-y-2 border-t border-slate-100 pt-2.5">
                  {t.rebates.map((r, rj) => (
                    <div key={rj} className="flex items-center gap-2">
                      <select
                        value={r.utilityId ?? "custom"}
                        onChange={(e) => {
                          const v = e.target.value;
                          const u = utilities.find((x) => x.id === v);
                          patchTier(i, {
                            rebates: t.rebates.map((x, k) => (k === rj
                              ? v === "custom"
                                ? { utilityId: null, name: x.name, amount: x.amount }
                                : { utilityId: v, name: u?.name ?? "", amount: x.amount || Number(u?.default_rebate) || 0 }
                              : x)),
                          });
                        }}
                        className="w-[38%] shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm font-semibold text-slate-700"
                        aria-label="Rebate source"
                      >
                        {utilities.map((u) => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                        <option value="custom">Custom…</option>
                      </select>
                      {r.utilityId == null ? (
                        <input type="text" value={r.name} placeholder="Rebate name"
                          onChange={(e) => patchTier(i, { rebates: t.rebates.map((x, k) => (k === rj ? { ...x, name: e.target.value } : x)) })}
                          className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-2 text-sm font-semibold text-slate-700 focus:border-red-500 focus:outline-none" />
                      ) : (
                        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-600">rebate</span>
                      )}
                      <span className="relative w-20 shrink-0">
                        <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span>
                        <input type="number" inputMode="decimal" value={r.amount || ""} placeholder="0"
                          onChange={(e) => patchTier(i, { rebates: t.rebates.map((x, k) => (k === rj ? { ...x, amount: Number(e.target.value) || 0 } : x)) })}
                          className="w-full rounded-lg border border-slate-200 py-2 pl-4 pr-1.5 text-right text-sm font-bold tabular-nums focus:border-red-500 focus:outline-none" />
                      </span>
                      <button type="button" onClick={() => patchTier(i, { rebates: t.rebates.filter((_, k) => k !== rj) })} className="rounded p-1 text-slate-300 active:text-red-500" aria-label="Remove rebate">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => {
                      const first = utilities[0];
                      patchTier(i, { rebates: [...t.rebates, first ? { utilityId: first.id, name: first.name, amount: Number(first.default_rebate) || 0 } : { utilityId: null, name: "", amount: 0 }] });
                    }}
                    className="text-[11px] font-bold text-emerald-600 underline-offset-2 active:underline">
                    + Add a Rebate{t.rebates.length > 0 ? " (stack them)" : ""}
                  </button>

                  <div className="flex items-center gap-2">
                    <span className="w-16 shrink-0 text-[12px] font-semibold text-slate-500">Sale</span>
                    <input type="text" value={t.discLabel} placeholder="Sale name (e.g. Fall Sale)"
                      onChange={(e) => patchTier(i, { discLabel: e.target.value })}
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-2 text-sm font-semibold text-slate-700 focus:border-red-500 focus:outline-none" />
                    <button type="button"
                      onClick={() => patchTier(i, { discMode: t.discMode === "amount" ? "percent" : "amount" })}
                      className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-extrabold text-slate-600 active:bg-slate-100"
                      title="Switch between $ and %">
                      {t.discMode === "amount" ? "$" : "%"}
                    </button>
                    <input type="number" inputMode="decimal" value={t.discount || ""} placeholder="0"
                      onChange={(e) => patchTier(i, { discount: Number(e.target.value) || 0 })}
                      className="w-20 shrink-0 rounded-lg border border-slate-200 py-2 px-2 text-right text-sm font-bold tabular-nums focus:border-red-500 focus:outline-none"
                      aria-label="Sale discount" />
                  </div>

                  {tierDiscount(t) > 0 && (
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>{t.discLabel.trim() || "Sale discount"} off {money$(tierGross(t))}</span>
                      <span className="tabular-nums text-red-500">−{money$(tierDiscount(t))}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-extrabold text-slate-900">
                    <span>Due to Sedgwick</span><span className="tabular-nums">{money$(tierTotal(t))}</span>
                  </div>
                  {tierRebateSum(t) > 0 && (
                    <div className="flex justify-between text-[13px] font-bold text-emerald-600">
                      <span>Net after {t.rebates.length > 1 ? `${t.rebates.length} rebates` : "rebate"} <span className="font-semibold text-emerald-500/70">(estimates)</span></span>
                      <span className="tabular-nums">{money$(Math.max(0, tierTotal(t) - tierRebateSum(t)))}</span>
                    </div>
                  )}
                  {tiers.length > 1 && (t.lines.some((l) => l.kind === "accessory") || t.rebates.length > 0 || t.discount > 0) && (
                    <button type="button" onClick={() => copyToAll(i)}
                      className="w-full rounded-xl border border-dashed border-slate-300 py-2 text-[11px] font-bold text-slate-500 active:bg-slate-50">
                      ⧉ Copy Accessories, Rebates & Discount to the Other Options
                    </button>
                  )}
                </div>
              </section>
            ))}
            {/* QUOTE-POLISH-10: Recommended Add-Ons — the shelf his app has at the
                bottom of the builder. Suggestions for THIS home, shown to the
                homeowner below the packages; nothing is in a package until they tap. */}
            <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[14px] font-extrabold text-slate-900">Recommended Add-Ons</div>
                  <div className="text-[11px] text-slate-500">Shown under the packages — the homeowner adds them. Same offer on every option.</div>
                </div>
                <button type="button" onClick={() => setPicker({ i: -1, kind: "accessory" })}
                  className="shrink-0 rounded-xl bg-amber-400 px-3 py-2 text-[12px] font-bold text-amber-950 active:opacity-80">
                  + Suggest One
                </button>
              </div>
              {addons.length > 0 && (
                <ul className="mt-2 divide-y divide-amber-100">
                  {addons.map((a, ai) => (
                    <li key={addonKeyOf(a)} className="flex items-center gap-2 py-2">
                      {a.image_url && (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
                          <img src={a.image_url} alt="" className="h-full w-full object-contain" loading="lazy" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-slate-800">{a.description}</span>
                      <span className="relative">
                        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                        <input type="number" inputMode="decimal" value={a.unitPrice}
                          onChange={(e) => setAddons((cur) => cur.map((x, k) => (k === ai ? { ...x, unitPrice: Number(e.target.value) || 0 } : x)))}
                          className="w-24 rounded-lg border border-slate-200 bg-white py-1.5 pl-5 pr-1.5 text-right text-sm font-bold tabular-nums focus:border-red-500 focus:outline-none"
                          aria-label="Add-on price" />
                      </span>
                      <button type="button" onClick={() => setAddons((cur) => cur.filter((_, k) => k !== ai))} className="rounded p-1 text-slate-300 active:text-red-500" aria-label="Remove add-on">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            {err && <p className="text-center text-xs font-semibold text-red-600">{err}</p>}
            <button type="button" onClick={onLead} className="mx-auto block text-center text-[11px] font-semibold text-slate-400 underline-offset-2 active:underline">
              Send to the Office as a Lead Instead →
            </button>
          </>
        )}
      </div>
      <footer className="space-y-2 border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        {/* QUOTE-POLISH-11: the note the HOMEOWNER sees on their proposal page. */}
        <textarea
          value={homeownerNote}
          onChange={(e) => setHomeownerNote(e.target.value)}
          rows={2}
          placeholder="Note to the homeowner (optional) — shows on their proposal as a note from you. e.g. “Your upstairs runs warm — the Best option's modulating furnace fixes that.”"
          className="w-full rounded-xl border border-emerald-200 bg-emerald-50/40 p-2.5 text-[13px] focus:border-emerald-500 focus:outline-none"
        />
        {/* OFFICE-NOTE-01: office-only context on the proposal record. */}
        <textarea
          value={officeNote}
          onChange={(e) => setOfficeNote(e.target.value)}
          rows={2}
          placeholder="Note to the office (optional) — financing talk, access quirks, who to call back. Customer never sees this."
          className="w-full rounded-xl border border-slate-200 p-2.5 text-[13px] focus:border-brand-400 focus:outline-none"
        />
        <button
          type="button"
          disabled={filled.length === 0 || present.isPending}
          onClick={() => present.mutate(true)}
          className={cn("w-full rounded-2xl py-4 text-[15px] font-bold text-white", filled.length > 0 ? "bg-slate-900 active:opacity-90" : "bg-slate-300")}
        >
          {present.isPending ? "Saving the proposal…" : filled.length === 0 ? "Add equipment to at least one option" : resume ? "Update & Present →" : "Present It Now →"}
        </button>
        <button
          type="button"
          disabled={filled.length === 0 || present.isPending}
          onClick={() => present.mutate(false)}
          className="w-full rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-600 active:bg-slate-50 disabled:opacity-40"
        >
          Save It — Email or Present Later
        </button>
      </footer>

      {picker && (
        <EquipPickerModal
          kind={picker.kind}
          brand={tiers[picker.i]?.brand ?? "Lennox"}
          onPick={(it) => {
            const kind = picker.kind;
            if (picker.i === -1) {
              // QUOTE-POLISH-10: a Recommended Add-On — one suggestion for every collection.
              const line: QLine = {
                kind: "accessory",
                description: [it.manufacturer, it.description ?? it.model ?? it.part_number].filter(Boolean).join(" ") || "Add-On",
                qty: 1, unitPrice: Number(it.sell_price ?? it.base_price) || 0,
                // Never snapshot a synthetic import key (PA-…) as the line's model (34i53 / QUOTE-POLISH-15).
                item_id: it.id, model: it.model ?? (isSyntheticPart(it.part_number) ? null : it.part_number), manufacturer: it.manufacturer, image_url: it.image_url,
                role: "recommended", inc: false,
              };
              setAddons((cur) => (cur.some((a) => addonKeyOf(a) === addonKeyOf(line)) ? cur : [...cur, line]));
              setPicker(null);
              return;
            }
            patchTier(picker.i, {
              lines: [...tiers[picker.i].lines, {
                kind,
                description: [it.manufacturer, it.description ?? it.model ?? it.part_number].filter(Boolean).join(" ") || "Equipment",
                qty: 1,
                unitPrice: Number(it.sell_price ?? it.base_price) || 0,
                item_id: it.id,
                model: it.model ?? (isSyntheticPart(it.part_number) ? null : it.part_number),
                manufacturer: it.manufacturer,
                image_url: it.image_url,
                role: kind === "accessory" ? "required" : "included",
                // QUOTE-POLISH-10: an accessory added WITH the equipment is agreed-to
                // gear — Included ($0, value shown) + Required until the tech taps otherwise.
                inc: kind === "accessory",
                // QUOTE-POLISH-07: the model's standard warranty from the
                // pricebook (SLP99 12/7) shows the moment it's picked; the tech
                // can still change this unit's numbers.
                warranty: kind !== "accessory" && (it.warranty_parts_years != null || it.warranty_labor_years != null)
                  ? `${it.warranty_parts_years ?? (tiers[picker.i]?.tier === "good" ? 5 : 10)}-Year Parts · ${it.warranty_labor_years ?? 5}-Year Labor`
                  : null,
              }],
            });
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------ tune-up checklist */

const TUNEUP_CHECKS: Record<string, string[]> = {
  "Furnace tune-up": [
    "Filter checked / replaced", "Heat exchanger inspected", "Flame sensor cleaned",
    "Ignitor tested", "Blower motor & wheel checked", "Gas pressure checked",
    "Temperature rise verified", "Safety switches tested", "CO check at registers",
    "Thermostat operation verified",
  ],
  "A/C tune-up": [
    "Filter checked / replaced", "Refrigerant pressures checked", "Capacitor tested",
    "Contactor inspected", "Condenser coil rinsed", "Evaporator coil inspected",
    "Condensate drain cleared", "Temperature split verified", "Disconnect & wiring inspected",
    "Thermostat operation verified",
  ],
  "Boiler tune-up": [
    "Filter / strainer checked", "System pressure & expansion tank checked", "Relief valve tested",
    "Burners inspected & cleaned", "Ignition tested", "Circulator pump checked",
    "Zone valves operated", "Venting inspected", "CO check", "Thermostat operation verified",
  ],
};

/** Service-type-first tune-up detection. Fitted to the REAL service-type
 *  catalog (Seth 2026-08-27 ×2): the maintenance types are "Demand AC Check",
 *  "Demand Furnace Check", "Demand Boiler Check", "PLUS … Tune-Up", "Annual
 *  Coil Cleaning", "Fall Mid-Season Comfort Check" — so a bare CHECK or CLEAN
 *  must fire, not just TUNE. Every non-maintenance type (No Cool, Furnace
 *  Certification…) contains none of these words, verified against the table. */
function detectChecklist(serviceType: string | null, notes: string | null): "Furnace tune-up" | "A/C tune-up" | "Boiler tune-up" | null {
  const st = (serviceType ?? "").toUpperCase();
  if (/TUNE|PRECISION|MAINT|CLEAN|CHECK/.test(st)) {
    if (/BOILER/.test(st)) return "Boiler tune-up";
    if (/A\/?C|COOL|COIL|AIR\s*COND|HEAT\s*PUMP/.test(st)) return "A/C tune-up";
    if (/FURN|\bHEAT\b|FALL|WINTER/.test(st)) return "Furnace tune-up";
    return "A/C tune-up";
  }
  return tuneupType(notes);
}

/** DB-first checklist resolution: the booked service type's own template
 *  (office-editable, Settings → Service Types → Edit → Checklist) wins; the
 *  legacy 3-bucket name detector covers ESC-sourced calls with no template. */
function useChecklistTemplate(s: Stop): { title: string; items: string[] } | null {
  const { data } = useQuery({
    queryKey: ["svc_checklist", s.serviceTypeId, s.serviceType],
    enabled: !!(s.serviceTypeId || s.serviceType),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ name: string; checklist: string[] | null } | null> => {
      const q = supabase.from("service_type").select("name, checklist");
      const { data: row } = s.serviceTypeId
        ? await q.eq("id", s.serviceTypeId).maybeSingle()
        : await q.eq("name", s.serviceType!).maybeSingle();
      return (row as { name: string; checklist: string[] | null } | null) ?? null;
    },
  });
  if (data?.checklist?.length) return { title: data.name, items: data.checklist };
  const kind = detectChecklist(s.serviceType, s.notes);
  return kind ? { title: kind, items: TUNEUP_CHECKS[kind] ?? [] } : null;
}

/** The visit checklist, living right in the call. Ticked items print onto
 *  the invoice write-up — the customer SEES the value of the visit. */
function ChecklistCard({ title, items, checks, onToggle }: {
  title: string;
  items: string[];
  checks: Record<string, boolean>;
  onToggle: (item: string) => void;
}) {
  const done = items.filter((i) => checks[i]).length;
  if (items.length === 0) return null;
  return (
    <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
      <div className="flex items-center justify-between">
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">{title} checklist</h4>
        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-extrabold", done === items.length ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500")}>
          {done} of {items.length}
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        {items.map((item) => {
          const on = !!checks[item];
          return (
            <li key={item}>
              <button type="button" onClick={() => onToggle(item)}
                className={cn("flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left", on ? "bg-emerald-50" : "active:bg-slate-50")}>
                <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2", on ? "border-emerald-500 bg-emerald-500" : "border-slate-300")}>
                  {on && <Check className="h-3.5 w-3.5 text-white" />}
                </span>
                <span className={cn("text-[13px] font-semibold", on ? "text-emerald-900" : "text-slate-700")}>{item}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="mt-1.5 text-[10.5px] text-slate-400">Checked items print on the invoice.</p>
    </section>
  );
}

/* ------------------------------------------------------------ the wizard */

function FlagWizard({ stop, kind, empno, workedUnits, onClose, onSent }: {
  stop: Stop;
  kind: "order_parts" | "return_visit";
  empno: string | null;
  /** PARTS-EQUIP-01: what the tech marked in the Equipment step — the part's unit, pre-picked. */
  workedUnits: WorkedUnit[];
  onClose: () => void;
  onSent: () => void;
}) {
  const isParts = kind === "order_parts";
  const steps = isParts ? 3 : 2;
  const [step, setStep] = useState(1);
  const [what, setWhat] = useState("");
  const [partNo, setPartNo] = useState("");
  const [partSel, setPartSel] = useState<ItemHit | null>(null); // inventory match, if they picked one
  const [prio, setPrio] = useState<"critical" | "soon" | "stock">("soon"); // PO-02 scheduling urgency
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [equipSel, setEquipSel] = useState<EquipRow | null>(null); // known unit instead of a photo
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const add = useAddTicket();
  const qcF = useQueryClient();
  const [sent, setSent] = useState(false);
  const equip = useEquipmentAt(isParts ? stop.custno : null, stop.locno);
  const partHits = useItemSearch(isParts && !partSel ? partNo : "");
  // The unit he marked as worked on is the unit the part is for — pre-picked
  // (Seth 2026-09-10: "the tech already did that in the field"). Still a tap
  // to change when a second unit is the one that needs the part.
  const markedKey = workedUnits[0]?.k ?? null;
  const [prePicked, setPrePicked] = useState(false);
  useEffect(() => {
    if (!isParts || equipSel || prePicked || !markedKey || !equip.data) return;
    const m = equip.data.find((e) => `${e.src === "peg" ? "peg-" : "esc-"}${e.counter}` === markedKey);
    if (m) { setEquipSel(m); setPrePicked(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equip.data, markedKey]);

  // Never throw away typed work silently (Seth 2026-08-27: "nothing kills
  // their vibe faster" than losing what they entered).
  const tryClose = () => {
    if (what.trim() || notes.trim() || photos.length > 0) {
      if (!window.confirm("Discard this flag? Your notes and photos will be lost.")) return;
    }
    onClose();
  };

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadErr(null);
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (f.size > 20 * 1024 * 1024) { setUploadErr("That photo is over 20 MB — try again."); continue; }
        const up = await uploadPartPhoto(f);
        setPhotos((p) => [...p, up]);
      }
    } catch {
      setUploadErr("Couldn't upload — check signal and tap the camera again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const canNext = step === 1 ? what.trim().length > 0 : step === 2 && isParts ? photos.length > 0 || equipSel != null : true;

  const send = () => {
    add.mutate(
      {
        lane_key: kind,
        custno: stop.custno,
        locno: stop.locno,
        customer_name: stop.name,
        address: stop.address,
        city: stop.city,
        lat: stop.lat,
        lng: stop.lng,
        phone: stop.phone,
        summary: what.trim(),
        notes: [
          notes.trim() || null,
          equipSel
            ? `Unit: ${[equipSel.mfg, equipSel.model].filter(Boolean).join(" ") || equipSel.eq_type || "on file"}${equipSel.serial ? ` · S/N ${equipSel.serial}` : ""} (from equipment on file)`
            : null,
          partSel
            ? `Inventory match: ${partSel.description ?? partSel.part_number}${partSel.qty_in_stock != null ? ` · ${partSel.qty_in_stock} in stock` : ""}`
            : null,
          `Flagged from Pegasus Field at call #${stop.no}.`,
        ].filter(Boolean).join(" · "),
        part_number: isParts ? partNo.trim() || null : null,
        part_photos: isParts && photos.length ? photos.map((p) => p.path) : null,
        // TICKET-EQUIP-01 (Seth 2026-09-03): the unit travels as DATA so the
        // parts manager sees model + serial on Today and in the PO form.
        part_equipment: isParts && equipSel
          ? { counter: equipSel.counter, mfg: equipSel.mfg, model: equipSel.model, serial: equipSel.serial, eq_type: equipSel.eq_type, equip_loc: equipSel.equip_loc, install_date: equipSel.install_date, src: equipSel.src ?? "esc" }
          : null,
        part_priority: isParts ? prio : null,
        origin_dispatch: stop.ref,
        affinity_empno: empno,
        source: "tech_mobile",
      },
      { onSuccess: () => { qcF.invalidateQueries({ queryKey: ["m_call_flags"] }); setSent(true); } },
    );
  };

  const title = isParts ? "Part Needed" : "Return Visit";

  // Seth 2026-08-28: "can it just do it automatically?" — flash the ✓, then
  // return to the call by itself.
  useEffect(() => {
    if (!sent) return;
    const id = window.setTimeout(onSent, 1300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col items-center justify-center bg-[#f6f5f1] p-8 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-8 w-8 text-emerald-600" />
        </span>
        <h3 className="mt-4 text-xl font-extrabold text-slate-900">Sent to the office</h3>
        <p className="mt-2 max-w-[30ch] text-sm leading-relaxed text-slate-500">
          {isParts
            ? "The parts manager has your photos. Track it under More → My Flags."
            : "Dispatch has it and will schedule the return. Track it under More → My Flags."}
        </p>
        <button type="button" onClick={onSent} className="mt-6 w-full rounded-2xl bg-gradient-to-b from-red-600 to-red-700 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_rgba(185,28,28,0.5)] transition-transform [touch-action:manipulation] active:scale-[0.98] text-[15px] font-bold text-white active:opacity-90">
          Back to the call
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (step > 1 ? setStep(step - 1) : tryClose())}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100"
            aria-label={step > 1 ? "Back a step" : "Cancel"}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold text-slate-900">{title}</div>
            <div className="truncate text-[11px] text-slate-500">{stop.name} · step {step} of {steps}</div>
          </div>
          <button type="button" onClick={tryClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 active:bg-slate-100" aria-label="Cancel">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-2 flex gap-1.5">
          {Array.from({ length: steps }).map((_, i) => (
            <span key={i} className={cn("h-1.5 flex-1 rounded-full", i < step ? "bg-slate-900" : "bg-slate-200")} />
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-800">
                {isParts ? "What part is needed?" : "Why does someone need to go back?"}
              </label>
              <textarea
                value={what}
                onChange={(e) => setWhat(e.target.value)}
                rows={3}
                autoFocus
                placeholder={isParts ? "Inducer motor for the Lennox SLP98V — bearing failed" : "Recheck cooling after the part goes in"}
                className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] leading-relaxed focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
              />
            </div>
            {isParts && (
              <div>
                <label className="block text-sm font-bold text-slate-800">Part number <span className="font-normal text-slate-400">(if you have it)</span></label>
                <div className="relative mt-2">
                  <input
                    type="text"
                    value={partNo}
                    onChange={(e) => { setPartNo(e.target.value); setPartSel(null); }}
                    placeholder="from the part or the manual"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
                  />
                  {partHits.isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-300" />}
                </div>
                {partSel ? (
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="min-w-0 flex-1 truncate text-xs font-bold text-emerald-800">
                      {partSel.description ?? partSel.part_number}
                      {partSel.qty_in_stock != null ? ` · ${partSel.qty_in_stock} in stock` : ""}
                    </span>
                    <button type="button" onClick={() => setPartSel(null)} className="rounded p-0.5 text-emerald-500 active:bg-white" aria-label="Clear match">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : partNo.trim().length >= 2 && (partHits.data?.length ?? 0) > 0 ? (
                  <ul className="mt-1.5 max-h-44 space-y-1 overflow-y-auto">
                    {(partHits.data ?? []).slice(0, 6).map((it) => (
                      <li key={it.id}>
                        <button
                          type="button"
                          onClick={() => { setPartSel(it); if (it.part_number) setPartNo(it.part_number); }}
                          className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left active:bg-slate-50"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-bold text-slate-800">{it.description ?? it.part_number}</div>
                            <div className="truncate text-[10px] text-slate-400">
                              {[it.part_number ? `#${it.part_number}` : null, it.qty_in_stock != null ? `${it.qty_in_stock} in stock` : null].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">ours ✓</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}
            {isParts && (
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-slate-800">How urgent once the part lands? <InfoTip label="What Critical does">Critical goes to the top of the office&apos;s list.</InfoTip></label>
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  {([["critical", "No heat / no cool", "bg-red-600 text-white"], ["soon", "Needed soon", "bg-slate-900 text-white"], ["stock", "Whenever", "bg-slate-900 text-white"]] as const).map(([k, label, on]) => (
                    <button key={k} type="button" onClick={() => setPrio(k)} data-meaning={k === "critical" ? "" : undefined}
                      className={cn("rounded-xl px-2 py-2.5 text-[12px] font-bold leading-tight [touch-action:manipulation]",
                        prio === k ? on : "bg-slate-100 text-slate-500 active:bg-slate-200")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-bold text-slate-800">Anything else the office should know? <span className="font-normal text-slate-400">(optional)</span></label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder={isParts ? "Customer approved $480 · prepaid" : "Best in the morning, dog in the yard"}
                className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
              />
            </div>
          </div>
        )}

        {step === 2 && isParts && (
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-bold text-slate-800">Photos of the Part &amp; Data Plate</h3>
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                A photo, or pick the unit on file below.
              </p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => void onFiles(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex w-full flex-col items-center justify-center gap-1.5 rounded-3xl border-2 border-dashed border-slate-300 bg-white py-8 text-slate-500 active:bg-slate-50"
            >
              {uploading ? <Loader2 className="h-7 w-7 animate-spin text-red-600" /> : <Camera className="h-7 w-7 text-red-600" />}
              <span className="text-sm font-bold text-slate-700">{uploading ? "Uploading…" : "Take a photo"}</span>
              <span className="text-[11px]">data plate · model sticker · the failed part</span>
            </button>
            {uploadErr && <p className="text-center text-xs font-semibold text-red-600">{uploadErr}</p>}
            {photos.length > 0 && (
              <ul className="space-y-1.5">
                {photos.map((p, i) => (
                  <li key={p.path} className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                    <span className="min-w-0 truncate text-xs font-semibold text-slate-700">Photo {i + 1} · {p.name}</span>
                    <span className="flex items-center gap-2">
                      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">uploaded ✓</span>
                      <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x.path !== p.path))} className="rounded p-1 text-slate-400 active:bg-white" aria-label={`Remove photo ${i + 1}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Known equipment = a real alternative to the camera. Model + serial
                are already in the system, so picking the unit is enough to order
                from (Seth 2026-08-27). Photos still win when both are possible. */}
            {(equip.data ?? []).length > 0 && (
              <div className="pt-1">
                <div className="flex items-center gap-2 py-1">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="text-[12.5px] font-semibold text-slate-600">or pick the unit on file</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                <ul className="space-y-1.5">
                  {(equip.data ?? []).map((e) => {
                    const sel = equipSel?.counter === e.counter;
                    return (
                      <li key={e.counter}>
                        <button
                          type="button"
                          onClick={() => setEquipSel(sel ? null : e)}
                          className={cn(
                            "flex w-full items-center gap-2.5 rounded-2xl border px-3 py-2.5 text-left",
                            sel ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white active:bg-slate-50",
                          )}
                        >
                          <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", sel ? "border-emerald-500 bg-emerald-500" : "border-slate-300")}>
                            {sel && <Check className="h-3 w-3 text-white" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="min-w-0 truncate text-[16px] font-bold text-slate-800">
                                {[e.mfg, e.model].filter(Boolean).join(" ") || e.eq_type || "Unit on file"}
                              </span>
                              {e.src === "peg" && <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-bold text-slate-600">field-added</span>}
                            </span>
                            <span className="block truncate font-mono text-[15px] font-medium text-slate-600">
                              {[e.serial ? `S/N ${e.serial}` : "no serial on file", e.equip_loc].filter(Boolean).join(" · ")}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {equipSel && photos.length === 0 && (
                  <p className="mt-1.5 text-center text-[11px] font-semibold text-emerald-700">
                    {prePicked && equipSel.counter === (equip.data ?? []).find((e) => `${e.src === "peg" ? "peg-" : "esc-"}${e.counter}` === markedKey)?.counter
                      ? "The unit you marked on this call — the office orders from its model & serial."
                      : "The office orders from this unit's model & serial."}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {step === steps && (
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-slate-800">Review, then send</h3>
            <div className="space-y-2.5 rounded-3xl border border-slate-200 bg-white p-4 text-sm">
              <Row k="Job" v={`${stop.name}${stop.city ? ` · ${stop.city}` : ""}`} />
              <Row k={isParts ? "Part" : "Reason"} v={what.trim() || "—"} />
              {isParts && partNo.trim() && <Row k="Part #" v={`${partNo.trim()}${partSel ? " · in our inventory ✓" : ""}`} />}
              {isParts && equipSel && (
                <Row k="Unit" v={`${[equipSel.mfg, equipSel.model].filter(Boolean).join(" ") || equipSel.eq_type || "on file"}${equipSel.serial ? ` · S/N ${equipSel.serial}` : ""}`} />
              )}
              {isParts && <Row k="Photos" v={photos.length > 0 ? `${photos.length} attached` : equipSel ? "none — using the unit on file" : "none"} />}
              {notes.trim() && <Row k="Notes" v={notes.trim()} />}
              <Row k="Goes to" v={isParts ? "Parts manager + office board" : "Dispatch board"} />
            </div>
          </div>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        {step < steps ? (
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setStep(step + 1)}
            className={cn("w-full rounded-2xl py-4 text-[15px] font-bold text-white", canNext ? "bg-red-700 active:opacity-90" : "bg-slate-300")}
          >
            {step === 2 && isParts && !canNext ? "Add a photo — or pick the unit on file" : "Next →"}
          </button>
        ) : (
          <button
            type="button"
            disabled={add.isPending}
            onClick={send}
            className="w-full rounded-2xl bg-gradient-to-b from-red-600 to-red-700 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_rgba(185,28,28,0.5)] transition-transform [touch-action:manipulation] active:scale-[0.98] text-[15px] font-bold text-white active:opacity-90 disabled:bg-red-300"
          >
            {add.isPending ? "Sending…" : "Send to the office"}
          </button>
        )}
      </footer>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-3">
      <span className="w-20 shrink-0 pt-px text-[12.5px] font-semibold text-slate-500">{k}</span>
      <span className="min-w-0 flex-1 whitespace-pre-wrap font-medium text-slate-800">{v}</span>
    </div>
  );
}

/* --------------------------------------------------------------- my flags */

function flagChip(t: PendingTicket): { label: string; cls: string } {
  if (t.status === "scheduled") {
    const d = t.scheduled_date ? ` ${t.scheduled_date.slice(5).replace("-", "/")}` : "";
    return { label: `Scheduled${d} ✓`, cls: "bg-emerald-50 text-emerald-700" };
  }
  if (t.status === "completed") return { label: "Done ✓", cls: "bg-emerald-50 text-emerald-700" };
  if (t.status === "cancelled") return { label: "Closed by Office", cls: "bg-slate-100 text-slate-500" };
  if (t.lane_key === "return_visit") return { label: "Waiting on Office", cls: "bg-amber-50 text-amber-700" };
  const s = (t.part_state ?? "needs_ordering") as PartState;
  const cls =
    s === "needs_ordering" ? "bg-red-50 text-red-700"
    : s === "backordered" ? "bg-amber-50 text-amber-700"
    : s === "ordered" ? "bg-slate-100 text-slate-600"
    : "bg-emerald-50 text-emerald-700";
  return { label: PART_STATE_LABEL[s], cls };
}

function MyFlags({ flags, loading }: { flags: PendingTicket[]; loading: boolean }) {
  if (loading) {
    return <div className="space-y-2.5">{[0, 1].map((i) => <div key={i} className="h-20 animate-pulse rounded-3xl bg-slate-200/60" />)}</div>;
  }
  if (flags.length === 0) {
    return <EmptyCard Icon={ClipboardList} title="Nothing flagged yet" sub="Parts and return visits you flag show here." />;
  }
  return (
    <div className="space-y-2.5">
      {flags.map((t) => {
        const chip = flagChip(t);
        return (
          <div key={t.id} className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {t.lane_key === "order_parts" ? <Package className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <RotateCcw className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                  <span className="truncate text-sm font-bold text-slate-900">{t.summary}</span>
                </div>
                <div className="mt-0.5 truncate text-xs text-slate-500">{displayCustomerName(null, t.customer_name)}{t.city ? ` · ${t.city}` : ""}</div>
              </div>
              <span className={cn("shrink-0 rounded-full px-2 py-1 text-[10px] font-bold", chip.cls)}>{chip.label}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
              {t.part_po && <CopyCode value={t.part_po} label="Copy PO number">PO {t.part_po}</CopyCode>}
              {t.part_eta && <span>expected {t.part_eta.slice(5).replace("-", "/")}</span>}
              {(t.part_photos ?? []).length > 0 && <span>{(t.part_photos ?? []).length} photo{(t.part_photos ?? []).length === 1 ? "" : "s"} 📷</span>}
              <span className="ml-auto">{new Date(t.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------ warehouse Today (role) */

/** MATERIAL-REQ-01: what the crews asked for — the warehouse works it on /warehouse. */
function WarehouseRequestsCard() {
  const { data } = useMaterialRequestQueue(null);
  const reqs = (data?.requests ?? []).filter((r) => r.status === "open");
  const lines = reqs.flatMap((r) => r.lines);
  const toDo = lines.filter((l) => l.pull_status === "requested" || l.pull_status === "pulling").length;
  const asap = reqs.filter((r) => r.urgency === "today" && r.lines.some((l) => l.pull_status === "requested" || l.pull_status === "pulling")).length;
  const onOrder = lines.filter((l) => l.pull_status === "ordered").length;
  const ready = lines.filter((l) => l.pull_status === "ready").length;
  if (!reqs.length) return null;
  return (
    <a href="/warehouse" className={cn(
      "block rounded-3xl border bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50",
      asap ? "border-rose-300" : "border-slate-200/80",
    )}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-bold text-slate-900">Material Requests</span>
        {asap > 0 && <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">{asap} ASAP</span>}
      </div>
      <div className="mt-1 text-[12.5px] text-slate-600">
        {toDo} to pull or order{onOrder ? ` · ${onOrder} on order` : ""}{ready ? ` · ${ready} ready for pickup` : ""}
      </div>
      <div className="mt-2 text-[12px] font-bold text-brand-700">Open the Requests →</div>
    </a>
  );
}

function WarehouseToday() {
  const { data, isLoading } = useWarehouseQueue();
  const rows = data ?? [];
  if (isLoading) {
    return <div className="space-y-2.5">{[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-3xl bg-slate-200/60" />)}</div>;
  }
  if (rows.length === 0) {
    return <div className="space-y-2.5"><WarehouseRequestsCard /><EmptyCard Icon={Warehouse} title="Nothing to pull" /></div>;
  }
  return (
    <div className="space-y-2.5">
      <WarehouseRequestsCard />
      {rows.map((r) => {
        const ready = r.total > 0 && r.pulled >= r.total;
        return (
          <a
            key={r.jobId}
            href={`/jobs/${r.jobId}?tab=warehouse`}
            className={cn(
              "block rounded-3xl border bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50",
              ready ? "border-emerald-200" : "border-slate-200/80",
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-[15px] font-bold text-slate-900">{r.customer}</div>
                <div className="mt-0.5 truncate text-xs text-slate-500">{r.address ?? "address on the job"}</div>
              </div>
              <span className={cn(
                "shrink-0 rounded-full px-2 py-1 text-[10px] font-bold",
                r.installDate && new Date(`${r.installDate}T12:00:00`).toDateString() === new Date().toDateString()
                  ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600",
              )}>
                {r.installDate
                  ? new Date(`${r.installDate}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
                  : "Not scheduled"}
                {r.window ? ` · ${r.window}` : ""}
              </span>
            </div>
            <div className="mt-2.5">
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div className={cn("h-full rounded-full", ready ? "bg-emerald-500" : "bg-brand-500")} style={{ width: `${r.total ? Math.round((r.pulled / r.total) * 100) : 0}%` }} />
              </div>
              <div className="mt-1 flex items-center justify-between text-[11px]">
                <span className={cn("font-bold", ready ? "text-emerald-700" : "text-slate-600")}>
                  {ready ? <span className="inline-flex items-center gap-1"><PackageCheck className="h-3 w-3" /> Ready — all pulled</span> : `${r.pulled} of ${r.total} pulled`}
                </span>
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------ chat (M1a) */

/** One roster row — module-scope (NOT defined inside ChatPane) so React keeps
 *  the DOM stable across the 12-second message polls; an inline component
 *  remounts every row on every poll, which can swallow a tap mid-refresh.
 *  Your own row is live too: it opens "Notes to self" (Seth 2026-08-28 tapped
 *  his own search result and rightly expected a chat to start). */
function PersonRow({ p, me, unread, sub, onOpen }: {
  p: ChatPerson; me: string; unread: number; sub?: string; onOpen: (email: string) => void;
}) {
  const isMe = p.email === me;
  const noEmail = !p.email;
  return (
    <button type="button" disabled={noEmail}
      onClick={() => { if (!noEmail) onOpen(p.email); }}
      className={cn(
        "flex w-full items-center gap-3 rounded-3xl border border-black/[0.08] bg-white px-3.5 py-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]",
        noEmail ? "opacity-70" : "active:bg-slate-50",
      )}>
      <TechAvatar empno={p.empno ?? p.email} name={p.name} size="md" />
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-sm", unread ? "font-extrabold text-slate-900" : "font-semibold text-slate-800")}>
          {p.name}{isMe && <span className="font-normal text-slate-400"> (you)</span>}
        </span>
        <span className={cn("block truncate text-xs", unread ? "font-semibold text-slate-700" : "text-slate-400")}>
          {sub ?? (isMe ? "Notes to self — only you see these" : p.role ?? p.group)}
        </span>
      </span>
      {unread > 0 && <span className="shrink-0 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{unread}</span>}
      {noEmail && <span className="shrink-0 text-[10px] font-semibold italic text-slate-300">No login yet</span>}
    </button>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="px-2 pb-1 pt-3 text-[12.5px] font-bold text-slate-500">{children}</p>;
}

/** CHAT-TAGS (Seth 2026-08-28): the tag picker techs actually need — the call
 *  they're on, that call's customer, or ANY customer by search. Picking one
 *  drops a [[tag]] into the draft; on desktop it renders as a chip that opens
 *  the record. Bottom-sheet, thumb-reach, iOS style. */
function ChatTagSheet({ currentStop, onPick, onClose }: {
  currentStop: Stop | null;
  onPick: (tag: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const hits = useMobileCustomerSearch(q).data ?? [];
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col justify-end bg-black/45" onClick={onClose}>
      <div
        className="max-h-[75dvh] overflow-hidden rounded-t-3xl bg-white pb-[max(env(safe-area-inset-bottom),12px)] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-slate-200" />
        <div className="flex items-center justify-between px-4 pb-1 pt-2.5">
          <h3 className="text-[15px] font-extrabold text-slate-900">Tag something</h3>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 [touch-action:manipulation] active:bg-slate-100" aria-label="Close">
            <X className="h-4.5 w-4.5" />
          </button>
        </div>
        <div className="max-h-[62dvh] space-y-1.5 overflow-y-auto px-3 pb-3">
          {currentStop && (
            <>
              <p className="px-1 pt-1 text-[11.5px] font-semibold text-slate-500">The call I&apos;m on</p>
              <button type="button"
                onClick={() => onPick(`[[disp:${currentStop.ref}|Call #${currentStop.no || "—"} — ${currentStop.name}]]`)}
                className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.08] bg-white px-3 py-2.5 text-left [touch-action:manipulation] active:bg-slate-50">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><Wrench className="h-4 w-4" /></span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold text-slate-800">Call #{currentStop.no || "—"} — {currentStop.name}</span>
                  <span className="block truncate text-[11px] text-slate-400">{currentStop.address ?? "This job"}</span>
                </span>
              </button>
              {currentStop.custno && (
                <button type="button"
                  onClick={() => onPick(`[[cust:${currentStop.custno}|${currentStop.name}]]`)}
                  className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.08] bg-white px-3 py-2.5 text-left [touch-action:manipulation] active:bg-slate-50">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><User className="h-4 w-4" /></span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-slate-800">{currentStop.name}</span>
                    <span className="block truncate text-[11px] text-slate-400">The customer&apos;s account</span>
                  </span>
                </button>
              )}
            </>
          )}
          <p className="px-1 pt-2 text-[11.5px] font-semibold text-slate-500">Any customer</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, address, or phone…"
              autoFocus={!currentStop}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-[15px] focus:border-red-500 focus:bg-white focus:outline-none"
            />
          </div>
          {hits.slice(0, 8).map((h) => (
            <button key={`${h.custno}-${h.locno ?? ""}`} type="button"
              onClick={() => onPick(`[[cust:${h.custno}|${h.name}]]`)}
              className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.08] bg-white px-3 py-2.5 text-left [touch-action:manipulation] active:bg-slate-50">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><User className="h-4 w-4" /></span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-bold text-slate-800">{h.name}</span>
                <span className="block truncate text-[11px] text-slate-400">{[h.address, h.city].filter(Boolean).join(", ") || h.custno}</span>
              </span>
            </button>
          ))}
          {q.trim().length >= 2 && hits.length === 0 && (
            <p className="px-1 py-2 text-center text-xs text-slate-400">No matches — check the spelling.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** CHAT-04 (Seth 2026-08-31: "It's not tagged properly inside of the pegasus
 *  mobile chat"): tags render as REAL chips in the bubbles — and a customer
 *  chip taps open to their full profile, right on the phone. */
const M_TAG_RE = /\[\[(cust|job|disp|inv|po|epo):([^|\]]+)\|([^\]]+)\]\]/g;
function MBodyText({ body, mine, onCust }: { body: string; mine: boolean; onCust: (custno: string, label: string) => void }) {
  const parts: ReactNode[] = [];
  const re = new RegExp(M_TAG_RE.source, "g");
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(body))) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const [, kind, id, label] = m;
    const clickable = kind === "cust";
    const Icon = kind === "cust" ? User : kind === "po" || kind === "epo" ? Package : kind === "inv" ? FileText : Wrench;
    parts.push(
      <button key={`t${i++}`} type="button" disabled={!clickable}
        onClick={() => { if (clickable) onCust(id, label); }}
        title={clickable ? "Open this customer's profile" : undefined}
        className={cn(
          "mx-0.5 inline-flex max-w-full items-center gap-1 truncate rounded-lg px-1.5 py-0.5 align-middle text-[13px] font-bold [touch-action:manipulation]",
          mine ? "bg-white/25 text-white" : "bg-red-50 text-red-700 ring-1 ring-red-200",
          clickable && "underline decoration-dotted underline-offset-2 active:opacity-75",
        )}>
        <Icon className="h-3 w-3 shrink-0" />{label}
      </button>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return <>{parts}</>;
}

/** A picked-but-unsent tag, parsed for its chip. */
function tagLabel(tag: string): { label: string; kind: string } {
  const m = new RegExp(M_TAG_RE.source).exec(tag);
  return m ? { kind: m[1], label: m[3] } : { kind: "cust", label: tag };
}

/** iMessage-style timestamp: time today, "Yesterday", weekday this week,
 *  short date beyond (CHAT-03). */
function chatWhen(iso: string): string {
  const d = new Date(iso), now = new Date();
  const key = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (key(d) === key(now)) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (key(d) === key(y)) return "Yesterday";
  if (now.getTime() - d.getTime() < 6 * 86400000) return d.toLocaleDateString("en-US", { weekday: "long" });
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" });
}

// CHAT-03 (Seth 2026-08-29: "act like an iPhone message"): the tab opens on
// YOUR conversations only — newest first, previews, timestamps, unread dots,
// Edit to delete (hides for you; the office copy + audit stay). The full
// browsable roster (pictures + job titles) moved behind the compose button,
// exactly where iMessage keeps contacts.
function ChatPane({ me, dms, currentStop, actingAs, empno, techFirst }: { me: string; dms: Dm[]; currentStop: Stop | null; actingAs: string | null; empno: string | null; techFirst: string | null }) {
  const people = useChatPeople(me).data ?? [];
  const send = useSendDm(me);
  const markRead = useMarkRead(me);
  const hides = useDmHides(me).data;
  const hideThread = useHideThread(me);
  const [who, setWho] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  // CHAT-04: picked tags ride as chips (never raw markup in the text box),
  // and a customer chip in any bubble opens their profile right here.
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [chipCust, setChipCust] = useState<CustHit | null>(null);
  const openCustTag = (custno: string, label: string) =>
    setChipCust({ custno, locno: null, name: label, address: null, city: null, phone: null, src: "esc" });
  const [pplQuery, setPplQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const hiddenAt = (email: string): string | null => hides?.get(email) ?? null;

  // Last VISIBLE message per person (a hide stamp curtains everything before
  // it — a newer message brings the conversation back, like a fresh text).
  const lastBy = useMemo(() => {
    const m = new Map<string, Dm>();
    for (const d of dms) {
      const other = low(d.from_email) === me ? low(d.to_email) : low(d.from_email);
      const h = hides?.get(other);
      if (h && d.created_at <= h) continue;
      m.set(other, d);
    }
    return m;
  }, [dms, me, hides]);
  const unreadBy = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dms) if (low(d.to_email) === me && !d.read_at) {
      const from = low(d.from_email);
      const h = hides?.get(from);
      if (h && d.created_at <= h) continue;
      m.set(from, (m.get(from) ?? 0) + 1);
    }
    return m;
  }, [dms, me, hides]);

  const thread = useMemo(() => {
    if (!who) return [];
    const h = hiddenAt(who);
    return dms.filter((d) =>
      ((low(d.from_email) === me && low(d.to_email) === who) || (low(d.from_email) === who && low(d.to_email) === me))
      && (!h || d.created_at > h));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dms, me, who, hides]);

  useEffect(() => {
    if (!who) return;
    if ((unreadBy.get(who) ?? 0) > 0) markRead.mutate(who);
    endRef.current?.scrollIntoView({ block: "end" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, thread.length]);

  if (!me) return <EmptyCard Icon={MessageCircle} title="Chat needs your login" sub="Sign in on this phone to use team chat." />;

  if (who) {
    const person = people.find((p) => p.email === who);
    const selfThread = who === me; // your own thread = a private notepad
    const doSend = () => {
      const body = [draft.trim(), ...pendingTags].filter(Boolean).join(" ").trim();
      if (!body) return;
      setDraft("");
      setPendingTags([]);
      send.mutate({ to: who, body });
    };
    const attachFile = async (list: FileList | null) => {
      const file = list?.[0];
      if (!file || !who) return;
      if (file.size > 25 * 1024 * 1024) { window.alert("Files up to 25 MB."); return; }
      setUploading(true);
      try {
        // Same bucket + path scheme as the office dock — one store, both ends.
        const path = `${me.replace(/[^a-z0-9]/gi, "_")}/${Date.now()}_${file.name.replace(/[^\w.\- ]/g, "_")}`;
        const { error } = await supabase.storage.from("chat-files").upload(path, file, { contentType: file.type || "application/octet-stream" });
        if (error) throw error;
        send.mutate({ to: who, body: draft.trim(), attachment: { path, name: file.name, type: file.type || "application/octet-stream" } });
        setDraft("");
      } catch {
        window.alert("Upload didn't go through — check signal and try again.");
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    return (
      <div className="fixed inset-0 z-40 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
        <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
          <button type="button" onClick={() => setWho(null)} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back to chats">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-slate-900">{selfThread ? "Notes to self" : person?.name ?? who}</div>
            <div className="truncate text-[11px] text-slate-500">
              {selfThread ? "Only you can see this thread" : person?.role ? `${person.role} · ${who}` : who}
            </div>
          </div>
        </header>
        {actingAs && (
          <div className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-center text-[11px] font-semibold text-amber-700">
            Chatting as {actingAs} — View-As (admin). This sends under their name.
          </div>
        )}
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
          {thread.length === 0 && (
            <p className="pt-8 text-center text-xs text-slate-400">
              {selfThread ? "Model numbers, reminders, a photo. Only you see this." : "No messages yet."}
            </p>
          )}
          {thread.map((d) => {
            const mine = low(d.from_email) === me;
            return (
              <div key={d.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                <div className={cn(
                  "max-w-[80%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm leading-relaxed",
                  mine ? "rounded-br-md bg-red-700 text-white" : "rounded-bl-md border border-slate-200 bg-white text-slate-800",
                )}>
                  {(d.body ?? "").trim() !== "" && <MBodyText body={d.body!} mine={mine} onCust={openCustTag} />}
                  <MAttachment d={d} mine={mine} />
                  <div className={cn("mt-0.5 text-right text-[9px]", mine ? "text-red-200" : "text-slate-400")}>
                    {new Date(d.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <footer className="border-t border-slate-200 bg-white p-2.5 pb-[max(env(safe-area-inset-bottom),10px)]">
          <div className="mb-1.5 flex items-center gap-1.5">
            {/* CHAT-TAGS (Seth 2026-08-28): a real tag picker — the call, the
                customer, or ANY customer by search — not just one chip. */}
            <button type="button" onClick={() => setTagOpen(true)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 [touch-action:manipulation] active:bg-slate-200">
              <ClipboardList className="h-3 w-3" /> Tag a call or customer
            </button>
            {currentStop && !pendingTags.some((t) => t.startsWith("[[disp:")) && (
              <button type="button"
                onClick={() => setPendingTags((ts) => [...ts, `[[disp:${currentStop.ref}|Call #${currentStop.no || "—"} — ${currentStop.name}]]`])}
                className="inline-flex min-w-0 items-center gap-1 truncate rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700 [touch-action:manipulation] active:bg-slate-200">
                This call ({currentStop.name})
              </button>
            )}
          </div>
          {/* Picked tags — clean chips, X pulls one off; the markup is
              assembled invisibly at send (CHAT-04). */}
          {pendingTags.length > 0 && (
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
              {pendingTags.map((t, i) => {
                const { label } = tagLabel(t);
                return (
                  <span key={`${t}-${i}`} className="inline-flex max-w-full items-center gap-1 rounded-full bg-red-50 py-1 pl-2.5 pr-1 text-[12px] font-bold text-red-700 ring-1 ring-red-200">
                    <span className="truncate">{label}</span>
                    <button type="button" onClick={() => setPendingTags((ts) => ts.filter((_, j) => j !== i))}
                      className="rounded-full p-1 text-red-400 [touch-action:manipulation] active:bg-red-100" aria-label={`Remove ${label}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="flex items-end gap-1.5">
            {/* Two ways in (Seth 2026-08-28): TAKE a photo (camera opens
                directly) or pick one from the phone. Same upload either way. */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => void attachFile(e.target.files)} />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => void attachFile(e.target.files)} />
            <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 [touch-action:manipulation] active:bg-slate-100 disabled:opacity-50" aria-label="Take a photo">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4.5 w-4.5" />}
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 [touch-action:manipulation] active:bg-slate-100 disabled:opacity-50" aria-label="Attach a photo or file">
              <ImagePlus className="h-4.5 w-4.5" />
            </button>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); } }}
              rows={1}
              placeholder="Message…"
              className="min-h-[44px] flex-1 resize-none rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
            />
            <button type="button" onClick={doSend} disabled={(!draft.trim() && pendingTags.length === 0) || send.isPending}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-red-600 to-red-700 text-white shadow-[0_2px_8px_-2px_rgba(185,28,28,0.5)] transition-transform [touch-action:manipulation] active:scale-95 disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none" aria-label="Send">
              <Send className="h-4.5 w-4.5" />
            </button>
          </div>
          {tagOpen && (
            <ChatTagSheet
              currentStop={currentStop}
              onPick={(tag) => { setPendingTags((ts) => [...ts, tag.trim()]); setTagOpen(false); }}
              onClose={() => setTagOpen(false)}
            />
          )}
        </footer>
        {/* A tapped customer chip opens the full profile — same sheet as the
            Customers tab (CHAT-04). */}
        {chipCust && <CustomerSheet hit={chipCust} empno={empno} techFirst={techFirst} onClose={() => setChipCust(null)} />}
      </div>
    );
  }

  // ── New Message: the contacts book (pictures + job titles), iMessage-style.
  if (composing) {
    const qTokens = pplQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const hits = qTokens.length
      ? people.filter((p) => qTokens.every((t) => `${p.name} ${p.email} ${p.role ?? ""} ${p.group}`.toLowerCase().includes(t)))
      : [];
    const GROUP_ORDER = ["Office", "Service", "Install", "Plumbing", "Sales", "New Construction"];
    const groups = new Map<string, ChatPerson[]>();
    for (const p of people) {
      if (!groups.has(p.group)) groups.set(p.group, []);
      groups.get(p.group)!.push(p);
    }
    const groupNames = [...groups.keys()].sort((a, b) => {
      if (a === "Pegasus") return 1;
      if (b === "Pegasus") return -1;
      const ia = GROUP_ORDER.indexOf(a), ib = GROUP_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
    });
    const openPerson = (email: string) => { setWho(email); setComposing(false); setPplQuery(""); };
    return (
      <div className="fixed inset-0 z-40 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
        <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { setComposing(false); setPplQuery(""); }}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Cancel">
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-[17px] font-extrabold tracking-tight text-slate-900">New Message</h2>
          </div>
          <div className="relative mt-2.5">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={pplQuery}
              onChange={(e) => setPplQuery(e.target.value)}
              placeholder="Search a name, or a job — try “CSR”…"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3 text-[15px] focus:border-red-500 focus:bg-white focus:outline-none"
            />
          </div>
        </header>
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
          {qTokens.length > 0 ? (
            <>
              {hits.length === 0 && <EmptyCard Icon={MessageCircle} title="No matches" sub="Try part of a name, or a job like “CSR”, “office”, or “install”." />}
              {hits.map((p) => (
                <PersonRow key={p.email || `e-${p.empno}`} p={p} me={me} unread={0} onOpen={openPerson} />
              ))}
            </>
          ) : (
            groupNames.map((g) => (
              <div key={g}>
                <SectionLabel>{g}</SectionLabel>
                <div className="space-y-1.5">
                  {groups.get(g)!.map((p) => (
                    <PersonRow key={p.email || `e-${p.empno}`} p={p} me={me} unread={0} onOpen={openPerson} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ── The conversation list (what the tab opens to) ──────────────────────────
  const convos = people
    .filter((p) => p.email && lastBy.has(p.email))
    .sort((a, b) => (lastBy.get(b.email)?.created_at ?? "").localeCompare(lastBy.get(a.email)?.created_at ?? ""));

  const doHide = (email: string) => {
    hideThread.mutate(email);
    markRead.mutate(email); // no phantom badge from a hidden thread
    setConfirmDel(null);
  };

  return (
    <div className="space-y-1.5">
      {actingAs && (
        <div className="rounded-2xl bg-amber-50 px-3 py-2 text-center text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200">
          Chatting as {actingAs} — View-As (admin). Messages send under their name.
        </div>
      )}
      <div className="flex items-center justify-between px-1 pb-1">
        <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900">Messages</h2>
        <div className="flex items-center gap-1.5">
          {convos.length > 0 && (
            <button type="button" onClick={() => { setEditMode((v) => !v); setConfirmDel(null); }}
              className={cn("rounded-full px-3 py-1.5 text-[13px] font-bold [touch-action:manipulation]",
                editMode ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-black/[0.06]")}>
              {editMode ? "Done" : "Edit"}
            </button>
          )}
          <button type="button" onClick={() => setComposing(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-b from-red-600 to-red-700 text-white shadow-[0_2px_8px_-2px_rgba(185,28,28,0.5)] [touch-action:manipulation] active:scale-95" aria-label="New message">
            <SquarePen className="h-4 w-4" />
          </button>
        </div>
      </div>

      {convos.length === 0 && (
        <div className="rounded-3xl border border-black/[0.08] bg-white p-6 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <MessageCircle className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-bold text-slate-800">No conversations yet</p>
          <button type="button" onClick={() => setComposing(true)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-b from-red-600 to-red-700 px-4 py-2 text-sm font-bold text-white shadow-[0_2px_8px_-2px_rgba(185,28,28,0.5)] [touch-action:manipulation] active:scale-95">
            <SquarePen className="h-4 w-4" /> Start a message
          </button>
        </div>
      )}

      {convos.map((p) => {
        const last = lastBy.get(p.email)!;
        const unread = unreadBy.get(p.email) ?? 0;
        const mineLast = low(last.from_email) === me;
        const isSelf = p.email === me;
        return (
          <div key={p.email} className="flex items-center gap-1.5">
            {editMode && (
              confirmDel === p.email ? null : (
                <button type="button" onClick={() => setConfirmDel(p.email)} aria-label={`Delete conversation with ${p.name}`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-white [touch-action:manipulation] active:scale-95">
                  <span className="mb-px block h-0.5 w-3 rounded bg-white" />
                </button>
              )
            )}
            <button type="button"
              onClick={() => { if (!editMode) setWho(p.email); }}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-3 rounded-3xl border border-black/[0.08] bg-white px-3.5 py-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]",
                !editMode && "active:bg-slate-50",
              )}>
              <span className="relative shrink-0">
                <TechAvatar empno={p.empno ?? p.email} name={p.name} size="md" />
                {unread > 0 && <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-white" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <span className={cn("min-w-0 truncate text-[15px]", unread ? "font-extrabold text-slate-900" : "font-bold text-slate-800")}>
                    {isSelf ? "Notes to self" : p.name}
                  </span>
                  <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400">{chatWhen(last.created_at)}</span>
                </span>
                <span className={cn("mt-0.5 block truncate text-[12.5px] leading-snug", unread ? "font-semibold text-slate-700" : "text-slate-400")}>
                  {mineLast && !isSelf ? "You: " : ""}{plainBody(last) || (last.attachment_name ? `📎 ${last.attachment_name}` : "")}
                </span>
              </span>
            </button>
            {editMode && confirmDel === p.email && (
              <button type="button" onClick={() => doHide(p.email)}
                className="shrink-0 rounded-2xl bg-red-600 px-3.5 py-3 text-sm font-bold text-white [touch-action:manipulation] active:scale-95">
                Delete
              </button>
            )}
          </div>
        );
      })}
      {editMode && convos.length > 0 && (
        <p className="px-2 pt-1 text-center text-[11px] text-slate-400">
          Deleting clears it on your phone only — they keep their copy.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------- library + ask (LIB-02) */

interface MDoc {
  id: string; folder: string; filename: string; mime: string | null;
  storage_path: string | null; external_url: string | null;
}

function useLibraryDocsM() {
  return useQuery({
    queryKey: ["m_library_docs"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MDoc[]> => {
      const { data } = await supabase.from("pegasus_library_doc")
        .select("id, folder, filename, mime, storage_path, external_url")
        .eq("active", true).order("folder").order("filename");
      return (data ?? []) as MDoc[];
    },
  });
}

function useLibrarySearchM(q: string) {
  return useQuery({
    queryKey: ["m_library_search", q.trim().toLowerCase()],
    enabled: q.trim().length >= 2,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<(MDoc & { snippet: string | null })[]> => {
      const { data } = await supabase.rpc("library_search", { p_q: q.trim(), p_limit: 20 });
      return ((data ?? []) as (MDoc & { snippet: string | null })[]);
    },
  });
}

async function openLibraryDoc(d: Pick<MDoc, "storage_path" | "external_url">) {
  if (d.storage_path) {
    const { data } = await supabase.storage.from("customer-files").createSignedUrl(d.storage_path, 600);
    if (data?.signedUrl) { window.open(data.signedUrl, "_blank", "noopener"); return; }
  }
  if (d.external_url) window.open(d.external_url, "_blank", "noopener");
}

/** In-app reader for Drive-linked docs whose binary hasn't migrated yet
 *  (Seth 2026-08-28: a Drive link on a tech's phone demands a Google sign-in
 *  — the exact thing the library exists to kill). Shows the extracted text
 *  right here; once the real file is uploaded, taps open the PDF instead. */
function TextDocSheet({ doc, onClose }: { doc: MDoc; onClose: () => void }) {
  const { data: text, isLoading } = useQuery({
    queryKey: ["m_library_text", doc.id],
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.from("pegasus_library_doc").select("content_text").eq("id", doc.id).maybeSingle();
      return (data as { content_text: string | null } | null)?.content_text ?? null;
    },
  });
  return (
    <div className="fixed inset-0 z-[50] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back to the library">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-extrabold text-slate-900">{doc.filename.replace(/\.[a-z0-9]+$/i, "")}</div>
          <div className="text-[10.5px] font-semibold text-slate-400">{doc.folder}</div>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-3xl bg-slate-200/60" />
        ) : text ? (
          <div className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
            <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-slate-800">{text}</p>
          </div>
        ) : (
          <EmptyCard Icon={FileText} title="No readable text yet" sub="Ask the office to upload the file." />
        )}
        <p className="px-2 py-3 text-center text-[10.5px] leading-relaxed text-slate-400">
          Text version — the original file isn&apos;t in the library yet.
        </p>
      </div>
    </div>
  );
}

/** The Library + Ask Pegasus pane — the truck's replacement for the Google
 *  Drive folders (Seth 2026-08-28): every company doc, searchable, plus an
 *  AI that answers from those docs AND the web (field-assist). */
function DocsPane({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<"docs" | "ask">("docs");
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState<string | null>(null);
  const [reader, setReader] = useState<MDoc | null>(null); // link-only docs read in-app
  const docs = useLibraryDocsM().data ?? [];
  const hits = useLibrarySearchM(q).data ?? [];
  const searching = q.trim().length >= 2;
  const folders = [...new Set(docs.map((d) => d.folder))].sort();
  const shown = searching ? hits : docs.filter((d) => !folder || d.folder === folder);

  // Ask thread — local to the visit; answers cite the documents used.
  const [thread, setThread] = useState<{ role: "q" | "a"; text: string; sources?: { id: string; filename: string }[]; web?: { title: string; url: string }[] }[]>([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const askEndRef = useRef<HTMLDivElement | null>(null);
  const ask = async () => {
    const query = question.trim();
    if (!query || asking) return;
    setQuestion("");
    setThread((t) => [...t, { role: "q", text: query }]);
    setAsking(true);
    try {
      const { data, error } = await supabase.functions.invoke("field-assist", { body: { question: query } });
      const d = data as { ok?: boolean; answer?: string; error?: string; sources?: { id: string; filename: string }[]; webSources?: { title: string; url: string }[] } | null;
      if (error || !d?.ok || !d.answer) throw new Error(d?.error || "no answer");
      setThread((t) => [...t, { role: "a", text: d.answer!, sources: d.sources, web: d.webSources }]);
    } catch {
      setThread((t) => [...t, { role: "a", text: "I couldn't reach the assistant — check signal and try again, or call the office." }]);
    } finally {
      setAsking(false);
      setTimeout(() => askEndRef.current?.scrollIntoView({ block: "end" }), 60);
    }
  };

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-[17px] font-extrabold tracking-tight text-slate-900">Library</h2>
        </div>
        <div className="mt-2.5 flex gap-0.5 rounded-full bg-slate-100 p-0.5">
          {([["docs", "Documents"], ["ask", "Ask Pegasus"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode(k)}
              className={cn("flex-1 rounded-full py-1.5 text-[12.5px] font-bold transition-all [touch-action:manipulation]",
                mode === k ? "bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.12)]" : "text-slate-500")}>
              {k === "ask" ? <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-slate-500" />{label}</span> : label}
            </button>
          ))}
        </div>
      </header>

      {mode === "docs" ? (
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search every company document…"
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-[15px] shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus:border-red-500 focus:outline-none" />
          </div>
          {/* LIB-03 (Seth 2026-08-31: "folders would be better organization" +
              Ask Pegasus front and center): browse opens on an Ask card and a
              FOLDER GRID; tap a folder to go inside. */}
          {!searching && !folder && (
            <>
              <button type="button" onClick={() => setMode("ask")}
                className="flex w-full items-center gap-3 rounded-3xl bg-gradient-to-br from-[#132c4b] to-[#0d2036] p-4 text-left shadow-[0_6px_16px_-6px_rgba(13,32,54,0.55)] [touch-action:manipulation] active:scale-[0.99]">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15"><Sparkles className="h-5 w-5 text-white" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-extrabold text-white">Ask Pegasus</span>
                  <span className="block truncate text-[11.5px] text-slate-300/90">&ldquo;What&apos;s our after-hours fee?&rdquo; · &ldquo;Who do we refer for duct cleaning?&rdquo;</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
              <div className="grid grid-cols-2 gap-1.5 pt-1">
                {folders.map((f) => {
                  const n = docs.filter((d) => d.folder === f).length;
                  return (
                    <button key={f} type="button" onClick={() => setFolder(f)}
                      className="flex items-center gap-2.5 rounded-2xl border border-black/[0.08] bg-white px-3 py-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] [touch-action:manipulation] active:bg-slate-50">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><FolderOpen className="h-4.5 w-4.5" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-bold leading-tight text-slate-800">{f}</span>
                        <span className="block text-[10.5px] text-slate-400">{n} doc{n === 1 ? "" : "s"}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {!searching && folder && (
            <button type="button" onClick={() => setFolder(null)}
              className="flex w-full items-center gap-1.5 px-1 py-0.5 text-left text-[13px] font-bold text-red-600 [touch-action:manipulation]">
              <ArrowLeft className="h-3.5 w-3.5" /> All folders
              <span className="ml-1 truncate font-extrabold text-slate-800">/ {folder}</span>
            </button>
          )}
          {(searching || folder) && shown.map((d) => (
            <button key={d.id} type="button"
              onClick={() => { if (d.storage_path) void openLibraryDoc(d); else setReader(d); }}
              className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.08] bg-white px-3 py-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] [touch-action:manipulation] active:bg-slate-50">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600"><FileText className="h-4.5 w-4.5" /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-slate-800">{d.filename.replace(/\.[a-z0-9]+$/i, "")}</span>
                <span className="block truncate text-[11px] text-slate-400">
                  {"snippet" in d && (d as { snippet?: string | null }).snippet
                    ? (d as { snippet?: string | null }).snippet!.replace(/<\/?b>/g, "")
                    : d.folder}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          ))}
          {(searching || folder) && shown.length === 0 && (
            <EmptyCard Icon={FileText} title={searching ? "Nothing matches" : "Empty folder"}
              sub={searching ? "Try different words." : undefined} />
          )}
          {reader && <TextDocSheet doc={reader} onClose={() => setReader(null)} />}
        </div>
      ) : (
        <>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {thread.length === 0 && (
              <div className="rounded-3xl border border-black/[0.08] bg-white p-5 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                <Sparkles className="mx-auto h-7 w-7 text-slate-400" />
                <h3 className="mt-2 text-sm font-bold text-slate-800">Ask anything</h3>
                <p className="mx-auto mt-1 max-w-[32ch] text-xs leading-relaxed text-slate-500">
                  Answers come from our documents — and the web for fault codes, specs and clearances.
                </p>
                {/* One-tap starters — preloaded into the box (Seth 2026-08-31). */}
                <div className="mt-3 space-y-1.5">
                  {["What's our after-hours diagnostic fee?", "Who do we refer for duct cleaning?", "How do I decode a Lennox serial number?"].map((s) => (
                    <button key={s} type="button" onClick={() => setQuestion(s)}
                      className="block w-full rounded-2xl bg-slate-100 px-3 py-2.5 text-left text-[13px] font-bold text-slate-800 ring-1 ring-black/[0.04] [touch-action:manipulation] active:bg-slate-200">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {thread.map((m, i) => (
              <div key={i} className={cn("flex", m.role === "q" ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[88%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                  m.role === "q" ? "rounded-br-md bg-slate-900 text-white" : "rounded-bl-md border border-black/[0.08] bg-white text-slate-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)]")}>
                  {m.text}
                  {m.role === "a" && !!m.sources?.length && (
                    <span className="mt-2 flex flex-wrap gap-1">
                      {[...new Map(m.sources.map((s) => [s.filename, s])).values()].map((s) => (
                        <span key={s.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                          {s.filename.replace(/\.[a-z0-9]+$/i, "")}
                        </span>
                      ))}
                    </span>
                  )}
                  {m.role === "a" && !!m.web?.length && (
                    <span className="mt-1.5 block text-[10px] text-slate-400">Checked the web: {m.web.map((w) => w.title).slice(0, 2).join(" · ")}</span>
                  )}
                </div>
              </div>
            ))}
            {asking && (
              <div className="flex items-center gap-1.5 px-1 text-[12px] text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> Pegasus is looking…
              </div>
            )}
            <div ref={askEndRef} />
          </div>
          <footer className="border-t border-slate-200 bg-white p-2.5 pb-[max(env(safe-area-inset-bottom),10px)]">
            <div className="flex items-end gap-1.5">
              <textarea value={question} onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void ask(); } }}
                rows={1} placeholder="Ask Pegasus…"
                className="min-h-[44px] flex-1 resize-none rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/20" />
              <button type="button" onClick={() => void ask()} disabled={!question.trim() || asking}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-slate-800 to-slate-950 text-white shadow-[0_2px_8px_-2px_rgba(15,23,42,0.5)] transition-transform [touch-action:manipulation] active:scale-95 disabled:from-slate-300 disabled:to-slate-300">
                <Send className="h-4.5 w-4.5" />
              </button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------- install calendar (LIB-02b) */

interface InstallVisit {
  id: string; job_id: string | null; visit_role: string | null; summary: string | null;
  customer_name: string | null; address: string | null; city: string | null;
  scheduled_date: string; scheduled_window: string | null; assigned_empno: string | null; status: string;
}

const mondayOf = (d: Date) => { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(12, 0, 0, 0); return x; };
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The weekly install calendar — REAL-TIME scheduled Pegasus install jobs
 *  (Seth 2026-08-28: check it before a sales call; verify a sold job landed
 *  on the calendar). Replaces the old Drive install-schedule file. */
function InstallCalPane({ onClose }: { onClose: () => void }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const dateRef = useRef<HTMLInputElement | null>(null);
  const days = Array.from({ length: 6 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const from = isoDay(days[0]), to = isoDay(days[5]);
  const techs = useAllActiveTechs().data ?? [];
  const nameOf = (e: string | null) => {
    if (!e) return null;
    const t = techs.find((x) => x.empno === e);
    const full = t?.empname ?? (`${t?.firstname ?? ""} ${t?.lastname ?? ""}`.trim() || e);
    return full.split(/\s+/)[0];
  };
  const { data: visits, isLoading } = useQuery({
    queryKey: ["m_install_week", from],
    refetchInterval: 60 * 1000, // real-time enough for a calendar
    queryFn: async (): Promise<InstallVisit[]> => {
      const { data } = await supabase.from("pegasus_dispatch")
        .select("id, job_id, visit_role, summary, customer_name, address, city, scheduled_date, scheduled_window, assigned_empno, status")
        .not("job_id", "is", null)
        .gte("scheduled_date", from).lte("scheduled_date", to)
        .neq("status", "cancelled")
        .is("deleted_at", null)
        .order("scheduled_date");
      return (data ?? []) as InstallVisit[];
    },
  });
  const byDay = new Map<string, InstallVisit[]>();
  for (const v of visits ?? []) {
    if (!byDay.has(v.scheduled_date)) byDay.set(v.scheduled_date, []);
    byDay.get(v.scheduled_date)!.push(v);
  }
  const fmtRange = `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[5].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  const todayIso = isoDay(new Date());
  const shift = (weeks: number) => setWeekStart((w) => { const x = new Date(w); x.setDate(x.getDate() + weeks * 7); return x; });

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="min-w-0 flex-1 text-[17px] font-extrabold tracking-tight text-slate-900">Install Calendar</h2>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => shift(-1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Previous week">‹</button>
            <button type="button" onClick={() => setWeekStart(mondayOf(new Date()))} className="rounded-full bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600 [touch-action:manipulation]">Today</button>
            {/* Date picker (Seth 2026-08-28) — tap the calendar, pick ANY day,
                the view jumps to that week. v2: a REAL button firing the
                native picker via showPicker() — the invisible-input overlay
                had its own hit shape on iOS, so taps half-missed. */}
            <button
              type="button"
              onClick={() => {
                const el = dateRef.current;
                if (!el) return;
                try { (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); }
                catch { el.focus(); el.click(); }
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95"
              aria-label="Jump to a date"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
            <input
              ref={dateRef}
              type="date"
              value={isoDay(weekStart)}
              onChange={(e) => { const v = e.target.value; if (v) setWeekStart(mondayOf(new Date(`${v}T12:00:00`))); }}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
            />
            <button type="button" onClick={() => shift(1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Next week">›</button>
          </div>
        </div>
        <p className="mt-1 pl-12 text-[11px] font-semibold text-slate-400">{fmtRange}</p>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {isLoading && <div className="h-24 animate-pulse rounded-3xl bg-slate-200/60" />}
        {days.map((d) => {
          const iso = isoDay(d);
          const list = byDay.get(iso) ?? [];
          return (
            <section key={iso}>
              <div className="flex items-baseline gap-2 px-1 pb-1">
                <span className={cn("text-[13px] font-extrabold", iso === todayIso ? "text-red-700" : "text-slate-800")}>
                  {d.toLocaleDateString("en-US", { weekday: "long" })}
                </span>
                <span className="text-[11px] font-semibold text-slate-400">{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                {iso === todayIso && <span className="rounded-full bg-red-600/10 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-red-700">Today</span>}
              </div>
              {list.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-slate-200 px-3 py-2.5 text-center text-[11px] text-slate-400">No installs</p>
              ) : (
                <div className="space-y-1.5">
                  {list.map((v) => (
                    <div key={v.id} className="rounded-2xl border border-black/[0.08] bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0 truncate text-sm font-bold text-slate-800">{v.customer_name ?? "Install"}</span>
                        {v.scheduled_window && <span className="shrink-0 font-mono text-[10.5px] font-extrabold text-slate-500">{v.scheduled_window}</span>}
                      </div>
                      <div className="truncate text-[11.5px] text-slate-500">{v.summary ?? "Install"}{v.city ? ` · ${v.city}` : ""}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {v.visit_role && v.visit_role !== "install" && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-slate-600">{v.visit_role.replace(/_/g, " ")}</span>
                        )}
                        {nameOf(v.assigned_empno) && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-bold text-slate-600">{nameOf(v.assigned_empno)}</span>
                        )}
                        <span className={cn("rounded-full px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide",
                          v.status === "scheduled" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
                          {v.status.replace(/_/g, " ")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ more */

/* ================================================ MY TIMESHEET (TIME-01) ==
 * Seth 2026-08-29: timesheets on the mobile app — week at a glance, day
 * detail, request-a-fix, own spiff/bonus per Holly's tiers, and time off:
 * request vacation / report sick, office alerted (chat DM via DB trigger),
 * approve/deny lands back in the tech's chat + status chips here.
 * Numbers ride the SAME rails as the office Pay Week engine: ESC per-ticket
 * hours (mirror_sales_emp) + office corrections (pegasus_payroll_hours) +
 * org_setting tiers — the tech sees exactly what Holly's sheet sees.
 */

const dayShift = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const tsChunk = <T,>(arr: T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};
const TS_PSEUDO = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);

interface MyDayLine { invoice: string; name: string | null; hours: number; dollars: number }
interface MyWeekDay { day: string; hours: number; overridden: boolean; dollars: number; lines: MyDayLine[] }

function useMyPayWeek(empno: string | null, mondayIso: string) {
  return useQuery({
    queryKey: ["m_payweek", empno, mondayIso],
    enabled: !!empno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<MyWeekDay[]> => {
      const monday = new Date(`${mondayIso}T12:00:00`);
      const sundayIso = isoDay(dayShift(monday, 6));
      // 1) The week's invoices (same window the office engine reads).
      const sales = await pagedAll<{ invoice: string; inv_date: string | null; inv_amount: number | null; ship_name: string | null }>(() =>
        supabase.from("mirror_sales")
          .select("invoice, inv_date, inv_amount, ship_name")
          .gte("inv_date", mondayIso).lte("inv_date", sundayIso)
          .order("invoice", { ascending: true }),
      );
      const byInvoice = new Map(sales.map((s) => [s.invoice, s]));
      // 2) Tech lines for those invoices — mine for hours, everyone's for the
      //    even credit split, exactly like the office engine.
      const emp: { invoice: string; empno: string | null; credit_pct: number | null; reg_hr: number | null; ot_hr: number | null }[] = [];
      for (const c of tsChunk([...byInvoice.keys()], 200)) {
        const { data, error } = await supabase.from("mirror_sales_emp")
          .select("invoice, empno, credit_pct, reg_hr, ot_hr").in("invoice", c);
        if (error) throw error;
        emp.push(...((data ?? []) as typeof emp));
      }
      const perInvoiceTechs = new Map<string, number>();
      for (const e of emp) {
        if (!e.empno || TS_PSEUDO.has(e.empno)) continue;
        perInvoiceTechs.set(e.invoice, (perInvoiceTechs.get(e.invoice) ?? 0) + 1);
      }
      // 3) Office corrections win over proposed hours.
      const { data: ov } = await supabase.from("pegasus_payroll_hours")
        .select("day, hours").eq("empno", empno as string).gte("day", mondayIso).lte("day", sundayIso);
      const overrides = new Map<string, number>();
      for (const r of (ov ?? []) as { day: string; hours: number }[]) overrides.set(r.day.slice(0, 10), Number(r.hours));
      // 4) Fold my week.
      const days: MyWeekDay[] = Array.from({ length: 7 }, (_, i) => ({
        day: isoDay(dayShift(monday, i)), hours: 0, overridden: false, dollars: 0, lines: [],
      }));
      const dayMap = new Map(days.map((d) => [d.day, d]));
      for (const e of emp) {
        if (e.empno !== empno) continue;
        const s = byInvoice.get(e.invoice);
        if (!s?.inv_date) continue;
        const d = dayMap.get(s.inv_date.slice(0, 10));
        if (!d) continue;
        const hours = (e.reg_hr ?? 0) + (e.ot_hr ?? 0);
        const n = perInvoiceTechs.get(e.invoice) ?? 1;
        const share = e.credit_pct && e.credit_pct > 0 ? ((s.inv_amount ?? 0) * e.credit_pct) / 100 : (s.inv_amount ?? 0) / n;
        d.hours += hours;
        d.dollars += share;
        d.lines.push({ invoice: e.invoice, name: s.ship_name, hours, dollars: share });
      }
      for (const d of days) {
        d.hours = Math.round(d.hours * 100) / 100;
        if (overrides.has(d.day)) { d.hours = overrides.get(d.day)!; d.overridden = true; }
      }
      return days;
    },
  });
}

function useMyPayBasis(empno: string | null) {
  return useQuery({
    queryKey: ["m_pay_basis", empno],
    enabled: !!empno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<number | null> => {
      // PAY-ONE-01: the RHP number from the one pay record.
      const { data } = await supabase.rpc("pegasus_rhp_basis", { p_empnos: [empno as string] });
      const row = ((data ?? []) as { empno: string; rhp: number | null }[])[0];
      return row?.rhp == null ? null : Number(row.rhp);
    },
  });
}

interface MyFix { id: string; day: string; claimed_hours: number | null; note: string | null; status: string }
function useMyFixes(empno: string | null, mondayIso: string) {
  return useQuery({
    queryKey: ["m_ts_fix", empno, mondayIso],
    enabled: !!empno,
    queryFn: async (): Promise<Map<string, MyFix>> => {
      const sundayIso = isoDay(dayShift(new Date(`${mondayIso}T12:00:00`), 6));
      const { data } = await supabase.from("pegasus_timesheet_fix")
        .select("id, day, claimed_hours, note, status").eq("empno", empno as string)
        .gte("day", mondayIso).lte("day", sundayIso).order("created_at", { ascending: false });
      const m = new Map<string, MyFix>();
      for (const r of (data ?? []) as MyFix[]) if (!m.has(r.day.slice(0, 10))) m.set(r.day.slice(0, 10), { ...r, day: r.day.slice(0, 10) });
      return m;
    },
  });
}

function useRequestFix(empno: string | null, mondayIso: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { day: string; shown: number; claimed: number; note: string }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? null;
      const { error } = await supabase.from("pegasus_timesheet_fix").insert({
        empno, day: v.day, shown_hours: v.shown, claimed_hours: v.claimed,
        note: v.note.trim() || null, created_by: who,
      });
      if (error) throw error;
      // The DB trigger DMs the payroll folks by itself.
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m_ts_fix", empno, mondayIso] }),
  });
}

interface MyTimeOffRow {
  id: string; kind: AvailKind; start_at: string; end_at: string; all_day: boolean;
  title: string | null; status: "requested" | "approved" | "denied"; decision_note: string | null;
}
function useMyTimeOff(empno: string | null) {
  return useQuery({
    queryKey: ["m_timeoff", empno],
    enabled: !!empno,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<MyTimeOffRow[]> => {
      const since = new Date(Date.now() - 45 * 86400000).toISOString();
      const { data, error } = await supabase.from("tech_availability")
        .select("id, kind, start_at, end_at, all_day, title, status, decision_note")
        .eq("empno", empno as string).gt("end_at", since)
        .order("start_at", { ascending: false }).limit(30);
      if (error) throw error;
      return (data ?? []) as MyTimeOffRow[];
    },
  });
}

/** SICK-02 — the ESST purposes. A category, never a diagnosis. */
type SickCategory = "sick" | "family" | "safety" | "other";
const SICK_CATEGORIES: { key: SickCategory; label: string }[] = [
  { key: "sick", label: "Sick" },
  { key: "family", label: "Family Member" },
  { key: "safety", label: "Safety Leave" },
  { key: "other", label: "Other" },
];
/** "06:30" → "6:30 AM" */
function fmtCutoff(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h)) return hhmm;
  const d = new Date(); d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function isAfterCutoff(hhmm: string): boolean {
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h)) return false;
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes() > h * 60 + (m || 0);
}

function useAddMyTimeOff(empno: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { kind: AvailKind; startDate: string; endDate: string; note?: string; sick?: boolean; category?: SickCategory }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? null;
      const span = computeSpan({ startDate: v.startDate, endDate: v.endDate, allDay: true });
      const { error } = await supabase.from("tech_availability").insert({
        empno, kind: v.kind, start_at: span.start_at, end_at: span.end_at, all_day: true,
        title: v.note?.trim() || null,
        // Sick is a report, not an ask — it hits the board NOW; the office is
        // alerted either way (timeoff_alert DB trigger → chat DM). SICK-02:
        // category rides along; the DB stamps late_notice against the cutoff.
        status: v.sick ? "approved" : "requested",
        category: v.sick ? (v.category ?? "sick") : null,
        self_reported: true, created_by: who,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["m_timeoff", empno] });
      void qc.invalidateQueries({ queryKey: ["tech_availability"] });
    },
  });
}

function useCancelMyTimeOff(empno: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tech_availability").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["m_timeoff", empno] });
      void qc.invalidateQueries({ queryKey: ["tech_availability"] });
    },
  });
}

function tsSpanLabel(e: { start_at: string; end_at: string; all_day: boolean }): string {
  const s = new Date(e.start_at);
  const eD = new Date(new Date(e.end_at).getTime() - (e.all_day ? 60000 : 0));
  const f = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (e.all_day) return f(s) === f(eD) ? f(s) : `${f(s)} – ${f(eD)}`;
  const t = (d: Date) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${f(s)}, ${t(s)} – ${t(eD)}`;
}

const hrs = (n: number) => (Math.round(n * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });

function TimesheetPane({ empno, actingAs, onClose }: { empno: string | null; actingAs: string | null; onClose: () => void }) {
  const [mode, setMode] = useState<"hours" | "timeoff">("hours");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const mondayIso = isoDay(weekStart);
  const isThisWeek = mondayIso === isoDay(mondayOf(new Date()));
  const week = useMyPayWeek(empno, mondayIso);
  const rhp = useMyPayBasis(empno).data ?? null;
  const fixes = useMyFixes(empno, mondayIso).data;
  const requestFix = useRequestFix(empno, mondayIso);
  const canRequest = useCan("team.request_timeoff");

  const threshold = useOrgSettingNumber("payroll_bonus_threshold_pct", 80).data ?? 80;
  const bonusPct = useOrgSettingNumber("payroll_bonus_pct", 3).data ?? 3;
  const spiffTiers = useOrgSettingJson<Array<{ min: number; pct: number }>>(
    "payroll_daily_spiff_tiers",
    [{ min: 3500, pct: 3 }, { min: 2500, pct: 2 }, { min: 1500, pct: 1 }],
  ).data ?? [{ min: 3500, pct: 3 }, { min: 2500, pct: 2 }, { min: 1500, pct: 1 }];
  const spiffPctFor = (dd: number): number => [...spiffTiers].sort((x, y) => y.min - x.min).find((t) => dd >= t.min)?.pct ?? 0;

  const [openDay, setOpenDay] = useState<string | null>(null);
  const [fixDay, setFixDay] = useState<string | null>(null);
  const [fixHours, setFixHours] = useState("");
  const [fixNote, setFixNote] = useState("");

  // Time-off state
  const timeOff = useMyTimeOff(empno);
  const addTimeOff = useAddMyTimeOff(empno);
  const cancelTimeOff = useCancelMyTimeOff(empno);
  const [toKind, setToKind] = useState<AvailKind>("vacation");
  const [toStart, setToStart] = useState("");
  const [toEnd, setToEnd] = useState("");
  const [toNote, setToNote] = useState("");
  const [toSent, setToSent] = useState(false);
  // ONCALL-SCHED-01b: published on-call days inside the dates being asked for.
  const oncallRange = useOncallDaysPublished(toStart || isoDay(new Date()), toEnd || toStart || isoDay(new Date()), !!toStart).data ?? [];
  const oncallHits = toStart ? myOncallDaysIn(oncallRange, empno, toStart, toEnd || toStart) : [];
  const [sickArm, setSickArm] = useState(false);
  const [sickSent, setSickSent] = useState<{ late: boolean } | null>(null);
  // SICK-02: category (the ESST purposes — never the medical reason), an
  // optional note, and the company's notice rule from Settings.
  const [sickCat, setSickCat] = useState<SickCategory | null>(null);
  const [sickNote, setSickNote] = useState("");
  const sickRule = useOrgSettingJson<string>("sick_notice_rule", "Report before 6:30 AM and call the office.").data;
  const sickCutoff = useOrgSettingJson<string>("sick_notice_cutoff", "06:30").data ?? "06:30";
  const [cancelArm, setCancelArm] = useState<string | null>(null);

  const days = week.data ?? [];
  const weekHours = days.reduce((s, d) => s + d.hours, 0);
  const weekDollars = days.reduce((s, d) => s + d.dollars, 0);
  const weekSpiff = days.reduce((s, d) => s + (spiffPctFor(d.dollars) > 0 ? (d.dollars * spiffPctFor(d.dollars)) / 100 : 0), 0);
  const perHour = weekHours > 0 ? weekDollars / weekHours : null;
  const pct = rhp && perHour != null ? (perHour / rhp) * 100 : null;
  const bonus = pct != null && pct >= threshold ? (weekDollars * bonusPct) / 100 : 0;

  const weekLabel = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${dayShift(weekStart, 6).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  const KIND_CHIP: { k: AvailKind; label: string; Icon: typeof Palmtree }[] = [
    { k: "vacation", label: "Vacation", Icon: Palmtree },
    { k: "personal", label: "Personal", Icon: User },
    { k: "other", label: "Other", Icon: Clock },
  ];
  const statusChip = (r: MyTimeOffRow) =>
    r.status === "approved" ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Approved</span>
    ) : r.status === "denied" ? (
      <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-600">Denied</span>
    ) : (
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">Waiting on the office</span>
    );

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-[17px] font-extrabold tracking-tight text-slate-900">My Week</h2>
        </div>
        <div className="mt-2.5 flex gap-0.5 rounded-full bg-slate-100 p-0.5">
          {([["hours", "Timesheet"], ["timeoff", "Time Off"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode(k)}
              className={cn("flex-1 rounded-full py-1.5 text-[12.5px] font-bold transition-all [touch-action:manipulation]",
                mode === k ? "bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.12)]" : "text-slate-500")}>
              {k === "timeoff" ? <span className="inline-flex items-center gap-1"><Palmtree className="h-3.5 w-3.5 text-emerald-600" />{label}</span> : label}
            </button>
          ))}
        </div>
      </header>

      {actingAs && (
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-center text-[11px] font-semibold text-amber-700">
          Viewing {actingAs}&apos;s week — View-As (admin).
        </div>
      )}

      {mode === "hours" ? (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
          {/* Week picker */}
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setWeekStart(dayShift(weekStart, -7))}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 ring-1 ring-black/[0.06] [touch-action:manipulation] active:scale-95" aria-label="Previous week">
              <ChevronLeft className="h-4.5 w-4.5" />
            </button>
            <div className="text-center">
              <div className="text-sm font-extrabold text-slate-900">{weekLabel}</div>
              {!isThisWeek && (
                <button type="button" onClick={() => setWeekStart(mondayOf(new Date()))} className="text-[11px] font-bold text-red-600 [touch-action:manipulation]">
                  Back to this week
                </button>
              )}
            </div>
            <button type="button" onClick={() => setWeekStart(dayShift(weekStart, 7))}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 ring-1 ring-black/[0.06] [touch-action:manipulation] active:scale-95" aria-label="Next week">
              <ChevronRight className="h-4.5 w-4.5" />
            </button>
          </div>

          {/* Week hero */}
          <div className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-500">Hours this week <InfoTip label="How your pay sheet works">Hours come from your tickets; office corrections (in blue) win. Spiff pays each day its band of that day&apos;s ticket dollars. This is the working sheet — final pay always comes from payroll.</InfoTip></div>
                <div className="mt-0.5 text-[32px] font-extrabold leading-none tracking-tight text-slate-900">{hrs(weekHours)}<span className="text-base font-bold text-slate-400"> h</span></div>
              </div>
              <div className="text-right">
                <div className="text-[11.5px] font-semibold text-slate-500">Ticket work</div>
                <div className="mt-0.5 text-lg font-extrabold text-slate-800">{money$(weekDollars)}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {weekSpiff > 0 && (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">Spiff so far: {money$(weekSpiff)}</span>
              )}
              {bonus > 0 ? (
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">Bonus unlocked: {money$(bonus)}</span>
              ) : pct != null ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">{Math.round(pct)}% toward the {threshold}% bonus goal</span>
              ) : null}
            </div>
            {pct != null && bonus === 0 && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-gradient-to-r from-red-500 to-red-600" style={{ width: `${Math.min(100, (pct / threshold) * 100)}%` }} />
              </div>
            )}
          </div>

          {/* Days */}
          {week.isLoading ? (
            <div className="space-y-2">
              <div className="h-14 animate-pulse rounded-3xl bg-slate-200/70" />
              <div className="h-14 animate-pulse rounded-3xl bg-slate-200/60" />
            </div>
          ) : days.map((d) => {
            const sp = spiffPctFor(d.dollars);
            const fx = fixes?.get(d.day);
            const open = openDay === d.day;
            const dd = new Date(`${d.day}T12:00:00`);
            return (
              <div key={d.day} className="overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <button type="button" onClick={() => { setOpenDay(open ? null : d.day); setFixDay(null); }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left [touch-action:manipulation] active:bg-slate-50">
                  <span className="w-16 shrink-0">
                    <span className="block text-sm font-extrabold text-slate-900">{dd.toLocaleDateString("en-US", { weekday: "short" })}</span>
                    <span className="block text-[11px] text-slate-400">{dd.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    {sp > 0 && <span className="mr-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">+{sp}% spiff · {money$((d.dollars * sp) / 100)}</span>}
                    {d.overridden && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500" title="The office corrected this day">office-set</span>}
                    {fx && (
                      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold",
                        fx.status === "open" ? "bg-amber-50 text-amber-700" : fx.status === "fixed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}>
                        {fx.status === "open" ? "Fix requested" : fx.status === "fixed" ? "Fixed" : "Reviewed"}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-sm font-extrabold tabular-nums text-slate-900">{d.hours ? `${hrs(d.hours)} h` : "—"}</span>
                    <span className="block text-[11px] tabular-nums text-slate-400">{d.dollars > 0 ? money$(d.dollars) : ""}</span>
                  </span>
                  <ChevronRight className={cn("h-4 w-4 shrink-0 text-slate-300 transition-transform", open && "rotate-90")} />
                </button>
                {open && (
                  <div className="border-t border-slate-100 px-4 py-2.5">
                    {d.lines.length === 0 ? (
                      <p className="py-1 text-xs text-slate-400">No invoiced work recorded this day.</p>
                    ) : d.lines.map((l) => (
                      <div key={l.invoice} className="flex items-center gap-2 py-1">
                        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">{l.name || `Invoice ${l.invoice}`}</span>
                        <span className="shrink-0 text-xs tabular-nums text-slate-500">{l.hours ? `${hrs(l.hours)} h` : ""}</span>
                        <span className="w-16 shrink-0 text-right text-xs tabular-nums text-slate-400">{money$(l.dollars)}</span>
                      </div>
                    ))}
                    {fx?.status === "open" ? (
                      <p className="mt-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-700">
                        You asked for {fx.claimed_hours != null ? `${hrs(Number(fx.claimed_hours))} h` : "a fix"} — the office has been notified and this updates here.
                      </p>
                    ) : fixDay === d.day ? (
                      <div className="mt-2 space-y-1.5 rounded-xl bg-slate-50 p-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-600">It should be</span>
                          <input type="number" inputMode="decimal" step="0.25" min="0" value={fixHours} onChange={(e) => setFixHours(e.target.value)}
                            placeholder={hrs(d.hours)} className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-center text-sm font-bold tabular-nums focus:border-red-500 focus:outline-none" />
                          <span className="text-xs font-bold text-slate-600">hours</span>
                        </div>
                        <input value={fixNote} onChange={(e) => setFixNote(e.target.value)} placeholder="What happened? (helps the office fix it fast)"
                          className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[13px] focus:border-red-500 focus:outline-none" />
                        <div className="flex gap-1.5">
                          <button type="button" disabled={!fixHours.trim() || requestFix.isPending}
                            onClick={() => requestFix.mutate(
                              { day: d.day, shown: d.hours, claimed: Number(fixHours), note: fixNote },
                              { onSuccess: () => { setFixDay(null); setFixHours(""); setFixNote(""); } },
                            )}
                            className="rounded-xl bg-gradient-to-b from-red-600 to-red-700 px-3 py-2 text-xs font-bold text-white [touch-action:manipulation] active:scale-95 disabled:from-slate-300 disabled:to-slate-300">
                            Send to the office
                          </button>
                          <button type="button" onClick={() => setFixDay(null)} className="rounded-xl px-2.5 py-2 text-xs font-bold text-slate-400 [touch-action:manipulation]">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { setFixDay(d.day); setFixHours(""); setFixNote(""); }}
                        className="mt-1.5 text-[11px] font-bold text-red-600 [touch-action:manipulation]">
                        Request a correction
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
          {!canRequest ? (
            <EmptyCard Icon={Palmtree} title="Time-off requests are off for your login" sub="Ask the office to turn them on." />
          ) : (
            <>
              {/* Sick — a report, not an ask (SICK-02: category + note + the
                  company's notice rule; the DB flags late notice). */}
              <div className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600"><Thermometer className="h-4.5 w-4.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-extrabold text-slate-900">Out today?</div>
                    <div className="text-[11px] text-slate-500">The office is notified and today&apos;s board shows you out.</div>
                  </div>
                </div>
                {sickRule && (
                  <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-[12px] leading-snug text-slate-600">
                    <span className="font-semibold text-slate-700">Company policy:</span> {sickRule}
                  </p>
                )}
                {sickSent ? (
                  <div className="mt-2.5 space-y-1.5">
                    <p className="rounded-xl bg-emerald-50 px-3 py-2 text-[12px] font-bold text-emerald-700">
                      The office has been notified and today&apos;s board shows you out.
                    </p>
                    {sickSent.late && (
                      <p className="rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-800">
                        Reported after {fmtCutoff(sickCutoff)} — recorded as late notice.
                      </p>
                    )}
                  </div>
                ) : sickArm ? (
                  <div className="mt-2.5 space-y-2">
                    <div>
                      <label className="text-[12.5px] font-semibold text-slate-600">Reason</label>
                      <div className="mt-1 grid grid-cols-2 gap-1.5">
                        {SICK_CATEGORIES.map((c) => (
                          <button key={c.key} type="button" onClick={() => setSickCat(c.key)}
                            className={cn(
                              "rounded-xl px-3 py-2 text-[13px] font-bold ring-1 [touch-action:manipulation]",
                              sickCat === c.key ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-black/[0.1] active:bg-slate-50",
                            )}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[12.5px] font-semibold text-slate-600">Anything the office should know (optional)</label>
                      <input value={sickNote} onChange={(e) => setSickNote(e.target.value)} placeholder="Expected back tomorrow"
                        className="mt-1 w-full rounded-xl border border-black/[0.1] bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button type="button" disabled={addTimeOff.isPending || !sickCat}
                        onClick={() => {
                          const today = isoDay(new Date());
                          const late = isAfterCutoff(sickCutoff);
                          addTimeOff.mutate({ kind: "sick", startDate: today, endDate: today, sick: true, category: sickCat ?? "sick", note: sickNote },
                            { onSuccess: () => { setSickArm(false); setSickSent({ late }); setSickCat(null); setSickNote(""); } });
                        }}
                        className="flex-1 rounded-xl bg-gradient-to-b from-red-600 to-red-700 px-3 py-2.5 text-sm font-bold text-white [touch-action:manipulation] active:scale-95 disabled:opacity-50">
                        Report out today
                      </button>
                      <button type="button" onClick={() => { setSickArm(false); setSickCat(null); }} className="rounded-xl px-3 py-2.5 text-sm font-bold text-slate-400 [touch-action:manipulation]">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setSickArm(true)}
                    className="mt-2.5 w-full rounded-xl bg-red-50 px-3 py-2.5 text-sm font-bold text-red-700 ring-1 ring-red-200 [touch-action:manipulation] active:bg-red-100">
                    I&apos;m out today
                  </button>
                )}
              </div>

              {/* Request time off */}
              <div className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <div className="text-sm font-extrabold text-slate-900">Request time off</div>
                <div className="mt-2 flex gap-1.5">
                  {KIND_CHIP.map(({ k, label, Icon }) => (
                    <button key={k} type="button" onClick={() => setToKind(k)}
                      className={cn("inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-[12.5px] font-bold [touch-action:manipulation]",
                        toKind === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 active:bg-slate-200")}>
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <label className="block">
                    <span className="text-[11.5px] font-semibold text-slate-500">First day</span>
                    <input type="date" value={toStart} onChange={(e) => { setToStart(e.target.value); if (!toEnd || e.target.value > toEnd) setToEnd(e.target.value); }}
                      className="mt-0.5 w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold focus:border-red-500 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="text-[11.5px] font-semibold text-slate-500">Last day</span>
                    <input type="date" value={toEnd} min={toStart || undefined} onChange={(e) => setToEnd(e.target.value)}
                      className="mt-0.5 w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold focus:border-red-500 focus:outline-none" />
                  </label>
                </div>
                {/* ONCALL-SCHED-01b: the request still goes through — the tech just
                    hears now that the office will need cover. */}
                {oncallHits.length > 0 && (
                  <div className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-900 ring-1 ring-amber-200">
                    <Moon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
                    <span>You&apos;re on call {oncallHits.map((h) => `${fmtOncallDay(h.day)} (${h.role})`).join(", ")}. The office will need to find cover — you can still send the request.</span>
                  </div>
                )}
                <input value={toNote} onChange={(e) => setToNote(e.target.value)} placeholder="Anything the office should know (optional)"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[13px] focus:border-red-500 focus:outline-none" />
                {toSent && <p className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-[12px] font-bold text-emerald-700">Request sent. You&apos;ll get a chat message when it&apos;s decided.</p>}
                <button type="button" disabled={!toStart || !toEnd || addTimeOff.isPending}
                  onClick={() => addTimeOff.mutate({ kind: toKind, startDate: toStart, endDate: toEnd, note: toNote },
                    { onSuccess: () => { setToStart(""); setToEnd(""); setToNote(""); setToSent(true); window.setTimeout(() => setToSent(false), 6000); } })}
                  className="mt-2 w-full rounded-xl bg-gradient-to-b from-red-600 to-red-700 px-3 py-2.5 text-sm font-bold text-white shadow-[0_2px_8px_-2px_rgba(185,28,28,0.5)] [touch-action:manipulation] active:scale-[0.98] disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none">
                  {addTimeOff.isPending ? "Sending…" : "Send the request"}
                </button>
              </div>

              {/* My time off */}
              <div className="rounded-3xl border border-black/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
                <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-extrabold text-slate-900">My time off</div>
                {(timeOff.data ?? []).length === 0 && (
                  <p className="px-4 py-4 text-center text-xs text-slate-400">No time off requested.</p>
                )}
                <ul className="divide-y divide-slate-100">
                  {(timeOff.data ?? []).map((r) => {
                    const future = new Date(r.end_at).getTime() > Date.now();
                    const Icon = r.kind === "vacation" ? Palmtree : r.kind === "sick" ? Thermometer : Clock;
                    return (
                      <li key={r.id} className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
                            r.kind === "sick" ? "bg-red-50 text-red-600" : r.kind === "vacation" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500")}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-slate-800">{AVAIL_KIND_LABEL[r.kind] ?? r.kind}</span>
                              {statusChip(r)}
                            </span>
                            <span className="block text-[11px] text-slate-500">{tsSpanLabel(r)}{r.title ? ` — "${r.title}"` : ""}</span>
                            {r.status === "denied" && r.decision_note && (
                              <span className="block text-[11px] font-semibold text-red-600">Office: &ldquo;{r.decision_note}&rdquo;</span>
                            )}
                          </span>
                          {future && r.status !== "denied" && (
                            cancelArm === r.id ? (
                              <button type="button" disabled={cancelTimeOff.isPending}
                                onClick={() => cancelTimeOff.mutate(r.id, { onSuccess: () => setCancelArm(null) })}
                                className="shrink-0 rounded-lg bg-red-600 px-2.5 py-1.5 text-[11px] font-bold text-white [touch-action:manipulation]">
                                Really cancel
                              </button>
                            ) : (
                              <button type="button" onClick={() => setCancelArm(r.id)} aria-label="Cancel this time off"
                                className="shrink-0 rounded-lg p-1.5 text-slate-300 [touch-action:manipulation] active:bg-slate-100">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================== MANAGER VIEW (MGR-01) ==
 * Seth 2026-08-31: "a manager tab with proper permissions so that the manager
 * can see the entire board and calls and who is running what… weekends or
 * after hours". Gated by the mobile.manager capability (Settings > Users &
 * Roles per manager; dispatcher role has it; admin wildcard). Techs without
 * it never see the row, and the pane itself re-checks — no exploit path.
 * READ-ONLY: look, don't move (moves stay on the office board).
 */

interface MgrStop {
  key: string;
  tech: string;
  dept: string | null;
  time: string | null;
  window: string | null;
  status: string | null;
  customer: string | null;
  city: string | null;
  notes: string | null;
  done: boolean;
  /** MGR-02: tap-through — the call's number, account, and phone. */
  no: string | null;
  custno: string | null;
  locno: string | null;
  phone: string | null;
  address: string | null;
}

function useManagerDay(dateISO: string, enabled: boolean) {
  const techs = useAllActiveTechs().data;
  return useQuery({
    queryKey: ["m_mgr_day", dateISO, (techs ?? []).length],
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<Map<string, MgrStop[]>> => {
      const [{ data: esc }, { data: peg }] = await Promise.all([
        supabase.from("v_dispatch_full")
          .select("dispatch, tech_name, tech_dept, tpromtime, window_code, status, customer_fullname, locname, city, notes, custno, locno, customer_phone")
          .eq("appointment_date", dateISO).order("tech_name").limit(250),
        supabase.from("pegasus_dispatch")
          .select("id, dispatch_no, assigned_empno, scheduled_time, scheduled_window, status, customer_name, city, summary, notes, custno, locno, phone, address")
          .eq("scheduled_date", dateISO).in("status", ["scheduled", "completed"]).is("deleted_at", null).limit(120),
      ]);
      const nameOf = new Map((techs ?? []).map((t) => [t.empno, (t.firstname ?? "") || (t.empname ?? "") || t.empno]));
      const out = new Map<string, MgrStop[]>();
      const push = (tech: string, s: MgrStop) => {
        if (!out.has(tech)) out.set(tech, []);
        out.get(tech)!.push(s);
      };
      for (const r of (esc ?? []) as { dispatch: string; tech_name: string | null; tech_dept: string | null; tpromtime: string | null; window_code: string | null; status: string | null; customer_fullname: string | null; locname: string | null; city: string | null; notes: string | null; custno: string | null; locno: string | null; customer_phone: string | null }[]) {
        const st = (r.status ?? "").toUpperCase();
        push(r.tech_name || "Unassigned", {
          key: `e:${r.dispatch}`, tech: r.tech_name || "Unassigned", dept: r.tech_dept,
          time: r.tpromtime, window: r.window_code, status: r.status,
          customer: displayCustomerName(null, r.locname ?? r.customer_fullname),
          city: r.city, notes: (r.notes ?? "").replace(/\s+/g, " ").trim() || null,
          done: st.startsWith("C") || st.startsWith("F"),
          no: r.dispatch, custno: r.custno, locno: r.locno, phone: r.customer_phone, address: null,
        });
      }
      for (const r of (peg ?? []) as { id: string; dispatch_no: number | null; assigned_empno: string | null; scheduled_time: string | null; scheduled_window: string | null; status: string; customer_name: string | null; city: string | null; summary: string | null; notes: string | null; custno: string | null; locno: string | null; phone: string | null; address: string | null }[]) {
        const tech = r.assigned_empno ? (nameOf.get(r.assigned_empno) ?? r.assigned_empno) : "Unassigned";
        push(tech, {
          key: `p:${r.id}`, tech, dept: null,
          time: r.scheduled_time, window: r.scheduled_window, status: r.status,
          customer: r.customer_name, city: r.city,
          notes: [r.summary, r.notes].filter(Boolean).join(" — ") || null,
          done: r.status === "completed",
          no: r.dispatch_no != null ? String(r.dispatch_no) : null,
          custno: r.custno, locno: r.locno, phone: r.phone, address: r.address,
        });
      }
      for (const [, list] of out) list.sort((a, b) => (a.time ?? "99").localeCompare(b.time ?? "99"));
      return out;
    },
  });
}

function ManagerPane({ empno, techFirst, onClose }: { empno: string | null; techFirst: string | null; onClose: () => void }) {
  const canManager = useCan("mobile.manager");
  const [day, setDay] = useState(() => todayISO());
  const [mode, setMode] = useState<"board" | "done">("board");
  const byTech = useManagerDay(day, canManager);
  const dd = new Date(`${day}T12:00:00`);
  const shift = (n: number) => { const x = new Date(`${day}T12:00:00`); x.setDate(x.getDate() + n); setDay(isoDay(x)); };
  // MGR-02 (Seth 2026-09-01): tap a call for its details; jump any date.
  const [peek, setPeek] = useState<MgrStop | null>(null);
  const [acct, setAcct] = useState<CustHit | null>(null);
  const dateRef = useRef<HTMLInputElement | null>(null);
  const openPicker = () => {
    const el = dateRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else { el.focus(); el.click(); }
  };

  if (!canManager) {
    return (
      <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1] p-4">
        <EmptyCard Icon={ShieldCheck} title="Managers only" sub="Ask the office for manager access." />
        <button type="button" onClick={onClose} className="mt-3 rounded-2xl bg-slate-900 py-3.5 text-[15px] font-bold text-white [touch-action:manipulation]">Back</button>
      </div>
    );
  }

  const groups = [...(byTech.data ?? new Map<string, MgrStop[]>()).entries()]
    .map(([tech, stops]) => ({ tech, stops: mode === "done" ? stops.filter((s) => s.done) : stops }))
    .filter((g) => g.stops.length > 0)
    .sort((a, b) => a.tech.localeCompare(b.tech));
  const totals = groups.reduce((acc, g) => {
    acc.calls += g.stops.length;
    acc.done += g.stops.filter((s) => s.done).length;
    return acc;
  }, { calls: 0, done: 0 });

  const statusChip = (s: MgrStop) => {
    const st = (s.status ?? "").toUpperCase();
    if (s.done) return <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-bold text-emerald-700">Done</span>;
    if (st.startsWith("D") || st.startsWith("A") || st === "SCHEDULED") return <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-bold text-slate-600">{st.startsWith("D") ? "Dispatched" : st.startsWith("A") ? "On site" : "Scheduled"}</span>;
    if (st.startsWith("X")) return <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[9.5px] font-bold text-red-600">Cancelled</span>;
    return <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-bold text-slate-500">{s.status ?? "Booked"}</span>;
  };

  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-extrabold tracking-tight text-slate-900">Whole Board</h2>
            <div className="text-[11px] font-semibold text-slate-400">Read-only · {totals.done} of {totals.calls} done</div>
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-1.5">
          <button type="button" onClick={() => shift(-1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 [touch-action:manipulation] active:scale-95" aria-label="Previous day"><ChevronLeft className="h-4.5 w-4.5" /></button>
          <div className="min-w-0 flex-1 text-center">
            <div className="text-[14px] font-extrabold text-slate-900">{dd.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>
            {day !== todayISO() && (
              <button type="button" onClick={() => setDay(todayISO())} className="text-[11px] font-bold text-red-600 [touch-action:manipulation]">Back to today</button>
            )}
          </div>
          <button type="button" onClick={() => shift(1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 [touch-action:manipulation] active:scale-95" aria-label="Next day"><ChevronRight className="h-4.5 w-4.5" /></button>
          <button type="button" onClick={openPicker} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white [touch-action:manipulation] active:scale-95" aria-label="Pick a date">
            <CalendarDays className="h-4.5 w-4.5" />
          </button>
          <input ref={dateRef} type="date" value={day} onChange={(e) => { if (e.target.value) setDay(e.target.value); }} className="sr-only" tabIndex={-1} aria-hidden />
        </div>
        <div className="mt-2 flex gap-0.5 rounded-full bg-slate-100 p-0.5">
          {([["board", "The Board"], ["done", "Completed"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode(k)}
              className={cn("flex-1 rounded-full py-1.5 text-[12.5px] font-bold transition-all [touch-action:manipulation]",
                mode === k ? "bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.12)]" : "text-slate-500")}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {byTech.isLoading ? (
          <div className="space-y-2">
            <div className="h-24 animate-pulse rounded-3xl bg-slate-200/70" />
            <div className="h-24 animate-pulse rounded-3xl bg-slate-200/60" />
          </div>
        ) : groups.length === 0 ? (
          <EmptyCard Icon={CalendarDays} title={mode === "done" ? "Nothing completed yet" : "Nothing on the board"} />
        ) : groups.map((g) => (
          <section key={g.tech} className="overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
            <div className="flex items-center gap-2.5 border-b border-slate-100 bg-slate-50/70 px-4 py-2.5">
              <TechAvatar empno={g.tech} name={g.tech} size="sm" />
              <span className="min-w-0 flex-1 truncate text-[14px] font-extrabold text-slate-900">{g.tech}</span>
              <span className="shrink-0 text-[11px] font-bold tabular-nums text-slate-400">
                {g.stops.filter((s) => s.done).length}/{g.stops.length} done
              </span>
            </div>
            <ul className="divide-y divide-slate-100">
              {g.stops.map((s) => (
                <li key={s.key}>
                  <button type="button" onClick={() => setPeek(s)}
                    className={cn("w-full px-4 py-2.5 text-left [touch-action:manipulation] active:bg-slate-50", s.done && "opacity-60")}>
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 font-mono text-[11px] font-extrabold text-slate-500">
                        {s.time ? s.time.slice(0, 5) : (s.window ?? "ANY")}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-slate-800">{s.customer ?? "—"}</span>
                      {statusChip(s)}
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                    </div>
                    <div className="mt-0.5 truncate pl-14 text-[11px] text-slate-400">
                      {[s.no ? `#${s.no}` : null, s.city, s.notes].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/* MGR-02: the call's details, right here. */}
      {peek && (
        <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col justify-end bg-black/45" onClick={() => setPeek(null)}>
          <div className="max-h-[80dvh] overflow-y-auto rounded-t-3xl bg-white p-4 pb-[max(env(safe-area-inset-bottom),16px)] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.35)]" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-200" />
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[17px] font-extrabold leading-tight text-slate-900">{peek.customer ?? "—"}</div>
                <div className="mt-0.5 text-[12px] font-semibold text-slate-500">
                  {[peek.no ? `Call #${peek.no}` : null, peek.tech, peek.time ? peek.time.slice(0, 5) : peek.window].filter(Boolean).join(" · ")}
                </div>
                {(peek.address || peek.city) && (
                  <div className="text-[12px] text-slate-400">{[peek.address, peek.city].filter(Boolean).join(", ")}</div>
                )}
              </div>
              {statusChip(peek)}
            </div>
            {peek.notes && (
              <p className="mt-3 whitespace-pre-wrap rounded-2xl bg-slate-50 px-3.5 py-2.5 text-[13.5px] leading-relaxed text-slate-700">{peek.notes}</p>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {peek.phone && (
                <a href={`tel:${peek.phone.replace(/[^0-9]/g, "")}`}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3 text-[13px] font-bold text-white [touch-action:manipulation] active:opacity-90">
                  <Phone className="h-4 w-4" /> Call Customer
                </a>
              )}
              {peek.custno && (
                <button type="button"
                  onClick={() => { setAcct({ custno: peek.custno!, locno: peek.locno, name: peek.customer ?? peek.custno!, address: peek.address, city: peek.city, phone: peek.phone, src: "esc" }); }}
                  className={cn("flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-700 [touch-action:manipulation] active:bg-slate-50", !peek.phone && "col-span-2")}>
                  View Full Account
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {acct && <CustomerSheet hit={acct} empno={empno} techFirst={techFirst} onClose={() => setAcct(null)} />}
    </div>
  );
}

function MorePane({ isAdmin, canManager, myEmpno, myName, roleLine, viewAs, techs, openFlagCount, onFlags, onDocs, onCal, onOncall, onTime, onScore, onProposals, onMgr, onPick, onSignOut, themePref, onTheme }: {
  /** MOBILE-LOOK-01: Appearance — follow the device, or pin light / dark. */
  themePref: ThemePref;
  onTheme: (p: ThemePref) => void;
  /** TECH-EXTRAS: the phone scoreboard (same numbers as the shop TV). */
  onScore: () => void;
  /** QUOTE-PKG-01: everything this tech has presented, with live statuses. */
  onProposals: () => void;
  canManager: boolean;
  onMgr: () => void;
  isAdmin: boolean;
  myEmpno: string | null;
  myName: string | null;
  roleLine: string;
  viewAs: string | null;
  techs: { empno: string; label: string }[];
  openFlagCount: number;
  onFlags: () => void;
  onDocs: () => void;
  onCal: () => void;
  onOncall: () => void;
  onTime: () => void;
  onPick: (e: string | null) => void;
  onSignOut: () => void;
}) {
  // ONCALL-SCHED-01d: the row says when the tech is next on call.
  const myOncall = useMyOncallDays(myEmpno).data;
  const { open: oncallOpenHour } = useOncallHours();
  const oncallToday = oncallDateFor(new Date(), oncallOpenHour);
  const nextBlock = blocksOf(myOncall ?? []).find((b) => b.end >= oncallToday) ?? null;
  const nextLabel = !nextBlock ? null : nextBlock.start <= oncallToday ? "On now" : daysUntil(nextBlock.start) === 1 ? "Tomorrow" : `In ${daysUntil(nextBlock.start)} days`;
  const [picking, setPicking] = useState(false);
  // GLASS v2 (Seth 2026-09-23: "still too many colors … a more glass look
  // overall"): More reads like iOS Settings — a few grouped lists with
  // hairlines instead of nine separate pill cards, grey icons, and a chip
  // only where it says something (a count, Manager, "On now").
  const Row = ({ Icon, label, onClick, href, children }: { Icon: typeof Clock; label: string; onClick?: () => void; href?: string; children?: ReactNode }) => {
    const inner = (
      <>
        <Icon className="h-[18px] w-[18px] shrink-0 text-[var(--m-ink3)]" />
        <span className="text-[15px] font-semibold text-[var(--m-ink)]">{label}</span>
        {children}
        <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-[var(--m-ink3)] opacity-60" />
      </>
    );
    const cls = "m-row flex w-full items-center gap-3 px-4 py-3.5 text-left [touch-action:manipulation] active:bg-[var(--m-soft)]";
    return href
      ? <a href={href} className={cls}>{inner}</a>
      : <button type="button" onClick={onClick} className={cls}>{inner}</button>;
  };
  return (
    <div className="space-y-4">
      <div className="m-card flex items-center gap-3 p-4">
        {myEmpno ? (
          <TechAvatar empno={myEmpno} name={myName ?? "Tech"} size="lg" />
        ) : (
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--m-tint)] text-base font-bold text-[var(--m-tint-ink)]">
            {(myName ?? "?").slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate text-[17px] font-bold text-[var(--m-ink)]">{myName ?? "Signed in"}</div>
          <div className="text-[13px] text-[var(--m-ink3)]">{roleLine}</div>
        </div>
      </div>

      <div className="m-group">
        <Row Icon={ClipboardList} label="My Flags" onClick={onFlags}>
          {openFlagCount > 0 && <span data-meaning className="rounded-full bg-red-600 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white">{openFlagCount}</span>}
        </Row>
        {/* TIME-01 (Seth 2026-08-29): my hours, my spiff, my time off. */}
        <Row Icon={Clock} label="My Timesheet & Time Off" onClick={onTime} />
        {/* ONCALL-SCHED-01d (Seth 2026-09-15: "it would be nice to have it under the
            more tab… see what days they are on-call and a bit more info"). */}
        <Row Icon={Moon} label="On-Call Calendar" onClick={onOncall}>
          {nextLabel && <span className={cn("m-chip", nextLabel === "On now" && "m-chip-warn")}>{nextLabel}</span>}
        </Row>
        {canManager && (
          <Row Icon={ShieldCheck} label="Whole Board" onClick={onMgr}>
            <span className="m-chip">Manager</span>
          </Row>
        )}
      </div>

      {/* LIB-02: the "find it fast" tools — the install week and the company
          library + Ask Pegasus (Seth 2026-08-28). */}
      <div className="m-group">
        <Row Icon={Trophy} label="Scoreboard" onClick={onScore} />
        <Row Icon={FileText} label="My Proposals" onClick={onProposals} />
        <Row Icon={CalendarDays} label="Install Calendar" onClick={onCal} />
        <Row Icon={FileText} label="Library & Ask Pegasus" onClick={onDocs} />
        <Row Icon={Warehouse} label="Warehouse Queue" href="/warehouse" />
      </div>

      {/* MOBILE-LOOK-01: Appearance — light by default (34ga); a tech can pin
          dark or follow the iPad's own setting. */}
      <div className="m-card p-4">
        <div className="text-[15px] font-semibold text-[var(--m-ink)]">Appearance</div>
        <div className="text-[13px] text-[var(--m-ink3)]">{themePref === "system" ? "Follows your device — dark at night, light by day." : themePref === "dark" ? "Always dark." : "Always light — easiest to read in sunlight."}</div>
        <div className="m-seg mt-3" role="radiogroup" aria-label="Appearance">
          {([["light", "Light"], ["dark", "Dark"], ["system", "Device"]] as const).map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={themePref === k} onClick={() => onTheme(k)} className={cn(themePref === k && "on")}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="m-card p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-[var(--m-ink)]">View as Tech</div>
              <div className="truncate text-[13px] text-[var(--m-ink3)]">{viewAs ? `Currently: ${techs.find((t) => t.empno === viewAs)?.label ?? viewAs}` : "See any tech's day"}</div>
            </div>
            <button type="button" onClick={() => setPicking((v) => !v)} className="m-btn m-btn-soft h-9 shrink-0 rounded-full px-4 text-[14px]">
              {picking ? "Close" : "Choose"}
            </button>
          </div>
          {picking && (
            <div className="m-group mt-3 max-h-64 overflow-y-auto !shadow-none bg-[var(--m-soft)]">
              <button type="button" onClick={() => { onPick(null); setPicking(false); }} className="m-row w-full px-4 py-3 text-left text-[15px] font-semibold text-[var(--m-ink2)] active:bg-[var(--m-soft2)]">
                Back to me
              </button>
              {techs.map((t) => (
                <button key={t.empno} type="button" onClick={() => { onPick(t.empno); setPicking(false); }}
                  className="m-row w-full px-4 py-3 text-left text-[15px] font-semibold text-[var(--m-ink)] active:bg-[var(--m-soft2)]">
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="m-group">
        <Row Icon={Monitor} label="Open Full Pegasus (Desktop View)" href="/" />
        <button type="button" onClick={onSignOut} className="m-row flex w-full items-center gap-3 px-4 py-3.5 text-left [touch-action:manipulation] active:bg-[var(--m-soft)]">
          <LogOut className="h-[18px] w-[18px] shrink-0 text-[var(--m-bad)]" />
          <span className="text-[15px] font-semibold text-[var(--m-bad)]">Sign Out</span>
        </button>
      </div>
    </div>
  );
}

/* ==================================================== WORK & INVOICE (M2) ==
 * The run-a-call heart: write-up → parts used (search / AI suggest / accepted
 * package) → PLUS member pricing auto-applied → my hours → save draft or
 * complete. Rides the SAME engine as the office invoice builder
 * (useSaveServiceInvoice): one draft per call that the truck and the office
 * round-trip; ESC calls key on the dispatch NUMBER, Pegasus calls complete
 * natively. Every keystroke stashes locally so a dead zone never eats work.
 */

const money$ = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface WkStash {
  n: string; l: BuilderLine[]; h?: number; c?: Record<string, boolean>; w?: boolean;
  /** Equipment step: units marked worked-on, explicit "no equipment" skip, current step. */
  e?: WorkedUnit[]; es?: boolean; st?: number;
  /** PO / job number typed or carried on this call. */
  p?: string;
  /** Internal (office-only) note + manual discount. */
  i?: string; dz?: { label: string; mode: "$" | "%"; value: string };
  /** INVOICE-LIVE-01: the server version this copy is based on, and whether
   *  every edit in it already reached the server (then the server copy wins
   *  on reopen — the office may have changed it since). */
  v?: number | null; sy?: boolean;
  t: number;
}

/* The call, in STEPS (Seth 2026-08-27: "the boxes kind of now flow together…
 * I was hoping some of these would be like different screens — flow like
 * steps"). One thing per screen, tap any step to jump, nothing is lost
 * between them — it's all the same stash. */
// Order per Seth 2026-08-28: diagnose → OPTIONS (quote/present) → PARTS (what
// actually lands on THIS invoice — an approved repair flows FORWARD into it).
const CALL_STEPS = [
  { key: "equip", label: "Equipment" },
  { key: "work", label: "The Work" },
  { key: "options", label: "Options" },
  { key: "parts", label: "Parts" },
  { key: "wrap", label: "Wrap Up" },
] as const;

/** Step 1 — WHAT are you working on. In the path on purpose: the record of
 *  which unit a visit touched was too easy to skip when it lived in a side
 *  tab (Seth 2026-08-27). Selection lands on the field row live. */
function EquipStep({ s, empno, techFirst, workedEq, eqSkipped, onToggle, onSkip }: {
  s: Stop;
  empno: string | null;
  techFirst: string | null;
  workedEq: WorkedUnit[];
  eqSkipped: boolean;
  onToggle: (u: WorkedUnit) => void;
  onSkip: () => void;
}) {
  const custno = s.custno ?? undefined;
  const rows = useCustomerEquipment(custno, s.locno);
  const aiOverrides = useAiEquipmentOverrides(rows.data ?? []).data;
  const { data: lifecycleOverrides } = useEquipmentOverrides(custno, s.locno);
  const pegUnits = (useEquipmentAt(s.custno, s.locno).data ?? []).filter((e) => e.src === "peg");
  const [addOpen, setAddOpen] = useState(false);

  const merged = useMemo(() => {
    const data = rows.data ?? [];
    if (!lifecycleOverrides || lifecycleOverrides.size === 0) return data;
    return data.map((r) => {
      const ov = lifecycleOverrides.get(r.counter);
      if (!ov) return r;
      return { ...r, mfg: ov.mfg ?? r.mfg, model: ov.model ?? r.model, serial: ov.serial ?? r.serial, install_date: ov.install_date ?? r.install_date, eq_type: ov.eq_type ?? r.eq_type };
    });
  }, [rows.data, lifecycleOverrides]);
  const view = useMemo(() => buildEquipmentView(merged, aiOverrides, lifecycleOverrides), [merged, aiOverrides, lifecycleOverrides]);

  // PROPOSAL-EQUIPMENT-02 r2: the same row every staff screen uses — the kind and the room lead,
  // the make + model read as one line, the serial sits beside it as data (Seth: "the technicians
  // need to see serial #" — six rooftops in three model pairs are told apart by nothing else).
  // `label` is what the invoice and the record keep ("BRYANT 581JP04A115A2A"), unchanged.
  const nice = (v: string | null | undefined) => { const t = (v ?? "").trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : null; };
  const choices: Array<{ k: string; label: string; kind: string | null; kindLabel: string; loc: string | null; brand: string | null; model: string | null; serial: string | null; year: number | null; note: string | null }> = [
    ...pegUnits.map((e) => ({
      k: `peg-${e.counter}`,
      label: [e.mfg, e.model].filter(Boolean).join(" ") || e.eq_type || "Unit",
      kind: e.eq_type ?? null, kindLabel: categoryLabelFor(e.eq_type ?? "other"), loc: nice(e.equip_loc),
      brand: makeFromMfg(e.mfg), model: (e.model ?? "").trim() || null, serial: (e.serial ?? "").trim() || null,
      year: installDateInfo(e.install_date)?.year ?? null, note: "field-added",
    })),
    ...[...view.systems, ...view.auxiliary].map((e) => ({
      k: `esc-${e.counter}`,
      label: [e.mfg, e.model].filter(Boolean).join(" ") || e.categoryLabel || "Unit",
      kind: e.category, kindLabel: e.categoryLabel, loc: nice(e.equip_loc),
      brand: makeFromMfg(e.mfg), model: (e.model ?? "").trim() || null, serial: (e.serial ?? "").trim() || null,
      year: e.installYear, note: null,
    })),
  ];
  const onIt = (k: string) => workedEq.some((x) => x.k === k);

  return (
    <>
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <h4 className="flex items-center gap-2 text-[15px] font-extrabold text-slate-900">What are you working on? <InfoTip label="Where this goes">Tap each unit this visit touches — it goes on the invoice and the customer&apos;s record.</InfoTip></h4>
        {s.part?.unit && workedEq.some((x) => x.k === s.part!.unit!.k) && (
          <p className="mt-1.5 flex items-center gap-1.5 rounded-xl bg-amber-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-amber-800">
            <Package className="h-3.5 w-3.5 shrink-0 text-amber-700" /> Pre-marked — the unit the {s.part.summary.toLowerCase()} is for.
          </p>
        )}
        {rows.isLoading ? (
          <div className="mt-3 h-24 animate-pulse rounded-2xl bg-slate-100" />
        ) : choices.length === 0 ? (
          <p className="mt-3 rounded-2xl bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500">
            Nothing on file at this address. Add the unit below, or mark the call as no-equipment.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {choices.map((u) => (
              <li key={u.k}>
                <UnitPickRow kind={u.kind} label={u.kindLabel} loc={u.loc} brand={u.brand} model={u.model} serial={u.serial} year={u.year} note={u.note}
                  on={onIt(u.k)} tone="work" size="md" onToggle={() => onToggle({ k: u.k, label: u.label })} />
              </li>
            ))}
          </ul>
        )}
        {s.custno && (
          <button type="button" onClick={() => setAddOpen(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 py-3 text-[13px] font-bold text-slate-600 active:bg-slate-50">
            <Camera className="h-4 w-4 text-red-600" /> Add a unit — photograph the data plate
          </button>
        )}
      </section>

      <button type="button" onClick={onSkip}
        className={cn("flex w-full items-center justify-center gap-2 rounded-2xl border py-3 text-[12.5px] font-bold",
          eqSkipped ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 active:bg-slate-50")}>
        {eqSkipped ? <><Check className="h-4 w-4" /> No equipment on this call — tap to undo</> : "No equipment on this call"}
      </button>

      {addOpen && <AddUnitSheet s={s} empno={empno} techFirst={techFirst} onClose={() => setAddOpen(false)} />}
    </>
  );
}

function WorkInvoice({ s, field, empno, techFirst, onFlag, onWrapped }: {
  s: Stop;
  field?: FieldStatusRow;
  empno: string | null;
  techFirst: string | null;
  onFlag: (kind: FlagKind) => void;
  onWrapped: () => void;
}) {
  const isPeg = s.kind === "pegasus";
  const dispatchId = isPeg ? s.ref : null;
  const dispatchNo = /^\d+$/.test(s.no) ? parseInt(s.no, 10) : null;
  const stashKey = `pegasus.m.wk.${s.ref}`;

  const draft = useDispatchDraftInvoice(dispatchId, dispatchId ? null : dispatchNo);
  const member = useMemberDiscount(s.custno);
  const pkg = useAcceptedServiceQuote(s.custno, s.locno);
  const custEmail = useCustomerEmail(s.custno, s.locno);
  const rates = useTechRates().data;
  const save = useSaveServiceInvoice();
  const suggest = useSuggestParts();
  const enqueue = useEnqueueOutbox();
  // Sell-PLUS-during-invoicing (same engine as the office invoice): plan
  // catalog + per-term discount tiers + the one-shot enroll write on Complete.
  const planBases = (usePlanPriceCatalog().data ?? []).filter((p) => p.kind === "base" && p.active);
  // PROPOSAL-EQUIPMENT-02: the second-system discount the per-system price uses (Settings → plan pricing).
  const secondDiscInv = Number(usePlanCatalog().data?.config?.second_system_discount ?? 0.3);
  const discountTiers = usePlanDiscounts().data;
  const sellPlan = useSellPlan();
  const callQuote = useCallQuote(s.no);
  // PLAN-DIAG-ENROLL: the plan they said yes to on the present screen (or were
  // offered on this call's quote) — one tap puts it on this bill, attached to that
  // offer so nothing is sold twice.
  const customerOffers = useCustomerPlanOffers(s.custno).data;
  const pendingOffer = useMemo(() => {
    const rows = customerOffers ?? [];
    return rows.find((o) => offerState(o) === "accepted_unpaid" && !o.offer_invoice_id)
      ?? rows.find((o) => ["offered", "viewed"].includes(offerState(o)) && o.offered_on === "repair" && (!o.quote_dispatch_no || String(o.quote_dispatch_no) === String(s.no)))
      ?? null;
  }, [customerOffers, s.no]);
  const [enrollOffer, setEnrollOffer] = useState<PlanOfferRow | null>(null);
  const qc2 = useQueryClient();
  // INVOICE-LIVE-01 (Seth 2026-09-24: "can the office see real time their
  // invoices? Can they edit on the fly ... reflected as well on their mobile
  // app?"). The iPad saves as the tech works (2.5 s after the last change);
  // the office's edits land here within seconds; a save built on an older
  // copy is refused and the tech picks — theirs or mine — never a silent
  // overwrite. The local stash still guards dead zones.
  useRealtimeRefresh(["pegasus_job_invoice", "pegasus_job_invoice_line"], [["dispatch_draft_invoice"]]);
  const serverVer = useRef<number | null>(null);
  const lastSig = useRef<string | null>(null);
  const seedAt = useRef(0);
  const inflight = useRef<Promise<void> | null>(null);
  const autosaveTimer = useRef<number | null>(null);
  const [sync, setSync] = useState<{ state: "idle" | "saving" | "saved" | "offline"; at: number }>({ state: "idle", at: 0 });
  const [conflict, setConflict] = useState<{ by: string; via: "field" | "office" | null; version: number } | null>(null);
  const conflictRef = useRef(conflict);
  conflictRef.current = conflict;
  const [remoteNote, setRemoteNote] = useState<string | null>(null);

  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<BuilderLine[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [saved, setSaved] = useState<null | "draft" | "done">(null);
  const [confirmDone, setConfirmDone] = useState(false);
  const [suggestions, setSuggestions] = useState<PartSuggestion[] | null>(null);
  const [pkgAdded, setPkgAdded] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSeeded, setEmailSeeded] = useState(false);
  const [emailStatus, setEmailStatus] = useState<null | "sending" | "queued" | "held" | "failed" | "skipped">(null);
  const [doneInvoiceId, setDoneInvoiceId] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<{ baseId: string; baseName: string; term: number; price: number; pct: number; desc: string; covers?: PlanOfferCover[] | null; systems?: number } | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [planBuilderOpen, setPlanBuilderOpen] = useState(false);
  const [firstCheckDone, setFirstCheckDone] = useState<boolean>(() => detectChecklist(s.serviceType, s.notes) != null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [packetQuoteId, setPacketQuoteId] = useState<string | null>(null);
  const [turnoverOpen, setTurnoverOpen] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [signOpen, setSignOpen] = useState(false);
  // Visit checklist — the booked service type's OWN template (office-editable
  // in Settings → Service Types) decides first; legacy name-detection second.
  const ckTemplate = useChecklistTemplate(s);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  // Sometimes the PLUS discount just shouldn't apply (warranty work, a promo
  // price, office said so) — one tap removes it for THIS invoice only, one
  // tap brings it back (Seth 2026-08-27).
  const [mdWaived, setMdWaived] = useState(false);
  // PO / job number — booked on the dispatch, mined from notes, or typed here.
  const [poNum, setPoNum] = useState("");
  // Office-only note — never prints, never emails (Seth 2026-08-28).
  const [internalNote, setInternalNote] = useState("");
  // Manual discount ($ or %) with its own label on the invoice.
  const [disc, setDisc] = useState<{ label: string; mode: "$" | "%"; value: string }>({ label: "", mode: "$", value: "" });
  const [waiveNudgeDismissed, setWaiveNudgeDismissed] = useState(false);
  // Flags born on THIS call — badges on the buttons + an editable list below.
  const callFlags = useCallFlags(s.ref);
  const partFlags = (callFlags.data ?? []).filter((f) => f.lane_key === "order_parts");
  const returnFlags = (callFlags.data ?? []).filter((f) => f.lane_key === "return_visit");
  const openFollowups = useCustomerFollowups(s.custno ?? undefined).data ?? [];
  // The guided call flow: Equipment → The Work → Parts → Options → Wrap Up.
  const [step, setStep] = useState(0);
  // Parts only ticks after the tech has actually LOOKED at it (or added real work).
  const [partsSeen, setPartsSeen] = useState(false);
  useEffect(() => { if (step === 3) setPartsSeen(true); }, [step]);
  const [workedEq, setWorkedEq] = useState<WorkedUnit[]>([]);
  const [eqSkipped, setEqSkipped] = useState(false);
  const setWorked = useSetWorkedEquipment();
  // PARTS-EQUIP-01: the unit seeded from the part ticket lands on the field row
  // as soon as that row exists, so the customer's record shows it worked on
  // even if the tech never touches the Equipment step.
  const partSeeded = useRef(false);
  useEffect(() => {
    if (!partSeeded.current || !field?.id || field.worked_equipment?.length || workedEq.length === 0) return;
    partSeeded.current = false;
    setWorked.mutate({ fieldId: field.id, units: workedEq });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.id, workedEq.length]);
  const toggleUnit = (u: WorkedUnit) => {
    setEqSkipped(false);
    const next = workedEq.some((x) => x.k === u.k) ? workedEq.filter((x) => x.k !== u.k) : [...workedEq, u];
    setWorkedEq(next);
    if (field?.id) setWorked.mutate({ fieldId: field.id, units: next });
  };
  const skipEquip = () => {
    const next = !eqSkipped;
    setEqSkipped(next);
    if (next) {
      setWorkedEq([]);
      if (field?.id) setWorked.mutate({ fieldId: field.id, units: [] });
    }
  };
  const eqDone = workedEq.length > 0 || eqSkipped;

  // Time is the arrival stamp doing the work — techs never type hours (Seth
  // 2026-08-27: "my time should be automatically logging"). Quarter-hour
  // rounding, 15-min floor; the geofenced punch-out tightens this at M3.
  const onSiteMs = field?.on_site_at ? new Date(field.on_site_at).getTime() : null;
  const autoHours = onSiteMs
    ? Math.max(0.25, Math.round(((Date.now() - onSiteMs) / 3.6e6) * 4) / 4)
    : 0;

  // A membership line only means something alongside a live enroll choice —
  // reseeding one without it would bill the plan and never activate it. Drop
  // it; the violet card re-adds it in two taps.
  const noPlanLines = (ls: BuilderLine[]) => ls.filter((l) => !/— PLUS membership \(\d+ yr\)$/.test(l.description));
  // The booking's quoted price seeds the bill — the tech starts from what the
  // office promised, never from a blank $0 (Seth 2026-08-27 ×2).
  const quotedSeed = (): BuilderLine[] =>
    s.quoted != null && s.quoted > 0
      ? [{ kind: "fee", description: s.serviceType ? `${s.serviceType} — as quoted` : "Service visit — as quoted at booking", qty: 1, unitPrice: s.quoted, print: true }]
      : [];
  // INVOICE-LIVE-01: put the SERVER copy of the invoice on screen — first
  // open, the office's live edits, or "use their version". Invoice fields
  // only; the tech's step, equipment picks and checklist ticks stay theirs
  // (ticks come back from the baked write-up).
  const applyDraft = (d: DraftInvoice) => {
    // A saved draft bakes the checklist + equipment blocks into its notes.
    // Un-bake them: restore the ticks into state and strip the text, so
    // finishing later never appends them twice. Title-agnostic — any
    // service type's template round-trips, not just the legacy three.
    const t = d.tech_notes ?? "";
    const m = t.match(/([^\n]{1,80}) — completed:\n([\s\S]*)$/);
    if (m && /^\s*✓/.test(m[2])) {
      const restored: Record<string, boolean> = {};
      for (const ln of m[2].split("\n")) {
        const item = ln.replace(/^\s*✓\s*/, "").trim();
        if (item) restored[item] = true;
      }
      if (Object.keys(restored).length) setChecks(restored);
    }
    setNotes(
      t.replace(/\s*[^\n]{1,80} — completed:\n\s*✓[\s\S]*$/, "")
        .replace(/\s*Equipment serviced:[^\n]*$/, "")
        .trimEnd(),
    );
    setPoNum(d.po_number ?? s.po ?? minePo(s.notes) ?? "");
    setInternalNote(d.internal_notes ?? "");
    // Discount lines re-derive on save; a manual one comes back as the
    // discount control (the member discount recomputes itself).
    const manual = d.lines.find((l) => l.unitPrice < 0 && !/member discount|repair discount/i.test(l.description));
    setDisc(manual
      ? { label: /^discount$/i.test(manual.description.trim()) ? "" : manual.description, mode: "$", value: String(Math.abs((Number(manual.unitPrice) || 0) * (Number(manual.qty) || 1))) }
      : { label: "", mode: "$", value: "" });
    const ls = noPlanLines(d.lines.filter((l) => l.unitPrice >= 0));
    setLines(ls.length ? ls : quotedSeed());
    serverVer.current = d.version;
    seedAt.current = Date.now();
  };

  // Seed once. An UNSENT local stash wins (it's the tech's newest work — a
  // dead zone ate the autosave); a stash whose edits all reached the server
  // yields to the server copy (the office may have changed it since). Waits
  // for any refetch to settle so a remount never seeds off a stale cache —
  // that's the "undo erased my notes" bug (Seth 2026-08-27).
  useEffect(() => {
    if (seeded || draft.isLoading || draft.isFetching) return;
    setSeeded(true);
    seedAt.current = Date.now();
    serverVer.current = draft.data?.version ?? 0;
    // Worked-on equipment: the field row is the shared truth (another device,
    // or the office, may have it); the stash overrides below when present.
    // PARTS-EQUIP-01: a part-install visit starts with the part's unit marked —
    // the tech chose it when he flagged the part; nobody asks twice.
    if (field?.worked_equipment?.length) setWorkedEq(field.worked_equipment);
    else if (s.part?.unit) { setWorkedEq([s.part.unit]); partSeeded.current = true; }
    try {
      const raw = localStorage.getItem(stashKey);
      if (raw) {
        const w = JSON.parse(raw) as WkStash;
        // The tech's own screen state always comes back.
        if (w.c && typeof w.c === "object") setChecks(w.c);
        if (w.w) setMdWaived(true);
        if (Array.isArray(w.e)) setWorkedEq(w.e);
        if (w.es) setEqSkipped(true);
        if (typeof w.st === "number" && w.st >= 0 && w.st < CALL_STEPS.length) setStep(w.st);
        if (w.sy && draft.data) {
          // Everything in the stash already reached the server — take the
          // server's copy of the invoice (it may hold the office's edits).
          applyDraft(draft.data);
          return;
        }
        setNotes(w.n ?? "");
        const ls = noPlanLines(Array.isArray(w.l) ? w.l : []);
        setLines(ls.length ? ls : quotedSeed());
        setPoNum(typeof w.p === "string" && w.p ? w.p : (s.po ?? minePo(s.notes) ?? ""));
        if (typeof w.i === "string") setInternalNote(w.i);
        if (w.dz && typeof w.dz === "object") setDisc({ label: w.dz.label ?? "", mode: w.dz.mode === "%" ? "%" : "$", value: w.dz.value ?? "" });
        // Unsent edits are based on the version the stash saw; if the office
        // saved since, the first autosave asks the tech which copy wins.
        if (typeof w.v === "number") serverVer.current = w.v;
        seedAt.current = 0;
        lastSig.current = "unsent"; // unsent work: autosave it, don't baseline it away
        return;
      }
    } catch { /* fall through to server draft */ }
    if (draft.data) {
      applyDraft(draft.data);
      return;
    }
    setPoNum(s.po ?? minePo(s.notes) ?? "");
    setLines(quotedSeed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.isLoading, draft.isFetching, seeded]);

  // The email field pre-fills from the record (Pegasus capture first, ESC file second).
  useEffect(() => {
    if (emailSeeded || custEmail.isLoading) return;
    setEmailSeeded(true);
    if (custEmail.data?.email) setEmail(custEmail.data.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custEmail.isLoading, emailSeeded]);

  // Stash on every change — offline-tolerant by construction. It also says
  // whether everything in it already reached the server (sy) and which
  // server version it's built on (v) — rewritten after every autosave.
  useEffect(() => {
    if (!seeded) return;
    try {
      const sy = sigRef.current != null && sigRef.current === lastSig.current;
      localStorage.setItem(stashKey, JSON.stringify({ n: notes, l: lines, c: checks, w: mdWaived, e: workedEq, es: eqSkipped, st: step, p: poNum, i: internalNote, dz: disc, v: serverVer.current, sy, t: Date.now() } satisfies WkStash));
    } catch { /* storage full — the save path still works */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, lines, checks, mdWaived, workedEq, eqSkipped, step, poNum, internalNote, disc, seeded, stashKey, sync.at]);

  const subtotal = invoiceTotal(lines);
  // Office parity (PLAN-DISC-SCOPE-01, Seth 2026-10-28): the member % touches REPAIR
  // PARTS AND LABOR only — not fees, the plan line, equipment, accessories / IAQ,
  // consumables or materials. lib/memberPricing decides from the pricebook category,
  // the same rule the office Invoice Builder applies; the plan line's own text is
  // excluded by the rule, so nothing is subtracted twice.
  const lineItemCats = useItemCategories(lines.map((l) => l.item_id)).data;
  const repairSub = memberEligibleSubtotal(lines, lineItemCats);
  const effPct = mdWaived ? 0 : member.data?.isMember ? (member.data.pct ?? 0) : (enroll?.pct ?? 0);
  const md = effPct > 0
    ? Math.round(Math.max(0, repairSub) * effPct * 100) / 100
    : 0;
  const discAmt = (() => {
    const v = Number(disc.value) || 0;
    if (v <= 0) return 0;
    const base = Math.max(0, subtotal - md);
    return Math.round((disc.mode === "%" ? (base * Math.min(v, 100)) / 100 : Math.min(v, base)) * 100) / 100;
  })();
  const total = Math.max(0, subtotal - md - discAmt);

  const addLine = (l: BuilderLine) => setLines((ls) => [...ls, l]);
  const patchLine = (i: number, p: Partial<BuilderLine>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  const rmLine = (i: number) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const planPriceForTerm = (b: { p1: number | null; p2: number | null; p3: number | null; p5: number | null; p10: number | null }, term: number): number => {
    const m: Record<number, number | null> = { 1: b.p1, 2: b.p2, 3: b.p3, 5: b.p5, 10: b.p10 };
    return Number(m[term] ?? 0);
  };
  // PROPOSAL-EQUIPMENT-02: the plan sold on the invoice names the systems it covers and is priced
  // per system (every extra one at the Settings second-system discount) — the same rule as the offer.
  const confirmEnroll = (base: (typeof planBases)[number], term: number, offer?: PlanOfferRow | null, covers?: PlanOfferCover[] | null) => {
    const systems = covers && covers.length ? planSystemsCount(covers) : (offer?.systems ?? 1);
    const mult = planSystemsMultiplier(systems, secondDiscInv);
    const price = offer && Number(offer.total) > 0 ? Number(offer.total) : Math.round(planPriceForTerm(base, term) * mult * 100) / 100;
    const pct = discountTiers && discountTiers.length ? repairPctForTerm(discountTiers, term) : (member.data?.entryPct ?? 0);
    const desc = `${base.name} — PLUS membership (${term} yr${systems > 1 ? ` · ${systems} systems` : ""})`;
    setEnroll({ baseId: base.id, baseName: base.name, term, price, pct, desc, covers: covers ?? offer?.covers ?? null, systems });
    setEnrollOffer(offer ?? null);
    addLine({ kind: "custom", description: desc, qty: 1, unitPrice: price, print: true });
    setEnrollOpen(false);
  };
  const cancelEnroll = () => {
    if (enroll) {
      const desc = enroll.desc;
      setLines((ls) => ls.filter((l) => l.description !== desc));
    }
    setEnrollOffer(null);
    setEnroll(null);
  };

  const runSuggest = () => {
    const body = [notes, s.notes].filter(Boolean).join("\n");
    if (!body.trim()) return;
    suggest.mutate(body, { onSuccess: (r) => setSuggestions(r) });
  };

  const validEmail = /\S+@\S+\.\S+/.test(email.trim());

  const sendInvoiceEmail = async (invoiceId: string) => {
    const to = email.trim();
    if (!/\S+@\S+\.\S+/.test(to)) { setEmailStatus("skipped"); return; }
    setEmailStatus("sending");
    try {
      // The row is the truth for the number + total (discount lines land server-side).
      const { data: inv } = await supabase
        .from("pegasus_job_invoice")
        .select("invoice_no, dispatch_no, total")
        .eq("id", invoiceId)
        .single();
      const r = inv as { invoice_no: string | null; dispatch_no: string | null; total: number | null } | null;
      const invoiceNo = r?.invoice_no || `SH-${r?.dispatch_no || invoiceId.slice(0, 6).toUpperCase()}`;
      const amt = Number(r?.total) || total;
      // Every undecided proposal on this call (repair AND/OR replacement) rides
      // the ONE invoice email as direct tokenized links — no portal or account
      // needed. One email per visit, never a pile (Seth's spam worry).
      // PROPOSAL-FLOW-02: sent ones AND drafts the tech presented in person (a
      // never-shown draft stays private).
      const { data: oqs } = await supabase
        .from("pegasus_quote")
        .select("id, quote_type, token, status")
        .eq("dispatch_no", s.no)
        .eq("is_practice", false)
        .or("status.in.(sent,viewed),and(status.eq.draft,presented_at.not.is.null)")
        .order("created_at", { ascending: false });
      const seenTypes = new Set<string>();
      const proposals: Array<{ label: string; url: string }> = [];
      const ridingIds: string[] = [];
      for (const q of (oqs ?? []) as Array<{ id: string; quote_type: string; token: string | null }>) {
        if (!q.token || seenTypes.has(q.quote_type)) continue;
        seenTypes.add(q.quote_type);
        ridingIds.push(q.id);
        proposals.push({
          label: q.quote_type === "install" ? "Review your system replacement proposal" : "Review your repair options",
          url: `${window.location.origin}/proposal?t=${q.token}`,
        });
      }
      const st = await enqueue.mutateAsync({
        automation_key: "invoice_send",
        dedupe_key: `invsend:${invoiceId}:${Date.now()}`,
        custno: s.custno,
        locno: s.locno,
        customer_name: s.name,
        channel: "email",
        to_address: to,
        subject: `Your Sedgwick Heating invoice ${invoiceNo}`,
        body: buildInvoiceEmailHtml({
          invoiceNo, total: amt, balance: amt,
          // A completed tune-up announces itself in one line — the itemized
          // ✓ list lives on the invoice, not in the email (Seth 2026-08-27).
          note: (() => {
            const done = ckTemplate ? ckTemplate.items.filter((i) => checks[i]).length : 0;
            return done > 0 && ckTemplate ? `${ckTemplate.title} completed — ${done} checklist items performed. The full detail is on your invoice.` : "";
          })(),
          proposals,
        }),
        trigger_kind: "invoice",
        trigger_ref: invoiceId,
      });
      setEmailStatus(st);
      // PROPOSAL-FLOW-02: a proposal link that rode the invoice email was sent — say so on each one.
      if (st === "queued") for (const id of ridingIds) void markProposalSent(id, "email", to).catch(() => { /* the email still goes */ });
    } catch {
      setEmailStatus("failed");
    }
  };

  // Ticked checklist items ride the write-up onto the invoice — proof of the
  // visit's value, zero extra typing.
  const checklistText = (() => {
    if (!ckTemplate) return "";
    const done = ckTemplate.items.filter((i) => checks[i]);
    if (done.length === 0) return "";
    return `\n\n${ckTemplate.title} — completed:\n${done.map((i) => `✓ ${i}`).join("\n")}`;
  })();
  // The worked-on units print too — the invoice says WHICH furnace.
  const equipText = workedEq.length
    ? `\n\nEquipment serviced: ${workedEq.map((x) => x.label).join(" · ")}`
    : "";

  const mdLabel = member.data?.isMember
    ? (member.data.planLabel ? `${member.data.planLabel} — repair discount` : "PLUS member discount")
    : enroll ? `New PLUS member discount (${Math.round(enroll.pct * 100)}%)` : "PLUS member discount";

  // INVOICE-LIVE-01 — what the office sees: the invoice exactly as Finish
  // would write it (write-up + equipment + checklist, lines + discounts),
  // minus the finish-only parts (email, signature, hours, completing).
  const livePayload = useMemo(() => {
    const { allLines, total: tot } = buildSaveLines({
      lines, memberDiscount: md, memberDiscountLabel: mdLabel,
      discount: discAmt > 0 ? discAmt : undefined,
      discountLabel: discAmt > 0 ? (disc.label.trim() || "Discount") : undefined,
    });
    return {
      header: {
        dispatch_id: dispatchId,
        dispatch_no: dispatchNo == null ? null : String(dispatchNo),
        custno: s.custno, locno: s.locno, type: "service",
        technician: techFirst,
        completed_date: todayISO(),
        tech_notes: (notes.trim() + equipText + checklistText).trim() || null,
        internal_notes: internalNote.trim() || null,
        po_number: poNum.trim() || null,
        total: tot,
      } as Record<string, unknown>,
      lines: allLines,
    };
  }, [lines, md, mdLabel, discAmt, disc.label, dispatchId, dispatchNo, s.custno, s.locno, techFirst, notes, equipText, checklistText, internalNote, poNum]);
  const liveSig = JSON.stringify(livePayload);
  const sigRef = useRef<string | null>(null);
  sigRef.current = seeded ? liveSig : null;
  const payloadRef = useRef(livePayload);
  payloadRef.current = livePayload;

  /** Push the current invoice to the server now (cancels the pending timer). */
  const flushSave = async (): Promise<void> => {
    if (autosaveTimer.current) { window.clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    if (inflight.current) { try { await inflight.current; } catch { /* reported below */ } }
    if (conflictRef.current || !sigRef.current || sigRef.current === lastSig.current) return;
    const sig = sigRef.current;
    const p = payloadRef.current;
    setSync({ state: "saving", at: Date.now() });
    const attempt = async (expected: number | null, retried: boolean): Promise<void> => {
      try {
        const r = await saveInvoiceRpc({
          dispatchId, dispatchNo, expectedVersion: expected, header: p.header, lines: p.lines,
          via: "field", actor: techFirst ?? null,
        });
        serverVer.current = r.version;
        lastSig.current = sig;
        setSync({ state: "saved", at: Date.now() });
        void qc2.invalidateQueries({ queryKey: ["pegasus_customer_invoices"] });
      } catch (e) {
        if (isInvoiceConflict(e)) {
          // Our own earlier save (a flush on the way out, another tab) — ours
          // is newer by definition; carry on from its version.
          if (!retried && e.via === "field" && e.by === (techFirst ?? "")) { serverVer.current = e.version; return attempt(e.version, true); }
          setConflict({ by: e.by, via: e.via, version: e.version });
          setSync({ state: "idle", at: Date.now() });
          return;
        }
        setSync({ state: "offline", at: Date.now() });
      }
    };
    const run = attempt(serverVer.current, false).finally(() => { inflight.current = null; });
    inflight.current = run;
    await run;
  };

  // Autosave 2.5 s after the last change. Right after the invoice lands on
  // screen (first open or a reload), the first couple of seconds only set the
  // baseline — the member discount and checklist load in behind the seed and
  // must not read as the tech's edits.
  useEffect(() => {
    if (!seeded || saved === "done") return;
    if (lastSig.current === null || (seedAt.current && Date.now() - seedAt.current < 2500)) {
      lastSig.current = liveSig;
      return;
    }
    if (liveSig === lastSig.current || conflict) return;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => { void flushSave(); }, 2500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSig, seeded, conflict, saved]);
  // Leaving the call (Back, another tab) pushes anything unsaved right away.
  useEffect(() => () => { if (sigRef.current && sigRef.current !== lastSig.current && !conflictRef.current) void flushSave(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  // An "offline" autosave retries on its own every 20 s.
  useEffect(() => {
    if (sync.state !== "offline") return;
    const t = window.setTimeout(() => { void flushSave(); }, 20000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sync.state, sync.at]);

  // Someone else saved a newer copy (the office, most often). Nothing unsaved
  // here → it just appears, with a note saying who. Unsaved edits here → the
  // tech picks.
  useEffect(() => {
    const d = draft.data;
    if (!seeded || !d || saved === "done" || conflict) return;
    if (serverVer.current != null && d.version <= serverVer.current) return;
    if (inflight.current) return;
    if (liveSig === lastSig.current) {
      applyDraft(d);
      setRemoteNote(`Updated by ${saverLabel(d.updated_by, d.updated_via)}`);
      window.setTimeout(() => setRemoteNote(null), 7000);
    } else if (!(d.updated_via === "field" && d.updated_by === (techFirst ?? ""))) {
      setConflict({ by: d.updated_by ?? "", via: d.updated_via, version: d.version });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.data?.version, seeded]);

  const takeTheirs = async () => {
    const r = await draft.refetch();
    if (r.data) applyDraft(r.data);
    lastSig.current = null; // re-baseline on the fresh copy
    setConflict(null);
  };
  const keepMine = () => {
    if (conflict) serverVer.current = conflict.version;
    setConflict(null);
    window.setTimeout(() => { void flushSave(); }, 0);
  };

  const doSave = async (complete: boolean) => {
    if (autosaveTimer.current) { window.clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    // Let an autosave already on the wire land first, so Finish carries its version.
    if (inflight.current) { try { await inflight.current; } catch { /* reported by the autosave */ } }
    save.mutate(
      {
        dispatchId,
        dispatchNo,
        custno: s.custno,
        locno: s.locno,
        technician: techFirst,
        // The office owns the salesperson — the truck never clears its pick.
        salesperson: undefined,
        completedDate: todayISO(),
        techNotes: (notes.trim() + equipText + checklistText).trim(),
        memberDiscount: md,
        memberDiscountLabel: mdLabel,
        expectedVersion: serverVer.current,
        via: "field",
        actor: techFirst ?? null,
        labor: autoHours > 0 && empno
          ? [{ empno, name: techFirst ?? empno, hours: autoHours, rate: rates?.get(empno) || 0 }]
          : [],
        lines,
        completeDispatch: complete && isPeg,
        recipientEmail: complete && validEmail ? email.trim() : null,
        saveEmailToRecord: complete && validEmail && email.trim() !== (custEmail.data?.email ?? ""),
        signatureUrl: complete ? signature : undefined,
        poNumber: poNum.trim() || null,
        internalNotes: internalNote.trim() || undefined,
        discount: discAmt > 0 ? discAmt : undefined,
        discountLabel: discAmt > 0 ? (disc.label.trim() || "Discount") : undefined,
      },
      {
        onError: (e) => {
          if (isInvoiceConflict(e)) setConflict({ by: e.by, via: e.via, version: e.version });
        },
        onSuccess: ({ id: invoiceId, version }) => {
          serverVer.current = version;
          lastSig.current = sigRef.current;
          try { localStorage.removeItem(stashKey); } catch { /* fine */ }
          setConfirmDone(false);
          if (complete) {
            setDoneInvoiceId(invoiceId);
            setSaved("done");
            void sendInvoiceEmail(invoiceId);
            // The membership sells only on Complete — a draft can't double-enroll.
            // PLAN-DIAG-ENROLL: written as ACCEPTED on this invoice; it activates when
            // the invoice is paid (the card at the door, a check, the ESC posting).
            if (enroll && s.custno) {
              const m = Number(todayISO().slice(5, 7));
              void sellPlan
                .mutateAsync({
                  custno: s.custno, locno: s.locno, term: enroll.term, baseId: enroll.baseId,
                  baseName: enroll.baseName, total: enroll.price,
                  invoiceId, via: "tech",
                  quoteId: enrollOffer?.offer_quote_id ?? callQuote.data?.id ?? null,
                  saleId: enrollOffer?.sale_id ?? null,
                  firstCheck: { category: m >= 4 && m <= 9 ? "cooling" : "heating", done: firstCheckDone },
                  covers: enroll.covers ?? null,
                })
                .catch((e) => console.error("membership enroll failed:", e));
            }
            onWrapped(); // stamps the field state → next call surfaces on Today
          } else {
            setSaved("draft");
            window.setTimeout(() => setSaved(null), 4000);
          }
        },
      },
    );
  };

  // Finishing requires the write-up AND the equipment step answered — either
  // units marked or an explicit "no equipment" (Seth 2026-08-27: require it).
  const canFinish = notes.trim().length > 0 && eqDone;

  if (saved === "done") {
    return (
      <div className="overflow-hidden rounded-3xl bg-emerald-600 text-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <div className="p-5 text-center">
          <Check className="mx-auto h-8 w-8" />
          <div className="mt-1.5 text-lg font-extrabold">Call wrapped ✓</div>
          <div className="mt-0.5 text-2xl font-extrabold tabular-nums">{money$(total)}</div>
          <div className="mt-1.5 text-xs leading-relaxed text-emerald-100">
            {isPeg
              ? "The invoice is finished — the office sees it right now."
              : "The office sees your write-up, parts and time right now — the ESC invoice finalizes from it until cutover."}
          </div>
        </div>
        <div className="space-y-2 bg-emerald-700/40 p-3.5">
          {enroll && (
            <div className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> PLUS membership activated
            </div>
          )}
          {signature && (
            <div className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold">
              <PenLine className="h-3.5 w-3.5 shrink-0" /> Signed — on the invoice
            </div>
          )}
          <div className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold">
            {emailStatus === "sending" ? (<><Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> Emailing the invoice…</>)
              : emailStatus === "queued" ? (<><Mail className="h-3.5 w-3.5 shrink-0" /> Invoice emailed to {email.trim()}</>)
              : emailStatus === "held" ? (<><Mail className="h-3.5 w-3.5 shrink-0" /> Email held — comms is in testing; the office has it in the outbox</>)
              : emailStatus === "failed" ? (<><Mail className="h-3.5 w-3.5 shrink-0" /> Email didn't queue — the office can send it from the invoice</>)
              : (<><Mail className="h-3.5 w-3.5 shrink-0" /> No email on this one — the office can send it later</>)}
          </div>
          {doneInvoiceId && (
            <a href={`/invoices/${doneInvoiceId}`} target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-1.5 rounded-xl bg-white py-2.5 text-[13px] font-bold text-emerald-700 active:opacity-90">
              View the invoice →
            </a>
          )}
          <p className="text-center text-[10px] leading-snug text-emerald-100/80">
            Collecting payment on the truck (tap to pay) arrives with the app-store build.
          </p>
        </div>
      </div>
    );
  }

  // A tick means the TECH did something there — the seeded quoted fee alone
  // never checks Parts (Seth 2026-08-28: "I don't want them bypassing that
  // screen because they think it's already checked").
  const stepDone = [
    eqDone,
    notes.trim().length > 0 || Object.values(checks).some(Boolean),
    !!callQuote.data || !!enroll,
    partsSeen || lines.some((l) => l.kind !== "fee"),
    false,
  ];

  return (
    <>
      {/* The step rail — one thing per screen; tap any step to jump. */}
      <div className="sticky top-0 z-10 -mx-3 border-b border-slate-200/80 bg-[#f6f5f1]/95 px-3 pb-1.5 pt-1 backdrop-blur">
        <div className="flex gap-1">
          {CALL_STEPS.map((cs, i) => {
            const on = step === i;
            const done = stepDone[i];
            return (
              <button key={cs.key} type="button" onClick={() => setStep(i)}
                className={cn("flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5", on ? "bg-slate-900 text-white" : "text-slate-500 active:bg-slate-200")}>
                <span className={cn("flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-extrabold",
                  on ? "bg-white/20 text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500")}>
                  {done && !on ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="text-[9.5px] font-bold leading-none">{cs.label}</span>
              </button>
            );
          })}
        </div>
        {/* INVOICE-LIVE-01: one quiet line — the office sees what's here. */}
        <div className="mt-1 flex h-4 items-center justify-center gap-1 text-[10.5px] font-semibold text-slate-400" aria-live="polite">
          {remoteNote ? (
            <span className="text-sky-700">{remoteNote} — it&apos;s on your screen now</span>
          ) : sync.state === "saving" ? (
            <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
          ) : sync.state === "saved" ? (
            <><Check className="h-3 w-3 text-emerald-600" /> Saved · the office sees this invoice</>
          ) : sync.state === "offline" ? (
            <span className="text-amber-700">No signal — kept on this iPad, sends when you&apos;re back online</span>
          ) : null}
        </div>
      </div>

      {conflict && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3.5">
          <p className="text-[13.5px] font-bold text-amber-900">{saverLabel(conflict.by, conflict.via)} changed this invoice while you were working.</p>
          <p className="mt-0.5 text-[12px] leading-snug text-amber-800">
            Use their version (your last changes are dropped), or keep yours (it replaces theirs).
          </p>
          <div className="mt-2.5 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => void takeTheirs()} className="m-btn m-btn-primary">Use Their Version</button>
            <button type="button" onClick={keepMine} className="m-btn m-btn-soft">Keep Mine</button>
          </div>
        </div>
      )}

      {/* STEP 1 · Equipment — what are you working on. */}
      {step === 0 && (
        <EquipStep s={s} empno={empno} techFirst={techFirst}
          workedEq={workedEq} eqSkipped={eqSkipped} onToggle={toggleUnit} onSkip={skipEquip} />
      )}

      {/* STEP 2 · The work — checklist, write-up, photos. */}
      {step === 1 && (
        <>
          {ckTemplate && (
            <ChecklistCard title={ckTemplate.title} items={ckTemplate.items} checks={checks} onToggle={(item) => setChecks((c) => ({ ...c, [item]: !c[item] }))} />
          )}

          <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
            <h4 className="text-[15px] font-bold tracking-tight text-slate-900">What You Found &amp; Did</h4>
            <NotesEditor
              value={notes}
              onChange={setNotes}
              rows={4}
              placeholder="Found… Replaced… Tested… (tap Dictate to speak)"
              className="mt-2"
            />
            <p className="mt-1 text-[11px] text-slate-400">Prints on the customer&apos;s invoice.</p>
          </section>

          <VisitPhotos s={s} techFirst={techFirst} />
        </>
      )}

      {/* STEP 4 · Parts & materials — what actually lands on THIS invoice; an
          approved option's lines are one tap away here. */}
      {step === 3 && (
      <>
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <div className="flex items-center justify-between">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Parts &amp; Materials Used</h4>
          <button
            type="button"
            onClick={runSuggest}
            disabled={suggest.isPending || (!notes.trim() && !s.notes)}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700 active:bg-slate-200 disabled:opacity-40"
          >
            {suggest.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            Suggest from my notes
          </button>
        </div>

        {/* accepted package one-tap */}
        {pkg.data && !pkgAdded && (
          <button
            type="button"
            onClick={() => {
              for (const l of pkg.data!.lines) {
                if ((l.kind ?? "") === "fee") continue; // diagnostic seeds separately
                addLine({ kind: "part", description: l.description, qty: l.qty, unitPrice: l.unitPrice, print: true, item_id: l.item_id });
              }
              setPkgAdded(true);
            }}
            className="mt-2 flex w-full items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-left active:bg-emerald-100"
          >
            <Check className="h-4 w-4 shrink-0 text-emerald-600" />
            <span className="min-w-0 flex-1 text-xs font-bold text-emerald-800">
              They accepted the {pkg.data.tier ?? "repair"} option — {money$(pkg.data.total)}. Tap to add those lines.
            </span>
          </button>
        )}

        {/* AI suggestions */}
        {suggestions && (
          <div className="mt-2 space-y-1.5 rounded-2xl bg-slate-50 p-2.5">
            <div className="flex items-center justify-between px-1">
              <span className="text-[12.5px] font-semibold text-slate-600">Suggested by Pegasus — confirm</span>
              <button type="button" onClick={() => setSuggestions(null)} className="rounded p-0.5 text-slate-400 active:bg-white" aria-label="Dismiss suggestions"><X className="h-3.5 w-3.5" /></button>
            </div>
            {suggestions.length === 0 && <p className="px-1 pb-1 text-[11px] text-slate-500">Nothing billable found in the notes.</p>}
            {suggestions.map((p, i) => (
              <div key={i} className="flex items-center gap-2 rounded-xl bg-white px-2.5 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-bold text-slate-800">{p.description}</div>
                  <div className="truncate text-[10px] text-slate-400">
                    {[p.part_number ? `#${p.part_number}` : null, p.matched ? money$(p.unitPrice) : "not in pricebook — price it", p.reason].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    addLine({ kind: "part", description: p.description, qty: p.qty || 1, unitPrice: p.unitPrice, print: true, item_id: p.item_id });
                    setSuggestions((cur) => (cur ?? []).filter((_, idx) => idx !== i));
                  }}
                  className="shrink-0 rounded-lg bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white active:opacity-90"
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        )}

        {/* the lines */}
        {lines.length > 0 && (
          <ul className="mt-2 divide-y divide-slate-100">
            {lines.map((l, i) => (
              <li key={i} className="py-2.5">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className={cn("text-[13.5px] font-bold leading-snug", l.waived ? "text-slate-400" : "text-slate-800")}>
                      {l.description || "Line"}
                      {l.waived && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-amber-700">Waived</span>}
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="inline-flex items-center overflow-hidden rounded-xl border border-slate-200">
                        <button type="button" onClick={() => patchLine(i, { qty: Math.max(1, l.qty - 1) })} className="px-2.5 py-1 text-slate-500 active:bg-slate-100" aria-label="Less"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="min-w-[2ch] text-center text-sm font-bold tabular-nums">{l.qty}</span>
                        <button type="button" onClick={() => patchLine(i, { qty: l.qty + 1 })} className="px-2.5 py-1 text-slate-500 active:bg-slate-100" aria-label="More"><Plus className="h-3.5 w-3.5" /></button>
                      </span>
                      <span className="relative">
                        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={l.unitPrice}
                          onChange={(e) => patchLine(i, { unitPrice: Number(e.target.value) || 0 })}
                          className="w-24 rounded-xl border border-slate-200 py-1 pl-5 pr-2 text-right text-sm font-bold tabular-nums focus:border-red-500 focus:outline-none"
                          aria-label="Price"
                        />
                      </span>
                      <span className={cn("ml-auto text-sm font-extrabold tabular-nums", l.waived ? "text-slate-400 line-through" : "text-slate-900")}>{money$(l.qty * l.unitPrice)}</span>
                    </div>
                    {/* WAIVE-01: keep the line, strike the charge — the customer
                        SEES the $109 given instead of it silently vanishing. */}
                    <button type="button" onClick={() => patchLine(i, { waived: !l.waived })}
                      className={cn("mt-1 text-[11px] font-bold underline-offset-2 active:underline", l.waived ? "text-emerald-700" : "text-slate-400")}>
                      {l.waived ? "Charge it again" : "Waive this charge (shows crossed out, $0)"}
                    </button>
                  </div>
                  <button type="button" onClick={() => rmLine(i)} className="mt-0.5 rounded-lg p-1.5 text-slate-300 active:bg-slate-100 active:text-red-500" aria-label="Remove line">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <PartSearchAdd onAdd={addLine} />
      </section>
      {/* PLAN-ON-PARTS (Seth 2026-09-14): what lands on THIS invoice includes
          the plan being sold — so the plan card sits under the parts. */}
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <div className="flex items-center justify-between">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Maintenance Plan</h4>
          <span className="text-[10px] font-semibold text-slate-400">
            {member.data?.isMember ? "member — discount applies below" : enroll ? "selling today — on this invoice" : "sell one here — it goes on this invoice"}
          </span>
        </div>
        <div className="mt-2">
      {/* Maintenance plan — a money conversation (Seth 2026-08-27), and the
          plan is a LINE on this invoice, so it lives with the parts (Seth
          2026-09-14: "a service plan builder on the parts tab"). A member's
          discount applies itself; a non-member is enrolled here and the
          discount hits this invoice. The full custom builder (with the
          describe-it AI) is one tap deeper. */}
      {member.data?.isMember ? (
        <button type="button" onClick={() => setMdWaived((v) => !v)}
          className={cn("flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left", mdWaived ? "border-slate-200 bg-slate-50" : "border-emerald-200 bg-emerald-50")}>
          {/* the switch — unmistakably on/off */}
          <span className={cn("relative h-7 w-12 shrink-0 rounded-full transition-colors", mdWaived ? "bg-slate-300" : "bg-emerald-500")}>
            <span className={cn("absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all", mdWaived ? "left-0.5" : "left-[22px]")} />
          </span>
          <span className="min-w-0 flex-1">
            <span className={cn("block text-[13px] font-extrabold leading-tight", mdWaived ? "text-slate-500" : "text-emerald-900")}>
              {member.data.planLabel} discount {mdWaived ? "OFF" : "ON"}
            </span>
            <span className={cn("block text-[11px] leading-snug", mdWaived ? "text-slate-400" : "text-emerald-700")}>
              {mdWaived
                ? "Removed for this invoice only — tap to put it back."
                : md > 0
                  ? `Saving ${money$(md)} — ${Math.round(member.data.pct * 100)}% off the repair work on this bill`
                  : `${Math.round(member.data.pct * 100)}% off repair lines — applies when you add a repair`}
            </span>
          </span>
          {!mdWaived && md > 0 && <span className="shrink-0 text-sm font-extrabold tabular-nums text-emerald-700">−{money$(md)}</span>}
        </button>
      ) : enroll ? (
        <div className="rounded-2xl border border-black/[0.08] bg-slate-50 p-3.5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="min-w-0 flex-1 text-xs font-bold text-amber-900">
              {enroll.baseName} · {enroll.term} yr — {money$(enroll.price)} on this invoice · {Math.round(enroll.pct * 100)}% off today's repair{md > 0 ? ` (−${money$(md)})` : ""}
            </span>
            <button type="button" onClick={cancelEnroll} className="shrink-0 rounded p-1 text-amber-500/70 active:bg-white" aria-label="Remove the plan">
              <X className="h-4 w-4" />
            </button>
          </div>
          <button type="button" onClick={() => setFirstCheckDone((v) => !v)} className="mt-2 flex w-full items-center gap-2 text-left">
            <span className={cn("flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-2", firstCheckDone ? "border-emerald-600 bg-emerald-600" : "border-slate-300")}>
              {firstCheckDone && <Check className="h-3 w-3 text-white" />}
            </span>
            <span className="text-[11px] font-semibold text-amber-800">Today's visit counts as their first plan check-up</span>
          </button>
        </div>
      ) : planBases.length > 0 && !member.isLoading && pendingOffer ? (
        <button type="button"
          onClick={() => { const b = planBases.find((x) => x.id === pendingOffer.base_id) ?? planBases[0]; if (b) confirmEnroll(b, pendingOffer.term, pendingOffer); }}
          className={cn("flex w-full items-center gap-2.5 rounded-2xl border px-3.5 py-3 text-left", offerState(pendingOffer) === "accepted_unpaid" ? "border-amber-300 bg-amber-50 active:bg-amber-100" : "border-emerald-200 bg-emerald-50 active:bg-emerald-100")}>
          <ShieldCheck className={cn("h-4 w-4 shrink-0", offerState(pendingOffer) === "accepted_unpaid" ? "text-amber-600" : "text-emerald-600")} />
          <span className="min-w-0 flex-1">
            <span className={cn("block text-xs font-bold", offerState(pendingOffer) === "accepted_unpaid" ? "text-amber-900" : "text-emerald-900")}>
              {offerState(pendingOffer) === "accepted_unpaid" ? "They said yes to the plan" : "Plan offered on the quote"} · {pendingOffer.term} yr · {offerMoney(pendingOffer.total)}
            </span>
            <span className={cn("block text-[11px]", offerState(pendingOffer) === "accepted_unpaid" ? "text-amber-700/80" : "text-emerald-700/80")}>Tap to put it on this invoice — it activates when the invoice is paid</span>
          </span>
          <Plus className={cn("h-4 w-4 shrink-0", offerState(pendingOffer) === "accepted_unpaid" ? "text-amber-600" : "text-emerald-600")} />
        </button>
      ) : planBases.length > 0 && !member.isLoading ? (
        <button type="button" onClick={() => setEnrollOpen(true)}
          className="flex w-full items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left active:bg-slate-50">
          <ShieldCheck className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-bold text-amber-900">Not on a maintenance plan</span>
            <span className="block text-[11px] text-amber-700/80">
              {member.data && member.data.entryPct > 0 && repairSub > 0
                ? `A plan today would save them ${money$(Math.round(repairSub * member.data.entryPct * 100) / 100)} on this repair`
                : "Seasonal check-ups, priority service, member repair pricing"}
            </span>
          </span>
          <Plus className="h-4 w-4 shrink-0 text-amber-600" />
        </button>
      ) : null}
        </div>
      </section>
      </>
      )}

      {/* STEP 3 · Options for the customer — repair proposal and/or a
          replacement quote. One, both, or neither; never forced. */}
      {step === 2 && (
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        <div className="flex items-center justify-between">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Give them options</h4>
          {callQuote.data && (
            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold",
              callQuote.data.status === "accepted" ? "bg-emerald-100 text-emerald-700"
              : callQuote.data.status === "declined" ? "bg-red-100 text-red-600"
              : "bg-amber-100 text-amber-700")}>
              {callQuote.data.status === "accepted" ? "accepted ✓"
                : callQuote.data.status === "declined" ? "declined"
                : callQuote.data.status === "sent" || callQuote.data.status === "viewed" ? "sent — awaiting decision"
                : callQuote.data.presented_at ? "presented — thinking it over"
                : "saved — not shown yet"}
            </span>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2.5">
          <button type="button" onClick={() => setQuoteOpen(true)}
            className="flex flex-col items-start gap-1 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
            <ClipboardList className="h-4 w-4 text-slate-500" />
            <span className="text-[13px] font-bold text-slate-800">Repair Options</span>
            <span className="text-[10.5px] leading-snug text-slate-400">Up to 3 choices</span>
          </button>
          <button type="button" onClick={() => setReplaceOpen(true)}
            className="flex flex-col items-start gap-1 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
            <BadgeDollarSign className="h-4 w-4 text-slate-500" />
            <span className="text-[13px] font-bold text-slate-800">Replacement Quote</span>
            <span className="text-[10.5px] leading-snug text-slate-400">Good / Better / Best</span>
          </button>
        </div>
        {callQuote.data?.status === "accepted" && pkg.data && !pkgAdded && (
          <button type="button" onClick={() => setStep(3)} className="mt-2 w-full text-left text-[11px] font-semibold text-emerald-700 active:opacity-80">
            Accepted — the accepted lines are in the Parts step →
          </button>
        )}
        {callQuote.data?.status === "accepted" && callQuote.data.quote_type === "install" && (
          <button type="button" onClick={() => setPacketQuoteId(callQuote.data!.id)}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3 text-[13px] font-bold text-white active:opacity-90">
            {callQuote.data.job_id ? "Open the install packet →" : "They accepted — start the install packet →"}
          </button>
        )}
        {callQuote.data && ["sent", "viewed"].includes(callQuote.data.status) && callQuote.data.quote_type === "install" && (
          <button type="button" onClick={() => setPacketQuoteId(callQuote.data!.id)}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-700 active:bg-slate-50">
            Undecided? Prepare the install packet while on site →
          </button>
        )}
      </section>
      )}

      {/* STEP 5 · Wrap up — plan & discount by the money (Seth), the bill,
          sign-off, email, finish. */}
      {step === 4 && (
      <>
      {/* WAIVE-01 nudge: they bought the SYSTEM — the diagnostic usually rides
          free into the install. One tap waives it + tells the story in the notes. */}
      {callQuote.data?.status === "accepted" && callQuote.data.quote_type === "install" && !waiveNudgeDismissed &&
        lines.some((l) => l.kind === "fee" && !l.waived) && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3.5">
          <p className="text-xs font-bold leading-snug text-emerald-900">
            They&apos;re replacing the system — waive the diagnostic toward the install?
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button"
              onClick={() => {
                setLines((ls) => ls.map((l) => (l.kind === "fee" && !l.waived ? { ...l, waived: true } : l)));
                setNotes((n) => `${n.trim()}${n.trim() ? "\n" : ""}Customer chose to replace the system — diagnostic waived toward the install.`);
                setWaiveNudgeDismissed(true);
              }}
              className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-xs font-bold text-white active:opacity-90">
              Waive (shows crossed out)
            </button>
            <button type="button" onClick={() => setWaiveNudgeDismissed(true)}
              className="rounded-xl border border-emerald-200 bg-white px-3 py-2.5 text-xs font-bold text-emerald-700 active:bg-emerald-100">
              Keep the charge
            </button>
          </div>
        </div>
      )}

      {/* The money line + finish */}
      <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
        {!eqDone && (
          <button type="button" onClick={() => setStep(0)}
            className="mb-2.5 w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left text-[11px] font-semibold leading-snug text-amber-800 active:bg-amber-100">
            No equipment marked for this visit — tap to pick what you worked on (or mark "no equipment").
          </button>
        )}
        {/* The write-up, reviewed where the money is — edit lives in The Work. */}
        <button type="button" onClick={() => setStep(1)}
          className="mb-2.5 w-full rounded-2xl bg-slate-50/80 px-3 py-2.5 text-left active:bg-slate-100">
          <span className="flex items-center justify-between">
            <span className="text-[12.5px] font-semibold text-slate-600">Your write-up (prints for the customer)</span>
            <span className="text-[10px] font-bold text-red-600">Edit ✎</span>
          </span>
          <span className={cn("mt-1 block text-xs leading-relaxed", notes.trim() ? "text-slate-700" : "italic text-slate-400")}>
            {notes.trim() ? (notes.length > 220 ? `${notes.slice(0, 220)}…` : notes) : "Nothing written yet — tap to write up the call."}
          </span>
        </button>

        {/* Office-only — never prints, never emails. */}
        <label className="mb-2.5 block">
          <span className="text-[12.5px] font-semibold text-slate-600">
            Internal note for the office <span className="font-semibold normal-case tracking-normal">(the customer never sees this)</span>
          </span>
          <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2}
            placeholder="Water heater near end of life — follow up in the spring…"
            className="mt-1 w-full rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none" />
        </label>

        <label className="mb-2.5 block">
          <span className="text-[12.5px] font-semibold text-slate-600">
            PO / job number <span className="font-semibold normal-case tracking-normal">(optional — prints on the invoice)</span>
          </span>
          <input
            value={poNum}
            onChange={(e) => setPoNum(e.target.value)}
            placeholder="From a vendor or property manager — e.g. PO-48211"
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[14px] focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25"
          />
        </label>
        {lines.length > 0 && (
          <ul className="mb-2.5 divide-y divide-slate-100 rounded-2xl bg-slate-50/70 px-3">
            {lines.map((l, i) => (
              <li key={i} className="flex items-baseline justify-between gap-2 py-2 text-xs">
                <span className={cn("min-w-0 flex-1 truncate font-semibold", l.waived ? "text-slate-400" : "text-slate-700")}>
                  {l.qty > 1 ? `${l.qty}× ` : ""}{l.description || "Line"}{l.waived ? " · waived" : ""}
                </span>
                <span className={cn("shrink-0 font-bold tabular-nums", l.waived ? "text-slate-400 line-through" : "text-slate-800")}>{money$(l.qty * l.unitPrice)}</span>
              </li>
            ))}
          </ul>
        )}
        {/* Manual discount — label + $ / % toggle, its own line on the invoice. */}
        <div className="mb-2.5 flex items-center gap-1.5">
          <input value={disc.label} onChange={(e) => setDisc((d0) => ({ ...d0, label: e.target.value }))}
            placeholder="Discount (optional) — e.g. Veteran"
            className="min-w-0 flex-1 rounded-xl border border-slate-200 px-2.5 py-2 text-xs focus:border-red-500 focus:outline-none" />
          <button type="button" onClick={() => setDisc((d0) => ({ ...d0, mode: d0.mode === "$" ? "%" : "$" }))}
            className="shrink-0 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs font-extrabold text-slate-600 active:bg-slate-100">
            {disc.mode}
          </button>
          <input type="number" inputMode="decimal" value={disc.value}
            onChange={(e) => setDisc((d0) => ({ ...d0, value: e.target.value }))}
            placeholder="0"
            className="w-16 shrink-0 rounded-xl border border-slate-200 px-2 py-2 text-right text-xs font-bold tabular-nums focus:border-red-500 focus:outline-none" />
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-slate-500"><span>Work &amp; materials</span><span className="tabular-nums">{money$(subtotal)}</span></div>
          {md > 0 && <div className="flex justify-between font-semibold text-emerald-700"><span>{member.data?.planLabel}</span><span className="tabular-nums">−{money$(md)}</span></div>}
          {discAmt > 0 && <div className="flex justify-between font-semibold text-red-600"><span>{disc.label.trim() || "Discount"}{disc.mode === "%" ? ` (${disc.value}%)` : ""}</span><span className="tabular-nums">−{money$(discAmt)}</span></div>}
          <div className="flex justify-between border-t border-slate-100 pt-1.5 text-base font-extrabold text-slate-900"><span>Customer total</span><span className="tabular-nums">{money$(total)}</span></div>
        </div>

        {saved === "draft" && (
          <p className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-center text-xs font-bold text-emerald-700">
            Draft saved — the office sees it instantly ✓
          </p>
        )}

        {!confirmDone ? (
          <div className="mt-3 space-y-2">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => (canFinish ? setConfirmDone(true) : setStep(eqDone ? 1 : 0))}
              className={cn("flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-[15px] font-bold text-white", canFinish ? "bg-slate-900 active:opacity-90" : "bg-slate-400 active:opacity-90")}
            >
              <Check className="h-4 w-4" />
              {canFinish
                ? `Finish This Call — ${money$(total)}`
                : !eqDone
                  ? "Equipment first — tap to mark what you worked on"
                  : "Write up the call first — tap to go there"}
            </button>
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => void doSave(false)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 text-[13px] font-bold text-slate-600 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save draft — finish later"}
            </button>
          </div>
        ) : (
          <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
            <p className="text-xs font-bold text-slate-700">Review before finalizing:</p>
            <ul className="mt-1.5 space-y-1 text-xs text-slate-600">
              <li className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> Write-up done</li>
              <li className="flex items-center gap-1.5">
                {lines.length > 0 ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-slate-300" />}
                {lines.length > 0 ? `${lines.length} line${lines.length === 1 ? "" : "s"} · ${money$(total)}` : "No charges (covered / no-charge call)"}
              </li>
              <li className="flex items-center gap-1.5">
                {autoHours > 0 ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-slate-300" />}
                {autoHours > 0
                  ? `${autoHours} hr on site — logged automatically from your arrival`
                  : "Time logs from your arrival stamp (none on this call)"}
              </li>
              {enroll && (
                <li className="flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                  PLUS membership · {enroll.term} yr — on this invoice, active when it is paid
                </li>
              )}
            </ul>

            {/* Sign-off — optional, one tap, renders right on the invoice. */}
            <button type="button" onClick={() => setSignOpen(true)}
              className={cn("mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl border py-2.5 text-xs font-bold",
                signature ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 active:bg-slate-100")}>
              <PenLine className="h-3.5 w-3.5" />
              {signature ? "Signed ✓ — tap to redo" : "Customer sign-off (optional)"}
            </button>

            {/* Email the invoice — prefilled when we have one on file. */}
            <div className="mt-2.5">
              <label className="text-[12.5px] font-semibold text-slate-600">
                Email the invoice{custEmail.data?.onFile ? " · on file" : ""} <span className="font-semibold normal-case tracking-normal">(leave blank to skip)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@email.com"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[14px] focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25"
              />
              {email.trim().length > 0 && !validEmail && (
                <p className="mt-1 text-[10px] font-semibold text-amber-600">That doesn't look like an email — fix it or clear it.</p>
              )}
            </div>

            <div className="mt-2.5 flex gap-2">
              <button type="button" onClick={() => setConfirmDone(false)} className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-bold text-slate-600 active:bg-slate-100">
                ← Keep working
              </button>
              <button
                type="button"
                disabled={save.isPending || (email.trim().length > 0 && !validEmail)}
                onClick={() => void doSave(true)}
                className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-xs font-bold text-white active:opacity-90 disabled:opacity-60"
              >
                {save.isPending ? "Finishing…" : isPeg ? "Complete call + invoice" : "Send to office & wrap"}
              </button>
            </div>
            {!isPeg && (
              <p className="mt-1.5 text-center text-[10px] leading-snug text-slate-400">
                ESC call — the office finishes the invoice in ESC.
              </p>
            )}
          </div>
        )}
      </section>
      </>
      )}

      {/* Flags — Part Needed / Return Visit / Follow Up live on the Parts step
          ONLY (Seth 2026-09-14: "should be on the parts tab, not also on The
          Work"). No hours section on purpose: time derives from the arrival
          stamp — techs never type it. NOT on Wrap Up — payment/invoicing is
          the LAST thing (Seth 2026-08-28). */}
      {step === 3 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={() => onFlag("order_parts")}
              className="relative flex items-center justify-center gap-1.5 rounded-2xl border border-red-200 bg-red-50 py-3 text-[12.5px] font-bold text-red-700 active:bg-red-100">
              <Package className="h-4 w-4" /> Part Needed
              {partFlags.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-extrabold text-white ring-2 ring-[#f5f7fa]">{partFlags.length}</span>
              )}
            </button>
            <button type="button" onClick={() => onFlag("return_visit")}
              className="relative flex items-center justify-center gap-1.5 rounded-2xl border border-slate-200 bg-white py-3 text-[12.5px] font-bold text-slate-700 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
              <RotateCcw className="h-4 w-4" /> Return Visit
              {returnFlags.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-slate-700 px-1 text-[10px] font-extrabold text-white ring-2 ring-[#f5f7fa]">{returnFlags.length}</span>
              )}
            </button>
            {/* FOLLOW-UP-01: "call them in March about the water heater" — a dated
                promise that comes due on Today (the office's, or his own phone). */}
            <button type="button" onClick={() => onFlag("follow_up")}
              className="relative flex items-center justify-center gap-1.5 rounded-2xl border border-amber-200 bg-amber-50 py-3 text-[12.5px] font-bold text-amber-800 active:bg-amber-100">
              <BellRing className="h-4 w-4" /> Follow Up
              {openFollowups.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-600 px-1 text-[10px] font-extrabold text-white ring-2 ring-[#f5f7fa]">{openFollowups.length}</span>
              )}
            </button>
          </div>
          <CallFlagsList flags={callFlags.data ?? []} />
        </>
      )}

      {/* Step navigation — Back / Next; the Wrap Up step's own buttons finish. */}
      {step < CALL_STEPS.length - 1 && (
        <div className="flex gap-2">
          {step > 0 && (
            <button type="button" onClick={() => setStep(step - 1)}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-[14px] font-bold text-slate-600 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
              ←
            </button>
          )}
          <button type="button" onClick={() => setStep(step + 1)}
            className="flex-1 rounded-2xl bg-slate-900 py-3.5 text-[14px] font-bold text-white active:opacity-90">
            Next: {CALL_STEPS[step + 1].label} →
          </button>
        </div>
      )}

      {/* full-screen flows launched from the workspace */}
      {enrollOpen && (
        <PlusPlanSheet
          bases={planBases}
          priceForTerm={planPriceForTerm}
          pctForTerm={(t) => (discountTiers && discountTiers.length ? repairPctForTerm(discountTiers, t) : (member.data?.entryPct ?? 0))}
          repairSub={repairSub}
          custno={s.custno} locno={s.locno} secondDisc={secondDiscInv}
          onPick={(b, t, covers) => confirmEnroll(b, t, null, covers)}
          onCustom={() => { setEnrollOpen(false); setPlanBuilderOpen(true); }}
          onClose={() => setEnrollOpen(false)}
        />
      )}
      {planBuilderOpen && s.custno && (
        <PlanBuilderModal
          custno={s.custno}
          locno={s.locno}
          onClose={() => {
            setPlanBuilderOpen(false);
            // A plan sold in the builder makes them a member RIGHT NOW — the
            // banner and discount pick it up on refetch.
            qc2.invalidateQueries({ queryKey: ["member_discount"] });
            qc2.invalidateQueries({ queryKey: ["membership_status"] }); qc2.invalidateQueries({ queryKey: ["quote_membership_status"] });
            qc2.invalidateQueries({ queryKey: ["m_membership"] });
          }}
        />
      )}
      {quoteOpen && (
        <RepairQuoteWizard
          s={s}
          empno={empno}
          techFirst={techFirst}
          workedEq={workedEq}
          onClose={() => setQuoteOpen(false)}
          // QUOTE-PKG-01: one wizard feel — flip to Replace without losing
          // either draft (each keeps its own stash).
          onSwitch={() => { setQuoteOpen(false); setReplaceOpen(true); }}
        />
      )}
      {replaceOpen && (
        <ReplaceQuoteWizard
          s={s}
          empno={empno}
          techFirst={techFirst}
          onAccepted={(qid) => { setReplaceOpen(false); setPacketQuoteId(qid); }}
          onLead={() => { setReplaceOpen(false); setTurnoverOpen(true); }}
          onClose={() => setReplaceOpen(false)}
          onSwitch={() => { setReplaceOpen(false); setQuoteOpen(true); }}
        />
      )}
      {packetQuoteId && (
        <InstallPacket s={s} techFirst={techFirst} empno={empno} quoteId={packetQuoteId} onClose={() => setPacketQuoteId(null)} />
      )}
      {turnoverOpen && <TurnoverSheet s={s} empno={empno} techFirst={techFirst} onClose={() => setTurnoverOpen(false)} />}
      {signOpen && (
        <SignaturePad
          customerName={s.name}
          total={total}
          onDone={(d) => { setSignature(d); setSignOpen(false); }}
          onClose={() => setSignOpen(false)}
        />
      )}
    </>
  );
}

/** Pick the plan + term, see the price and what it saves on TODAY'S repair —
 *  the tech's version of the office invoice's "Add PLUS membership". */
function PlusPlanSheet<B extends { id: string; name: string; p1: number | null; p2: number | null; p3: number | null; p5: number | null; p10: number | null }>({ bases, priceForTerm, pctForTerm, repairSub, onPick, onCustom, onClose, custno, locno, secondDisc }: {
  bases: B[];
  priceForTerm: (b: B, term: number) => number;
  pctForTerm: (term: number) => number;
  repairSub: number;
  onPick: (base: B, term: number, covers: PlanOfferCover[] | null) => void;
  onCustom: () => void;
  onClose: () => void;
  /** PROPOSAL-EQUIPMENT-02: the home's systems — tap any off; the prices follow (per system, every extra at the second-system discount). */
  custno: string | null; locno: string | null; secondDisc: number;
}) {
  const [coversState, setCoversState] = useState<PlanOfferCover[] | null>(null);
  const covers = useTechPlanCovers(custno, locno, coversState);
  const systems = planSystemsCount(covers);
  const mult = planSystemsMultiplier(systems, secondDisc);
  const priceAt = (b: B, t: number) => Math.round(priceForTerm(b, t) * mult * 100) / 100;
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-slate-900">Add a maintenance plan</div>
          <div className="text-[11px] text-slate-500">Member pricing applies to this invoice</div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {covers.length > 0 && (
          <section className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-4">
            <div className="flex items-center justify-between text-[10.5px] font-bold uppercase tracking-wide text-emerald-800/70">
              <span>Covers</span>
              <span>{systems} system{systems === 1 ? "" : "s"}{systems > 1 ? ` · each extra ${Math.round(secondDisc * 100)}% off` : ""}</span>
            </div>
            <div className="mt-1.5 space-y-1.5">
              {covers.map((c) => (
                <UnitPickRow key={c.counter} kind={c.kind} label={c.kind === "ac" ? "A/C" : c.label} loc={c.loc}
                  brand={c.brand ?? null} model={c.model ?? null} serial={c.serial ?? null} year={c.year ?? null}
                  on={c.on} tone="plan"
                  onToggle={() => { const next = covers.map((x) => (x.counter === c.counter ? { ...x, on: !x.on } : x)); if (next.some((x) => x.on)) setCoversState(next); }} />
              ))}
            </div>
            <p className="mt-2 text-[10.5px] leading-snug text-emerald-800/70">Tap a unit off to leave it out — the plan is priced per system.</p>
          </section>
        )}
        {bases.map((b) => {
          const terms = [1, 2, 3, 5, 10].filter((t) => priceForTerm(b, t) > 0);
          if (terms.length === 0) return null;
          return (
            <section key={b.id} className="rounded-3xl border border-amber-300/70 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-amber-600" />
                <h4 className="text-[14px] font-extrabold text-slate-900">{b.name}</h4>
              </div>
              <div className="mt-2.5 space-y-1.5">
                {terms.map((t) => {
                  const price = priceAt(b, t);
                  const pct = pctForTerm(t);
                  const saves = Math.round(Math.max(0, repairSub) * pct * 100) / 100;
                  return (
                    <button key={t} type="button" onClick={() => onPick(b, t, covers.length ? covers : null)}
                      className="flex w-full items-center gap-2 rounded-2xl border border-slate-200 px-3.5 py-3 text-left active:bg-slate-50">
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-extrabold text-slate-900">{t} year{t > 1 ? "s" : ""} · {money$(price)}</span>
                        <span className="block text-[11px] text-amber-700/80">
                          {Math.round(pct * 100)}% off repairs{saves > 0 ? ` — saves ${money$(saves)} on today's` : " from today"}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
        <button type="button" onClick={onCustom}
          className="flex w-full items-center gap-2.5 rounded-3xl border border-black/[0.08] bg-white p-4 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
          <Sparkles className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-bold text-slate-800">Build a custom plan</span>
            <span className="block text-[11px] text-slate-400">Add-ons, second system, à la carte</span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
        </button>
        <p className="px-2 text-center text-[10.5px] leading-snug text-slate-400">
          Activates when you finish this call.
        </p>
      </div>
    </div>
  );
}

/** Pricebook search → add a line. Big targets, part # + stock + price shown. */
function PartSearchAdd({ onAdd }: { onAdd: (l: BuilderLine) => void }) {
  const [q, setQ] = useState("");
  const { data: hits, isFetching } = useItemSearch(q);
  return (
    <div className="mt-2.5">
      <div className="relative">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search parts — name, model, part #…"
          className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25"
        />
        {isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-300" />}
      </div>
      {q.trim().length >= 2 && (hits?.length ?? 0) > 0 && (
        <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto">
          {(hits ?? []).map((it) => (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => {
                  onAdd({
                    kind: "part",
                    description: [it.description, it.model].filter(Boolean).join(" ") || it.part_number || "Part",
                    qty: 1,
                    unitPrice: it.base_price != null ? Number(it.base_price) : 0,
                    print: true,
                    item_id: it.id,
                  });
                  setQ("");
                }}
                className="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left active:bg-slate-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-bold text-slate-800">{it.description ?? it.part_number}</div>
                  <div className="truncate text-[10px] text-slate-400">
                    {[it.part_number ? `#${it.part_number}` : null, it.qty_in_stock != null ? `${it.qty_in_stock} in stock` : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span className="shrink-0 text-xs font-extrabold tabular-nums text-slate-700">
                  {it.base_price != null ? money$(Number(it.base_price)) : "—"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => onAdd({ kind: "custom", description: "Misc / describe on review", qty: 1, unitPrice: 0, print: true })}
        className="mt-1.5 text-xs font-bold text-slate-400 underline-offset-2 active:underline"
      >
        + add a blank line
      </button>
    </div>
  );
}

/* ================================================= ONCALL HUB (M2.5) ==== */
/* Seth 2026-08-28: the truck stops being blind outside its own day. A
 * Customers tab (search anything → the same customer picture the call shows →
 * add a customer → BOOK a call onto my own board), and an On-Call tab where
 * the answering service's pages land — qualify, book onto me, hold for the
 * office with a note, or close it out; the office's morning on-call panel
 * reads the exact same rows, so dispatch walks in to a told story. */

const RECENT_CUST_KEY = "pegasus.m.recentcust";
function readRecentCust(): CustHit[] {
  try { const v = JSON.parse(localStorage.getItem(RECENT_CUST_KEY) ?? "[]"); return Array.isArray(v) ? v.slice(0, 8) : []; } catch { return []; }
}
function pushRecentCust(h: CustHit) {
  try {
    const cur = readRecentCust().filter((x) => x.custno !== h.custno);
    localStorage.setItem(RECENT_CUST_KEY, JSON.stringify([h, ...cur].slice(0, 8)));
  } catch { /* fine */ }
}

interface CustHit {
  custno: string;
  locno: string | null;
  name: string;
  address: string | null;
  city: string | null;
  phone: string | null;
  src: "esc" | "pegasus";
}

function useMobileCustomerSearch(term: string) {
  return useQuery({
    queryKey: ["m_cust_search", term.trim().toLowerCase()],
    enabled: term.trim().length >= 2,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CustHit[]> => {
      // SEARCH-M2 (Seth 2026-08-31: "3141 Dean Court #604… says no customer in
      // the system" — but they ARE): the old single-phrase match couldn't hit
      // "Larson,Jeff & Deborah" from "Jeff Larson", and "#604" broke it.
      // v2: split into WORDS, every word must match SOMEWHERE on the record
      // (name in any order, street + unit, city). "#" is stripped so unit
      // numbers behave.
      const t = term.trim().replace(/[,#%_()]/g, " ").replace(/\s+/g, " ").trim();
      const like = `%${t}%`;
      const digits = t.replace(/[^0-9]/g, "");
      const phoneLike = digits.length >= 7 ? `%${digits.slice(0, 3)}%${digits.slice(3, 6)}%${digits.slice(6)}%` : null;
      const tokens = t.split(" ").filter((w) => w.length >= 2 || /\d/.test(w)).slice(0, 5);
      // Each token ANDs on (chained .or groups AND together in PostgREST).
      let qC = supabase.from("mirror_customer").select("custno, fullname, add1, city, phone1").limit(12);
      let qL = supabase.from("mirror_location").select("custno, locno, locname, add1, city, email").limit(10);
      let qP = supabase.from("pegasus_customer").select("custno, fullname, add1, city, phone, email").limit(10);
      if (tokens.length > 1) {
        for (const w of tokens) {
          const wl = `%${w}%`;
          qC = qC.or(`fullname.ilike.${wl},add1.ilike.${wl},city.ilike.${wl},phone1.ilike.${wl},phone2.ilike.${wl}`);
          qL = qL.or(`locname.ilike.${wl},add1.ilike.${wl},city.ilike.${wl},email.ilike.${wl}`);
          qP = qP.or(`fullname.ilike.${wl},add1.ilike.${wl},city.ilike.${wl},phone.ilike.${wl},email.ilike.${wl}`);
        }
      } else {
        qC = qC.or([
          `fullname.ilike.${like}`, `add1.ilike.${like}`, `phone1.ilike.${like}`, `phone2.ilike.${like}`,
          ...(phoneLike ? [`phone1.ilike.${phoneLike}`, `phone2.ilike.${phoneLike}`] : []),
        ].join(","));
        qL = qL.or(`add1.ilike.${like},locname.ilike.${like},email.ilike.${like}`);
        qP = qP.or(`fullname.ilike.${like},add1.ilike.${like},phone.ilike.${like},email.ilike.${like}`);
      }
      const [{ data: mc }, { data: ml }, { data: pc }] = await Promise.all([qC, qL, qP]);
      const out = new Map<string, CustHit>();
      for (const r of (mc ?? []) as { custno: string; fullname: string | null; add1: string | null; city: string | null; phone1: string | null }[]) {
        out.set(r.custno, { custno: r.custno, locno: null, name: displayCustomerName(null, r.fullname) || r.custno, address: r.add1, city: r.city, phone: r.phone1, src: "esc" });
      }
      const missing: string[] = [];
      for (const r of (ml ?? []) as { custno: string; locno: string; locname: string | null; add1: string | null; city: string | null }[]) {
        if (!out.has(r.custno)) {
          out.set(r.custno, { custno: r.custno, locno: r.locno, name: r.locname || r.custno, address: r.add1, city: r.city, phone: null, src: "esc" });
          missing.push(r.custno);
        }
      }
      if (missing.length) {
        const { data: names } = await supabase.from("mirror_customer").select("custno, fullname, phone1").in("custno", missing);
        for (const n of (names ?? []) as { custno: string; fullname: string | null; phone1: string | null }[]) {
          const hit = out.get(n.custno);
          if (hit && n.fullname) { hit.name = displayCustomerName(null, n.fullname) || hit.name; hit.phone = hit.phone ?? n.phone1; }
        }
      }
      for (const r of (pc ?? []) as { custno: string; fullname: string | null; add1: string | null; city: string | null; phone: string | null }[]) {
        if (!out.has(r.custno)) out.set(r.custno, { custno: r.custno, locno: null, name: r.fullname || r.custno, address: r.add1, city: r.city, phone: r.phone, src: "pegasus" });
      }
      return [...out.values()].slice(0, 20);
    },
  });
}

/** A customer (no dispatch) dressed as a Stop so the in-call tabs — Customer /
 *  Equipment / History — render EXACTLY the picture a service call shows. */
function custToStop(c: CustHit): Stop {
  return {
    key: `cust:${c.custno}`, kind: "pegasus", ref: `cust:${c.custno}`, no: "",
    band: 0, time: null, window: null, status: null,
    name: c.name, custno: c.custno, locno: c.locno,
    address: c.address, city: c.city, zip: null, phone: c.phone,
    lat: null, lng: null, notes: null,
    quoted: null, payStatus: null, serviceType: null, serviceTypeId: null, po: null,
    pseudo: false, pickup: false, done: false, selfBooked: false, part: null,
    jobId: null, visitRole: null, jobVisit: false,
  };
}

function useSvcTypesM() {
  return useQuery({
    queryKey: ["m_svc_types"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("service_type").select("id, name, category").eq("active", true).order("category").order("sort_order");
      return (data ?? []) as { id: string; name: string; category: string }[];
    },
  });
}

const M_WINDOWS = ["ANYTIME", "8-12", "10-2", "12-4", "2-6", "4-8"];

/** Book a call — the tech's version of Create Dispatch. Defaults to MY board,
 *  today; or hand it to the office to schedule. */
function BookCallSheet({ cust, empno, techFirst, prefillSummary, prefillPhone, onClose, onBooked }: {
  cust: { custno: string | null; locno: string | null; name: string; address: string | null; city: string | null; phone: string | null };
  empno: string | null;
  techFirst: string | null;
  prefillSummary?: string | null;
  prefillPhone?: string | null;
  onClose: () => void;
  onBooked?: () => void;
}) {
  const qc = useQueryClient();
  const types = useSvcTypesM().data ?? [];
  const [typeId, setTypeId] = useState<string>("");
  const [summary, setSummary] = useState(prefillSummary ?? "");
  const [notes, setNotes] = useState("");
  const [quoted, setQuoted] = useState("");
  const [po, setPo] = useState("");
  const [day, setDay] = useState(todayISO());
  const [at, setAt] = useState("");
  const [win, setWin] = useState("ANYTIME");
  const [selfSchedule, setSelfSchedule] = useState(true);
  const [saved, setSaved] = useState(false);
  const selType = types.find((t) => t.id === typeId) ?? null;

  const book = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const row: Record<string, unknown> = {
        lane_key: "incoming",
        custno: cust.custno, locno: cust.locno,
        customer_name: cust.name,
        address: cust.address, city: cust.city,
        phone: (prefillPhone ?? cust.phone) || null,
        summary: (summary.trim() || selType?.name || "Service call").slice(0, 200),
        notes: notes.trim() || null,
        urgency: "soon",
        service_type_id: selType?.id ?? null,
        service_type_name: selType?.name ?? null,
        quoted_amount: Number(quoted) > 0 ? Number(quoted) : null,
        po_number: po.trim() || null,
        tags: [],
        source: "tech_mobile",
        created_by: email, created_at: now, updated_at: now,
      };
      if (selfSchedule && empno) {
        Object.assign(row, {
          status: "scheduled", assigned_empno: empno,
          scheduled_date: day, scheduled_window: win === "ANYTIME" ? null : win,
          scheduled_time: at || null,
          scheduled_at: now, scheduled_by: email,
        });
      } else {
        Object.assign(row, { status: "pending_schedule", target_date: day || null, target_window: win === "ANYTIME" ? null : win });
      }
      const { error } = await supabase.from("pegasus_dispatch").insert(row);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      setSaved(true);
      onBooked?.();
    },
  });

  if (saved) {
    return (
      <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col items-center justify-center bg-[#f6f5f1] p-8 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"><Check className="h-8 w-8 text-emerald-600" /></span>
        <h3 className="mt-4 text-xl font-extrabold text-slate-900">{selfSchedule ? "On your board" : "Sent to the office"}</h3>
        <p className="mt-2 max-w-[32ch] text-sm leading-relaxed text-slate-500">
          {selfSchedule
            ? "It's on your Today and on the office board."
            : "Sent to the office queue — dispatch schedules it in the morning."}
        </p>
        <button type="button" onClick={onClose} className="mt-6 w-full rounded-2xl bg-gradient-to-b from-red-600 to-red-700 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_rgba(185,28,28,0.5)] transition-transform [touch-action:manipulation] active:scale-[0.98] text-[15px] font-bold text-white active:opacity-90">Done</button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold text-slate-900">Book a Call</div>
            <div className="truncate text-[11px] text-slate-500">{cust.name}{cust.address ? ` · ${cust.address}` : ""}</div>
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 pb-8">
        <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">What Kind of Call</h4>
          <select value={typeId} onChange={(e) => { setTypeId(e.target.value); const tt = types.find((x) => x.id === e.target.value); if (tt && !summary.trim()) setSummary(tt.name); }}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none">
            <option value="">Pick the service type…</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What's going on (one line)"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none" />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes for the ticket (optional)"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-red-500 focus:outline-none" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[12.5px] font-semibold text-slate-600">Quoted $ (optional)</span>
              <input type="number" inputMode="decimal" value={quoted} onChange={(e) => setQuoted(e.target.value)} placeholder="139"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-red-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-[12.5px] font-semibold text-slate-600">PO / job # (optional)</span>
              <input value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO-48211"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-red-500 focus:outline-none" />
            </label>
          </div>
        </section>

        <section className="rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">When</h4>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none" />
            <input type="time" value={at} onChange={(e) => setAt(e.target.value)}
              title="Arrival time — sets where this lands in YOUR running order (optional)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none" />
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {M_WINDOWS.map((w) => (
              <button key={w} type="button" onClick={() => setWin(w)}
                className={cn("rounded-full px-3 py-1.5 font-mono text-[12px] font-extrabold", win === w ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 active:bg-slate-200")}>
                {w}
              </button>
            ))}
          </div>
        </section>

        <button type="button" onClick={() => setSelfSchedule((v) => !v)}
          className={cn("flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left", selfSchedule ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white")}>
          <span className={cn("relative h-7 w-12 shrink-0 rounded-full transition-colors", selfSchedule ? "bg-emerald-500" : "bg-slate-300")}>
            <span className={cn("absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all", selfSchedule ? "left-[22px]" : "left-0.5")} />
          </span>
          <span className="min-w-0 flex-1">
            <span className={cn("block text-[13px] font-extrabold", selfSchedule ? "text-emerald-900" : "text-slate-600")}>
              {selfSchedule ? `On MY schedule (${techFirst ?? "me"})` : "Office schedules it"}
            </span>
            <span className={cn("block text-[11px] leading-snug", selfSchedule ? "text-emerald-700" : "text-slate-400")}>
              {selfSchedule ? "Added to your Today and the office board." : "Sent to the office queue for dispatch to schedule."}
            </span>
          </span>
        </button>

        <button type="button" disabled={book.isPending || (!summary.trim() && !selType)}
          onClick={() => book.mutate()}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-4 text-[15px] font-bold text-white active:opacity-90 disabled:opacity-50">
          {book.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
          {selfSchedule ? "Book It On My Board" : "Send To The Office"}
        </button>
        {book.isError && <p className="text-center text-xs font-semibold text-red-600">Didn&apos;t save — check signal and try again.</p>}
      </div>
    </div>
  );
}

/** The customer, full-screen — the SAME tabs a service call shows. */
function CustomerSheet({ hit, empno, techFirst, onClose }: {
  hit: CustHit;
  empno: string | null;
  techFirst: string | null;
  onClose: () => void;
}) {
  const s = custToStop(hit);
  const [ctab, setCtab] = useState<"customer" | "equipment" | "history">("customer");
  const [booking, setBooking] = useState(false);
  return (
    <div className="fixed inset-0 z-40 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2 px-3 pt-2.5">
          <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[17px] font-extrabold leading-tight text-slate-900">{hit.name}</div>
            <div className="truncate text-[12px] font-semibold text-slate-500">{[hit.address, hit.city].filter(Boolean).join(", ") || `#${hit.custno}`}</div>
          </div>
        </div>
        <div className="flex gap-1 px-3 pb-2 pt-2">
          {([["customer", "Customer"], ["equipment", "Equipment"], ["history", "History"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setCtab(k)}
              className={cn("flex-1 rounded-full py-1.5 text-[12.5px] font-bold transition-colors", ctab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 active:bg-slate-200")}>
              {label}
            </button>
          ))}
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 pb-24">
        {ctab === "customer" ? <CustomerTab s={s} /> : ctab === "equipment" ? <EquipmentTabM s={s} empno={empno} techFirst={techFirst} /> : <HistoryTab s={s} />}
      </div>
      <div className="border-t border-slate-200 bg-white/95 p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button type="button" onClick={() => setBooking(true)}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-b from-red-600 to-red-700 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_rgba(185,28,28,0.5)] transition-transform [touch-action:manipulation] active:scale-[0.98] text-[15px] font-bold text-white active:opacity-90">
          <CalendarDays className="h-4 w-4" /> Book a Call for {hit.name.split(/\s+/)[0]}
        </button>
      </div>
      {booking && (
        <BookCallSheet cust={hit} empno={empno} techFirst={techFirst} onClose={() => setBooking(false)} />
      )}
    </div>
  );
}

/** Add a customer from the truck — dedup-checked, then straight to booking. */
function MobileAddCustomer({ empno, techFirst, onClose, onCreated }: {
  empno: string | null;
  techFirst: string | null;
  onClose: () => void;
  onCreated: (hit: CustHit) => void;
}) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [add1, setAdd1] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  void empno; void techFirst;

  const { data: dupes } = useQuery({
    queryKey: ["m_cust_dupes", phone, add1, city],
    enabled: !!(phone.trim().length >= 7 || add1.trim().length >= 5),
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data } = await supabase.rpc("find_customer_matches", { p_phone: phone, p_add1: add1, p_city: city });
      return (data ?? []) as { custno: string; name: string; address: string; src: string; match_on: string }[];
    },
  });

  const create = useMutation({
    mutationFn: async (): Promise<CustHit> => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "truck";
      const nextNo = async (): Promise<string> => {
        const { data } = await supabase.from("pegasus_customer").select("custno").like("custno", "N%").order("custno", { ascending: false }).limit(1);
        const maxN = data?.[0]?.custno ? parseInt(String(data[0].custno).slice(1), 10) || 0 : 0;
        return "N" + String(maxN + 1).padStart(6, "0");
      };
      const fullname = [first.trim(), last.trim()].filter(Boolean).join(" ") || phone.trim() || "New customer";
      const row = (custno: string) => ({
        custno, firstname: first.trim() || null, lastname: last.trim() || null, fullname,
        phone: phone.trim() || null, email: email.trim() || null,
        add1: add1.trim() || null, city: city.trim() || null, state: "MN", zip: zip.trim() || null,
        source: "tech_mobile", active: true, created_by: who,
      });
      let custno = await nextNo();
      let { error } = await supabase.from("pegasus_customer").insert(row(custno));
      if (error && error.code === "23505") { custno = await nextNo(); ({ error } = await supabase.from("pegasus_customer").insert(row(custno))); }
      if (error) throw error;
      return { custno, locno: null, name: fullname, address: add1.trim() || null, city: city.trim() || null, phone: phone.trim() || null, src: "pegasus" };
    },
    onSuccess: (hit) => onCreated(hit),
  });

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none";
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="text-base font-bold text-slate-900">New Customer</div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 pb-8">
        <section className="space-y-2 rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <div className="grid grid-cols-2 gap-2">
            <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" autoFocus className={inp} />
            <input value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" className={inp} />
          </div>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="Phone" className={inp} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email (optional)" className={inp} />
          <AddressAutocomplete value={add1} onChange={setAdd1}
            onResolved={(a) => { setAdd1(a.add1); if (a.city) setCity(a.city); if (a.zip) setZip(a.zip); }}
            placeholder="Street address — Google suggests as you type" className={inp} />
          <div className="grid grid-cols-2 gap-2">
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className={inp} />
            <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="Zip" className={inp} />
          </div>
        </section>

        {dupes && dupes.length > 0 && (
          <section className="rounded-3xl border border-amber-200 bg-amber-50 p-3.5">
            <div className="text-xs font-extrabold text-amber-800">Might already be in the system:</div>
            <ul className="mt-1 space-y-1">
              {dupes.slice(0, 3).map((dd) => (
                <li key={dd.custno}>
                  <button type="button"
                    onClick={() => onCreated({ custno: dd.custno, locno: null, name: dd.name, address: dd.address || null, city: null, phone: null, src: dd.src === "pegasus" ? "pegasus" : "esc" })}
                    className="text-left text-xs font-semibold text-amber-900 underline-offset-2 active:underline">
                    {dd.name} · {dd.address || dd.custno} <span className="font-normal text-amber-700">(matched {dd.match_on})</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[10px] text-amber-700">Tap one to open it instead of creating a duplicate.</p>
          </section>
        )}

        <button type="button" disabled={create.isPending || !(first.trim() || last.trim() || phone.trim())}
          onClick={() => create.mutate()}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-4 text-[15px] font-bold text-white active:opacity-90 disabled:opacity-50">
          {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Create Customer
        </button>
        {create.isError && <p className="text-center text-xs font-semibold text-red-600">Didn&apos;t save — try again.</p>}
      </div>
    </div>
  );
}

function CustomersPane({ empno, techFirst, seed, onSeedUsed }: {
  empno: string | null;
  techFirst: string | null;
  seed: string | null;
  onSeedUsed: () => void;
}) {
  const [term, setTerm] = useState("");
  const [openHit, setOpenHit] = useState<CustHit | null>(null);
  const [adding, setAdding] = useState(false);
  const [recents, setRecents] = useState<CustHit[]>(readRecentCust);
  const openCustomer = (h: CustHit) => { pushRecentCust(h); setRecents(readRecentCust()); setOpenHit(h); };
  useEffect(() => {
    if (seed) { setTerm(seed); onSeedUsed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);
  const results = useMobileCustomerSearch(term);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Name, address, phone, or email…"
          className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 pl-10 pr-3 text-[15px] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] focus:border-red-500 focus:outline-none"
        />
      </div>

      {term.trim().length < 2 ? (
        recents.length > 0 ? (
          <div className="rounded-3xl border border-black/[0.08] bg-white p-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
            <div className="flex items-baseline justify-between px-0.5">
              <span className="text-[15px] font-bold tracking-tight text-slate-900">Recent</span>
              <button type="button" onClick={() => { try { localStorage.removeItem(RECENT_CUST_KEY); } catch { /* fine */ } setRecents([]); }}
                className="text-[10px] font-semibold text-slate-300 active:text-slate-500">clear</button>
            </div>
            <ul className="mt-1 divide-y divide-slate-100">
              {recents.map((h) => (
                <li key={h.custno}>
                  <button type="button" onClick={() => openCustomer(h)} className="flex w-full items-center gap-2.5 py-2.5 text-left active:opacity-80">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold text-slate-800">{h.name}</span>
                      <span className="block truncate text-[11px] text-slate-500">{[h.address, h.city].filter(Boolean).join(", ") || `#${h.custno}`}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <EmptyCard title="Look anyone up" sub="Search by name, street, phone or email." />
        )
      ) : results.isLoading ? (
        <div className="h-24 animate-pulse rounded-3xl bg-slate-200/60" />
      ) : (results.data ?? []).length === 0 ? (
        <EmptyCard title="No matches" sub="Check the spelling — or add them as a new customer below." />
      ) : (
        <ul className="space-y-2">
          {(results.data ?? []).map((h) => (
            <li key={h.custno}>
              <button type="button" onClick={() => openCustomer(h)}
                className="flex w-full items-center gap-3 rounded-3xl border border-black/[0.08] bg-white px-4 py-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-bold text-slate-900">{h.name}</span>
                  <span className="block truncate text-xs text-slate-500">{[h.address, h.city].filter(Boolean).join(", ") || `#${h.custno}`}</span>
                </span>
                {h.src === "pegasus" && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">Pegasus</span>}
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button type="button" onClick={() => setAdding(true)}
        className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 py-3.5 text-sm font-bold text-slate-600 active:bg-slate-50">
        <UserPlus className="h-4 w-4 text-red-600" /> Add a New Customer
      </button>

      {openHit && <CustomerSheet hit={openHit} empno={empno} techFirst={techFirst} onClose={() => setOpenHit(null)} />}
      {adding && (
        <MobileAddCustomer empno={empno} techFirst={techFirst} onClose={() => setAdding(false)}
          onCreated={(hit) => { setAdding(false); openCustomer(hit); }} />
      )}
    </div>
  );
}

/* -------------------------------------------------------- ON-CALL PANE -- */

/** ONCALL-PHONE-01 (Seth 2026-09-23: "maybe we only show same day and sales
 *  requests from the on call center. Hold for offices don't need to be shown
 *  to the techs"). The phone lists only the answering-service classes the
 *  office picked in Settings → On-Call (default Same Day + Sales) from the
 *  last few days (default 3). Everything else stays with the office on Today. */
function useOncallRequests() {
  const rules = useOncallPhoneRules();
  const classes = [...rules.classes].sort();
  return useQuery({
    queryKey: ["m_oncall_requests", classes.join(","), rules.days],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      // A page's row can be written up to a night after the call (the 7 AM
      // sheet), so the server cut gets a day of slack; the exact cut is by
      // the time the call came in, below.
      const since = new Date(Date.now() - (rules.days + 1) * 86400000).toISOString();
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .contains("tags", ["oncall"])
        .overlaps("tags", classes)
        .eq("status", "pending_schedule")
        .is("deleted_at", null)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      const cut = Date.now() - rules.days * 86400000;
      return ((data ?? []) as PendingTicket[]).filter((t) => {
        // The answering service's class decides — a tech's Hold For Office
        // puts the hold tag on top of a same-day page and must not hide it.
        if (!classes.includes(oncallClassOf(t.tags))) return false;
        const iso = oncallCameIn(t);
        const at = iso ? new Date(iso).getTime() : NaN;
        return isNaN(at) || at >= cut;
      });
    },
  });
}

/** Still waiting on someone: not booked, not closed, not held for the office. */
function oncallIsOpen(t: PendingTicket): boolean {
  const tg = t.tags ?? [];
  return tg.includes("needs_triage") && !tg.includes("no_service_needed") && !tg.includes("ran_overnight") && !heldByTech(tg);
}

/** ONCALL v10 (Seth 2026-09-01: "a little badge instead of the schedule
 *  first"): PLUS members wear a compact gold badge by their name — the LIST
 *  stays strictly oldest-first so no caller ever gets skipped. */
const oncallCameIn = (t: PendingTicket): string | null => {
  const iso = t.source_received_at || t.created_at || null; // reliable ISO
  const taken = t.oncall_detail?.taken_at?.replace(/(\d)(AM|PM)\b/i, "$1 $2") ?? null;
  if (taken && !isNaN(new Date(taken).getTime())) return new Date(taken).toISOString();
  return iso;
};

/** "4 min" · "2h 5m" · "1d 3h" — how long the caller has been waiting. */
function waitLabel(mins: number): string {
  if (mins < 60) return `${Math.max(1, Math.round(mins))} min`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ${Math.floor(mins % 60)}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
}

/** When the call came in and how long the caller has waited — quiet grey
 *  text, red once it passes four hours (GLASS v2: status is a word, not a box). */
function OncallWaitLine({ t }: { t: PendingTicket }) {
  const iso = oncallCameIn(t);
  if (!iso) return null;
  const at = new Date(iso);
  if (isNaN(at.getTime())) return null;
  const mins = Math.max(0, (Date.now() - at.getTime()) / 60000);
  const sameDay = isoDay(at) === isoDay(new Date());
  const when = `${sameDay ? "" : at.toLocaleDateString("en-US", { weekday: "short" }) + " "}${at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  const hot = mins >= 240;
  return (
    <span className={cn("inline-flex items-center gap-1.5", hot ? "font-semibold text-[var(--m-bad)]" : "text-[var(--m-ink3)]")}>
      <Clock className="h-3.5 w-3.5" />
      Came in {when} · waiting {waitLabel(mins)}
    </span>
  );
}

/** The words for the empty state: "same-day or sales". */
const ONCALL_KIND_WORD: Record<OncallClass, string> = {
  same_day: "same-day", sales: "sales", service_request: "service", hold_for_office: "hold-for-office", plumbing: "plumbing",
};
function oncallKinds(classes: OncallClass[]): string {
  const w = ONCALL_CLASS_OPTIONS.filter((o) => classes.includes(o.key)).map((o) => ONCALL_KIND_WORD[o.key]);
  return w.length <= 1 ? (w[0] ?? "") : `${w.slice(0, -1).join(", ")} or ${w[w.length - 1]}`;
}

function usePlusCustnos(custnos: string[]) {
  const key = [...custnos].sort().join(",");
  return useQuery({
    queryKey: ["m_oncall_plus", key],
    enabled: custnos.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Set<string>> => {
      const today = todayISO();
      const { data } = await supabase.from("mirror_serv_agr")
        .select("custno, expire_date, sa_inactive")
        .in("custno", custnos).eq("sa_inactive", 0).gte("expire_date", today);
      return new Set(((data ?? []) as { custno: string }[]).map((r) => r.custno));
    },
  });
}

/** Bottom action sheet — the reason gets asked AFTER the button, and closing a
 *  call out ALWAYS carries one (Seth 2026-08-31: "I'd like the tech to have to
 *  give a reason why service isn't needed"). */
function OncallNoteSheet({ title, hint, placeholder, defaultNote, requireNote, confirmLabel, danger, onConfirm, onClose }: {
  title: string;
  hint: string;
  placeholder?: string;
  defaultNote?: string;
  requireNote: boolean;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState(defaultNote ?? "");
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col justify-end bg-black/45" onClick={onClose}>
      <div className="rounded-t-3xl bg-white p-4 pb-[max(env(safe-area-inset-bottom),16px)] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.35)]" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-200" />
        <h3 className="text-[16px] font-extrabold text-slate-900">{title}</h3>
        <p className="mt-0.5 text-[12px] leading-snug text-slate-500">{hint}</p>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus
          placeholder={placeholder ?? "What did you and the customer decide?"}
          className="mt-3 w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-3 text-[15px] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25" />
        {requireNote && !note.trim() && <p className="mt-1 text-[11px] font-semibold text-amber-600">A reason is required — the office reads these in the morning.</p>}
        <div className="mt-3 flex gap-2">
          <button type="button" disabled={requireNote && !note.trim()}
            onClick={() => onConfirm(note.trim())}
            className={cn("flex-1 rounded-2xl py-3.5 text-[15px] font-bold text-white [touch-action:manipulation] active:opacity-90 disabled:opacity-40",
              danger ? "bg-gradient-to-b from-red-600 to-red-700" : "bg-slate-900")}>
            {confirmLabel}
          </button>
          <button type="button" onClick={onClose} className="rounded-2xl px-4 py-3.5 text-[15px] font-bold text-slate-400 [touch-action:manipulation] active:bg-slate-100">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function OncallPane({ empno, techFirst, onQualify, onOpenCalendar }: {
  empno: string | null;
  techFirst: string | null;
  onQualify: (term: string) => void;
  onOpenCalendar: () => void;
}) {
  const qc = useQueryClient();
  const rules = useOncallPhoneRules();
  const reqs = useOncallRequests();
  const [openHit, setOpenHit] = useState<CustHit | null>(null);
  const [booking, setBooking] = useState<PendingTicket | null>(null);
  const [sheet, setSheet] = useState<{ kind: "hold" | "noservice" | "note"; t: PendingTicket } | null>(null);
  const [showHandled, setShowHandled] = useState(false);

  const stamp = () => {
    const d = new Date();
    return `${techFirst ?? "Tech"} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  };
  const update = useMutation({
    mutationFn: async (v: { id: string; patch: Record<string, unknown> }) => {
      const { error } = await supabase.from("pegasus_dispatch").update({ ...v.patch, updated_at: new Date().toISOString() }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m_oncall_requests"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
    },
  });
  const appendNote = (t: PendingTicket, text: string, extra?: Record<string, unknown>) => {
    const merged = `${(t.notes ?? "").trim()}\n— ${stamp()}: ${text}`.trim();
    update.mutate({ id: t.id, patch: { notes: merged, ...(extra ?? {}) } });
  };

  const all = reqs.data ?? [];
  const open = all.filter(oncallIsOpen);
  const handled = all.filter((t) => !oncallIsOpen(t)).slice(0, 12);

  // PLUS = a gold badge on the card (v10) — the list itself stays strictly
  // oldest-first so nobody gets skipped.
  const plusSet = usePlusCustnos([...new Set(open.map((t) => t.custno).filter(Boolean))] as string[]).data;
  const isPlus = (t: PendingTicket) => !!t.custno && !!plusSet?.has(t.custno);
  const openSorted = [...open].sort((a, b) => {
    const ta = oncallCameIn(a) ?? "9999", tb = oncallCameIn(b) ?? "9999";
    return ta.localeCompare(tb); // oldest waiting first
  });
  // One section per kind of call, in the office's order (Same Day first) —
  // the heading says "Same Day", so no card needs a red SAME DAY pill.
  const groups = ONCALL_CLASS_OPTIONS
    .filter((o) => rules.classes.includes(o.key))
    .map((o) => ({ key: o.key, label: o.label, items: openSorted.filter((t) => oncallClassOf(t.tags) === o.key) }))
    .filter((g) => g.items.length > 0);

  const od = (t: PendingTicket) => t.oncall_detail;
  const openAccount = (t: PendingTicket) => {
    const phone = t.phone ?? od(t)?.phone ?? null;
    const hh: CustHit = { custno: t.custno!, locno: t.locno, name: t.customer_name || t.custno!, address: t.address, city: t.city, phone, src: "esc" };
    pushRecentCust(hh);
    setOpenHit(hh);
  };

  return (
    <div className="space-y-3">
      {/* GLASS v2: the big title already says On-Call — the card that repeated
          it (with a sentence under it) is gone; the calendar line leads. */}
      <OncallTabLine empno={empno} onOpen={onOpenCalendar} />

      {reqs.isLoading ? (
        <div className="h-44 animate-pulse rounded-[22px] bg-[var(--m-soft)]" />
      ) : open.length === 0 ? (
        <EmptyCard Icon={Moon} title="No calls waiting" sub={`When the answering service sends a ${oncallKinds(rules.classes)} call, it lands here.`} />
      ) : (
        groups.map((g) => (
          <section key={g.key} aria-label={g.label}>
            <div className="flex items-baseline justify-between px-1 pb-2 pt-1.5">
              <h2 className="text-[17px] font-semibold tracking-tight text-[var(--m-ink)]">{g.label}</h2>
              <span className="text-[13px] tabular-nums text-[var(--m-ink3)]">{g.items.length}</span>
            </div>
            <div className="space-y-3">
              {g.items.map((t) => {
                const d = od(t);
                const words = oncallCallerWords(t.notes) || t.summary;
                const phone = t.phone ?? d?.phone ?? null;
                const plus = isPlus(t);
                const addr = [t.address ?? d?.address, t.city ?? d?.city].filter(Boolean).join(", ");
                return (
                  <article key={t.id} className="m-card p-4">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <h3 className="text-[17px] font-bold leading-snug tracking-tight text-[var(--m-ink)]">{t.customer_name || d?.caller_name || "Caller"}</h3>
                      {/* ONCALL v11: solid gold = PLUS (the one color on the card besides the action). */}
                      {plus && (
                        <span title="PLUS member — priority customer" className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full bg-amber-400 px-2.5 text-[11.5px] font-extrabold uppercase tracking-wide text-amber-950">
                          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} /> Plus
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[14px] leading-snug text-[var(--m-ink2)]">{addr || "No address given"}</p>
                    <p className="mt-3 rounded-2xl bg-[var(--m-soft)] px-3.5 py-2.5 text-[15px] leading-relaxed text-[var(--m-ink)]">&ldquo;{words}&rdquo;</p>
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
                      <OncallWaitLine t={t} />
                      {!t.custno && <span className="text-[var(--m-ink3)]">Not matched to a customer</span>}
                    </div>
                    {/* One blue action (Book Onto Me), one tint (Call), and the
                        two other outcomes as soft glass — the reason is asked
                        AFTER the tap, in a sheet (Seth 2026-08-31). */}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {phone && (
                        <a href={`tel:${phone.replace(/[^0-9]/g, "")}`} className="m-btn m-btn-tint">
                          <Phone className="h-[18px] w-[18px]" /> Call Them
                        </a>
                      )}
                      <button type="button" onClick={() => setBooking(t)} className={cn("m-btn m-btn-primary", !phone && "col-span-2")}>
                        <CalendarDays className="h-[18px] w-[18px]" /> Book Onto Me
                      </button>
                      <button type="button" onClick={() => setSheet({ kind: "hold", t })} className="m-btn m-btn-soft">
                        Hold For Office
                      </button>
                      <button type="button" onClick={() => setSheet({ kind: "noservice", t })} className="m-btn m-btn-soft">
                        No Service Needed
                      </button>
                    </div>
                    <div className="mt-1 flex items-center justify-center">
                      {t.custno ? (
                        <button type="button" onClick={() => openAccount(t)}
                          className="flex items-center gap-1.5 px-3 py-2.5 text-[14px] font-semibold text-[var(--m-ink2)] [touch-action:manipulation] active:opacity-60">
                          <User className="h-4 w-4" /> View Their Account
                        </button>
                      ) : (
                        <button type="button" onClick={() => onQualify((t.customer_name || d?.caller_name || t.address || d?.address || phone || "").trim())}
                          className="flex items-center gap-1.5 px-3 py-2.5 text-[14px] font-semibold text-[var(--m-ink2)] [touch-action:manipulation] active:opacity-60">
                          <Search className="h-4 w-4" /> Qualify
                        </button>
                      )}
                      <span className="text-[var(--m-ink3)]" aria-hidden>·</span>
                      <button type="button" onClick={() => setSheet({ kind: "note", t })}
                        className="flex items-center gap-1.5 px-3 py-2.5 text-[14px] font-semibold text-[var(--m-ink2)] [touch-action:manipulation] active:opacity-60">
                        <PenLine className="h-4 w-4" /> Add a Note
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))
      )}

      {handled.length > 0 && (
        <section>
          <button type="button" onClick={() => setShowHandled((v) => !v)} aria-expanded={showHandled}
            className="flex w-full items-baseline justify-between px-1 pb-2 pt-3 [touch-action:manipulation]">
            <h2 className="text-[17px] font-semibold tracking-tight text-[var(--m-ink)]">Handled</h2>
            <span className="flex items-center gap-1 text-[13px] tabular-nums text-[var(--m-ink3)]">
              {handled.length}
              <ChevronRight className={cn("h-4 w-4 transition-transform", showHandled && "rotate-90")} />
            </span>
          </button>
          {showHandled && (
            <div className="m-group">
              {handled.map((t) => {
                const tg = t.tags ?? [];
                const booked = tg.includes("ran_overnight");
                const label = booked ? "Booked ✓" : tg.includes("no_service_needed") ? "Closed" : heldByTech(tg) ? "Held for Office" : "With the Office";
                return (
                  <div key={t.id} className="m-row flex items-center gap-3 px-4 py-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-[var(--m-ink)]">{t.customer_name || "Caller"}</span>
                      <span className="block truncate text-[13px] text-[var(--m-ink3)]">{oncallCallerWords(t.notes) || t.summary}</span>
                    </span>
                    <span className={cn("shrink-0 text-[13px] font-semibold", booked ? "text-[var(--m-good)]" : "text-[var(--m-ink3)]")}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* The reason sheets — tap the action, THEN say why. */}
      {sheet?.kind === "hold" && (
        <OncallNoteSheet
          title="Hold it for the office"
          hint="What did you and the customer decide? Dispatch reads this in the morning."
          defaultNote="Spoke to customer — office to schedule in the morning."
          requireNote
          confirmLabel="Hold For Office"
          onConfirm={(note) => {
            const t = sheet.t;
            appendNote(t, note, { tags: [...new Set([...(t.tags ?? []), "hold_for_office"])] });
            setSheet(null);
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === "noservice" && (
        <OncallNoteSheet
          title="Close it — no service needed"
          hint="Why is no visit needed? This closes the page with your name on it; the office sees the reason."
          placeholder="e.g. Walked them through relighting the pilot — running fine now."
          requireNote
          confirmLabel="Close It Out"
          onConfirm={(note) => {
            const t = sheet.t;
            // The answering service's class tag stays (same as the office's
            // Not needed) — it is a fact about the call, not its status.
            const base = (t.tags ?? []).filter((x) => x !== "needs_triage");
            appendNote(t, note, {
              tags: [...new Set([...base, "no_service_needed"])],
              resolved_at: new Date().toISOString(),
              resolved_by: empno,
            });
            setSheet(null);
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === "note" && (
        <OncallNoteSheet
          title="Add a note"
          hint="Rides on this request — the office sees it with your name and the time."
          requireNote
          confirmLabel="Save The Note"
          onConfirm={(note) => { appendNote(sheet.t, note); setSheet(null); }}
          onClose={() => setSheet(null)}
        />
      )}

      {openHit && <CustomerSheet hit={openHit} empno={empno} techFirst={techFirst} onClose={() => setOpenHit(null)} />}
      {booking && (
        <BookCallSheet
          cust={{ custno: booking.custno, locno: booking.locno, name: booking.customer_name || od(booking)?.caller_name || "New caller", address: booking.address ?? od(booking)?.address ?? null, city: booking.city ?? od(booking)?.city ?? null, phone: booking.phone ?? od(booking)?.phone ?? null }}
          empno={empno}
          techFirst={techFirst}
          prefillSummary={oncallCallerWords(booking.notes) || booking.summary}
          onClose={() => setBooking(null)}
          onBooked={() => {
            const t = booking;
            const base = (t.tags ?? []).filter((x) => x !== "needs_triage");
            const merged = `${(t.notes ?? "").trim()}\n— ${stamp()}: booked onto my board from the truck.`.trim();
            update.mutate({ id: t.id, patch: { notes: merged, tags: [...new Set([...base, "ran_overnight"])], resolved_at: new Date().toISOString(), resolved_by: empno } });
          }}
        />
      )}
    </div>
  );
}


/* ---------------------------------------- flags ON the call (visibility) -- */

/** The part tickets / return visits flagged FROM this call — visible, editable,
 *  cancelable right where they were born (Seth 2026-08-28). */
function useCallFlags(originRef: string) {
  return useQuery({
    queryKey: ["m_call_flags", originRef],
    staleTime: 20 * 1000,
    refetchInterval: 45 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const { data } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("origin_dispatch", originRef)
        .in("lane_key", ["order_parts", "return_visit"])
        .neq("status", "cancelled")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      return (data ?? []) as PendingTicket[];
    },
  });
}

function FlagEditSheet({ flag, onClose }: { flag: PendingTicket; onClose: () => void }) {
  const qc = useQueryClient();
  const isParts = flag.lane_key === "order_parts";
  const [summary, setSummary] = useState(flag.summary);
  const [partNo, setPartNo] = useState(flag.part_number ?? "");
  const [notes, setNotes] = useState(flag.notes ?? "");
  const save = useMutation({
    mutationFn: async (v: Record<string, unknown>) => {
      const { error } = await supabase.from("pegasus_dispatch").update({ ...v, updated_at: new Date().toISOString() }).eq("id", flag.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m_call_flags"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      onClose();
    },
  });
  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[15px] focus:border-red-500 focus:outline-none";
  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="border-b border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <div className="text-base font-bold text-slate-900">{isParts ? "Part Ticket" : "Return Visit"}</div>
            {isParts && flag.part_state && <div className="text-[11px] font-semibold text-slate-500">{PART_STATE_LABEL[flag.part_state]}{flag.part_po ? ` · PO ${flag.part_po}` : ""}</div>}
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 pb-8">
        <section className="space-y-2 rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]">
          <label className="block">
            <span className="text-[12.5px] font-semibold text-slate-600">{isParts ? "What part" : "What for"}</span>
            <input value={summary} onChange={(e) => setSummary(e.target.value)} className={cn(inp, "mt-1")} />
          </label>
          {isParts && (
            <label className="block">
              <span className="text-[12.5px] font-semibold text-slate-600">Part number (if you have it)</span>
              <input value={partNo} onChange={(e) => setPartNo(e.target.value)} className={cn(inp, "mt-1")} />
            </label>
          )}
          <label className="block">
            <span className="text-[12.5px] font-semibold text-slate-600">Notes for the office</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className={cn(inp, "mt-1")} />
          </label>
        </section>
        <button type="button" disabled={save.isPending || !summary.trim()}
          onClick={() => save.mutate({ summary: summary.trim(), part_number: isParts ? (partNo.trim() || null) : flag.part_number, notes: notes.trim() || null })}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-4 text-[15px] font-bold text-white active:opacity-90 disabled:opacity-50">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Changes
        </button>
        <button type="button" disabled={save.isPending}
          onClick={() => {
            if (!window.confirm(isParts ? "Cancel this part ticket? The office will no longer see it." : "Cancel this return visit?")) return;
            save.mutate({ status: "cancelled" });
          }}
          className="w-full rounded-2xl border border-red-200 bg-red-50 py-3 text-[13px] font-bold text-red-700 active:bg-red-100">
          Cancel This {isParts ? "Part Ticket" : "Return Visit"}
        </button>
      </div>
    </div>
  );
}

function CallFlagsList({ flags }: { flags: PendingTicket[] }) {
  const [editing, setEditing] = useState<PendingTicket | null>(null);
  if (flags.length === 0) return null;
  return (
    <>
      <ul className="space-y-1.5">
        {flags.map((f) => {
          const isParts = f.lane_key === "order_parts";
          return (
            <li key={f.id}>
              <button type="button" onClick={() => setEditing(f)}
                className="flex w-full items-center gap-2.5 rounded-2xl border border-black/[0.08] bg-white px-3.5 py-2.5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)] active:bg-slate-50">
                {isParts ? <Package className="h-4 w-4 shrink-0 text-red-500" /> : <RotateCcw className="h-4 w-4 shrink-0 text-slate-400" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-bold text-slate-800">{f.summary}</span>
                  <span className="block text-[10.5px] text-slate-400">{isParts ? (f.part_state ? PART_STATE_LABEL[f.part_state] : "Sent") : "Return visit — office schedules"}{f.part_po ? ` · PO ${f.part_po}` : ""}</span>
                </span>
                <span className="shrink-0 text-[10px] font-bold text-slate-400">Edit ✎</span>
              </button>
            </li>
          );
        })}
      </ul>
      {editing && <FlagEditSheet flag={editing} onClose={() => setEditing(null)} />}
    </>
  );
}


/** One chat message's attachment — image inline, anything else a tap-to-open
 *  chip. Same chat-files bucket + signed URLs as the office dock. */
function MAttachment({ d, mine }: { d: Dm; mine: boolean }) {
  const { data: url } = useQuery({
    queryKey: ["chat_att", d.attachment_path],
    enabled: !!d.attachment_path,
    staleTime: 45 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.storage.from("chat-files").createSignedUrl(d.attachment_path!, 60 * 60);
      return data?.signedUrl ?? null;
    },
  });
  if (!d.attachment_path) return null;
  const isImage = (d.attachment_type ?? "").startsWith("image/");
  if (isImage && url) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={d.attachment_name ?? "attachment"} className="mt-1 max-h-52 max-w-full rounded-xl object-cover" />
      </a>
    );
  }
  return (
    <a href={url ?? "#"} target="_blank" rel="noreferrer"
      className={cn("mt-1 flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[12px] font-semibold",
        mine ? "bg-white/15 text-white" : "bg-slate-100 text-slate-700")}>
      📎 <span className="min-w-0 truncate">{d.attachment_name ?? "Attachment"}</span>
    </a>
  );
}
